/**
 * Enum for date format patterns to avoid string literals
 */ var DateFormatPattern;
(function(DateFormatPattern) {
    /** Full date with time (e.g., "January 1st 2023, 3:30 PM") */ DateFormatPattern["FullDateTime"] = "MMMM Do YYYY, h:mmA";
    /** Full date with seconds (e.g., "January 1st 2023, 3:30:45 pm") */ DateFormatPattern["FullDateTimeWithSeconds"] = "MMMM Do YYYY, h:mm:ss a";
    /** Credit card expiry format (e.g., "01/23") */ DateFormatPattern["CardExpiry"] = "MM/YY";
    /** Time only with AM/PM (e.g., "3:30 PM") */ DateFormatPattern["TimeOnly"] = "h:mm A";
    /** ISO-like date and time (e.g., "2023-01-01 15:30:00") */ DateFormatPattern["IsoDateTime"] = "YYYY-MM-DD HH:mm:ss";
    /** Date and time with slashes (e.g., "01/01/2023 03:30 pm") */ DateFormatPattern["SlashDateTime"] = "MM/DD/YYYY hh:mm a";
    /** Short date format (e.g., "01/01/23") */ DateFormatPattern["ShortDate"] = "MM/DD/YY";
    /** Month and day with ordinal (e.g., "January 1st") */ DateFormatPattern["MonthDayWithOrdinal"] = "MMMM Do";
    /** Month and day with ordinal and year (e.g., "January 1st, 2023") */ DateFormatPattern["MonthDayWithOrdinalAndYear"] = "MMMM Do, YYYY";
})(DateFormatPattern || (DateFormatPattern = {}));
/**
 * Formats a date according to the specified format.
 * This function maintains compatibility with the existing helper.ts dateFormat method.
 *
 * @param value - The date to format (string or Date object)
 * @param type - The format type to use (predefined type or custom string)
 * @param format - Optional format string or DateFormatPattern enum value when type is 'format'
 * @param locale - The locale to use for formatting (defaults to 'en-US')
 * @param skipLocale - Whether to use UTC time (true) or local time (false)
 * @returns The formatted date string
 */ function dateFormat(value, type = 'calendar', format, locale = 'en-US', skipLocale = false) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    const now = new Date();
    // Default format if not provided
    if (!format && type === 'format') {
        format = DateFormatPattern.FullDateTime;
    }
    // Helper function to pad numbers with leading zeros
    const pad = (num)=>{
        return num.toString().padStart(2, '0');
    };
    // Helper function for suffixes (1st, 2nd, 3rd)
    const getOrdinalSuffix = (day)=>{
        if (day > 3 && day < 21) return `${day}th`;
        switch(day % 10){
            case 1:
                return `${day}st`;
            case 2:
                return `${day}nd`;
            case 3:
                return `${day}rd`;
            default:
                return `${day}th`;
        }
    };
    // Format time component (h:mm A)
    const formatTime = (d)=>{
        const hours = d.getHours() % 12 || 12;
        const minutes = pad(d.getMinutes());
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        return `${hours}:${minutes} ${ampm}`;
    };
    // Format full date with time
    const formatFullDateTime = (d, includeSeconds = false)=>{
        const month = d.toLocaleString(locale, {
            month: 'long'
        });
        const day = getOrdinalSuffix(d.getDate());
        const year = d.getFullYear();
        const hours = d.getHours() % 12 || 12;
        const minutes = pad(d.getMinutes());
        const seconds = includeSeconds ? `:${pad(d.getSeconds())}` : '';
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        return `${month} ${day} ${year}, ${hours}:${minutes}${seconds} ${ampm}`;
    };
    // Get time difference for relative time display
    const getTimeDiff = (date)=>{
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours < 24) {
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
        }
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        return days === 1 ? '1 day ago' : `${days} days ago`;
    };
    // Check if two dates are the same day
    const isSameDay = (d1, d2)=>{
        return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    };
    // Get yesterday and tomorrow
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    // Handle UTC vs local time
    const getDateToUse = ()=>{
        if (skipLocale) {
            // Use UTC time
            const utcDate = new Date(date.getTime());
            utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset());
            return utcDate;
        }
        // Use local time
        return date;
    };
    const dateToUse = getDateToUse();
    switch(type){
        case 'calendar':
            if (isSameDay(dateToUse, now)) return `Today at ${formatTime(dateToUse)}`;
            if (isSameDay(dateToUse, yesterday)) return `Yesterday at ${formatTime(dateToUse)}`;
            if (isSameDay(dateToUse, tomorrow)) return `Tomorrow at ${formatTime(dateToUse)}`;
            // For dates within the next week
            {
                const nextWeek = new Date(now);
                nextWeek.setDate(now.getDate() + 7);
                if (dateToUse > now && dateToUse < nextWeek) {
                    return dateToUse.toLocaleDateString(locale, {
                        weekday: 'long'
                    }) + ` at ${formatTime(dateToUse)}`;
                }
            }
            // Default to full date format
            return formatFullDateTime(dateToUse);
        case 'calendarPrice':
            if (isSameDay(dateToUse, now)) {
                return getTimeDiff(dateToUse);
            }
            if (isSameDay(dateToUse, yesterday)) {
                return `Yesterday at ${formatTime(dateToUse)}`;
            }
            return formatFullDateTime(dateToUse);
        case 'format':
            if (!format) {
                format = DateFormatPattern.FullDateTime;
            }
            switch(format){
                case DateFormatPattern.FullDateTime:
                    return formatFullDateTime(dateToUse);
                case DateFormatPattern.FullDateTimeWithSeconds:
                    return formatFullDateTime(dateToUse, true);
                case DateFormatPattern.CardExpiry:
                    return `${pad(dateToUse.getMonth() + 1)}/${dateToUse.getFullYear().toString().slice(2)}`;
                case DateFormatPattern.TimeOnly:
                    return formatTime(dateToUse);
                case DateFormatPattern.IsoDateTime:
                    return `${dateToUse.getFullYear()}-${pad(dateToUse.getMonth() + 1)}-${pad(dateToUse.getDate())} ${pad(dateToUse.getHours())}:${pad(dateToUse.getMinutes())}:${pad(dateToUse.getSeconds())}`;
                case DateFormatPattern.SlashDateTime:
                    {
                        const hours = dateToUse.getHours() % 12 || 12;
                        const ampm = dateToUse.getHours() >= 12 ? 'pm' : 'am';
                        return `${pad(dateToUse.getMonth() + 1)}/${pad(dateToUse.getDate())}/${dateToUse.getFullYear()} ${pad(hours)}:${pad(dateToUse.getMinutes())} ${ampm}`;
                    }
                case DateFormatPattern.ShortDate:
                    return `${pad(dateToUse.getMonth() + 1)}/${pad(dateToUse.getDate())}/${dateToUse.getFullYear().toString().slice(2)}`;
                case DateFormatPattern.MonthDayWithOrdinal:
                    {
                        const day = dateToUse.getDate();
                        const month = dateToUse.toLocaleString('en-US', {
                            month: 'long'
                        });
                        return `${month} ${getOrdinalSuffix(day)}`;
                    }
                case DateFormatPattern.MonthDayWithOrdinalAndYear:
                    {
                        const day = dateToUse.getDate();
                        const month = dateToUse.toLocaleString('en-US', {
                            month: 'long'
                        });
                        const year = dateToUse.getFullYear();
                        return `${month} ${getOrdinalSuffix(day)}, ${year}`;
                    }
            }
            // Fallback to default format
            return formatFullDateTime(dateToUse);
        case 'dateFromNow':
            {
                const diff = now.getTime() - dateToUse.getTime();
                const minutes = Math.floor(diff / (1000 * 60));
                if (minutes < 60) {
                    return minutes <= 1 ? '1 minute ago' : `${minutes} minutes ago`;
                }
                const hours = Math.floor(diff / (1000 * 60 * 60));
                if (hours < 24) {
                    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
                }
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                return days === 1 ? '1 day ago' : `${days} days ago`;
            }
        case 'timeless':
            return dateToUse.toLocaleDateString(locale, {
                month: 'long',
                year: 'numeric'
            });
        default:
            // For backward compatibility, treat unknown types as format strings
            return formatFullDateTime(dateToUse);
    }
}
/**
 * Convert a Unix timestamp to a formatted date and time string
 * @param timestamp - Unix timestamp (seconds since epoch)
 * @returns Formatted date and time string
 */ function timestampToDateHour(timestamp) {
    const date = new Date(timestamp * 1000);
    const options = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    };
    return date.toLocaleString('en-US', options);
}
/**
 * Generate an array of formatted hours for a time range
 * @param startHour - Starting hour (0-23)
 * @param maxHour - Ending hour (0-23)
 * @returns Array of formatted time strings
 */ function getArrayOfHours(startHour, maxHour) {
    const hours = [];
    const options = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };
    for(let hour = startHour; hour < maxHour; hour++){
        const date = new Date(0, 0, 0);
        date.setHours(hour, 0, 0, 0);
        hours.push(date.toLocaleString('en-US', options));
        date.setMinutes(30);
        hours.push(date.toLocaleString('en-US', options));
    }
    return hours;
}
/**
 * Check if a banner should reappear based on milestone dates
 * @param closedDate - Date when the banner was closed
 * @param closedReappearanceDate - Last date when the banner reappeared
 * @returns Whether the banner should reappear
 */ function shouldShowBanner(closedDate, closedReappearanceDate) {
    if (!closedDate) return false;
    const milestones = [
        6,
        12,
        24,
        48
    ]; // Months
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const closedDateObj = new Date(closedDate);
    closedDateObj.setHours(0, 0, 0, 0);
    const lastReappearance = closedReappearanceDate ? new Date(closedReappearanceDate) : null;
    if (lastReappearance) {
        lastReappearance.setHours(0, 0, 0, 0);
    }
    return milestones.some((months)=>{
        const milestoneDate = new Date(closedDateObj);
        milestoneDate.setMonth(closedDateObj.getMonth() + months);
        return isSameDay(now, milestoneDate) && (!lastReappearance || !isSameDay(lastReappearance, milestoneDate));
    });
}
/**
 * Check if two dates are the same day
 * @param d1 - First date
 * @param d2 - Second date
 * @returns Whether the dates are the same day
 */ function isSameDay(d1, d2) {
    return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}
/**
 * Returns a YYYY-MM-DD formatted string from a valid date string
 * @param dateString - The date string to format
 * @returns Returns YYYY-MM-DD if valid, otherwise null
 */ function formatDateYYYYMMDD(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return null;
    }
    return date.toISOString().slice(0, 10);
}
/**
 * A class that provides a chainable API for date operations
 */ class DateChain {
    date;
    constructor(date){
        this.date = date ? new Date(date) : new Date();
    }
    /**
     * Converts UTC time to local time
     * @returns This instance for chaining
     */ local() {
        // Convert from UTC to local by subtracting the timezone offset
        const localDate = new Date(this.date);
        localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
        this.date = localDate;
        return this;
    }
    /**
     * Formats the date according to the specified format
     * @param formatStr - The format string or DateFormatPattern enum value
     * @returns The formatted date string
     */ format(formatStr) {
        return dateFormat(this.date, 'format', formatStr);
    }
    /**
     * Returns the timezone abbreviation
     * @returns The timezone abbreviation (e.g., "EST", "PST")
     */ zoneAbbr() {
        // Get timezone abbreviation using Intl.DateTimeFormat
        const options = {
            timeZoneName: 'short'
        };
        const tz = new Intl.DateTimeFormat('en-US', options).formatToParts(this.date).find((part)=>part.type === 'timeZoneName')?.value || '';
        return tz;
    }
    /**
     * Adds the specified amount of time to the date
     * @param amount - The amount to add
     * @param unit - The unit of time
     * @returns This instance for chaining
     */ add(amount, unit) {
        const date = new Date(this.date);
        switch(unit){
            case 'year':
            case 'years':
                date.setFullYear(date.getFullYear() + amount);
                break;
            case 'months':
                date.setMonth(date.getMonth() + amount);
                break;
            case 'days':
                date.setDate(date.getDate() + amount);
                break;
            case 'hours':
                date.setHours(date.getHours() + amount);
                break;
            case 'minutes':
                date.setMinutes(date.getMinutes() + amount);
                break;
            case 'seconds':
                date.setSeconds(date.getSeconds() + amount);
                break;
        }
        this.date = date;
        return this;
    }
    /**
     * Sets the minutes of the date
     * @param minutes - The minutes to set
     * @returns This instance for chaining
     */ minutes(minutes) {
        const date = new Date(this.date);
        date.setMinutes(minutes);
        return new DateChain(date);
    }
    /**
     * Sets the seconds of the date
     * @param seconds - The seconds to set
     * @returns This instance for chaining
     */ seconds(seconds) {
        const date = new Date(this.date);
        date.setSeconds(seconds);
        return new DateChain(date);
    }
    /**
     * Gets the minutes of the date
     * @returns The minutes
     */ getMinutes() {
        return this.date.getMinutes();
    }
    /**
     * Gets the hours of the date
     * @returns The hours
     */ getHours() {
        return this.date.getHours();
    }
    /**
     * Gets the hour of the date
     * @returns The hour
     */ hour() {
        return this.date.getHours();
    }
    /**
     * Gets the month of the date (0-11)
     * @returns The month
     */ month() {
        return this.date.getMonth();
    }
    /**
     * Gets the year of the date
     * @returns The year
     */ year() {
        return this.date.getFullYear();
    }
}
/**
 * Date utility object with chainable methods
 */ const date = {
    /**
     * Creates a DateChain instance with the current date or specified date in UTC
     * @param value - Optional date to convert to UTC (defaults to current date/time)
     * @returns A DateChain instance in UTC
     */ utc: function(value) {
        const instance = new DateChain(value);
        // Convert to UTC by adding the timezone offset
        const utcDate = new Date(instance.date);
        utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset());
        instance.date = utcDate;
        return instance;
    },
    /**
     * Creates a DateChain instance with the current date
     * @returns A DateChain instance
     */ tz: function() {
        return new DateChain();
    },
    /**
     * Creates a DateChain instance with the specified parameters
     * @param params - Object with hour, minute, etc.
     * @returns A new DateChain instance
     */ fromParams: function(params = {}) {
        const date = new Date();
        if (params.hour !== undefined) date.setHours(params.hour);
        if (params.minute !== undefined) date.setMinutes(params.minute);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return new DateChain(date);
    },
    /**
     * Parses a time string and returns a DateChain instance
     * @param timeString - The time string to parse
     * @param format - The format of the time string or DateFormatPattern enum value
     * @returns A DateChain instance
     */ parseTime: function(timeString, format) {
        return parseTime(timeString, format);
    }
};
/**
 * Creates a DateChain instance from a date
 * @param value - The date to use (defaults to current date/time)
 * @returns A new DateChain instance
 */ function createDate(value) {
    return new DateChain(value);
}
/**
 * Parses a time string and returns a DateChain instance
 * @param timeString - The time string to parse
 * @param format - The format of the time string or DateFormatPattern enum value
 * @returns A DateChain instance
 */ function parseTime(timeString, format) {
    // For 'hh:mm A' format (e.g., "03:30 PM")
    if (format === 'hh:mm A' || format === 'h:mm A' || format === DateFormatPattern.TimeOnly) {
        const [timePart, amPm] = timeString.split(' ');
        const [hourStr, minuteStr] = timePart.split(':');
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        // Handle AM/PM
        if (amPm.toUpperCase() === 'PM' && hour < 12) {
            hour += 12;
        } else if (amPm.toUpperCase() === 'AM' && hour === 12) {
            hour = 0;
        }
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return new DateChain(date);
    }
    // Fallback to default
    return new DateChain(timeString);
}
date.parseTime = parseTime;
/**
 * Check if the current date modified by the time parameter is before or after the date parameter
 * @param {Date} date
 * @param {number} time - The amount of time to subtract from the current date
 * @param {'seconds' | 'minutes' | 'hours' | 'days'} scale
 * @param {'before' | 'after'} type
 * @param {'add' | 'subtract'} action - Whether the time param should add or subtract the current date
 * @param {boolean} include - Whether the comparison should pass if the dates are equal
 * @returns {boolean} - The result of the comparison
 */ function dateRangeComparison(date, time, scale = 'seconds', type = 'before', action = 'subtract', include = true) {
    const SECOND = 1000;
    const MINUTE = SECOND * 60;
    const HOUR = MINUTE * 60;
    const DAY = HOUR * 24;
    let modDate = new Date(Date.now());
    switch(scale){
        case 'minutes':
            action === 'subtract' ? modDate = new Date(modDate.getTime() - MINUTE * time) : modDate = new Date(modDate.getTime() + MINUTE * time);
            break;
        case 'hours':
            action === 'subtract' ? modDate = new Date(modDate.getTime() - HOUR * time) : modDate = new Date(modDate.getTime() + HOUR * time);
            break;
        case 'days':
            action === 'subtract' ? modDate = new Date(modDate.getTime() - DAY * time) : modDate = new Date(modDate.getTime() + DAY * time);
            break;
        default:
            action === 'subtract' ? modDate = new Date(modDate.getTime() - SECOND * time) : modDate = new Date(modDate.getTime() + SECOND * time);
            break;
    }
    if (type === 'before') {
        if (include) return modDate <= date;
        return modDate < date;
    }
    if (include) return modDate >= date;
    return modDate > date;
}

/**
 * Debounces a function to be executed after a specified wait time.
 * @param {(...args: any[]) => void} func The function to be debounced.
 * @param {number} time The time to wait before executing the function.
 * @returns {DebouncedFunction} A debounced version of the function.
 */ function debounce(func, time) {
    let timeout;
    const debouncedFunction = (...args)=>{
        if (debouncedFunction.isDebouncing) {
            clearTimeout(timeout);
        }
        debouncedFunction.isDebouncing = true;
        debouncedFunction.isRunning = false;
        timeout = window.setTimeout(async ()=>{
            debouncedFunction.isRunning = true;
            await func(...args);
            debouncedFunction.isRunning = false;
            debouncedFunction.isDebouncing = false;
        }, time);
    };
    debouncedFunction.isDebouncing = false;
    debouncedFunction.isRunning = false;
    debouncedFunction.cancel = ()=>{
        if (debouncedFunction.isDebouncing) {
            clearTimeout(timeout);
            debouncedFunction.isDebouncing = false;
            debouncedFunction.isRunning = false;
        }
    };
    return debouncedFunction;
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
  if (Object.prototype.hasOwnProperty.call(n, '__esModule')) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			if (this instanceof a) {
        return Reflect.construct(f, arguments, this.constructor);
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var qrcode$1 = {exports: {}};

var hasRequiredQrcode;

function requireQrcode () {
	if (hasRequiredQrcode) return qrcode$1.exports;
	hasRequiredQrcode = 1;
	(function (module, exports) {
		//---------------------------------------------------------------------
		//
		// QR Code Generator for JavaScript
		//
		// Copyright (c) 2009 Kazuhiko Arase
		//
		// URL: http://www.d-project.com/
		//
		// Licensed under the MIT license:
		//  http://www.opensource.org/licenses/mit-license.php
		//
		// The word 'QR Code' is registered trademark of
		// DENSO WAVE INCORPORATED
		//  http://www.denso-wave.com/qrcode/faqpatent-e.html
		//
		//---------------------------------------------------------------------

		var qrcode = function() {

		  //---------------------------------------------------------------------
		  // qrcode
		  //---------------------------------------------------------------------

		  /**
		   * qrcode
		   * @param typeNumber 1 to 40
		   * @param errorCorrectionLevel 'L','M','Q','H'
		   */
		  var qrcode = function(typeNumber, errorCorrectionLevel) {

		    var PAD0 = 0xEC;
		    var PAD1 = 0x11;

		    var _typeNumber = typeNumber;
		    var _errorCorrectionLevel = QRErrorCorrectionLevel[errorCorrectionLevel];
		    var _modules = null;
		    var _moduleCount = 0;
		    var _dataCache = null;
		    var _dataList = [];

		    var _this = {};

		    var makeImpl = function(test, maskPattern) {

		      _moduleCount = _typeNumber * 4 + 17;
		      _modules = function(moduleCount) {
		        var modules = new Array(moduleCount);
		        for (var row = 0; row < moduleCount; row += 1) {
		          modules[row] = new Array(moduleCount);
		          for (var col = 0; col < moduleCount; col += 1) {
		            modules[row][col] = null;
		          }
		        }
		        return modules;
		      }(_moduleCount);

		      setupPositionProbePattern(0, 0);
		      setupPositionProbePattern(_moduleCount - 7, 0);
		      setupPositionProbePattern(0, _moduleCount - 7);
		      setupPositionAdjustPattern();
		      setupTimingPattern();
		      setupTypeInfo(test, maskPattern);

		      if (_typeNumber >= 7) {
		        setupTypeNumber(test);
		      }

		      if (_dataCache == null) {
		        _dataCache = createData(_typeNumber, _errorCorrectionLevel, _dataList);
		      }

		      mapData(_dataCache, maskPattern);
		    };

		    var setupPositionProbePattern = function(row, col) {

		      for (var r = -1; r <= 7; r += 1) {

		        if (row + r <= -1 || _moduleCount <= row + r) continue;

		        for (var c = -1; c <= 7; c += 1) {

		          if (col + c <= -1 || _moduleCount <= col + c) continue;

		          if ( (0 <= r && r <= 6 && (c == 0 || c == 6) )
		              || (0 <= c && c <= 6 && (r == 0 || r == 6) )
		              || (2 <= r && r <= 4 && 2 <= c && c <= 4) ) {
		            _modules[row + r][col + c] = true;
		          } else {
		            _modules[row + r][col + c] = false;
		          }
		        }
		      }
		    };

		    var getBestMaskPattern = function() {

		      var minLostPoint = 0;
		      var pattern = 0;

		      for (var i = 0; i < 8; i += 1) {

		        makeImpl(true, i);

		        var lostPoint = QRUtil.getLostPoint(_this);

		        if (i == 0 || minLostPoint > lostPoint) {
		          minLostPoint = lostPoint;
		          pattern = i;
		        }
		      }

		      return pattern;
		    };

		    var setupTimingPattern = function() {

		      for (var r = 8; r < _moduleCount - 8; r += 1) {
		        if (_modules[r][6] != null) {
		          continue;
		        }
		        _modules[r][6] = (r % 2 == 0);
		      }

		      for (var c = 8; c < _moduleCount - 8; c += 1) {
		        if (_modules[6][c] != null) {
		          continue;
		        }
		        _modules[6][c] = (c % 2 == 0);
		      }
		    };

		    var setupPositionAdjustPattern = function() {

		      var pos = QRUtil.getPatternPosition(_typeNumber);

		      for (var i = 0; i < pos.length; i += 1) {

		        for (var j = 0; j < pos.length; j += 1) {

		          var row = pos[i];
		          var col = pos[j];

		          if (_modules[row][col] != null) {
		            continue;
		          }

		          for (var r = -2; r <= 2; r += 1) {

		            for (var c = -2; c <= 2; c += 1) {

		              if (r == -2 || r == 2 || c == -2 || c == 2
		                  || (r == 0 && c == 0) ) {
		                _modules[row + r][col + c] = true;
		              } else {
		                _modules[row + r][col + c] = false;
		              }
		            }
		          }
		        }
		      }
		    };

		    var setupTypeNumber = function(test) {

		      var bits = QRUtil.getBCHTypeNumber(_typeNumber);

		      for (var i = 0; i < 18; i += 1) {
		        var mod = (!test && ( (bits >> i) & 1) == 1);
		        _modules[Math.floor(i / 3)][i % 3 + _moduleCount - 8 - 3] = mod;
		      }

		      for (var i = 0; i < 18; i += 1) {
		        var mod = (!test && ( (bits >> i) & 1) == 1);
		        _modules[i % 3 + _moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
		      }
		    };

		    var setupTypeInfo = function(test, maskPattern) {

		      var data = (_errorCorrectionLevel << 3) | maskPattern;
		      var bits = QRUtil.getBCHTypeInfo(data);

		      // vertical
		      for (var i = 0; i < 15; i += 1) {

		        var mod = (!test && ( (bits >> i) & 1) == 1);

		        if (i < 6) {
		          _modules[i][8] = mod;
		        } else if (i < 8) {
		          _modules[i + 1][8] = mod;
		        } else {
		          _modules[_moduleCount - 15 + i][8] = mod;
		        }
		      }

		      // horizontal
		      for (var i = 0; i < 15; i += 1) {

		        var mod = (!test && ( (bits >> i) & 1) == 1);

		        if (i < 8) {
		          _modules[8][_moduleCount - i - 1] = mod;
		        } else if (i < 9) {
		          _modules[8][15 - i - 1 + 1] = mod;
		        } else {
		          _modules[8][15 - i - 1] = mod;
		        }
		      }

		      // fixed module
		      _modules[_moduleCount - 8][8] = (!test);
		    };

		    var mapData = function(data, maskPattern) {

		      var inc = -1;
		      var row = _moduleCount - 1;
		      var bitIndex = 7;
		      var byteIndex = 0;
		      var maskFunc = QRUtil.getMaskFunction(maskPattern);

		      for (var col = _moduleCount - 1; col > 0; col -= 2) {

		        if (col == 6) col -= 1;

		        while (true) {

		          for (var c = 0; c < 2; c += 1) {

		            if (_modules[row][col - c] == null) {

		              var dark = false;

		              if (byteIndex < data.length) {
		                dark = ( ( (data[byteIndex] >>> bitIndex) & 1) == 1);
		              }

		              var mask = maskFunc(row, col - c);

		              if (mask) {
		                dark = !dark;
		              }

		              _modules[row][col - c] = dark;
		              bitIndex -= 1;

		              if (bitIndex == -1) {
		                byteIndex += 1;
		                bitIndex = 7;
		              }
		            }
		          }

		          row += inc;

		          if (row < 0 || _moduleCount <= row) {
		            row -= inc;
		            inc = -inc;
		            break;
		          }
		        }
		      }
		    };

		    var createBytes = function(buffer, rsBlocks) {

		      var offset = 0;

		      var maxDcCount = 0;
		      var maxEcCount = 0;

		      var dcdata = new Array(rsBlocks.length);
		      var ecdata = new Array(rsBlocks.length);

		      for (var r = 0; r < rsBlocks.length; r += 1) {

		        var dcCount = rsBlocks[r].dataCount;
		        var ecCount = rsBlocks[r].totalCount - dcCount;

		        maxDcCount = Math.max(maxDcCount, dcCount);
		        maxEcCount = Math.max(maxEcCount, ecCount);

		        dcdata[r] = new Array(dcCount);

		        for (var i = 0; i < dcdata[r].length; i += 1) {
		          dcdata[r][i] = 0xff & buffer.getBuffer()[i + offset];
		        }
		        offset += dcCount;

		        var rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
		        var rawPoly = qrPolynomial(dcdata[r], rsPoly.getLength() - 1);

		        var modPoly = rawPoly.mod(rsPoly);
		        ecdata[r] = new Array(rsPoly.getLength() - 1);
		        for (var i = 0; i < ecdata[r].length; i += 1) {
		          var modIndex = i + modPoly.getLength() - ecdata[r].length;
		          ecdata[r][i] = (modIndex >= 0)? modPoly.getAt(modIndex) : 0;
		        }
		      }

		      var totalCodeCount = 0;
		      for (var i = 0; i < rsBlocks.length; i += 1) {
		        totalCodeCount += rsBlocks[i].totalCount;
		      }

		      var data = new Array(totalCodeCount);
		      var index = 0;

		      for (var i = 0; i < maxDcCount; i += 1) {
		        for (var r = 0; r < rsBlocks.length; r += 1) {
		          if (i < dcdata[r].length) {
		            data[index] = dcdata[r][i];
		            index += 1;
		          }
		        }
		      }

		      for (var i = 0; i < maxEcCount; i += 1) {
		        for (var r = 0; r < rsBlocks.length; r += 1) {
		          if (i < ecdata[r].length) {
		            data[index] = ecdata[r][i];
		            index += 1;
		          }
		        }
		      }

		      return data;
		    };

		    var createData = function(typeNumber, errorCorrectionLevel, dataList) {

		      var rsBlocks = QRRSBlock.getRSBlocks(typeNumber, errorCorrectionLevel);

		      var buffer = qrBitBuffer();

		      for (var i = 0; i < dataList.length; i += 1) {
		        var data = dataList[i];
		        buffer.put(data.getMode(), 4);
		        buffer.put(data.getLength(), QRUtil.getLengthInBits(data.getMode(), typeNumber) );
		        data.write(buffer);
		      }

		      // calc num max data.
		      var totalDataCount = 0;
		      for (var i = 0; i < rsBlocks.length; i += 1) {
		        totalDataCount += rsBlocks[i].dataCount;
		      }

		      if (buffer.getLengthInBits() > totalDataCount * 8) {
		        throw 'code length overflow. ('
		          + buffer.getLengthInBits()
		          + '>'
		          + totalDataCount * 8
		          + ')';
		      }

		      // end code
		      if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) {
		        buffer.put(0, 4);
		      }

		      // padding
		      while (buffer.getLengthInBits() % 8 != 0) {
		        buffer.putBit(false);
		      }

		      // padding
		      while (true) {

		        if (buffer.getLengthInBits() >= totalDataCount * 8) {
		          break;
		        }
		        buffer.put(PAD0, 8);

		        if (buffer.getLengthInBits() >= totalDataCount * 8) {
		          break;
		        }
		        buffer.put(PAD1, 8);
		      }

		      return createBytes(buffer, rsBlocks);
		    };

		    _this.addData = function(data, mode) {

		      mode = mode || 'Byte';

		      var newData = null;

		      switch(mode) {
		      case 'Numeric' :
		        newData = qrNumber(data);
		        break;
		      case 'Alphanumeric' :
		        newData = qrAlphaNum(data);
		        break;
		      case 'Byte' :
		        newData = qr8BitByte(data);
		        break;
		      case 'Kanji' :
		        newData = qrKanji(data);
		        break;
		      default :
		        throw 'mode:' + mode;
		      }

		      _dataList.push(newData);
		      _dataCache = null;
		    };

		    _this.isDark = function(row, col) {
		      if (row < 0 || _moduleCount <= row || col < 0 || _moduleCount <= col) {
		        throw row + ',' + col;
		      }
		      return _modules[row][col];
		    };

		    _this.getModuleCount = function() {
		      return _moduleCount;
		    };

		    _this.make = function() {
		      if (_typeNumber < 1) {
		        var typeNumber = 1;

		        for (; typeNumber < 40; typeNumber++) {
		          var rsBlocks = QRRSBlock.getRSBlocks(typeNumber, _errorCorrectionLevel);
		          var buffer = qrBitBuffer();

		          for (var i = 0; i < _dataList.length; i++) {
		            var data = _dataList[i];
		            buffer.put(data.getMode(), 4);
		            buffer.put(data.getLength(), QRUtil.getLengthInBits(data.getMode(), typeNumber) );
		            data.write(buffer);
		          }

		          var totalDataCount = 0;
		          for (var i = 0; i < rsBlocks.length; i++) {
		            totalDataCount += rsBlocks[i].dataCount;
		          }

		          if (buffer.getLengthInBits() <= totalDataCount * 8) {
		            break;
		          }
		        }

		        _typeNumber = typeNumber;
		      }

		      makeImpl(false, getBestMaskPattern() );
		    };

		    _this.createTableTag = function(cellSize, margin) {

		      cellSize = cellSize || 2;
		      margin = (typeof margin == 'undefined')? cellSize * 4 : margin;

		      var qrHtml = '';

		      qrHtml += '<table style="';
		      qrHtml += ' border-width: 0px; border-style: none;';
		      qrHtml += ' border-collapse: collapse;';
		      qrHtml += ' padding: 0px; margin: ' + margin + 'px;';
		      qrHtml += '">';
		      qrHtml += '<tbody>';

		      for (var r = 0; r < _this.getModuleCount(); r += 1) {

		        qrHtml += '<tr>';

		        for (var c = 0; c < _this.getModuleCount(); c += 1) {
		          qrHtml += '<td style="';
		          qrHtml += ' border-width: 0px; border-style: none;';
		          qrHtml += ' border-collapse: collapse;';
		          qrHtml += ' padding: 0px; margin: 0px;';
		          qrHtml += ' width: ' + cellSize + 'px;';
		          qrHtml += ' height: ' + cellSize + 'px;';
		          qrHtml += ' background-color: ';
		          qrHtml += _this.isDark(r, c)? '#000000' : '#ffffff';
		          qrHtml += ';';
		          qrHtml += '"/>';
		        }

		        qrHtml += '</tr>';
		      }

		      qrHtml += '</tbody>';
		      qrHtml += '</table>';

		      return qrHtml;
		    };

		    _this.createSvgTag = function(cellSize, margin, alt, title) {

		      var opts = {};
		      if (typeof arguments[0] == 'object') {
		        // Called by options.
		        opts = arguments[0];
		        // overwrite cellSize and margin.
		        cellSize = opts.cellSize;
		        margin = opts.margin;
		        alt = opts.alt;
		        title = opts.title;
		      }

		      cellSize = cellSize || 2;
		      margin = (typeof margin == 'undefined')? cellSize * 4 : margin;

		      // Compose alt property surrogate
		      alt = (typeof alt === 'string') ? {text: alt} : alt || {};
		      alt.text = alt.text || null;
		      alt.id = (alt.text) ? alt.id || 'qrcode-description' : null;

		      // Compose title property surrogate
		      title = (typeof title === 'string') ? {text: title} : title || {};
		      title.text = title.text || null;
		      title.id = (title.text) ? title.id || 'qrcode-title' : null;

		      var size = _this.getModuleCount() * cellSize + margin * 2;
		      var c, mc, r, mr, qrSvg='', rect;

		      rect = 'l' + cellSize + ',0 0,' + cellSize +
		        ' -' + cellSize + ',0 0,-' + cellSize + 'z ';

		      qrSvg += '<svg version="1.1" xmlns="http://www.w3.org/2000/svg"';
		      qrSvg += !opts.scalable ? ' width="' + size + 'px" height="' + size + 'px"' : '';
		      qrSvg += ' viewBox="0 0 ' + size + ' ' + size + '" ';
		      qrSvg += ' preserveAspectRatio="xMinYMin meet"';
		      qrSvg += (title.text || alt.text) ? ' role="img" aria-labelledby="' +
		          escapeXml([title.id, alt.id].join(' ').trim() ) + '"' : '';
		      qrSvg += '>';
		      qrSvg += (title.text) ? '<title id="' + escapeXml(title.id) + '">' +
		          escapeXml(title.text) + '</title>' : '';
		      qrSvg += (alt.text) ? '<description id="' + escapeXml(alt.id) + '">' +
		          escapeXml(alt.text) + '</description>' : '';
		      qrSvg += '<rect width="100%" height="100%" fill="white" cx="0" cy="0"/>';
		      qrSvg += '<path d="';

		      for (r = 0; r < _this.getModuleCount(); r += 1) {
		        mr = r * cellSize + margin;
		        for (c = 0; c < _this.getModuleCount(); c += 1) {
		          if (_this.isDark(r, c) ) {
		            mc = c*cellSize+margin;
		            qrSvg += 'M' + mc + ',' + mr + rect;
		          }
		        }
		      }

		      qrSvg += '" stroke="transparent" fill="black"/>';
		      qrSvg += '</svg>';

		      return qrSvg;
		    };

		    _this.createDataURL = function(cellSize, margin) {

		      cellSize = cellSize || 2;
		      margin = (typeof margin == 'undefined')? cellSize * 4 : margin;

		      var size = _this.getModuleCount() * cellSize + margin * 2;
		      var min = margin;
		      var max = size - margin;

		      return createDataURL(size, size, function(x, y) {
		        if (min <= x && x < max && min <= y && y < max) {
		          var c = Math.floor( (x - min) / cellSize);
		          var r = Math.floor( (y - min) / cellSize);
		          return _this.isDark(r, c)? 0 : 1;
		        } else {
		          return 1;
		        }
		      } );
		    };

		    _this.createImgTag = function(cellSize, margin, alt) {

		      cellSize = cellSize || 2;
		      margin = (typeof margin == 'undefined')? cellSize * 4 : margin;

		      var size = _this.getModuleCount() * cellSize + margin * 2;

		      var img = '';
		      img += '<img';
		      img += '\u0020src="';
		      img += _this.createDataURL(cellSize, margin);
		      img += '"';
		      img += '\u0020width="';
		      img += size;
		      img += '"';
		      img += '\u0020height="';
		      img += size;
		      img += '"';
		      if (alt) {
		        img += '\u0020alt="';
		        img += escapeXml(alt);
		        img += '"';
		      }
		      img += '/>';

		      return img;
		    };

		    var escapeXml = function(s) {
		      var escaped = '';
		      for (var i = 0; i < s.length; i += 1) {
		        var c = s.charAt(i);
		        switch(c) {
		        case '<': escaped += '&lt;'; break;
		        case '>': escaped += '&gt;'; break;
		        case '&': escaped += '&amp;'; break;
		        case '"': escaped += '&quot;'; break;
		        default : escaped += c; break;
		        }
		      }
		      return escaped;
		    };

		    var _createHalfASCII = function(margin) {
		      var cellSize = 1;
		      margin = (typeof margin == 'undefined')? cellSize * 2 : margin;

		      var size = _this.getModuleCount() * cellSize + margin * 2;
		      var min = margin;
		      var max = size - margin;

		      var y, x, r1, r2, p;

		      var blocks = {
		        '██': '█',
		        '█ ': '▀',
		        ' █': '▄',
		        '  ': ' '
		      };

		      var blocksLastLineNoMargin = {
		        '██': '▀',
		        '█ ': '▀',
		        ' █': ' ',
		        '  ': ' '
		      };

		      var ascii = '';
		      for (y = 0; y < size; y += 2) {
		        r1 = Math.floor((y - min) / cellSize);
		        r2 = Math.floor((y + 1 - min) / cellSize);
		        for (x = 0; x < size; x += 1) {
		          p = '█';

		          if (min <= x && x < max && min <= y && y < max && _this.isDark(r1, Math.floor((x - min) / cellSize))) {
		            p = ' ';
		          }

		          if (min <= x && x < max && min <= y+1 && y+1 < max && _this.isDark(r2, Math.floor((x - min) / cellSize))) {
		            p += ' ';
		          }
		          else {
		            p += '█';
		          }

		          // Output 2 characters per pixel, to create full square. 1 character per pixels gives only half width of square.
		          ascii += (margin < 1 && y+1 >= max) ? blocksLastLineNoMargin[p] : blocks[p];
		        }

		        ascii += '\n';
		      }

		      if (size % 2 && margin > 0) {
		        return ascii.substring(0, ascii.length - size - 1) + Array(size+1).join('▀');
		      }

		      return ascii.substring(0, ascii.length-1);
		    };

		    _this.createASCII = function(cellSize, margin) {
		      cellSize = cellSize || 1;

		      if (cellSize < 2) {
		        return _createHalfASCII(margin);
		      }

		      cellSize -= 1;
		      margin = (typeof margin == 'undefined')? cellSize * 2 : margin;

		      var size = _this.getModuleCount() * cellSize + margin * 2;
		      var min = margin;
		      var max = size - margin;

		      var y, x, r, p;

		      var white = Array(cellSize+1).join('██');
		      var black = Array(cellSize+1).join('  ');

		      var ascii = '';
		      var line = '';
		      for (y = 0; y < size; y += 1) {
		        r = Math.floor( (y - min) / cellSize);
		        line = '';
		        for (x = 0; x < size; x += 1) {
		          p = 1;

		          if (min <= x && x < max && min <= y && y < max && _this.isDark(r, Math.floor((x - min) / cellSize))) {
		            p = 0;
		          }

		          // Output 2 characters per pixel, to create full square. 1 character per pixels gives only half width of square.
		          line += p ? white : black;
		        }

		        for (r = 0; r < cellSize; r += 1) {
		          ascii += line + '\n';
		        }
		      }

		      return ascii.substring(0, ascii.length-1);
		    };

		    _this.renderTo2dContext = function(context, cellSize) {
		      cellSize = cellSize || 2;
		      var length = _this.getModuleCount();
		      for (var row = 0; row < length; row++) {
		        for (var col = 0; col < length; col++) {
		          context.fillStyle = _this.isDark(row, col) ? 'black' : 'white';
		          context.fillRect(row * cellSize, col * cellSize, cellSize, cellSize);
		        }
		      }
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // qrcode.stringToBytes
		  //---------------------------------------------------------------------

		  qrcode.stringToBytesFuncs = {
		    'default' : function(s) {
		      var bytes = [];
		      for (var i = 0; i < s.length; i += 1) {
		        var c = s.charCodeAt(i);
		        bytes.push(c & 0xff);
		      }
		      return bytes;
		    }
		  };

		  qrcode.stringToBytes = qrcode.stringToBytesFuncs['default'];

		  //---------------------------------------------------------------------
		  // qrcode.createStringToBytes
		  //---------------------------------------------------------------------

		  /**
		   * @param unicodeData base64 string of byte array.
		   * [16bit Unicode],[16bit Bytes], ...
		   * @param numChars
		   */
		  qrcode.createStringToBytes = function(unicodeData, numChars) {

		    // create conversion map.

		    var unicodeMap = function() {

		      var bin = base64DecodeInputStream(unicodeData);
		      var read = function() {
		        var b = bin.read();
		        if (b == -1) throw 'eof';
		        return b;
		      };

		      var count = 0;
		      var unicodeMap = {};
		      while (true) {
		        var b0 = bin.read();
		        if (b0 == -1) break;
		        var b1 = read();
		        var b2 = read();
		        var b3 = read();
		        var k = String.fromCharCode( (b0 << 8) | b1);
		        var v = (b2 << 8) | b3;
		        unicodeMap[k] = v;
		        count += 1;
		      }
		      if (count != numChars) {
		        throw count + ' != ' + numChars;
		      }

		      return unicodeMap;
		    }();

		    var unknownChar = '?'.charCodeAt(0);

		    return function(s) {
		      var bytes = [];
		      for (var i = 0; i < s.length; i += 1) {
		        var c = s.charCodeAt(i);
		        if (c < 128) {
		          bytes.push(c);
		        } else {
		          var b = unicodeMap[s.charAt(i)];
		          if (typeof b == 'number') {
		            if ( (b & 0xff) == b) {
		              // 1byte
		              bytes.push(b);
		            } else {
		              // 2bytes
		              bytes.push(b >>> 8);
		              bytes.push(b & 0xff);
		            }
		          } else {
		            bytes.push(unknownChar);
		          }
		        }
		      }
		      return bytes;
		    };
		  };

		  //---------------------------------------------------------------------
		  // QRMode
		  //---------------------------------------------------------------------

		  var QRMode = {
		    MODE_NUMBER :    1 << 0,
		    MODE_ALPHA_NUM : 1 << 1,
		    MODE_8BIT_BYTE : 1 << 2,
		    MODE_KANJI :     1 << 3
		  };

		  //---------------------------------------------------------------------
		  // QRErrorCorrectionLevel
		  //---------------------------------------------------------------------

		  var QRErrorCorrectionLevel = {
		    L : 1,
		    M : 0,
		    Q : 3,
		    H : 2
		  };

		  //---------------------------------------------------------------------
		  // QRMaskPattern
		  //---------------------------------------------------------------------

		  var QRMaskPattern = {
		    PATTERN000 : 0,
		    PATTERN001 : 1,
		    PATTERN010 : 2,
		    PATTERN011 : 3,
		    PATTERN100 : 4,
		    PATTERN101 : 5,
		    PATTERN110 : 6,
		    PATTERN111 : 7
		  };

		  //---------------------------------------------------------------------
		  // QRUtil
		  //---------------------------------------------------------------------

		  var QRUtil = function() {

		    var PATTERN_POSITION_TABLE = [
		      [],
		      [6, 18],
		      [6, 22],
		      [6, 26],
		      [6, 30],
		      [6, 34],
		      [6, 22, 38],
		      [6, 24, 42],
		      [6, 26, 46],
		      [6, 28, 50],
		      [6, 30, 54],
		      [6, 32, 58],
		      [6, 34, 62],
		      [6, 26, 46, 66],
		      [6, 26, 48, 70],
		      [6, 26, 50, 74],
		      [6, 30, 54, 78],
		      [6, 30, 56, 82],
		      [6, 30, 58, 86],
		      [6, 34, 62, 90],
		      [6, 28, 50, 72, 94],
		      [6, 26, 50, 74, 98],
		      [6, 30, 54, 78, 102],
		      [6, 28, 54, 80, 106],
		      [6, 32, 58, 84, 110],
		      [6, 30, 58, 86, 114],
		      [6, 34, 62, 90, 118],
		      [6, 26, 50, 74, 98, 122],
		      [6, 30, 54, 78, 102, 126],
		      [6, 26, 52, 78, 104, 130],
		      [6, 30, 56, 82, 108, 134],
		      [6, 34, 60, 86, 112, 138],
		      [6, 30, 58, 86, 114, 142],
		      [6, 34, 62, 90, 118, 146],
		      [6, 30, 54, 78, 102, 126, 150],
		      [6, 24, 50, 76, 102, 128, 154],
		      [6, 28, 54, 80, 106, 132, 158],
		      [6, 32, 58, 84, 110, 136, 162],
		      [6, 26, 54, 82, 110, 138, 166],
		      [6, 30, 58, 86, 114, 142, 170]
		    ];
		    var G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
		    var G18 = (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0);
		    var G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);

		    var _this = {};

		    var getBCHDigit = function(data) {
		      var digit = 0;
		      while (data != 0) {
		        digit += 1;
		        data >>>= 1;
		      }
		      return digit;
		    };

		    _this.getBCHTypeInfo = function(data) {
		      var d = data << 10;
		      while (getBCHDigit(d) - getBCHDigit(G15) >= 0) {
		        d ^= (G15 << (getBCHDigit(d) - getBCHDigit(G15) ) );
		      }
		      return ( (data << 10) | d) ^ G15_MASK;
		    };

		    _this.getBCHTypeNumber = function(data) {
		      var d = data << 12;
		      while (getBCHDigit(d) - getBCHDigit(G18) >= 0) {
		        d ^= (G18 << (getBCHDigit(d) - getBCHDigit(G18) ) );
		      }
		      return (data << 12) | d;
		    };

		    _this.getPatternPosition = function(typeNumber) {
		      return PATTERN_POSITION_TABLE[typeNumber - 1];
		    };

		    _this.getMaskFunction = function(maskPattern) {

		      switch (maskPattern) {

		      case QRMaskPattern.PATTERN000 :
		        return function(i, j) { return (i + j) % 2 == 0; };
		      case QRMaskPattern.PATTERN001 :
		        return function(i, j) { return i % 2 == 0; };
		      case QRMaskPattern.PATTERN010 :
		        return function(i, j) { return j % 3 == 0; };
		      case QRMaskPattern.PATTERN011 :
		        return function(i, j) { return (i + j) % 3 == 0; };
		      case QRMaskPattern.PATTERN100 :
		        return function(i, j) { return (Math.floor(i / 2) + Math.floor(j / 3) ) % 2 == 0; };
		      case QRMaskPattern.PATTERN101 :
		        return function(i, j) { return (i * j) % 2 + (i * j) % 3 == 0; };
		      case QRMaskPattern.PATTERN110 :
		        return function(i, j) { return ( (i * j) % 2 + (i * j) % 3) % 2 == 0; };
		      case QRMaskPattern.PATTERN111 :
		        return function(i, j) { return ( (i * j) % 3 + (i + j) % 2) % 2 == 0; };

		      default :
		        throw 'bad maskPattern:' + maskPattern;
		      }
		    };

		    _this.getErrorCorrectPolynomial = function(errorCorrectLength) {
		      var a = qrPolynomial([1], 0);
		      for (var i = 0; i < errorCorrectLength; i += 1) {
		        a = a.multiply(qrPolynomial([1, QRMath.gexp(i)], 0) );
		      }
		      return a;
		    };

		    _this.getLengthInBits = function(mode, type) {

		      if (1 <= type && type < 10) {

		        // 1 - 9

		        switch(mode) {
		        case QRMode.MODE_NUMBER    : return 10;
		        case QRMode.MODE_ALPHA_NUM : return 9;
		        case QRMode.MODE_8BIT_BYTE : return 8;
		        case QRMode.MODE_KANJI     : return 8;
		        default :
		          throw 'mode:' + mode;
		        }

		      } else if (type < 27) {

		        // 10 - 26

		        switch(mode) {
		        case QRMode.MODE_NUMBER    : return 12;
		        case QRMode.MODE_ALPHA_NUM : return 11;
		        case QRMode.MODE_8BIT_BYTE : return 16;
		        case QRMode.MODE_KANJI     : return 10;
		        default :
		          throw 'mode:' + mode;
		        }

		      } else if (type < 41) {

		        // 27 - 40

		        switch(mode) {
		        case QRMode.MODE_NUMBER    : return 14;
		        case QRMode.MODE_ALPHA_NUM : return 13;
		        case QRMode.MODE_8BIT_BYTE : return 16;
		        case QRMode.MODE_KANJI     : return 12;
		        default :
		          throw 'mode:' + mode;
		        }

		      } else {
		        throw 'type:' + type;
		      }
		    };

		    _this.getLostPoint = function(qrcode) {

		      var moduleCount = qrcode.getModuleCount();

		      var lostPoint = 0;

		      // LEVEL1

		      for (var row = 0; row < moduleCount; row += 1) {
		        for (var col = 0; col < moduleCount; col += 1) {

		          var sameCount = 0;
		          var dark = qrcode.isDark(row, col);

		          for (var r = -1; r <= 1; r += 1) {

		            if (row + r < 0 || moduleCount <= row + r) {
		              continue;
		            }

		            for (var c = -1; c <= 1; c += 1) {

		              if (col + c < 0 || moduleCount <= col + c) {
		                continue;
		              }

		              if (r == 0 && c == 0) {
		                continue;
		              }

		              if (dark == qrcode.isDark(row + r, col + c) ) {
		                sameCount += 1;
		              }
		            }
		          }

		          if (sameCount > 5) {
		            lostPoint += (3 + sameCount - 5);
		          }
		        }
		      }
		      // LEVEL2

		      for (var row = 0; row < moduleCount - 1; row += 1) {
		        for (var col = 0; col < moduleCount - 1; col += 1) {
		          var count = 0;
		          if (qrcode.isDark(row, col) ) count += 1;
		          if (qrcode.isDark(row + 1, col) ) count += 1;
		          if (qrcode.isDark(row, col + 1) ) count += 1;
		          if (qrcode.isDark(row + 1, col + 1) ) count += 1;
		          if (count == 0 || count == 4) {
		            lostPoint += 3;
		          }
		        }
		      }

		      // LEVEL3

		      for (var row = 0; row < moduleCount; row += 1) {
		        for (var col = 0; col < moduleCount - 6; col += 1) {
		          if (qrcode.isDark(row, col)
		              && !qrcode.isDark(row, col + 1)
		              &&  qrcode.isDark(row, col + 2)
		              &&  qrcode.isDark(row, col + 3)
		              &&  qrcode.isDark(row, col + 4)
		              && !qrcode.isDark(row, col + 5)
		              &&  qrcode.isDark(row, col + 6) ) {
		            lostPoint += 40;
		          }
		        }
		      }

		      for (var col = 0; col < moduleCount; col += 1) {
		        for (var row = 0; row < moduleCount - 6; row += 1) {
		          if (qrcode.isDark(row, col)
		              && !qrcode.isDark(row + 1, col)
		              &&  qrcode.isDark(row + 2, col)
		              &&  qrcode.isDark(row + 3, col)
		              &&  qrcode.isDark(row + 4, col)
		              && !qrcode.isDark(row + 5, col)
		              &&  qrcode.isDark(row + 6, col) ) {
		            lostPoint += 40;
		          }
		        }
		      }

		      // LEVEL4

		      var darkCount = 0;

		      for (var col = 0; col < moduleCount; col += 1) {
		        for (var row = 0; row < moduleCount; row += 1) {
		          if (qrcode.isDark(row, col) ) {
		            darkCount += 1;
		          }
		        }
		      }

		      var ratio = Math.abs(100 * darkCount / moduleCount / moduleCount - 50) / 5;
		      lostPoint += ratio * 10;

		      return lostPoint;
		    };

		    return _this;
		  }();

		  //---------------------------------------------------------------------
		  // QRMath
		  //---------------------------------------------------------------------

		  var QRMath = function() {

		    var EXP_TABLE = new Array(256);
		    var LOG_TABLE = new Array(256);

		    // initialize tables
		    for (var i = 0; i < 8; i += 1) {
		      EXP_TABLE[i] = 1 << i;
		    }
		    for (var i = 8; i < 256; i += 1) {
		      EXP_TABLE[i] = EXP_TABLE[i - 4]
		        ^ EXP_TABLE[i - 5]
		        ^ EXP_TABLE[i - 6]
		        ^ EXP_TABLE[i - 8];
		    }
		    for (var i = 0; i < 255; i += 1) {
		      LOG_TABLE[EXP_TABLE[i] ] = i;
		    }

		    var _this = {};

		    _this.glog = function(n) {

		      if (n < 1) {
		        throw 'glog(' + n + ')';
		      }

		      return LOG_TABLE[n];
		    };

		    _this.gexp = function(n) {

		      while (n < 0) {
		        n += 255;
		      }

		      while (n >= 256) {
		        n -= 255;
		      }

		      return EXP_TABLE[n];
		    };

		    return _this;
		  }();

		  //---------------------------------------------------------------------
		  // qrPolynomial
		  //---------------------------------------------------------------------

		  function qrPolynomial(num, shift) {

		    if (typeof num.length == 'undefined') {
		      throw num.length + '/' + shift;
		    }

		    var _num = function() {
		      var offset = 0;
		      while (offset < num.length && num[offset] == 0) {
		        offset += 1;
		      }
		      var _num = new Array(num.length - offset + shift);
		      for (var i = 0; i < num.length - offset; i += 1) {
		        _num[i] = num[i + offset];
		      }
		      return _num;
		    }();

		    var _this = {};

		    _this.getAt = function(index) {
		      return _num[index];
		    };

		    _this.getLength = function() {
		      return _num.length;
		    };

		    _this.multiply = function(e) {

		      var num = new Array(_this.getLength() + e.getLength() - 1);

		      for (var i = 0; i < _this.getLength(); i += 1) {
		        for (var j = 0; j < e.getLength(); j += 1) {
		          num[i + j] ^= QRMath.gexp(QRMath.glog(_this.getAt(i) ) + QRMath.glog(e.getAt(j) ) );
		        }
		      }

		      return qrPolynomial(num, 0);
		    };

		    _this.mod = function(e) {

		      if (_this.getLength() - e.getLength() < 0) {
		        return _this;
		      }

		      var ratio = QRMath.glog(_this.getAt(0) ) - QRMath.glog(e.getAt(0) );

		      var num = new Array(_this.getLength() );
		      for (var i = 0; i < _this.getLength(); i += 1) {
		        num[i] = _this.getAt(i);
		      }

		      for (var i = 0; i < e.getLength(); i += 1) {
		        num[i] ^= QRMath.gexp(QRMath.glog(e.getAt(i) ) + ratio);
		      }

		      // recursive call
		      return qrPolynomial(num, 0).mod(e);
		    };

		    return _this;
		  }
		  //---------------------------------------------------------------------
		  // QRRSBlock
		  //---------------------------------------------------------------------

		  var QRRSBlock = function() {

		    var RS_BLOCK_TABLE = [

		      // L
		      // M
		      // Q
		      // H

		      // 1
		      [1, 26, 19],
		      [1, 26, 16],
		      [1, 26, 13],
		      [1, 26, 9],

		      // 2
		      [1, 44, 34],
		      [1, 44, 28],
		      [1, 44, 22],
		      [1, 44, 16],

		      // 3
		      [1, 70, 55],
		      [1, 70, 44],
		      [2, 35, 17],
		      [2, 35, 13],

		      // 4
		      [1, 100, 80],
		      [2, 50, 32],
		      [2, 50, 24],
		      [4, 25, 9],

		      // 5
		      [1, 134, 108],
		      [2, 67, 43],
		      [2, 33, 15, 2, 34, 16],
		      [2, 33, 11, 2, 34, 12],

		      // 6
		      [2, 86, 68],
		      [4, 43, 27],
		      [4, 43, 19],
		      [4, 43, 15],

		      // 7
		      [2, 98, 78],
		      [4, 49, 31],
		      [2, 32, 14, 4, 33, 15],
		      [4, 39, 13, 1, 40, 14],

		      // 8
		      [2, 121, 97],
		      [2, 60, 38, 2, 61, 39],
		      [4, 40, 18, 2, 41, 19],
		      [4, 40, 14, 2, 41, 15],

		      // 9
		      [2, 146, 116],
		      [3, 58, 36, 2, 59, 37],
		      [4, 36, 16, 4, 37, 17],
		      [4, 36, 12, 4, 37, 13],

		      // 10
		      [2, 86, 68, 2, 87, 69],
		      [4, 69, 43, 1, 70, 44],
		      [6, 43, 19, 2, 44, 20],
		      [6, 43, 15, 2, 44, 16],

		      // 11
		      [4, 101, 81],
		      [1, 80, 50, 4, 81, 51],
		      [4, 50, 22, 4, 51, 23],
		      [3, 36, 12, 8, 37, 13],

		      // 12
		      [2, 116, 92, 2, 117, 93],
		      [6, 58, 36, 2, 59, 37],
		      [4, 46, 20, 6, 47, 21],
		      [7, 42, 14, 4, 43, 15],

		      // 13
		      [4, 133, 107],
		      [8, 59, 37, 1, 60, 38],
		      [8, 44, 20, 4, 45, 21],
		      [12, 33, 11, 4, 34, 12],

		      // 14
		      [3, 145, 115, 1, 146, 116],
		      [4, 64, 40, 5, 65, 41],
		      [11, 36, 16, 5, 37, 17],
		      [11, 36, 12, 5, 37, 13],

		      // 15
		      [5, 109, 87, 1, 110, 88],
		      [5, 65, 41, 5, 66, 42],
		      [5, 54, 24, 7, 55, 25],
		      [11, 36, 12, 7, 37, 13],

		      // 16
		      [5, 122, 98, 1, 123, 99],
		      [7, 73, 45, 3, 74, 46],
		      [15, 43, 19, 2, 44, 20],
		      [3, 45, 15, 13, 46, 16],

		      // 17
		      [1, 135, 107, 5, 136, 108],
		      [10, 74, 46, 1, 75, 47],
		      [1, 50, 22, 15, 51, 23],
		      [2, 42, 14, 17, 43, 15],

		      // 18
		      [5, 150, 120, 1, 151, 121],
		      [9, 69, 43, 4, 70, 44],
		      [17, 50, 22, 1, 51, 23],
		      [2, 42, 14, 19, 43, 15],

		      // 19
		      [3, 141, 113, 4, 142, 114],
		      [3, 70, 44, 11, 71, 45],
		      [17, 47, 21, 4, 48, 22],
		      [9, 39, 13, 16, 40, 14],

		      // 20
		      [3, 135, 107, 5, 136, 108],
		      [3, 67, 41, 13, 68, 42],
		      [15, 54, 24, 5, 55, 25],
		      [15, 43, 15, 10, 44, 16],

		      // 21
		      [4, 144, 116, 4, 145, 117],
		      [17, 68, 42],
		      [17, 50, 22, 6, 51, 23],
		      [19, 46, 16, 6, 47, 17],

		      // 22
		      [2, 139, 111, 7, 140, 112],
		      [17, 74, 46],
		      [7, 54, 24, 16, 55, 25],
		      [34, 37, 13],

		      // 23
		      [4, 151, 121, 5, 152, 122],
		      [4, 75, 47, 14, 76, 48],
		      [11, 54, 24, 14, 55, 25],
		      [16, 45, 15, 14, 46, 16],

		      // 24
		      [6, 147, 117, 4, 148, 118],
		      [6, 73, 45, 14, 74, 46],
		      [11, 54, 24, 16, 55, 25],
		      [30, 46, 16, 2, 47, 17],

		      // 25
		      [8, 132, 106, 4, 133, 107],
		      [8, 75, 47, 13, 76, 48],
		      [7, 54, 24, 22, 55, 25],
		      [22, 45, 15, 13, 46, 16],

		      // 26
		      [10, 142, 114, 2, 143, 115],
		      [19, 74, 46, 4, 75, 47],
		      [28, 50, 22, 6, 51, 23],
		      [33, 46, 16, 4, 47, 17],

		      // 27
		      [8, 152, 122, 4, 153, 123],
		      [22, 73, 45, 3, 74, 46],
		      [8, 53, 23, 26, 54, 24],
		      [12, 45, 15, 28, 46, 16],

		      // 28
		      [3, 147, 117, 10, 148, 118],
		      [3, 73, 45, 23, 74, 46],
		      [4, 54, 24, 31, 55, 25],
		      [11, 45, 15, 31, 46, 16],

		      // 29
		      [7, 146, 116, 7, 147, 117],
		      [21, 73, 45, 7, 74, 46],
		      [1, 53, 23, 37, 54, 24],
		      [19, 45, 15, 26, 46, 16],

		      // 30
		      [5, 145, 115, 10, 146, 116],
		      [19, 75, 47, 10, 76, 48],
		      [15, 54, 24, 25, 55, 25],
		      [23, 45, 15, 25, 46, 16],

		      // 31
		      [13, 145, 115, 3, 146, 116],
		      [2, 74, 46, 29, 75, 47],
		      [42, 54, 24, 1, 55, 25],
		      [23, 45, 15, 28, 46, 16],

		      // 32
		      [17, 145, 115],
		      [10, 74, 46, 23, 75, 47],
		      [10, 54, 24, 35, 55, 25],
		      [19, 45, 15, 35, 46, 16],

		      // 33
		      [17, 145, 115, 1, 146, 116],
		      [14, 74, 46, 21, 75, 47],
		      [29, 54, 24, 19, 55, 25],
		      [11, 45, 15, 46, 46, 16],

		      // 34
		      [13, 145, 115, 6, 146, 116],
		      [14, 74, 46, 23, 75, 47],
		      [44, 54, 24, 7, 55, 25],
		      [59, 46, 16, 1, 47, 17],

		      // 35
		      [12, 151, 121, 7, 152, 122],
		      [12, 75, 47, 26, 76, 48],
		      [39, 54, 24, 14, 55, 25],
		      [22, 45, 15, 41, 46, 16],

		      // 36
		      [6, 151, 121, 14, 152, 122],
		      [6, 75, 47, 34, 76, 48],
		      [46, 54, 24, 10, 55, 25],
		      [2, 45, 15, 64, 46, 16],

		      // 37
		      [17, 152, 122, 4, 153, 123],
		      [29, 74, 46, 14, 75, 47],
		      [49, 54, 24, 10, 55, 25],
		      [24, 45, 15, 46, 46, 16],

		      // 38
		      [4, 152, 122, 18, 153, 123],
		      [13, 74, 46, 32, 75, 47],
		      [48, 54, 24, 14, 55, 25],
		      [42, 45, 15, 32, 46, 16],

		      // 39
		      [20, 147, 117, 4, 148, 118],
		      [40, 75, 47, 7, 76, 48],
		      [43, 54, 24, 22, 55, 25],
		      [10, 45, 15, 67, 46, 16],

		      // 40
		      [19, 148, 118, 6, 149, 119],
		      [18, 75, 47, 31, 76, 48],
		      [34, 54, 24, 34, 55, 25],
		      [20, 45, 15, 61, 46, 16]
		    ];

		    var qrRSBlock = function(totalCount, dataCount) {
		      var _this = {};
		      _this.totalCount = totalCount;
		      _this.dataCount = dataCount;
		      return _this;
		    };

		    var _this = {};

		    var getRsBlockTable = function(typeNumber, errorCorrectionLevel) {

		      switch(errorCorrectionLevel) {
		      case QRErrorCorrectionLevel.L :
		        return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 0];
		      case QRErrorCorrectionLevel.M :
		        return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 1];
		      case QRErrorCorrectionLevel.Q :
		        return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 2];
		      case QRErrorCorrectionLevel.H :
		        return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 3];
		      default :
		        return undefined;
		      }
		    };

		    _this.getRSBlocks = function(typeNumber, errorCorrectionLevel) {

		      var rsBlock = getRsBlockTable(typeNumber, errorCorrectionLevel);

		      if (typeof rsBlock == 'undefined') {
		        throw 'bad rs block @ typeNumber:' + typeNumber +
		            '/errorCorrectionLevel:' + errorCorrectionLevel;
		      }

		      var length = rsBlock.length / 3;

		      var list = [];

		      for (var i = 0; i < length; i += 1) {

		        var count = rsBlock[i * 3 + 0];
		        var totalCount = rsBlock[i * 3 + 1];
		        var dataCount = rsBlock[i * 3 + 2];

		        for (var j = 0; j < count; j += 1) {
		          list.push(qrRSBlock(totalCount, dataCount) );
		        }
		      }

		      return list;
		    };

		    return _this;
		  }();

		  //---------------------------------------------------------------------
		  // qrBitBuffer
		  //---------------------------------------------------------------------

		  var qrBitBuffer = function() {

		    var _buffer = [];
		    var _length = 0;

		    var _this = {};

		    _this.getBuffer = function() {
		      return _buffer;
		    };

		    _this.getAt = function(index) {
		      var bufIndex = Math.floor(index / 8);
		      return ( (_buffer[bufIndex] >>> (7 - index % 8) ) & 1) == 1;
		    };

		    _this.put = function(num, length) {
		      for (var i = 0; i < length; i += 1) {
		        _this.putBit( ( (num >>> (length - i - 1) ) & 1) == 1);
		      }
		    };

		    _this.getLengthInBits = function() {
		      return _length;
		    };

		    _this.putBit = function(bit) {

		      var bufIndex = Math.floor(_length / 8);
		      if (_buffer.length <= bufIndex) {
		        _buffer.push(0);
		      }

		      if (bit) {
		        _buffer[bufIndex] |= (0x80 >>> (_length % 8) );
		      }

		      _length += 1;
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // qrNumber
		  //---------------------------------------------------------------------

		  var qrNumber = function(data) {

		    var _mode = QRMode.MODE_NUMBER;
		    var _data = data;

		    var _this = {};

		    _this.getMode = function() {
		      return _mode;
		    };

		    _this.getLength = function(buffer) {
		      return _data.length;
		    };

		    _this.write = function(buffer) {

		      var data = _data;

		      var i = 0;

		      while (i + 2 < data.length) {
		        buffer.put(strToNum(data.substring(i, i + 3) ), 10);
		        i += 3;
		      }

		      if (i < data.length) {
		        if (data.length - i == 1) {
		          buffer.put(strToNum(data.substring(i, i + 1) ), 4);
		        } else if (data.length - i == 2) {
		          buffer.put(strToNum(data.substring(i, i + 2) ), 7);
		        }
		      }
		    };

		    var strToNum = function(s) {
		      var num = 0;
		      for (var i = 0; i < s.length; i += 1) {
		        num = num * 10 + chatToNum(s.charAt(i) );
		      }
		      return num;
		    };

		    var chatToNum = function(c) {
		      if ('0' <= c && c <= '9') {
		        return c.charCodeAt(0) - '0'.charCodeAt(0);
		      }
		      throw 'illegal char :' + c;
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // qrAlphaNum
		  //---------------------------------------------------------------------

		  var qrAlphaNum = function(data) {

		    var _mode = QRMode.MODE_ALPHA_NUM;
		    var _data = data;

		    var _this = {};

		    _this.getMode = function() {
		      return _mode;
		    };

		    _this.getLength = function(buffer) {
		      return _data.length;
		    };

		    _this.write = function(buffer) {

		      var s = _data;

		      var i = 0;

		      while (i + 1 < s.length) {
		        buffer.put(
		          getCode(s.charAt(i) ) * 45 +
		          getCode(s.charAt(i + 1) ), 11);
		        i += 2;
		      }

		      if (i < s.length) {
		        buffer.put(getCode(s.charAt(i) ), 6);
		      }
		    };

		    var getCode = function(c) {

		      if ('0' <= c && c <= '9') {
		        return c.charCodeAt(0) - '0'.charCodeAt(0);
		      } else if ('A' <= c && c <= 'Z') {
		        return c.charCodeAt(0) - 'A'.charCodeAt(0) + 10;
		      } else {
		        switch (c) {
		        case ' ' : return 36;
		        case '$' : return 37;
		        case '%' : return 38;
		        case '*' : return 39;
		        case '+' : return 40;
		        case '-' : return 41;
		        case '.' : return 42;
		        case '/' : return 43;
		        case ':' : return 44;
		        default :
		          throw 'illegal char :' + c;
		        }
		      }
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // qr8BitByte
		  //---------------------------------------------------------------------

		  var qr8BitByte = function(data) {

		    var _mode = QRMode.MODE_8BIT_BYTE;
		    var _bytes = qrcode.stringToBytes(data);

		    var _this = {};

		    _this.getMode = function() {
		      return _mode;
		    };

		    _this.getLength = function(buffer) {
		      return _bytes.length;
		    };

		    _this.write = function(buffer) {
		      for (var i = 0; i < _bytes.length; i += 1) {
		        buffer.put(_bytes[i], 8);
		      }
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // qrKanji
		  //---------------------------------------------------------------------

		  var qrKanji = function(data) {

		    var _mode = QRMode.MODE_KANJI;

		    var stringToBytes = qrcode.stringToBytesFuncs['SJIS'];
		    if (!stringToBytes) {
		      throw 'sjis not supported.';
		    }
		    !function(c, code) {
		      // self test for sjis support.
		      var test = stringToBytes(c);
		      if (test.length != 2 || ( (test[0] << 8) | test[1]) != code) {
		        throw 'sjis not supported.';
		      }
		    }('\u53cb', 0x9746);

		    var _bytes = stringToBytes(data);

		    var _this = {};

		    _this.getMode = function() {
		      return _mode;
		    };

		    _this.getLength = function(buffer) {
		      return ~~(_bytes.length / 2);
		    };

		    _this.write = function(buffer) {

		      var data = _bytes;

		      var i = 0;

		      while (i + 1 < data.length) {

		        var c = ( (0xff & data[i]) << 8) | (0xff & data[i + 1]);

		        if (0x8140 <= c && c <= 0x9FFC) {
		          c -= 0x8140;
		        } else if (0xE040 <= c && c <= 0xEBBF) {
		          c -= 0xC140;
		        } else {
		          throw 'illegal char at ' + (i + 1) + '/' + c;
		        }

		        c = ( (c >>> 8) & 0xff) * 0xC0 + (c & 0xff);

		        buffer.put(c, 13);

		        i += 2;
		      }

		      if (i < data.length) {
		        throw 'illegal char at ' + (i + 1);
		      }
		    };

		    return _this;
		  };

		  //=====================================================================
		  // GIF Support etc.
		  //

		  //---------------------------------------------------------------------
		  // byteArrayOutputStream
		  //---------------------------------------------------------------------

		  var byteArrayOutputStream = function() {

		    var _bytes = [];

		    var _this = {};

		    _this.writeByte = function(b) {
		      _bytes.push(b & 0xff);
		    };

		    _this.writeShort = function(i) {
		      _this.writeByte(i);
		      _this.writeByte(i >>> 8);
		    };

		    _this.writeBytes = function(b, off, len) {
		      off = off || 0;
		      len = len || b.length;
		      for (var i = 0; i < len; i += 1) {
		        _this.writeByte(b[i + off]);
		      }
		    };

		    _this.writeString = function(s) {
		      for (var i = 0; i < s.length; i += 1) {
		        _this.writeByte(s.charCodeAt(i) );
		      }
		    };

		    _this.toByteArray = function() {
		      return _bytes;
		    };

		    _this.toString = function() {
		      var s = '';
		      s += '[';
		      for (var i = 0; i < _bytes.length; i += 1) {
		        if (i > 0) {
		          s += ',';
		        }
		        s += _bytes[i];
		      }
		      s += ']';
		      return s;
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // base64EncodeOutputStream
		  //---------------------------------------------------------------------

		  var base64EncodeOutputStream = function() {

		    var _buffer = 0;
		    var _buflen = 0;
		    var _length = 0;
		    var _base64 = '';

		    var _this = {};

		    var writeEncoded = function(b) {
		      _base64 += String.fromCharCode(encode(b & 0x3f) );
		    };

		    var encode = function(n) {
		      if (n < 0) ; else if (n < 26) {
		        return 0x41 + n;
		      } else if (n < 52) {
		        return 0x61 + (n - 26);
		      } else if (n < 62) {
		        return 0x30 + (n - 52);
		      } else if (n == 62) {
		        return 0x2b;
		      } else if (n == 63) {
		        return 0x2f;
		      }
		      throw 'n:' + n;
		    };

		    _this.writeByte = function(n) {

		      _buffer = (_buffer << 8) | (n & 0xff);
		      _buflen += 8;
		      _length += 1;

		      while (_buflen >= 6) {
		        writeEncoded(_buffer >>> (_buflen - 6) );
		        _buflen -= 6;
		      }
		    };

		    _this.flush = function() {

		      if (_buflen > 0) {
		        writeEncoded(_buffer << (6 - _buflen) );
		        _buffer = 0;
		        _buflen = 0;
		      }

		      if (_length % 3 != 0) {
		        // padding
		        var padlen = 3 - _length % 3;
		        for (var i = 0; i < padlen; i += 1) {
		          _base64 += '=';
		        }
		      }
		    };

		    _this.toString = function() {
		      return _base64;
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // base64DecodeInputStream
		  //---------------------------------------------------------------------

		  var base64DecodeInputStream = function(str) {

		    var _str = str;
		    var _pos = 0;
		    var _buffer = 0;
		    var _buflen = 0;

		    var _this = {};

		    _this.read = function() {

		      while (_buflen < 8) {

		        if (_pos >= _str.length) {
		          if (_buflen == 0) {
		            return -1;
		          }
		          throw 'unexpected end of file./' + _buflen;
		        }

		        var c = _str.charAt(_pos);
		        _pos += 1;

		        if (c == '=') {
		          _buflen = 0;
		          return -1;
		        } else if (c.match(/^\s$/) ) {
		          // ignore if whitespace.
		          continue;
		        }

		        _buffer = (_buffer << 6) | decode(c.charCodeAt(0) );
		        _buflen += 6;
		      }

		      var n = (_buffer >>> (_buflen - 8) ) & 0xff;
		      _buflen -= 8;
		      return n;
		    };

		    var decode = function(c) {
		      if (0x41 <= c && c <= 0x5a) {
		        return c - 0x41;
		      } else if (0x61 <= c && c <= 0x7a) {
		        return c - 0x61 + 26;
		      } else if (0x30 <= c && c <= 0x39) {
		        return c - 0x30 + 52;
		      } else if (c == 0x2b) {
		        return 62;
		      } else if (c == 0x2f) {
		        return 63;
		      } else {
		        throw 'c:' + c;
		      }
		    };

		    return _this;
		  };

		  //---------------------------------------------------------------------
		  // gifImage (B/W)
		  //---------------------------------------------------------------------

		  var gifImage = function(width, height) {

		    var _width = width;
		    var _height = height;
		    var _data = new Array(width * height);

		    var _this = {};

		    _this.setPixel = function(x, y, pixel) {
		      _data[y * _width + x] = pixel;
		    };

		    _this.write = function(out) {

		      //---------------------------------
		      // GIF Signature

		      out.writeString('GIF87a');

		      //---------------------------------
		      // Screen Descriptor

		      out.writeShort(_width);
		      out.writeShort(_height);

		      out.writeByte(0x80); // 2bit
		      out.writeByte(0);
		      out.writeByte(0);

		      //---------------------------------
		      // Global Color Map

		      // black
		      out.writeByte(0x00);
		      out.writeByte(0x00);
		      out.writeByte(0x00);

		      // white
		      out.writeByte(0xff);
		      out.writeByte(0xff);
		      out.writeByte(0xff);

		      //---------------------------------
		      // Image Descriptor

		      out.writeString(',');
		      out.writeShort(0);
		      out.writeShort(0);
		      out.writeShort(_width);
		      out.writeShort(_height);
		      out.writeByte(0);

		      //---------------------------------
		      // Local Color Map

		      //---------------------------------
		      // Raster Data

		      var lzwMinCodeSize = 2;
		      var raster = getLZWRaster(lzwMinCodeSize);

		      out.writeByte(lzwMinCodeSize);

		      var offset = 0;

		      while (raster.length - offset > 255) {
		        out.writeByte(255);
		        out.writeBytes(raster, offset, 255);
		        offset += 255;
		      }

		      out.writeByte(raster.length - offset);
		      out.writeBytes(raster, offset, raster.length - offset);
		      out.writeByte(0x00);

		      //---------------------------------
		      // GIF Terminator
		      out.writeString(';');
		    };

		    var bitOutputStream = function(out) {

		      var _out = out;
		      var _bitLength = 0;
		      var _bitBuffer = 0;

		      var _this = {};

		      _this.write = function(data, length) {

		        if ( (data >>> length) != 0) {
		          throw 'length over';
		        }

		        while (_bitLength + length >= 8) {
		          _out.writeByte(0xff & ( (data << _bitLength) | _bitBuffer) );
		          length -= (8 - _bitLength);
		          data >>>= (8 - _bitLength);
		          _bitBuffer = 0;
		          _bitLength = 0;
		        }

		        _bitBuffer = (data << _bitLength) | _bitBuffer;
		        _bitLength = _bitLength + length;
		      };

		      _this.flush = function() {
		        if (_bitLength > 0) {
		          _out.writeByte(_bitBuffer);
		        }
		      };

		      return _this;
		    };

		    var getLZWRaster = function(lzwMinCodeSize) {

		      var clearCode = 1 << lzwMinCodeSize;
		      var endCode = (1 << lzwMinCodeSize) + 1;
		      var bitLength = lzwMinCodeSize + 1;

		      // Setup LZWTable
		      var table = lzwTable();

		      for (var i = 0; i < clearCode; i += 1) {
		        table.add(String.fromCharCode(i) );
		      }
		      table.add(String.fromCharCode(clearCode) );
		      table.add(String.fromCharCode(endCode) );

		      var byteOut = byteArrayOutputStream();
		      var bitOut = bitOutputStream(byteOut);

		      // clear code
		      bitOut.write(clearCode, bitLength);

		      var dataIndex = 0;

		      var s = String.fromCharCode(_data[dataIndex]);
		      dataIndex += 1;

		      while (dataIndex < _data.length) {

		        var c = String.fromCharCode(_data[dataIndex]);
		        dataIndex += 1;

		        if (table.contains(s + c) ) {

		          s = s + c;

		        } else {

		          bitOut.write(table.indexOf(s), bitLength);

		          if (table.size() < 0xfff) {

		            if (table.size() == (1 << bitLength) ) {
		              bitLength += 1;
		            }

		            table.add(s + c);
		          }

		          s = c;
		        }
		      }

		      bitOut.write(table.indexOf(s), bitLength);

		      // end code
		      bitOut.write(endCode, bitLength);

		      bitOut.flush();

		      return byteOut.toByteArray();
		    };

		    var lzwTable = function() {

		      var _map = {};
		      var _size = 0;

		      var _this = {};

		      _this.add = function(key) {
		        if (_this.contains(key) ) {
		          throw 'dup key:' + key;
		        }
		        _map[key] = _size;
		        _size += 1;
		      };

		      _this.size = function() {
		        return _size;
		      };

		      _this.indexOf = function(key) {
		        return _map[key];
		      };

		      _this.contains = function(key) {
		        return typeof _map[key] != 'undefined';
		      };

		      return _this;
		    };

		    return _this;
		  };

		  var createDataURL = function(width, height, getPixel) {
		    var gif = gifImage(width, height);
		    for (var y = 0; y < height; y += 1) {
		      for (var x = 0; x < width; x += 1) {
		        gif.setPixel(x, y, getPixel(x, y) );
		      }
		    }

		    var b = byteArrayOutputStream();
		    gif.write(b);

		    var base64 = base64EncodeOutputStream();
		    var bytes = b.toByteArray();
		    for (var i = 0; i < bytes.length; i += 1) {
		      base64.writeByte(bytes[i]);
		    }
		    base64.flush();

		    return 'data:image/gif;base64,' + base64;
		  };

		  //---------------------------------------------------------------------
		  // returns qrcode function.

		  return qrcode;
		}();

		// multibyte support
		!function() {

		  qrcode.stringToBytesFuncs['UTF-8'] = function(s) {
		    // http://stackoverflow.com/questions/18729405/how-to-convert-utf8-string-to-byte-array
		    function toUTF8Array(str) {
		      var utf8 = [];
		      for (var i=0; i < str.length; i++) {
		        var charcode = str.charCodeAt(i);
		        if (charcode < 0x80) utf8.push(charcode);
		        else if (charcode < 0x800) {
		          utf8.push(0xc0 | (charcode >> 6),
		              0x80 | (charcode & 0x3f));
		        }
		        else if (charcode < 0xd800 || charcode >= 0xe000) {
		          utf8.push(0xe0 | (charcode >> 12),
		              0x80 | ((charcode>>6) & 0x3f),
		              0x80 | (charcode & 0x3f));
		        }
		        // surrogate pair
		        else {
		          i++;
		          // UTF-16 encodes 0x10000-0x10FFFF by
		          // subtracting 0x10000 and splitting the
		          // 20 bits of 0x0-0xFFFFF into two halves
		          charcode = 0x10000 + (((charcode & 0x3ff)<<10)
		            | (str.charCodeAt(i) & 0x3ff));
		          utf8.push(0xf0 | (charcode >>18),
		              0x80 | ((charcode>>12) & 0x3f),
		              0x80 | ((charcode>>6) & 0x3f),
		              0x80 | (charcode & 0x3f));
		        }
		      }
		      return utf8;
		    }
		    return toUTF8Array(s);
		  };

		}();

		(function (factory) {
		  {
		      module.exports = factory();
		  }
		}(function () {
		    return qrcode;
		})); 
	} (qrcode$1));
	return qrcode$1.exports;
}

var qrcodeExports = requireQrcode();
const qrcode = /*@__PURE__*/getDefaultExportFromCjs(qrcodeExports);

const CG_QR_HEADER = 'data:image/jpeg;base64,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';

const CX_QR_HEADER = 'data:image/jpeg;base64,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';

/**
 * Generates a QR code image for a referral program and returns it as a Blob to be consumed by client.
 *
 * @param {string} url - The URL to be encoded in the QR code.
 * @param {ReferralsQRCodeData} data - The data for the referral program.
 * @param {string} websiteName - The name of the website for the referral program.
 * @param {string} activeCode
 * @returns {Promise<Blob | null>} A promise that resolves to a Blob containing the QR code image, or null if the generation fails.
 * @throws {Error} If the 2D context of the canvas cannot be obtained.
 */ function generateReferralsQRCodeImage(url, data, websiteName, activeCode) {
    const qr = qrcode(0, 'L');
    qr.addData(url);
    qr.make();
    const moduleCount = qr.getModuleCount();
    const sizeMultiplier = 136 / moduleCount;
    const qrSize = moduleCount * sizeMultiplier;
    const headerText = `Join ${activeCode}'s Referral Program Today!`;
    const headerSize = 14;
    const headerMarginTop = 84;
    const headerMarginBottom = 10;
    const subText = `Get $${data.referrerBase} for each customer who spends $${data.referredCondition}, and $${data.referrerFee} on fees in perpetuity.`;
    const subTextSize = 14;
    const subTextMarginBottom = 24;
    const canvasWidth = 258;
    const canvasHeight = 404;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to get 2D context from canvas.');
    }
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    context.scale(dpr, dpr);
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvasWidth, canvasHeight);
    const headerImage = new Image();
    websiteName == 'ChicksGold' ? headerImage.src = CG_QR_HEADER : headerImage.src = CX_QR_HEADER;
    return new Promise((resolve, reject)=>{
        headerImage.onload = ()=>{
            context.drawImage(headerImage, 0, 0, canvasWidth, 56);
            context.textAlign = 'center';
            context.fillStyle = '#000000';
            context.font = `${headerSize}px Roboto, sans-serif`;
            const headerX = canvasWidth / 2;
            const headerY = headerMarginTop;
            const maxTextWidth = canvasWidth - 40;
            const headerLineHeight = headerSize + 8;
            const subTextStartY = wrapText(context, headerText, headerX, headerY, maxTextWidth, headerLineHeight) + headerMarginBottom;
            context.font = `${subTextSize}px Roboto, sans-serif`;
            const subTextX = canvasWidth / 2;
            const subTextLineHeight = subTextSize + 8;
            const qrStartY = wrapText(context, subText, subTextX, subTextStartY, maxTextWidth, subTextLineHeight) + subTextMarginBottom;
            const hrX = 20;
            const hrY = qrStartY - subTextMarginBottom;
            const hrWidth = canvasWidth - 40;
            const hrHeight = 1;
            context.fillStyle = '#F0F0F0';
            context.fillRect(hrX, hrY, hrWidth, hrHeight);
            const qrX = (canvasWidth - qrSize) / 2;
            for(let row = 0; row < moduleCount; row++){
                for(let col = 0; col < moduleCount; col++){
                    context.fillStyle = qr.isDark(row, col) ? '#484848' : '#ffffff';
                    context.fillRect(qrX + col * sizeMultiplier - 0.25, qrStartY + row * sizeMultiplier - 0.25, sizeMultiplier + 0.5, sizeMultiplier + 0.5);
                }
            }
            canvas.toBlob((blob)=>{
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to generate QR code blob.'));
                }
            }, 'image/png');
        };
        headerImage.onerror = ()=>{
            reject(new Error('Failed to load header image.'));
        };
    });
}
/**
 * Wraps text within a specified width and draws it on the canvas.
 *
 * @param {CanvasRenderingContext2D} context - The 2D rendering context for the drawing surface of the canvas.
 * @param {string} text - The text to be wrapped and drawn.
 * @param {number} x - The x-coordinate of the text's starting point.
 * @param {number} y - The y-coordinate of the text's starting point.
 * @param {number} maxWidth - The maximum width of the text.
 * @param {number} lineHeight - The height of each line of text.
 * @returns {number} The new y-coordinate after drawing the text.
 */ function wrapText(context, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    for(let i = 0; i < words.length; i++){
        const testLine = line + words[i] + ' ';
        const metrics = context.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > maxWidth && i > 0) {
            context.fillText(line, x, y);
            line = words[i] + ' ';
            y += lineHeight;
        } else {
            line = testLine;
        }
    }
    context.fillText(line, x, y);
    return y + lineHeight;
}

function toRoute(value) {
    return value.toLowerCase().trim().replaceAll(' ', '-');
}
/**
 * Transform a sell product category name to its buy version. For example: Sell Item -> Items
 * @param {string} name
 * @returns {string} - The transformed category name.
 */ function transformProductCategoryName(name) {
    if (!name.includes('Sell')) {
        return name;
    }
    const trimmed = name.replace('Sell ', '');
    if (trimmed !== 'Currency' && !trimmed.endsWith('s')) {
        return trimmed + 's';
    }
    return trimmed;
}
/**
 * Checks if the given string is a valid email address
 * @param {string} email
 * @returns `true` if the string is a valid email address, `false` otherwise
 */ function isEmail(email) {
    return /^[\d\w._+-]+@[\d\w._-]+\.[\w]+$/i.test(email);
}
/**
 * Checks if the given string is a valid URL.
 *
 * @param {string} str - The string to be checked.
 * @returns {boolean} `true` if the string is a valid URL, otherwise `false`.
 */ function isUrl(str) {
    try {
        return Boolean(new URL(str));
    } catch (_) {
        return false;
    }
}
/**
 *  Returns the base domain from a subdomain, or the same value if there's no subdomain to remove
 * @param hostname
 * @returns The base domain. (example: blog.chicksgold.com -> chicksgold.com)
 */ function getBaseDomain(hostname) {
    if (!hostname) {
        return '';
    }
    if (hostname === 'localhost') {
        return `localhost:${location.port}`;
    }
    const parts = hostname.split('.');
    if (parts.length <= 2) {
        return hostname;
    }
    return parts.slice(-2).join('.');
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics = function(d, b) {
  extendStatics = Object.setPrototypeOf ||
      ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
      function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
  return extendStatics(d, b);
};

function __extends(d, b) {
  if (typeof b !== "function" && b !== null)
      throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
  extendStatics(d, b);
  function __() { this.constructor = d; }
  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
  return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
      function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
      function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

function __generator(thisArg, body) {
  var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
  return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
  function verb(n) { return function (v) { return step([n, v]); }; }
  function step(op) {
      if (f) throw new TypeError("Generator is already executing.");
      while (g && (g = 0, op[0] && (_ = 0)), _) try {
          if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
          if (y = 0, t) op = [op[0] & 2, t.value];
          switch (op[0]) {
              case 0: case 1: t = op; break;
              case 4: _.label++; return { value: op[1], done: false };
              case 5: _.label++; y = op[1]; op = [0]; continue;
              case 7: op = _.ops.pop(); _.trys.pop(); continue;
              default:
                  if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                  if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                  if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                  if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                  if (t[2]) _.ops.pop();
                  _.trys.pop(); continue;
          }
          op = body.call(thisArg, _);
      } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
      if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
  }
}

function __values(o) {
  var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
  if (m) return m.call(o);
  if (o && typeof o.length === "number") return {
      next: function () {
          if (o && i >= o.length) o = void 0;
          return { value: o && o[i++], done: !o };
      }
  };
  throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o), r, ar = [], e;
  try {
      while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  }
  catch (error) { e = { error: error }; }
  finally {
      try {
          if (r && !r.done && (m = i["return"])) m.call(i);
      }
      finally { if (e) throw e.error; }
  }
  return ar;
}

function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
      if (ar || !(i in from)) {
          if (!ar) ar = Array.prototype.slice.call(from, 0, i);
          ar[i] = from[i];
      }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

function __await(v) {
  return this instanceof __await ? (this.v = v, this) : new __await(v);
}

function __asyncGenerator(thisArg, _arguments, generator) {
  if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
  var g = generator.apply(thisArg, _arguments || []), i, q = [];
  return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
  function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
  function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
  function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
  function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
  function fulfill(value) { resume("next", value); }
  function reject(value) { resume("throw", value); }
  function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
}

function __asyncValues(o) {
  if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
  var m = o[Symbol.asyncIterator], i;
  return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
  function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
  function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function isFunction(value) {
    return typeof value === 'function';
}

function createErrorClass(createImpl) {
    var _super = function (instance) {
        Error.call(instance);
        instance.stack = new Error().stack;
    };
    var ctorFunc = createImpl(_super);
    ctorFunc.prototype = Object.create(Error.prototype);
    ctorFunc.prototype.constructor = ctorFunc;
    return ctorFunc;
}

var UnsubscriptionError = createErrorClass(function (_super) {
    return function UnsubscriptionErrorImpl(errors) {
        _super(this);
        this.message = errors
            ? errors.length + " errors occurred during unsubscription:\n" + errors.map(function (err, i) { return i + 1 + ") " + err.toString(); }).join('\n  ')
            : '';
        this.name = 'UnsubscriptionError';
        this.errors = errors;
    };
});

function arrRemove(arr, item) {
    if (arr) {
        var index = arr.indexOf(item);
        0 <= index && arr.splice(index, 1);
    }
}

var Subscription = (function () {
    function Subscription(initialTeardown) {
        this.initialTeardown = initialTeardown;
        this.closed = false;
        this._parentage = null;
        this._finalizers = null;
    }
    Subscription.prototype.unsubscribe = function () {
        var e_1, _a, e_2, _b;
        var errors;
        if (!this.closed) {
            this.closed = true;
            var _parentage = this._parentage;
            if (_parentage) {
                this._parentage = null;
                if (Array.isArray(_parentage)) {
                    try {
                        for (var _parentage_1 = __values(_parentage), _parentage_1_1 = _parentage_1.next(); !_parentage_1_1.done; _parentage_1_1 = _parentage_1.next()) {
                            var parent_1 = _parentage_1_1.value;
                            parent_1.remove(this);
                        }
                    }
                    catch (e_1_1) { e_1 = { error: e_1_1 }; }
                    finally {
                        try {
                            if (_parentage_1_1 && !_parentage_1_1.done && (_a = _parentage_1.return)) _a.call(_parentage_1);
                        }
                        finally { if (e_1) throw e_1.error; }
                    }
                }
                else {
                    _parentage.remove(this);
                }
            }
            var initialFinalizer = this.initialTeardown;
            if (isFunction(initialFinalizer)) {
                try {
                    initialFinalizer();
                }
                catch (e) {
                    errors = e instanceof UnsubscriptionError ? e.errors : [e];
                }
            }
            var _finalizers = this._finalizers;
            if (_finalizers) {
                this._finalizers = null;
                try {
                    for (var _finalizers_1 = __values(_finalizers), _finalizers_1_1 = _finalizers_1.next(); !_finalizers_1_1.done; _finalizers_1_1 = _finalizers_1.next()) {
                        var finalizer = _finalizers_1_1.value;
                        try {
                            execFinalizer(finalizer);
                        }
                        catch (err) {
                            errors = errors !== null && errors !== void 0 ? errors : [];
                            if (err instanceof UnsubscriptionError) {
                                errors = __spreadArray(__spreadArray([], __read(errors)), __read(err.errors));
                            }
                            else {
                                errors.push(err);
                            }
                        }
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_finalizers_1_1 && !_finalizers_1_1.done && (_b = _finalizers_1.return)) _b.call(_finalizers_1);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
            if (errors) {
                throw new UnsubscriptionError(errors);
            }
        }
    };
    Subscription.prototype.add = function (teardown) {
        var _a;
        if (teardown && teardown !== this) {
            if (this.closed) {
                execFinalizer(teardown);
            }
            else {
                if (teardown instanceof Subscription) {
                    if (teardown.closed || teardown._hasParent(this)) {
                        return;
                    }
                    teardown._addParent(this);
                }
                (this._finalizers = (_a = this._finalizers) !== null && _a !== void 0 ? _a : []).push(teardown);
            }
        }
    };
    Subscription.prototype._hasParent = function (parent) {
        var _parentage = this._parentage;
        return _parentage === parent || (Array.isArray(_parentage) && _parentage.includes(parent));
    };
    Subscription.prototype._addParent = function (parent) {
        var _parentage = this._parentage;
        this._parentage = Array.isArray(_parentage) ? (_parentage.push(parent), _parentage) : _parentage ? [_parentage, parent] : parent;
    };
    Subscription.prototype._removeParent = function (parent) {
        var _parentage = this._parentage;
        if (_parentage === parent) {
            this._parentage = null;
        }
        else if (Array.isArray(_parentage)) {
            arrRemove(_parentage, parent);
        }
    };
    Subscription.prototype.remove = function (teardown) {
        var _finalizers = this._finalizers;
        _finalizers && arrRemove(_finalizers, teardown);
        if (teardown instanceof Subscription) {
            teardown._removeParent(this);
        }
    };
    Subscription.EMPTY = (function () {
        var empty = new Subscription();
        empty.closed = true;
        return empty;
    })();
    return Subscription;
}());
var EMPTY_SUBSCRIPTION = Subscription.EMPTY;
function isSubscription(value) {
    return (value instanceof Subscription ||
        (value && 'closed' in value && isFunction(value.remove) && isFunction(value.add) && isFunction(value.unsubscribe)));
}
function execFinalizer(finalizer) {
    if (isFunction(finalizer)) {
        finalizer();
    }
    else {
        finalizer.unsubscribe();
    }
}

var config = {
    Promise: undefined};

var timeoutProvider = {
    setTimeout: function (handler, timeout) {
        var args = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            args[_i - 2] = arguments[_i];
        }
        return setTimeout.apply(void 0, __spreadArray([handler, timeout], __read(args)));
    },
    clearTimeout: function (handle) {
        return (clearTimeout)(handle);
    },
    delegate: undefined,
};

function reportUnhandledError(err) {
    timeoutProvider.setTimeout(function () {
        {
            throw err;
        }
    });
}

function noop() { }

function errorContext(cb) {
    {
        cb();
    }
}

var Subscriber = (function (_super) {
    __extends(Subscriber, _super);
    function Subscriber(destination) {
        var _this = _super.call(this) || this;
        _this.isStopped = false;
        if (destination) {
            _this.destination = destination;
            if (isSubscription(destination)) {
                destination.add(_this);
            }
        }
        else {
            _this.destination = EMPTY_OBSERVER;
        }
        return _this;
    }
    Subscriber.create = function (next, error, complete) {
        return new SafeSubscriber(next, error, complete);
    };
    Subscriber.prototype.next = function (value) {
        if (this.isStopped) ;
        else {
            this._next(value);
        }
    };
    Subscriber.prototype.error = function (err) {
        if (this.isStopped) ;
        else {
            this.isStopped = true;
            this._error(err);
        }
    };
    Subscriber.prototype.complete = function () {
        if (this.isStopped) ;
        else {
            this.isStopped = true;
            this._complete();
        }
    };
    Subscriber.prototype.unsubscribe = function () {
        if (!this.closed) {
            this.isStopped = true;
            _super.prototype.unsubscribe.call(this);
            this.destination = null;
        }
    };
    Subscriber.prototype._next = function (value) {
        this.destination.next(value);
    };
    Subscriber.prototype._error = function (err) {
        try {
            this.destination.error(err);
        }
        finally {
            this.unsubscribe();
        }
    };
    Subscriber.prototype._complete = function () {
        try {
            this.destination.complete();
        }
        finally {
            this.unsubscribe();
        }
    };
    return Subscriber;
}(Subscription));
var ConsumerObserver = (function () {
    function ConsumerObserver(partialObserver) {
        this.partialObserver = partialObserver;
    }
    ConsumerObserver.prototype.next = function (value) {
        var partialObserver = this.partialObserver;
        if (partialObserver.next) {
            try {
                partialObserver.next(value);
            }
            catch (error) {
                handleUnhandledError(error);
            }
        }
    };
    ConsumerObserver.prototype.error = function (err) {
        var partialObserver = this.partialObserver;
        if (partialObserver.error) {
            try {
                partialObserver.error(err);
            }
            catch (error) {
                handleUnhandledError(error);
            }
        }
        else {
            handleUnhandledError(err);
        }
    };
    ConsumerObserver.prototype.complete = function () {
        var partialObserver = this.partialObserver;
        if (partialObserver.complete) {
            try {
                partialObserver.complete();
            }
            catch (error) {
                handleUnhandledError(error);
            }
        }
    };
    return ConsumerObserver;
}());
var SafeSubscriber = (function (_super) {
    __extends(SafeSubscriber, _super);
    function SafeSubscriber(observerOrNext, error, complete) {
        var _this = _super.call(this) || this;
        var partialObserver;
        if (isFunction(observerOrNext) || !observerOrNext) {
            partialObserver = {
                next: (observerOrNext !== null && observerOrNext !== void 0 ? observerOrNext : undefined),
                error: error !== null && error !== void 0 ? error : undefined,
                complete: complete !== null && complete !== void 0 ? complete : undefined,
            };
        }
        else {
            {
                partialObserver = observerOrNext;
            }
        }
        _this.destination = new ConsumerObserver(partialObserver);
        return _this;
    }
    return SafeSubscriber;
}(Subscriber));
function handleUnhandledError(error) {
    {
        reportUnhandledError(error);
    }
}
function defaultErrorHandler(err) {
    throw err;
}
var EMPTY_OBSERVER = {
    closed: true,
    next: noop,
    error: defaultErrorHandler,
    complete: noop,
};

var observable = (function () { return (typeof Symbol === 'function' && Symbol.observable) || '@@observable'; })();

function identity(x) {
    return x;
}

function pipeFromArray(fns) {
    if (fns.length === 0) {
        return identity;
    }
    if (fns.length === 1) {
        return fns[0];
    }
    return function piped(input) {
        return fns.reduce(function (prev, fn) { return fn(prev); }, input);
    };
}

var Observable = (function () {
    function Observable(subscribe) {
        if (subscribe) {
            this._subscribe = subscribe;
        }
    }
    Observable.prototype.lift = function (operator) {
        var observable = new Observable();
        observable.source = this;
        observable.operator = operator;
        return observable;
    };
    Observable.prototype.subscribe = function (observerOrNext, error, complete) {
        var _this = this;
        var subscriber = isSubscriber(observerOrNext) ? observerOrNext : new SafeSubscriber(observerOrNext, error, complete);
        errorContext(function () {
            var _a = _this, operator = _a.operator, source = _a.source;
            subscriber.add(operator
                ?
                    operator.call(subscriber, source)
                : source
                    ?
                        _this._subscribe(subscriber)
                    :
                        _this._trySubscribe(subscriber));
        });
        return subscriber;
    };
    Observable.prototype._trySubscribe = function (sink) {
        try {
            return this._subscribe(sink);
        }
        catch (err) {
            sink.error(err);
        }
    };
    Observable.prototype.forEach = function (next, promiseCtor) {
        var _this = this;
        promiseCtor = getPromiseCtor(promiseCtor);
        return new promiseCtor(function (resolve, reject) {
            var subscriber = new SafeSubscriber({
                next: function (value) {
                    try {
                        next(value);
                    }
                    catch (err) {
                        reject(err);
                        subscriber.unsubscribe();
                    }
                },
                error: reject,
                complete: resolve,
            });
            _this.subscribe(subscriber);
        });
    };
    Observable.prototype._subscribe = function (subscriber) {
        var _a;
        return (_a = this.source) === null || _a === void 0 ? void 0 : _a.subscribe(subscriber);
    };
    Observable.prototype[observable] = function () {
        return this;
    };
    Observable.prototype.pipe = function () {
        var operations = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            operations[_i] = arguments[_i];
        }
        return pipeFromArray(operations)(this);
    };
    Observable.prototype.toPromise = function (promiseCtor) {
        var _this = this;
        promiseCtor = getPromiseCtor(promiseCtor);
        return new promiseCtor(function (resolve, reject) {
            var value;
            _this.subscribe(function (x) { return (value = x); }, function (err) { return reject(err); }, function () { return resolve(value); });
        });
    };
    Observable.create = function (subscribe) {
        return new Observable(subscribe);
    };
    return Observable;
}());
function getPromiseCtor(promiseCtor) {
    var _a;
    return (_a = promiseCtor !== null && promiseCtor !== void 0 ? promiseCtor : config.Promise) !== null && _a !== void 0 ? _a : Promise;
}
function isObserver(value) {
    return value && isFunction(value.next) && isFunction(value.error) && isFunction(value.complete);
}
function isSubscriber(value) {
    return (value && value instanceof Subscriber) || (isObserver(value) && isSubscription(value));
}

function hasLift(source) {
    return isFunction(source === null || source === void 0 ? void 0 : source.lift);
}
function operate(init) {
    return function (source) {
        if (hasLift(source)) {
            return source.lift(function (liftedSource) {
                try {
                    return init(liftedSource, this);
                }
                catch (err) {
                    this.error(err);
                }
            });
        }
        throw new TypeError('Unable to lift unknown Observable type');
    };
}

function createOperatorSubscriber(destination, onNext, onComplete, onError, onFinalize) {
    return new OperatorSubscriber(destination, onNext, onComplete, onError, onFinalize);
}
var OperatorSubscriber = (function (_super) {
    __extends(OperatorSubscriber, _super);
    function OperatorSubscriber(destination, onNext, onComplete, onError, onFinalize, shouldUnsubscribe) {
        var _this = _super.call(this, destination) || this;
        _this.onFinalize = onFinalize;
        _this.shouldUnsubscribe = shouldUnsubscribe;
        _this._next = onNext
            ? function (value) {
                try {
                    onNext(value);
                }
                catch (err) {
                    destination.error(err);
                }
            }
            : _super.prototype._next;
        _this._error = onError
            ? function (err) {
                try {
                    onError(err);
                }
                catch (err) {
                    destination.error(err);
                }
                finally {
                    this.unsubscribe();
                }
            }
            : _super.prototype._error;
        _this._complete = onComplete
            ? function () {
                try {
                    onComplete();
                }
                catch (err) {
                    destination.error(err);
                }
                finally {
                    this.unsubscribe();
                }
            }
            : _super.prototype._complete;
        return _this;
    }
    OperatorSubscriber.prototype.unsubscribe = function () {
        var _a;
        if (!this.shouldUnsubscribe || this.shouldUnsubscribe()) {
            var closed_1 = this.closed;
            _super.prototype.unsubscribe.call(this);
            !closed_1 && ((_a = this.onFinalize) === null || _a === void 0 ? void 0 : _a.call(this));
        }
    };
    return OperatorSubscriber;
}(Subscriber));

var ObjectUnsubscribedError = createErrorClass(function (_super) {
    return function ObjectUnsubscribedErrorImpl() {
        _super(this);
        this.name = 'ObjectUnsubscribedError';
        this.message = 'object unsubscribed';
    };
});

var Subject = (function (_super) {
    __extends(Subject, _super);
    function Subject() {
        var _this = _super.call(this) || this;
        _this.closed = false;
        _this.currentObservers = null;
        _this.observers = [];
        _this.isStopped = false;
        _this.hasError = false;
        _this.thrownError = null;
        return _this;
    }
    Subject.prototype.lift = function (operator) {
        var subject = new AnonymousSubject(this, this);
        subject.operator = operator;
        return subject;
    };
    Subject.prototype._throwIfClosed = function () {
        if (this.closed) {
            throw new ObjectUnsubscribedError();
        }
    };
    Subject.prototype.next = function (value) {
        var _this = this;
        errorContext(function () {
            var e_1, _a;
            _this._throwIfClosed();
            if (!_this.isStopped) {
                if (!_this.currentObservers) {
                    _this.currentObservers = Array.from(_this.observers);
                }
                try {
                    for (var _b = __values(_this.currentObservers), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var observer = _c.value;
                        observer.next(value);
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            }
        });
    };
    Subject.prototype.error = function (err) {
        var _this = this;
        errorContext(function () {
            _this._throwIfClosed();
            if (!_this.isStopped) {
                _this.hasError = _this.isStopped = true;
                _this.thrownError = err;
                var observers = _this.observers;
                while (observers.length) {
                    observers.shift().error(err);
                }
            }
        });
    };
    Subject.prototype.complete = function () {
        var _this = this;
        errorContext(function () {
            _this._throwIfClosed();
            if (!_this.isStopped) {
                _this.isStopped = true;
                var observers = _this.observers;
                while (observers.length) {
                    observers.shift().complete();
                }
            }
        });
    };
    Subject.prototype.unsubscribe = function () {
        this.isStopped = this.closed = true;
        this.observers = this.currentObservers = null;
    };
    Object.defineProperty(Subject.prototype, "observed", {
        get: function () {
            var _a;
            return ((_a = this.observers) === null || _a === void 0 ? void 0 : _a.length) > 0;
        },
        enumerable: false,
        configurable: true
    });
    Subject.prototype._trySubscribe = function (subscriber) {
        this._throwIfClosed();
        return _super.prototype._trySubscribe.call(this, subscriber);
    };
    Subject.prototype._subscribe = function (subscriber) {
        this._throwIfClosed();
        this._checkFinalizedStatuses(subscriber);
        return this._innerSubscribe(subscriber);
    };
    Subject.prototype._innerSubscribe = function (subscriber) {
        var _this = this;
        var _a = this, hasError = _a.hasError, isStopped = _a.isStopped, observers = _a.observers;
        if (hasError || isStopped) {
            return EMPTY_SUBSCRIPTION;
        }
        this.currentObservers = null;
        observers.push(subscriber);
        return new Subscription(function () {
            _this.currentObservers = null;
            arrRemove(observers, subscriber);
        });
    };
    Subject.prototype._checkFinalizedStatuses = function (subscriber) {
        var _a = this, hasError = _a.hasError, thrownError = _a.thrownError, isStopped = _a.isStopped;
        if (hasError) {
            subscriber.error(thrownError);
        }
        else if (isStopped) {
            subscriber.complete();
        }
    };
    Subject.prototype.asObservable = function () {
        var observable = new Observable();
        observable.source = this;
        return observable;
    };
    Subject.create = function (destination, source) {
        return new AnonymousSubject(destination, source);
    };
    return Subject;
}(Observable));
var AnonymousSubject = (function (_super) {
    __extends(AnonymousSubject, _super);
    function AnonymousSubject(destination, source) {
        var _this = _super.call(this) || this;
        _this.destination = destination;
        _this.source = source;
        return _this;
    }
    AnonymousSubject.prototype.next = function (value) {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.next) === null || _b === void 0 ? void 0 : _b.call(_a, value);
    };
    AnonymousSubject.prototype.error = function (err) {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, err);
    };
    AnonymousSubject.prototype.complete = function () {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.complete) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    AnonymousSubject.prototype._subscribe = function (subscriber) {
        var _a, _b;
        return (_b = (_a = this.source) === null || _a === void 0 ? void 0 : _a.subscribe(subscriber)) !== null && _b !== void 0 ? _b : EMPTY_SUBSCRIPTION;
    };
    return AnonymousSubject;
}(Subject));

var dateTimestampProvider = {
    now: function () {
        return (dateTimestampProvider.delegate || Date).now();
    },
    delegate: undefined,
};

var ReplaySubject = (function (_super) {
    __extends(ReplaySubject, _super);
    function ReplaySubject(_bufferSize, _windowTime, _timestampProvider) {
        if (_bufferSize === void 0) { _bufferSize = Infinity; }
        if (_windowTime === void 0) { _windowTime = Infinity; }
        if (_timestampProvider === void 0) { _timestampProvider = dateTimestampProvider; }
        var _this = _super.call(this) || this;
        _this._bufferSize = _bufferSize;
        _this._windowTime = _windowTime;
        _this._timestampProvider = _timestampProvider;
        _this._buffer = [];
        _this._infiniteTimeWindow = true;
        _this._infiniteTimeWindow = _windowTime === Infinity;
        _this._bufferSize = Math.max(1, _bufferSize);
        _this._windowTime = Math.max(1, _windowTime);
        return _this;
    }
    ReplaySubject.prototype.next = function (value) {
        var _a = this, isStopped = _a.isStopped, _buffer = _a._buffer, _infiniteTimeWindow = _a._infiniteTimeWindow, _timestampProvider = _a._timestampProvider, _windowTime = _a._windowTime;
        if (!isStopped) {
            _buffer.push(value);
            !_infiniteTimeWindow && _buffer.push(_timestampProvider.now() + _windowTime);
        }
        this._trimBuffer();
        _super.prototype.next.call(this, value);
    };
    ReplaySubject.prototype._subscribe = function (subscriber) {
        this._throwIfClosed();
        this._trimBuffer();
        var subscription = this._innerSubscribe(subscriber);
        var _a = this, _infiniteTimeWindow = _a._infiniteTimeWindow, _buffer = _a._buffer;
        var copy = _buffer.slice();
        for (var i = 0; i < copy.length && !subscriber.closed; i += _infiniteTimeWindow ? 1 : 2) {
            subscriber.next(copy[i]);
        }
        this._checkFinalizedStatuses(subscriber);
        return subscription;
    };
    ReplaySubject.prototype._trimBuffer = function () {
        var _a = this, _bufferSize = _a._bufferSize, _timestampProvider = _a._timestampProvider, _buffer = _a._buffer, _infiniteTimeWindow = _a._infiniteTimeWindow;
        var adjustedBufferSize = (_infiniteTimeWindow ? 1 : 2) * _bufferSize;
        _bufferSize < Infinity && adjustedBufferSize < _buffer.length && _buffer.splice(0, _buffer.length - adjustedBufferSize);
        if (!_infiniteTimeWindow) {
            var now = _timestampProvider.now();
            var last = 0;
            for (var i = 1; i < _buffer.length && _buffer[i] <= now; i += 2) {
                last = i;
            }
            last && _buffer.splice(0, last + 1);
        }
    };
    return ReplaySubject;
}(Subject));

function isScheduler(value) {
    return value && isFunction(value.schedule);
}

function last(arr) {
    return arr[arr.length - 1];
}
function popResultSelector(args) {
    return isFunction(last(args)) ? args.pop() : undefined;
}
function popScheduler(args) {
    return isScheduler(last(args)) ? args.pop() : undefined;
}

var isArrayLike = (function (x) { return x && typeof x.length === 'number' && typeof x !== 'function'; });

function isPromise(value) {
    return isFunction(value === null || value === void 0 ? void 0 : value.then);
}

function isInteropObservable(input) {
    return isFunction(input[observable]);
}

function isAsyncIterable(obj) {
    return Symbol.asyncIterator && isFunction(obj === null || obj === void 0 ? void 0 : obj[Symbol.asyncIterator]);
}

function createInvalidObservableTypeError(input) {
    return new TypeError("You provided " + (input !== null && typeof input === 'object' ? 'an invalid object' : "'" + input + "'") + " where a stream was expected. You can provide an Observable, Promise, ReadableStream, Array, AsyncIterable, or Iterable.");
}

function getSymbolIterator() {
    if (typeof Symbol !== 'function' || !Symbol.iterator) {
        return '@@iterator';
    }
    return Symbol.iterator;
}
var iterator = getSymbolIterator();

function isIterable(input) {
    return isFunction(input === null || input === void 0 ? void 0 : input[iterator]);
}

function readableStreamLikeToAsyncGenerator(readableStream) {
    return __asyncGenerator(this, arguments, function readableStreamLikeToAsyncGenerator_1() {
        var reader, _a, value, done;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    reader = readableStream.getReader();
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, , 9, 10]);
                    _b.label = 2;
                case 2:
                    return [4, __await(reader.read())];
                case 3:
                    _a = _b.sent(), value = _a.value, done = _a.done;
                    if (!done) return [3, 5];
                    return [4, __await(void 0)];
                case 4: return [2, _b.sent()];
                case 5: return [4, __await(value)];
                case 6: return [4, _b.sent()];
                case 7:
                    _b.sent();
                    return [3, 2];
                case 8: return [3, 10];
                case 9:
                    reader.releaseLock();
                    return [7];
                case 10: return [2];
            }
        });
    });
}
function isReadableStreamLike(obj) {
    return isFunction(obj === null || obj === void 0 ? void 0 : obj.getReader);
}

function innerFrom(input) {
    if (input instanceof Observable) {
        return input;
    }
    if (input != null) {
        if (isInteropObservable(input)) {
            return fromInteropObservable(input);
        }
        if (isArrayLike(input)) {
            return fromArrayLike(input);
        }
        if (isPromise(input)) {
            return fromPromise(input);
        }
        if (isAsyncIterable(input)) {
            return fromAsyncIterable(input);
        }
        if (isIterable(input)) {
            return fromIterable(input);
        }
        if (isReadableStreamLike(input)) {
            return fromReadableStreamLike(input);
        }
    }
    throw createInvalidObservableTypeError(input);
}
function fromInteropObservable(obj) {
    return new Observable(function (subscriber) {
        var obs = obj[observable]();
        if (isFunction(obs.subscribe)) {
            return obs.subscribe(subscriber);
        }
        throw new TypeError('Provided object does not correctly implement Symbol.observable');
    });
}
function fromArrayLike(array) {
    return new Observable(function (subscriber) {
        for (var i = 0; i < array.length && !subscriber.closed; i++) {
            subscriber.next(array[i]);
        }
        subscriber.complete();
    });
}
function fromPromise(promise) {
    return new Observable(function (subscriber) {
        promise
            .then(function (value) {
            if (!subscriber.closed) {
                subscriber.next(value);
                subscriber.complete();
            }
        }, function (err) { return subscriber.error(err); })
            .then(null, reportUnhandledError);
    });
}
function fromIterable(iterable) {
    return new Observable(function (subscriber) {
        var e_1, _a;
        try {
            for (var iterable_1 = __values(iterable), iterable_1_1 = iterable_1.next(); !iterable_1_1.done; iterable_1_1 = iterable_1.next()) {
                var value = iterable_1_1.value;
                subscriber.next(value);
                if (subscriber.closed) {
                    return;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (iterable_1_1 && !iterable_1_1.done && (_a = iterable_1.return)) _a.call(iterable_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        subscriber.complete();
    });
}
function fromAsyncIterable(asyncIterable) {
    return new Observable(function (subscriber) {
        process$1(asyncIterable, subscriber).catch(function (err) { return subscriber.error(err); });
    });
}
function fromReadableStreamLike(readableStream) {
    return fromAsyncIterable(readableStreamLikeToAsyncGenerator(readableStream));
}
function process$1(asyncIterable, subscriber) {
    var asyncIterable_1, asyncIterable_1_1;
    var e_2, _a;
    return __awaiter(this, void 0, void 0, function () {
        var value, e_2_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 5, 6, 11]);
                    asyncIterable_1 = __asyncValues(asyncIterable);
                    _b.label = 1;
                case 1: return [4, asyncIterable_1.next()];
                case 2:
                    if (!(asyncIterable_1_1 = _b.sent(), !asyncIterable_1_1.done)) return [3, 4];
                    value = asyncIterable_1_1.value;
                    subscriber.next(value);
                    if (subscriber.closed) {
                        return [2];
                    }
                    _b.label = 3;
                case 3: return [3, 1];
                case 4: return [3, 11];
                case 5:
                    e_2_1 = _b.sent();
                    e_2 = { error: e_2_1 };
                    return [3, 11];
                case 6:
                    _b.trys.push([6, , 9, 10]);
                    if (!(asyncIterable_1_1 && !asyncIterable_1_1.done && (_a = asyncIterable_1.return))) return [3, 8];
                    return [4, _a.call(asyncIterable_1)];
                case 7:
                    _b.sent();
                    _b.label = 8;
                case 8: return [3, 10];
                case 9:
                    if (e_2) throw e_2.error;
                    return [7];
                case 10: return [7];
                case 11:
                    subscriber.complete();
                    return [2];
            }
        });
    });
}

function executeSchedule(parentSubscription, scheduler, work, delay, repeat) {
    if (delay === void 0) { delay = 0; }
    if (repeat === void 0) { repeat = false; }
    var scheduleSubscription = scheduler.schedule(function () {
        work();
        if (repeat) {
            parentSubscription.add(this.schedule(null, delay));
        }
        else {
            this.unsubscribe();
        }
    }, delay);
    parentSubscription.add(scheduleSubscription);
    if (!repeat) {
        return scheduleSubscription;
    }
}

function observeOn(scheduler, delay) {
    if (delay === void 0) { delay = 0; }
    return operate(function (source, subscriber) {
        source.subscribe(createOperatorSubscriber(subscriber, function (value) { return executeSchedule(subscriber, scheduler, function () { return subscriber.next(value); }, delay); }, function () { return executeSchedule(subscriber, scheduler, function () { return subscriber.complete(); }, delay); }, function (err) { return executeSchedule(subscriber, scheduler, function () { return subscriber.error(err); }, delay); }));
    });
}

function subscribeOn(scheduler, delay) {
    if (delay === void 0) { delay = 0; }
    return operate(function (source, subscriber) {
        subscriber.add(scheduler.schedule(function () { return source.subscribe(subscriber); }, delay));
    });
}

function scheduleObservable(input, scheduler) {
    return innerFrom(input).pipe(subscribeOn(scheduler), observeOn(scheduler));
}

function schedulePromise(input, scheduler) {
    return innerFrom(input).pipe(subscribeOn(scheduler), observeOn(scheduler));
}

function scheduleArray(input, scheduler) {
    return new Observable(function (subscriber) {
        var i = 0;
        return scheduler.schedule(function () {
            if (i === input.length) {
                subscriber.complete();
            }
            else {
                subscriber.next(input[i++]);
                if (!subscriber.closed) {
                    this.schedule();
                }
            }
        });
    });
}

function scheduleIterable(input, scheduler) {
    return new Observable(function (subscriber) {
        var iterator$1;
        executeSchedule(subscriber, scheduler, function () {
            iterator$1 = input[iterator]();
            executeSchedule(subscriber, scheduler, function () {
                var _a;
                var value;
                var done;
                try {
                    (_a = iterator$1.next(), value = _a.value, done = _a.done);
                }
                catch (err) {
                    subscriber.error(err);
                    return;
                }
                if (done) {
                    subscriber.complete();
                }
                else {
                    subscriber.next(value);
                }
            }, 0, true);
        });
        return function () { return isFunction(iterator$1 === null || iterator$1 === void 0 ? void 0 : iterator$1.return) && iterator$1.return(); };
    });
}

function scheduleAsyncIterable(input, scheduler) {
    if (!input) {
        throw new Error('Iterable cannot be null');
    }
    return new Observable(function (subscriber) {
        executeSchedule(subscriber, scheduler, function () {
            var iterator = input[Symbol.asyncIterator]();
            executeSchedule(subscriber, scheduler, function () {
                iterator.next().then(function (result) {
                    if (result.done) {
                        subscriber.complete();
                    }
                    else {
                        subscriber.next(result.value);
                    }
                });
            }, 0, true);
        });
    });
}

function scheduleReadableStreamLike(input, scheduler) {
    return scheduleAsyncIterable(readableStreamLikeToAsyncGenerator(input), scheduler);
}

function scheduled(input, scheduler) {
    if (input != null) {
        if (isInteropObservable(input)) {
            return scheduleObservable(input, scheduler);
        }
        if (isArrayLike(input)) {
            return scheduleArray(input, scheduler);
        }
        if (isPromise(input)) {
            return schedulePromise(input, scheduler);
        }
        if (isAsyncIterable(input)) {
            return scheduleAsyncIterable(input, scheduler);
        }
        if (isIterable(input)) {
            return scheduleIterable(input, scheduler);
        }
        if (isReadableStreamLike(input)) {
            return scheduleReadableStreamLike(input, scheduler);
        }
    }
    throw createInvalidObservableTypeError(input);
}

function from(input, scheduler) {
    return scheduler ? scheduled(input, scheduler) : innerFrom(input);
}

function map(project, thisArg) {
    return operate(function (source, subscriber) {
        var index = 0;
        source.subscribe(createOperatorSubscriber(subscriber, function (value) {
            subscriber.next(project.call(thisArg, value, index++));
        }));
    });
}

var isArray$1 = Array.isArray;
function callOrApply(fn, args) {
    return isArray$1(args) ? fn.apply(void 0, __spreadArray([], __read(args))) : fn(args);
}
function mapOneOrManyArgs(fn) {
    return map(function (args) { return callOrApply(fn, args); });
}

var isArray = Array.isArray;
var getPrototypeOf = Object.getPrototypeOf, objectProto = Object.prototype, getKeys = Object.keys;
function argsArgArrayOrObject(args) {
    if (args.length === 1) {
        var first_1 = args[0];
        if (isArray(first_1)) {
            return { args: first_1, keys: null };
        }
        if (isPOJO(first_1)) {
            var keys = getKeys(first_1);
            return {
                args: keys.map(function (key) { return first_1[key]; }),
                keys: keys,
            };
        }
    }
    return { args: args, keys: null };
}
function isPOJO(obj) {
    return obj && typeof obj === 'object' && getPrototypeOf(obj) === objectProto;
}

function createObject(keys, values) {
    return keys.reduce(function (result, key, i) { return ((result[key] = values[i]), result); }, {});
}

function combineLatest() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    var scheduler = popScheduler(args);
    var resultSelector = popResultSelector(args);
    var _a = argsArgArrayOrObject(args), observables = _a.args, keys = _a.keys;
    if (observables.length === 0) {
        return from([], scheduler);
    }
    var result = new Observable(combineLatestInit(observables, scheduler, keys
        ?
            function (values) { return createObject(keys, values); }
        :
            identity));
    return resultSelector ? result.pipe(mapOneOrManyArgs(resultSelector)) : result;
}
function combineLatestInit(observables, scheduler, valueTransform) {
    if (valueTransform === void 0) { valueTransform = identity; }
    return function (subscriber) {
        maybeSchedule(scheduler, function () {
            var length = observables.length;
            var values = new Array(length);
            var active = length;
            var remainingFirstValues = length;
            var _loop_1 = function (i) {
                maybeSchedule(scheduler, function () {
                    var source = from(observables[i], scheduler);
                    var hasFirstValue = false;
                    source.subscribe(createOperatorSubscriber(subscriber, function (value) {
                        values[i] = value;
                        if (!hasFirstValue) {
                            hasFirstValue = true;
                            remainingFirstValues--;
                        }
                        if (!remainingFirstValues) {
                            subscriber.next(valueTransform(values.slice()));
                        }
                    }, function () {
                        if (!--active) {
                            subscriber.complete();
                        }
                    }));
                }, subscriber);
            };
            for (var i = 0; i < length; i++) {
                _loop_1(i);
            }
        }, subscriber);
    };
}
function maybeSchedule(scheduler, execute, subscription) {
    if (scheduler) {
        executeSchedule(subscription, scheduler, execute);
    }
    else {
        execute();
    }
}

function mergeInternals(source, subscriber, project, concurrent, onBeforeNext, expand, innerSubScheduler, additionalFinalizer) {
    var buffer = [];
    var active = 0;
    var index = 0;
    var isComplete = false;
    var checkComplete = function () {
        if (isComplete && !buffer.length && !active) {
            subscriber.complete();
        }
    };
    var outerNext = function (value) { return (active < concurrent ? doInnerSub(value) : buffer.push(value)); };
    var doInnerSub = function (value) {
        active++;
        var innerComplete = false;
        innerFrom(project(value, index++)).subscribe(createOperatorSubscriber(subscriber, function (innerValue) {
            {
                subscriber.next(innerValue);
            }
        }, function () {
            innerComplete = true;
        }, undefined, function () {
            if (innerComplete) {
                try {
                    active--;
                    var _loop_1 = function () {
                        var bufferedValue = buffer.shift();
                        if (innerSubScheduler) ;
                        else {
                            doInnerSub(bufferedValue);
                        }
                    };
                    while (buffer.length && active < concurrent) {
                        _loop_1();
                    }
                    checkComplete();
                }
                catch (err) {
                    subscriber.error(err);
                }
            }
        }));
    };
    source.subscribe(createOperatorSubscriber(subscriber, outerNext, function () {
        isComplete = true;
        checkComplete();
    }));
    return function () {
    };
}

function mergeMap(project, resultSelector, concurrent) {
    if (concurrent === void 0) { concurrent = Infinity; }
    if (isFunction(resultSelector)) {
        return mergeMap(function (a, i) { return map(function (b, ii) { return resultSelector(a, b, i, ii); })(innerFrom(project(a, i))); }, concurrent);
    }
    else if (typeof resultSelector === 'number') {
        concurrent = resultSelector;
    }
    return operate(function (source, subscriber) { return mergeInternals(source, subscriber, project, concurrent); });
}

function mergeAll(concurrent) {
    return mergeMap(identity, concurrent);
}

function concatAll() {
    return mergeAll(1);
}

function concat() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    return concatAll()(from(args, popScheduler(args)));
}

var nodeEventEmitterMethods = ['addListener', 'removeListener'];
var eventTargetMethods = ['addEventListener', 'removeEventListener'];
var jqueryMethods = ['on', 'off'];
function fromEvent(target, eventName, options, resultSelector) {
    if (isFunction(options)) {
        resultSelector = options;
        options = undefined;
    }
    if (resultSelector) {
        return fromEvent(target, eventName, options).pipe(mapOneOrManyArgs(resultSelector));
    }
    var _a = __read(isEventTarget(target)
        ? eventTargetMethods.map(function (methodName) { return function (handler) { return target[methodName](eventName, handler, options); }; })
        :
            isNodeStyleEventEmitter(target)
                ? nodeEventEmitterMethods.map(toCommonHandlerRegistry(target, eventName))
                : isJQueryStyleEventEmitter(target)
                    ? jqueryMethods.map(toCommonHandlerRegistry(target, eventName))
                    : [], 2), add = _a[0], remove = _a[1];
    if (!add) {
        if (isArrayLike(target)) {
            return mergeMap(function (subTarget) { return fromEvent(subTarget, eventName, options); })(innerFrom(target));
        }
    }
    if (!add) {
        throw new TypeError('Invalid event target');
    }
    return new Observable(function (subscriber) {
        var handler = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return subscriber.next(1 < args.length ? args : args[0]);
        };
        add(handler);
        return function () { return remove(handler); };
    });
}
function toCommonHandlerRegistry(target, eventName) {
    return function (methodName) { return function (handler) { return target[methodName](eventName, handler); }; };
}
function isNodeStyleEventEmitter(target) {
    return isFunction(target.addListener) && isFunction(target.removeListener);
}
function isJQueryStyleEventEmitter(target) {
    return isFunction(target.on) && isFunction(target.off);
}
function isEventTarget(target) {
    return isFunction(target.addEventListener) && isFunction(target.removeEventListener);
}

function share(options) {
    if (options === void 0) { options = {}; }
    var _a = options.connector, connector = _a === void 0 ? function () { return new Subject(); } : _a, _b = options.resetOnError, resetOnError = _b === void 0 ? true : _b, _c = options.resetOnComplete, resetOnComplete = _c === void 0 ? true : _c, _d = options.resetOnRefCountZero, resetOnRefCountZero = _d === void 0 ? true : _d;
    return function (wrapperSource) {
        var connection;
        var resetConnection;
        var subject;
        var refCount = 0;
        var hasCompleted = false;
        var hasErrored = false;
        var cancelReset = function () {
            resetConnection === null || resetConnection === void 0 ? void 0 : resetConnection.unsubscribe();
            resetConnection = undefined;
        };
        var reset = function () {
            cancelReset();
            connection = subject = undefined;
            hasCompleted = hasErrored = false;
        };
        var resetAndUnsubscribe = function () {
            var conn = connection;
            reset();
            conn === null || conn === void 0 ? void 0 : conn.unsubscribe();
        };
        return operate(function (source, subscriber) {
            refCount++;
            if (!hasErrored && !hasCompleted) {
                cancelReset();
            }
            var dest = (subject = subject !== null && subject !== void 0 ? subject : connector());
            subscriber.add(function () {
                refCount--;
                if (refCount === 0 && !hasErrored && !hasCompleted) {
                    resetConnection = handleReset(resetAndUnsubscribe, resetOnRefCountZero);
                }
            });
            dest.subscribe(subscriber);
            if (!connection &&
                refCount > 0) {
                connection = new SafeSubscriber({
                    next: function (value) { return dest.next(value); },
                    error: function (err) {
                        hasErrored = true;
                        cancelReset();
                        resetConnection = handleReset(reset, resetOnError, err);
                        dest.error(err);
                    },
                    complete: function () {
                        hasCompleted = true;
                        cancelReset();
                        resetConnection = handleReset(reset, resetOnComplete);
                        dest.complete();
                    },
                });
                innerFrom(source).subscribe(connection);
            }
        })(wrapperSource);
    };
}
function handleReset(reset, on) {
    var args = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        args[_i - 2] = arguments[_i];
    }
    if (on === true) {
        reset();
        return;
    }
    if (on === false) {
        return;
    }
    var onSubscriber = new SafeSubscriber({
        next: function () {
            onSubscriber.unsubscribe();
            reset();
        },
    });
    return innerFrom(on.apply(void 0, __spreadArray([], __read(args)))).subscribe(onSubscriber);
}

function shareReplay(configOrBufferSize, windowTime, scheduler) {
    var _a, _b, _c;
    var bufferSize;
    var refCount = false;
    if (configOrBufferSize && typeof configOrBufferSize === 'object') {
        (_a = configOrBufferSize.bufferSize, bufferSize = _a === void 0 ? Infinity : _a, _b = configOrBufferSize.windowTime, windowTime = _b === void 0 ? Infinity : _b, _c = configOrBufferSize.refCount, refCount = _c === void 0 ? false : _c, scheduler = configOrBufferSize.scheduler);
    }
    else {
        bufferSize = (configOrBufferSize !== null && configOrBufferSize !== void 0 ? configOrBufferSize : Infinity);
    }
    return share({
        connector: function () { return new ReplaySubject(bufferSize, windowTime, scheduler); },
        resetOnError: true,
        resetOnComplete: false,
        resetOnRefCountZero: refCount,
    });
}

function startWith() {
    var values = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        values[_i] = arguments[_i];
    }
    var scheduler = popScheduler(values);
    return operate(function (source, subscriber) {
        (scheduler ? concat(values, source, scheduler) : concat(values, source)).subscribe(subscriber);
    });
}

/**
 * Represents the breakpoint value for phone devices in pixels.
 * This constant is used to define the maximum width for phone-specific layouts or styles.
 */ const PHONE = 640;
/**
 * Represents the minimum screen width in pixels for tablet devices.
 * This constant is typically used for responsive design breakpoints.
 */ const TABLET = 1007;
/**
 * Represents the breakpoint value for desktop screens in pixels.
 * This constant is used to define the width threshold for desktop devices.
 */ const DESKTOP = 1008;
/**
 * Checks if the userAgent is of mobile origin.
 * @returns {boolean} A boolean representing if the device is mobile or not.
 */ function isOnMobileDevice() {
    let check = false;
    (function(a) {
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) {
            check = true;
        }
    })(navigator.userAgent || navigator.vendor);
    return check;
}
/**
 * @returns {number}
 */ function getWindowWidth() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}
/**
 * Determines if the current viewport width corresponds to a phone-sized screen.
 *
 * @returns {boolean} `true` if the viewport width is less than or equal to the `PHONE` constant
 * and greater than 0, otherwise `false`.
 */ function isPhone() {
    const width = window.innerWidth || document.body.clientWidth;
    return width <= PHONE && width > 0;
}
/**
 * Determines if the current device is a tablet based on the viewport width.
 *
 * @returns {boolean} `true` if the viewport width is less than or equal to the `TABLET` breakpoint
 * and greater than the `PHONE` breakpoint, otherwise `false`.
 */ function isTablet() {
    const width = window.innerWidth || document.body.clientWidth;
    return width <= TABLET && width > PHONE;
}
/**
 * Determines if the current viewport width corresponds to a desktop device.
 *
 * @returns {boolean} `true` if the viewport width is greater than the `DESKTOP` breakpoint
 * or falls between the `TABLET` and `DESKTOP` breakpoints; otherwise, `false`.
 */ function isDesktop() {
    const width = window.innerWidth || document.body.clientWidth;
    return width > DESKTOP || width <= DESKTOP && width > TABLET;
}

/**
 * The BreakpointObserver class allows you to observe changes in predefined breakpoints ('phone', 'tablet', 'desktop') or custom
 * media queries based on the window width. It uses window.matchMedia to detect when the breakpoints change and notifies the
 * registered callbacks with the current state.
 *
 * @example
 * // Example 1: Observing changes in 'phone' and 'tablet' breakpoints using Breakpoints enum
 * const observer = new BreakpointObserver();
 * const callback = (state: BreakpointState) => {
 *     console.log('Breakpoints state:', state);
 *     if (state.matches) {
 *         console.log('At least one breakpoint matches:', state.breakpoints);
 *     }
 * };
 * observer.observe(['phone', 'tablet'], callback);
 *
 * // Later, when done, unobserve using the same callback
 * observer.unobserve(['phone', 'tablet'], callback);
 *
 * @example
 * // Example 2: Combining a predefined breakpoint with a custom media query
 * const customCallback = (state: BreakpointState) => {
 *     console.log('Custom query state:', state);
 *     if (state.breakpoints.get('desktop')) {
 *         console.log('Desktop breakpoint is active');
 *     }
 * };
 * observer.observe(['desktop', '(min-width: 800px) and (orientation: portrait)'], customCallback);
 *
 * // Unobserve when done
 * observer.unobserve(['desktop', '(min-width: 800px) and (orientation: portrait)'], customCallback);
 *
 * @example
 * // Example 3: Disconnecting all observers when the application shuts down
 * const shutdownCallback = (state: BreakpointState) => {
 *     console.log('Shutdown observer state:', state);
 * };
 * observer.observe(['phone'], shutdownCallback);
 *
 * // When the application shuts down or you no longer need any observers
 * observer.disconnect(); // This will stop all observations
 */ class BreakpointObserver {
    #breakpointQueries = {
        'phone': `(max-width: ${PHONE}px)`,
        'tablet': `(min-width: ${PHONE + 1}px) and (max-width: ${TABLET}px)`,
        'desktop': `(min-width: ${TABLET + 1}px)`
    };
    #breakpointObservables = new Map();
    #observers = [];
    /**
     * Registers a callback to be notified when the specified breakpoints change.
     * The callback will be called with a BreakpointState object that indicates whether any of the observed breakpoints match and the state of each breakpoint.
     *
     * @param breakpoints - The breakpoints to observe. Can be a single breakpoint or an array of breakpoints.
     * @param callback - The function to call when the breakpoints change. It receives a BreakpointState object.
     */ observe(breakpoints, callback) {
        const breakpointsList = Array.isArray(breakpoints) ? breakpoints : [
            breakpoints
        ];
        const observables = breakpointsList.map((bp)=>this.#getBreakpointObservable(bp));
        const combined = combineLatest(observables).pipe(map((matches)=>{
            const breakpointMap = new Map();
            breakpointsList.forEach((bp, index)=>breakpointMap.set(bp, matches[index]));
            const anyMatch = matches.some((match)=>match);
            return {
                matches: anyMatch,
                breakpoints: breakpointMap
            };
        }));
        const subscription = combined.subscribe((state)=>callback(state));
        this.#observers.push({
            breakpoints: breakpointsList,
            callback,
            subscription
        });
    }
    /**
     * Unregisters a previously registered callback for the specified breakpoints.
     * This method should be called with the same breakpoints and callback that were used in observe to properly clean up.
     *
     * @param breakpoints - The breakpoints that were being observed.
     * @param callback - The callback function that was registered.
     */ unobserve(breakpoints, callback) {
        const bpArray = Array.isArray(breakpoints) ? breakpoints : [
            breakpoints
        ];
        const index = this.#observers.findIndex((obs)=>{
            return obs.callback === callback && obs.breakpoints.length === bpArray.length && obs.breakpoints.every((bp, i)=>bp === bpArray[i]);
        });
        if (index === -1) return;
        this.#observers[index].subscription.unsubscribe();
        this.#observers.splice(index, 1);
    }
    /**
     * Checks if any of the specified breakpoints currently match.
     *
     * @param breakpoints - The breakpoints to check.
     * @returns True if at least one of the breakpoints matches, false otherwise.
     */ isMatched(breakpoints) {
        const bpArray = Array.isArray(breakpoints) ? breakpoints : [
            breakpoints
        ];
        return bpArray.some((bp)=>window.matchMedia(this.#getQueryForBreakpoint(bp)).matches);
    }
    /**
     * Disconnects all observers, stopping all callbacks from being called.
     * This method should be used with caution as it affects all observers, not just those of the current instance.
     */ disconnect() {
        this.#observers.forEach((obs)=>obs.subscription.unsubscribe());
        this.#observers = [];
    }
    /**
     * Retrieves the media query string associated with the specified breakpoint.
     *
     * @param breakpoint - The breakpoint key or custom query to look up.
     * @returns The corresponding media query string for the given breakpoint.
     */ #getQueryForBreakpoint(breakpoint) {
        const query = this.#breakpointQueries[breakpoint] || breakpoint;
        return query;
    }
    /**
     * Gets an Observable that emits true when the breakpoint matches and false otherwise.
     * The Observable is created using window.matchMedia and shared among multiple subscribers.
     *
     * @param breakpoint - The breakpoint to observe.
     * @returns An Observable that emits the matching state of the breakpoint.
     */ #getBreakpointObservable(breakpoint) {
        if (!this.#breakpointObservables.has(breakpoint)) {
            const query = this.#getQueryForBreakpoint(breakpoint);
            const mql = window.matchMedia(query);
            const observable = fromEvent(mql, 'change').pipe(startWith(mql), map(({ matches })=>matches), shareReplay({
                bufferSize: 1,
                refCount: true
            }));
            this.#breakpointObservables.set(breakpoint, observable);
        }
        return this.#breakpointObservables.get(breakpoint);
    }
}

class SearchService {
    query;
    fetching = false;
    data;
    onDataChange;
    constructor(query, onDataChange){
        this.query = query;
        this.onDataChange = onDataChange;
    }
}

class ListService extends SearchService {
    onFetch;
    queryProperties = [];
    useCache = true;
    cachedResponse;
    constructor(onFetch, query = undefined, queryProperties = [], onDataChange){
        super(query, onDataChange);
        this.onFetch = onFetch;
        this.queryProperties = queryProperties;
    }
    reset() {
        this.useCache = false;
    }
    async fetchIfEmpty() {
        if (!this.data) {
            this.useCache = false;
            await this.search();
        }
    }
    async search() {
        if (this.fetching) {
            return null;
        }
        if (!this.useCache) {
            this.cachedResponse = undefined;
        }
        if (!this.cachedResponse) {
            this.fetching = true;
            this.cachedResponse = await this.onFetch();
        }
        if (this.cachedResponse && !this.useCache) {
            this.useCache = true;
        }
        let results = this.cachedResponse;
        if (this.query && this.queryProperties?.length) {
            results = results?.filter((resultItem)=>{
                for (const property of this.queryProperties){
                    // @ts-ignore
                    const propertyValue = resultItem[property];
                    if (typeof propertyValue === 'string' && propertyValue.toLowerCase().includes(this.query.toLowerCase())) {
                        return true;
                    }
                }
                return false;
            }) ?? null;
        }
        this.data = results;
        this.onDataChange?.();
        this.fetching = false;
        return results;
    }
}

class CancellablePromise {
    callback;
    cancelled = false;
    abortController;
    constructor(callback){
        this.callback = callback;
    }
    async run() {
        this.abortController = new AbortController();
        const result = await this.callback(this.abortController.signal);
        if (this.isCancelled) {
            throw this.abortController.signal.reason ?? new DOMException('Operation aborted', 'AbortError');
        }
        return result;
    }
    get isCancelled() {
        return this.abortController?.signal?.aborted || this.cancelled;
    }
    activate() {
        this.cancelled = false;
    }
    cancel(reason) {
        this.cancelled = true;
        this.abortController?.abort(reason);
    }
}

class PaginatedService extends SearchService {
    page = 0;
    pageSize = 20;
    onFetch;
    #activeRequests = [];
    constructor(onFetch, onDataChange, query = undefined, page = 0, pageSize = 20){
        super(query, onDataChange);
        this.page = page;
        this.pageSize = pageSize;
        this.onFetch = onFetch;
    }
    /**
     * Resets the page number back to zero and clears the `data` property
     */ reset() {
        this.fetching = true;
        this.data = undefined;
        this.query = undefined;
        this.page = 0;
        this.onDataChange?.();
    }
    setPageSize(size) {
        this.pageSize = size;
    }
    async fetchIfEmpty() {
        if (!this.data) {
            await this.fetchNextPage(true);
        }
    }
    /**
     * @param params parameters to pass to the `fetchNextPage` function
     */ async search(params) {
        await this.fetchNextPage(params?.force ?? true);
        return this.data;
    }
    /**
     * @param force Start a new request even if there's one already in progress
     */ async fetchNextPage(force = true) {
        if (this.fetching && !force || this.data?.totalPages && this.page >= this.data.totalPages) {
            return;
        }
        this.#activeRequests = this.#activeRequests.filter((request)=>!request.isCancelled);
        this.#activeRequests.forEach((request)=>request.cancel('New search request started'));
        this.fetching = true;
        try {
            const request = new CancellablePromise(async (signal)=>{
                return await this.onFetch(this.query, this.page, this.pageSize, signal);
            });
            this.#activeRequests.push(request);
            const response = await request.run();
            if (request.isCancelled) {
                return;
            } else if (!response) {
                this.fetching = false;
                return;
            }
            if (this.data) {
                this.data.results.push(...response.results);
            } else {
                this.data = response;
            }
            this.onDataChange?.();
            this.page += 1;
            this.fetching = false;
        } catch (e) {
            console.debug(e);
        }
    }
}

class CursorPaginatedService extends SearchService {
    pageSize = 20;
    cursor;
    onFetch;
    #activeRequests = [];
    constructor(onFetch, onDataChange, query = undefined, pageSize = 20){
        super(query, onDataChange);
        this.pageSize = pageSize;
        this.onFetch = onFetch;
    }
    reset() {
        this.fetching = true;
        this.data = undefined;
        this.query = undefined;
        this.cursor = undefined;
        this.onDataChange?.();
    }
    setPageSize(size) {
        this.pageSize = size;
    }
    async fetchIfEmpty() {
        if (!this.data) {
            await this.fetchNextPage(true);
        }
    }
    async search(params) {
        await this.fetchNextPage(params?.force ?? true);
        return this.data;
    }
    async fetchNextPage(force = true) {
        if ((this.fetching || this.data?.results.length && !this.cursor) && !force) {
            return;
        }
        this.#activeRequests = this.#activeRequests.filter((request)=>!request.isCancelled);
        this.#activeRequests.forEach((request)=>request.cancel('New search request started'));
        this.fetching = true;
        try {
            const request = new CancellablePromise(async (signal)=>{
                return await this.onFetch(this.query, this.cursor, this.pageSize, signal);
            });
            this.#activeRequests.push(request);
            const response = await request.run();
            if (request.isCancelled) {
                return;
            } else if (!response) {
                this.fetching = false;
                return;
            }
            if (this.data) {
                this.data.results.push(...response.results);
            } else {
                this.data = response;
            }
            this.cursor = response.cursor;
            this.onDataChange?.();
            this.fetching = false;
        } catch (e) {
            console.debug(e);
        }
    }
}

/**
 * Keeps numeric value between a range
 *
 * @param {value} value - The value to be clamped.
 * @param {value} min - The minimum value to accept.
 * @param {value} max - The max value to accept.
 * @returns {number} The clamped value.
 */ function clamp(value, max, min = 0) {
    return Math.min(Math.max(value, min), max);
}

function isRGBA(input) {
    return /^rgba\((((((((1?[1-9]?\d)|10\d|(2[0-4]\d)|25[0-5]),\s?)){3})|(((([1-9]?\d(\.\d+)?)|100|(\.\d+))%,\s?){3}))|(((((1?[1-9]?\d)|10\d|(2[0-4]\d)|25[0-5])\s){3})|(((([1-9]?\d(\.\d+)?)|100|(\.\d+))%\s){3}))\/\s)((0?\.\d+)|[01]|(([1-9]?\d(\.\d+)?)|100|(\.\d+))%)\)$/i.test(input);
}
function isRGB(input) {
    return /^rgb\((((((((1?[1-9]?\d)|10\d|(2[0-4]\d)|25[0-5]),\s?)){2}|((((1?[1-9]?\d)|10\d|(2[0-4]\d)|25[0-5])\s)){2})((1?[1-9]?\d)|10\d|(2[0-4]\d)|25[0-5]))|((((([1-9]?\d(\.\d+)?)|100|(\.\d+))%,\s?){2}|((([1-9]?\d(\.\d+)?)|100|(\.\d+))%\s){2})(([1-9]?\d(\.\d+)?)|100|(\.\d+))%))\)$/i.test(input);
}
function isHex(input) {
    return /^#([\da-f]{3}){1,2}$/i.test(input);
}
function isHexA(input) {
    return /^#([\da-f]{4}){1,2}$/i.test(input);
}
function isAlpha(input) {
    return isHexA(input) || isRGBA(input);
}
/**
 * @param {string} rgb The RGB color to be converted to hex
 * @returns The hex representation of the given RGB value
 * @throws Error An exception will be thrown if the input is not a valid RGB color
 */ function RGBToHex(rgb) {
    if (!isRGB(rgb)) {
        throw new Error('Input string is not a valid RGB');
    }
    // Choose correct separator
    let sep = rgb.indexOf(',') > -1 ? ',' : ' ';
    // Turn "rgb(r,g,b)" into [r,g,b]
    let rgbValues = rgb.slice(4, -1).split(sep).map((part)=>part.trim());
    let r = parseInt(rgbValues[0]).toString(16).padStart(2, '0');
    let g = parseInt(rgbValues[1]).toString(16).padStart(2, '0');
    let b = parseInt(rgbValues[2]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
}
/**
 * @param {string} rgba The RGBA color to be converted to hex
 * @returns The hex representation of the given RGBA value
 * @throws Error An exception will be thrown if the input is not a valid RGBA color
 */ function RGBAToHexA(rgba) {
    if (!isRGBA(rgba)) {
        throw new Error('Input string is not a valid RGBA');
    }
    let sep = rgba.indexOf(',') > -1 ? ',' : ' ';
    let rgbaValues = rgba.slice(5, -1).split(sep).map((part)=>part.trim());
    // Strip the slash if using space-separated syntax
    if (rgbaValues.indexOf('/') > -1) {
        rgbaValues.splice(3, 1);
    }
    for(let i = 0; i < rgbaValues.length; i++){
        let value = rgbaValues[i];
        if (value.indexOf('%') > -1) {
            let p = parseFloat(value) / 100;
            if (i < 3) {
                rgbaValues[i] = Math.round(p * 255).toString();
            } else {
                rgbaValues[i] = p.toString();
            }
        }
    }
    let r = parseInt(rgbaValues[0]).toString(16).padStart(2, '0');
    let g = parseInt(rgbaValues[1]).toString(16).padStart(2, '0');
    let b = parseInt(rgbaValues[2]).toString(16).padStart(2, '0');
    let a = Math.round(parseFloat(rgbaValues[3]) * 255).toString(16).padStart(2, '0');
    return `#${r}${g}${b}${a}`;
}
/**
 * @param {string} hex The hex color to be converted to RGB
 * @returns The RGB representation of the given hex value
 * @throws Error An exception will be thrown if the input is not a valid hex color
 */ function hexToRGB(hex) {
    if (!isHex(hex)) {
        throw new Error('Input string is not a valid hex color');
    }
    let r = 0, g = 0, b = 0;
    // 3 digits
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    // 6 digits
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return `rgb(${r}, ${g}, ${b})`;
}
/**
 * @param {string} hex The hex color to be converted to RGBA
 * @returns The RGBA representation of the given hex value
 * @throws Error An exception will be thrown if the input is not a valid hex color
 */ function hexAToRGBA(hex) {
    if (!isHexA(hex)) {
        throw new Error('Input string is not a valid hex color');
    }
    let r = 0, g = 0, b = 0, a = 1;
    // 4 digits
    if (hex.length === 5) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
        a = parseInt(hex[4] + hex[4], 16);
    // 8 digits
    } else if (hex.length === 9) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
        a = parseInt(hex[7] + hex[8], 16);
    }
    a = +(a / 255).toFixed(3);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
/**
 * @param {string} rgb The RGB or RGBA color to be converted to hex
 * @param {boolean} noAlpha Whether to return the hex string without the alpha value
 * @returns The hex representation of the given RGB value
 * @throws Error An exception will be thrown if the input is not a valid RGB or RGBA color
 */ function anyRGBToHex(rgb, noAlpha = false) {
    if (isHex(rgb)) {
        return rgb;
    }
    if (isRGB(rgb)) {
        return RGBToHex(rgb);
    }
    if (isRGBA(rgb)) {
        rgb = RGBAToHexA(rgb);
    }
    if (isHexA(rgb)) {
        return noAlpha ? rgb.slice(0, -2) : rgb;
    }
    throw new Error('Input string is not a valid Hex, RGB or RGBA color');
}
/**
 * @param {string} color The color to extract the alpha value from
 * @returns The number representing the alpha value
 * @throws Error An exception will be thrown if the input is not a valid RGB or RGBA color
 */ function extractAlphaValue(color) {
    if (!isValidValue(color)) {
        throw new Error('Input string is not a valid Hex, RGB or RGBA color');
    }
    if (!isAlpha(color)) {
        return 1;
    }
    if (isHexA(color)) {
        const isAlpha = color.length === 9;
        return isAlpha ? parseInt(color.slice(7, 9), 16) / 255 : 1;
    }
    if (isRGBA(color)) {
        const sep = color.indexOf(', ') > -1 ? ', ' : ' ';
        const rgbaValues = color.slice(5, -1).split(sep).map((part)=>part.trim());
        return parseFloat(rgbaValues[3]);
    }
    throw new Error('Input string is not a valid Hex, RGB or RGBA color');
}
/**
 * Checks if the provided color string is a valid color value.
 *
 * This method validates the color string by checking if it is in one of the following formats:
 * - RGB
 * - RGBA
 * - Hexadecimal
 * - Hexadecimal with alpha
 *
 * @param {string} color - The color string to validate.
 * @returns {boolean} whether the color string is valid or not.
 */ function isValidValue(color) {
    return isRGB(color) || isRGBA(color) || isHex(color) || isHexA(color);
}
/**
 * Retrieves the value of a design token from the provided styles.
 *
 * @param {GetDesignTokenValueParams} params - An object containing the parameters for retrieving the design token value.
 * @returns {number | string} The value of the design token, either as a number or a string.
 */ function getDesignTokenValue(params) {
    const { styles, prefix, property, suffix } = params;
    const value = styles.getPropertyValue(`${prefix}-${property}`);
    const noSuffix = suffix ? value.replace(suffix, '') : null;
    return Number(noSuffix || value) || String(noSuffix || value);
}

var lib$5 = {};

var Parser = {};

var Tokenizer = {};

var decode = {};

var decodeDataHtml = {};

var hasRequiredDecodeDataHtml;

function requireDecodeDataHtml () {
	if (hasRequiredDecodeDataHtml) return decodeDataHtml;
	hasRequiredDecodeDataHtml = 1;
	// Generated using scripts/write-decode-map.ts
	Object.defineProperty(decodeDataHtml, "__esModule", { value: true });
	decodeDataHtml.default = new Uint16Array(
	// prettier-ignore
	"\u1d41<\xd5\u0131\u028a\u049d\u057b\u05d0\u0675\u06de\u07a2\u07d6\u080f\u0a4a\u0a91\u0da1\u0e6d\u0f09\u0f26\u10ca\u1228\u12e1\u1415\u149d\u14c3\u14df\u1525\0\0\0\0\0\0\u156b\u16cd\u198d\u1c12\u1ddd\u1f7e\u2060\u21b0\u228d\u23c0\u23fb\u2442\u2824\u2912\u2d08\u2e48\u2fce\u3016\u32ba\u3639\u37ac\u38fe\u3a28\u3a71\u3ae0\u3b2e\u0800EMabcfglmnoprstu\\bfms\x7f\x84\x8b\x90\x95\x98\xa6\xb3\xb9\xc8\xcflig\u803b\xc6\u40c6P\u803b&\u4026cute\u803b\xc1\u40c1reve;\u4102\u0100iyx}rc\u803b\xc2\u40c2;\u4410r;\uc000\ud835\udd04rave\u803b\xc0\u40c0pha;\u4391acr;\u4100d;\u6a53\u0100gp\x9d\xa1on;\u4104f;\uc000\ud835\udd38plyFunction;\u6061ing\u803b\xc5\u40c5\u0100cs\xbe\xc3r;\uc000\ud835\udc9cign;\u6254ilde\u803b\xc3\u40c3ml\u803b\xc4\u40c4\u0400aceforsu\xe5\xfb\xfe\u0117\u011c\u0122\u0127\u012a\u0100cr\xea\xf2kslash;\u6216\u0176\xf6\xf8;\u6ae7ed;\u6306y;\u4411\u0180crt\u0105\u010b\u0114ause;\u6235noullis;\u612ca;\u4392r;\uc000\ud835\udd05pf;\uc000\ud835\udd39eve;\u42d8c\xf2\u0113mpeq;\u624e\u0700HOacdefhilorsu\u014d\u0151\u0156\u0180\u019e\u01a2\u01b5\u01b7\u01ba\u01dc\u0215\u0273\u0278\u027ecy;\u4427PY\u803b\xa9\u40a9\u0180cpy\u015d\u0162\u017aute;\u4106\u0100;i\u0167\u0168\u62d2talDifferentialD;\u6145leys;\u612d\u0200aeio\u0189\u018e\u0194\u0198ron;\u410cdil\u803b\xc7\u40c7rc;\u4108nint;\u6230ot;\u410a\u0100dn\u01a7\u01adilla;\u40b8terDot;\u40b7\xf2\u017fi;\u43a7rcle\u0200DMPT\u01c7\u01cb\u01d1\u01d6ot;\u6299inus;\u6296lus;\u6295imes;\u6297o\u0100cs\u01e2\u01f8kwiseContourIntegral;\u6232eCurly\u0100DQ\u0203\u020foubleQuote;\u601duote;\u6019\u0200lnpu\u021e\u0228\u0247\u0255on\u0100;e\u0225\u0226\u6237;\u6a74\u0180git\u022f\u0236\u023aruent;\u6261nt;\u622fourIntegral;\u622e\u0100fr\u024c\u024e;\u6102oduct;\u6210nterClockwiseContourIntegral;\u6233oss;\u6a2fcr;\uc000\ud835\udc9ep\u0100;C\u0284\u0285\u62d3ap;\u624d\u0580DJSZacefios\u02a0\u02ac\u02b0\u02b4\u02b8\u02cb\u02d7\u02e1\u02e6\u0333\u048d\u0100;o\u0179\u02a5trahd;\u6911cy;\u4402cy;\u4405cy;\u440f\u0180grs\u02bf\u02c4\u02c7ger;\u6021r;\u61a1hv;\u6ae4\u0100ay\u02d0\u02d5ron;\u410e;\u4414l\u0100;t\u02dd\u02de\u6207a;\u4394r;\uc000\ud835\udd07\u0100af\u02eb\u0327\u0100cm\u02f0\u0322ritical\u0200ADGT\u0300\u0306\u0316\u031ccute;\u40b4o\u0174\u030b\u030d;\u42d9bleAcute;\u42ddrave;\u4060ilde;\u42dcond;\u62c4ferentialD;\u6146\u0470\u033d\0\0\0\u0342\u0354\0\u0405f;\uc000\ud835\udd3b\u0180;DE\u0348\u0349\u034d\u40a8ot;\u60dcqual;\u6250ble\u0300CDLRUV\u0363\u0372\u0382\u03cf\u03e2\u03f8ontourIntegra\xec\u0239o\u0274\u0379\0\0\u037b\xbb\u0349nArrow;\u61d3\u0100eo\u0387\u03a4ft\u0180ART\u0390\u0396\u03a1rrow;\u61d0ightArrow;\u61d4e\xe5\u02cang\u0100LR\u03ab\u03c4eft\u0100AR\u03b3\u03b9rrow;\u67f8ightArrow;\u67faightArrow;\u67f9ight\u0100AT\u03d8\u03derrow;\u61d2ee;\u62a8p\u0241\u03e9\0\0\u03efrrow;\u61d1ownArrow;\u61d5erticalBar;\u6225n\u0300ABLRTa\u0412\u042a\u0430\u045e\u047f\u037crrow\u0180;BU\u041d\u041e\u0422\u6193ar;\u6913pArrow;\u61f5reve;\u4311eft\u02d2\u043a\0\u0446\0\u0450ightVector;\u6950eeVector;\u695eector\u0100;B\u0459\u045a\u61bdar;\u6956ight\u01d4\u0467\0\u0471eeVector;\u695fector\u0100;B\u047a\u047b\u61c1ar;\u6957ee\u0100;A\u0486\u0487\u62a4rrow;\u61a7\u0100ct\u0492\u0497r;\uc000\ud835\udc9frok;\u4110\u0800NTacdfglmopqstux\u04bd\u04c0\u04c4\u04cb\u04de\u04e2\u04e7\u04ee\u04f5\u0521\u052f\u0536\u0552\u055d\u0560\u0565G;\u414aH\u803b\xd0\u40d0cute\u803b\xc9\u40c9\u0180aiy\u04d2\u04d7\u04dcron;\u411arc\u803b\xca\u40ca;\u442dot;\u4116r;\uc000\ud835\udd08rave\u803b\xc8\u40c8ement;\u6208\u0100ap\u04fa\u04fecr;\u4112ty\u0253\u0506\0\0\u0512mallSquare;\u65fberySmallSquare;\u65ab\u0100gp\u0526\u052aon;\u4118f;\uc000\ud835\udd3csilon;\u4395u\u0100ai\u053c\u0549l\u0100;T\u0542\u0543\u6a75ilde;\u6242librium;\u61cc\u0100ci\u0557\u055ar;\u6130m;\u6a73a;\u4397ml\u803b\xcb\u40cb\u0100ip\u056a\u056fsts;\u6203onentialE;\u6147\u0280cfios\u0585\u0588\u058d\u05b2\u05ccy;\u4424r;\uc000\ud835\udd09lled\u0253\u0597\0\0\u05a3mallSquare;\u65fcerySmallSquare;\u65aa\u0370\u05ba\0\u05bf\0\0\u05c4f;\uc000\ud835\udd3dAll;\u6200riertrf;\u6131c\xf2\u05cb\u0600JTabcdfgorst\u05e8\u05ec\u05ef\u05fa\u0600\u0612\u0616\u061b\u061d\u0623\u066c\u0672cy;\u4403\u803b>\u403emma\u0100;d\u05f7\u05f8\u4393;\u43dcreve;\u411e\u0180eiy\u0607\u060c\u0610dil;\u4122rc;\u411c;\u4413ot;\u4120r;\uc000\ud835\udd0a;\u62d9pf;\uc000\ud835\udd3eeater\u0300EFGLST\u0635\u0644\u064e\u0656\u065b\u0666qual\u0100;L\u063e\u063f\u6265ess;\u62dbullEqual;\u6267reater;\u6aa2ess;\u6277lantEqual;\u6a7eilde;\u6273cr;\uc000\ud835\udca2;\u626b\u0400Aacfiosu\u0685\u068b\u0696\u069b\u069e\u06aa\u06be\u06caRDcy;\u442a\u0100ct\u0690\u0694ek;\u42c7;\u405eirc;\u4124r;\u610clbertSpace;\u610b\u01f0\u06af\0\u06b2f;\u610dizontalLine;\u6500\u0100ct\u06c3\u06c5\xf2\u06a9rok;\u4126mp\u0144\u06d0\u06d8ownHum\xf0\u012fqual;\u624f\u0700EJOacdfgmnostu\u06fa\u06fe\u0703\u0707\u070e\u071a\u071e\u0721\u0728\u0744\u0778\u078b\u078f\u0795cy;\u4415lig;\u4132cy;\u4401cute\u803b\xcd\u40cd\u0100iy\u0713\u0718rc\u803b\xce\u40ce;\u4418ot;\u4130r;\u6111rave\u803b\xcc\u40cc\u0180;ap\u0720\u072f\u073f\u0100cg\u0734\u0737r;\u412ainaryI;\u6148lie\xf3\u03dd\u01f4\u0749\0\u0762\u0100;e\u074d\u074e\u622c\u0100gr\u0753\u0758ral;\u622bsection;\u62c2isible\u0100CT\u076c\u0772omma;\u6063imes;\u6062\u0180gpt\u077f\u0783\u0788on;\u412ef;\uc000\ud835\udd40a;\u4399cr;\u6110ilde;\u4128\u01eb\u079a\0\u079ecy;\u4406l\u803b\xcf\u40cf\u0280cfosu\u07ac\u07b7\u07bc\u07c2\u07d0\u0100iy\u07b1\u07b5rc;\u4134;\u4419r;\uc000\ud835\udd0dpf;\uc000\ud835\udd41\u01e3\u07c7\0\u07ccr;\uc000\ud835\udca5rcy;\u4408kcy;\u4404\u0380HJacfos\u07e4\u07e8\u07ec\u07f1\u07fd\u0802\u0808cy;\u4425cy;\u440cppa;\u439a\u0100ey\u07f6\u07fbdil;\u4136;\u441ar;\uc000\ud835\udd0epf;\uc000\ud835\udd42cr;\uc000\ud835\udca6\u0580JTaceflmost\u0825\u0829\u082c\u0850\u0863\u09b3\u09b8\u09c7\u09cd\u0a37\u0a47cy;\u4409\u803b<\u403c\u0280cmnpr\u0837\u083c\u0841\u0844\u084dute;\u4139bda;\u439bg;\u67ealacetrf;\u6112r;\u619e\u0180aey\u0857\u085c\u0861ron;\u413ddil;\u413b;\u441b\u0100fs\u0868\u0970t\u0500ACDFRTUVar\u087e\u08a9\u08b1\u08e0\u08e6\u08fc\u092f\u095b\u0390\u096a\u0100nr\u0883\u088fgleBracket;\u67e8row\u0180;BR\u0899\u089a\u089e\u6190ar;\u61e4ightArrow;\u61c6eiling;\u6308o\u01f5\u08b7\0\u08c3bleBracket;\u67e6n\u01d4\u08c8\0\u08d2eeVector;\u6961ector\u0100;B\u08db\u08dc\u61c3ar;\u6959loor;\u630aight\u0100AV\u08ef\u08f5rrow;\u6194ector;\u694e\u0100er\u0901\u0917e\u0180;AV\u0909\u090a\u0910\u62a3rrow;\u61a4ector;\u695aiangle\u0180;BE\u0924\u0925\u0929\u62b2ar;\u69cfqual;\u62b4p\u0180DTV\u0937\u0942\u094cownVector;\u6951eeVector;\u6960ector\u0100;B\u0956\u0957\u61bfar;\u6958ector\u0100;B\u0965\u0966\u61bcar;\u6952ight\xe1\u039cs\u0300EFGLST\u097e\u098b\u0995\u099d\u09a2\u09adqualGreater;\u62daullEqual;\u6266reater;\u6276ess;\u6aa1lantEqual;\u6a7dilde;\u6272r;\uc000\ud835\udd0f\u0100;e\u09bd\u09be\u62d8ftarrow;\u61daidot;\u413f\u0180npw\u09d4\u0a16\u0a1bg\u0200LRlr\u09de\u09f7\u0a02\u0a10eft\u0100AR\u09e6\u09ecrrow;\u67f5ightArrow;\u67f7ightArrow;\u67f6eft\u0100ar\u03b3\u0a0aight\xe1\u03bfight\xe1\u03caf;\uc000\ud835\udd43er\u0100LR\u0a22\u0a2ceftArrow;\u6199ightArrow;\u6198\u0180cht\u0a3e\u0a40\u0a42\xf2\u084c;\u61b0rok;\u4141;\u626a\u0400acefiosu\u0a5a\u0a5d\u0a60\u0a77\u0a7c\u0a85\u0a8b\u0a8ep;\u6905y;\u441c\u0100dl\u0a65\u0a6fiumSpace;\u605flintrf;\u6133r;\uc000\ud835\udd10nusPlus;\u6213pf;\uc000\ud835\udd44c\xf2\u0a76;\u439c\u0480Jacefostu\u0aa3\u0aa7\u0aad\u0ac0\u0b14\u0b19\u0d91\u0d97\u0d9ecy;\u440acute;\u4143\u0180aey\u0ab4\u0ab9\u0aberon;\u4147dil;\u4145;\u441d\u0180gsw\u0ac7\u0af0\u0b0eative\u0180MTV\u0ad3\u0adf\u0ae8ediumSpace;\u600bhi\u0100cn\u0ae6\u0ad8\xeb\u0ad9eryThi\xee\u0ad9ted\u0100GL\u0af8\u0b06reaterGreate\xf2\u0673essLes\xf3\u0a48Line;\u400ar;\uc000\ud835\udd11\u0200Bnpt\u0b22\u0b28\u0b37\u0b3areak;\u6060BreakingSpace;\u40a0f;\u6115\u0680;CDEGHLNPRSTV\u0b55\u0b56\u0b6a\u0b7c\u0ba1\u0beb\u0c04\u0c5e\u0c84\u0ca6\u0cd8\u0d61\u0d85\u6aec\u0100ou\u0b5b\u0b64ngruent;\u6262pCap;\u626doubleVerticalBar;\u6226\u0180lqx\u0b83\u0b8a\u0b9bement;\u6209ual\u0100;T\u0b92\u0b93\u6260ilde;\uc000\u2242\u0338ists;\u6204reater\u0380;EFGLST\u0bb6\u0bb7\u0bbd\u0bc9\u0bd3\u0bd8\u0be5\u626fqual;\u6271ullEqual;\uc000\u2267\u0338reater;\uc000\u226b\u0338ess;\u6279lantEqual;\uc000\u2a7e\u0338ilde;\u6275ump\u0144\u0bf2\u0bfdownHump;\uc000\u224e\u0338qual;\uc000\u224f\u0338e\u0100fs\u0c0a\u0c27tTriangle\u0180;BE\u0c1a\u0c1b\u0c21\u62eaar;\uc000\u29cf\u0338qual;\u62ecs\u0300;EGLST\u0c35\u0c36\u0c3c\u0c44\u0c4b\u0c58\u626equal;\u6270reater;\u6278ess;\uc000\u226a\u0338lantEqual;\uc000\u2a7d\u0338ilde;\u6274ested\u0100GL\u0c68\u0c79reaterGreater;\uc000\u2aa2\u0338essLess;\uc000\u2aa1\u0338recedes\u0180;ES\u0c92\u0c93\u0c9b\u6280qual;\uc000\u2aaf\u0338lantEqual;\u62e0\u0100ei\u0cab\u0cb9verseElement;\u620cghtTriangle\u0180;BE\u0ccb\u0ccc\u0cd2\u62ebar;\uc000\u29d0\u0338qual;\u62ed\u0100qu\u0cdd\u0d0cuareSu\u0100bp\u0ce8\u0cf9set\u0100;E\u0cf0\u0cf3\uc000\u228f\u0338qual;\u62e2erset\u0100;E\u0d03\u0d06\uc000\u2290\u0338qual;\u62e3\u0180bcp\u0d13\u0d24\u0d4eset\u0100;E\u0d1b\u0d1e\uc000\u2282\u20d2qual;\u6288ceeds\u0200;EST\u0d32\u0d33\u0d3b\u0d46\u6281qual;\uc000\u2ab0\u0338lantEqual;\u62e1ilde;\uc000\u227f\u0338erset\u0100;E\u0d58\u0d5b\uc000\u2283\u20d2qual;\u6289ilde\u0200;EFT\u0d6e\u0d6f\u0d75\u0d7f\u6241qual;\u6244ullEqual;\u6247ilde;\u6249erticalBar;\u6224cr;\uc000\ud835\udca9ilde\u803b\xd1\u40d1;\u439d\u0700Eacdfgmoprstuv\u0dbd\u0dc2\u0dc9\u0dd5\u0ddb\u0de0\u0de7\u0dfc\u0e02\u0e20\u0e22\u0e32\u0e3f\u0e44lig;\u4152cute\u803b\xd3\u40d3\u0100iy\u0dce\u0dd3rc\u803b\xd4\u40d4;\u441eblac;\u4150r;\uc000\ud835\udd12rave\u803b\xd2\u40d2\u0180aei\u0dee\u0df2\u0df6cr;\u414cga;\u43a9cron;\u439fpf;\uc000\ud835\udd46enCurly\u0100DQ\u0e0e\u0e1aoubleQuote;\u601cuote;\u6018;\u6a54\u0100cl\u0e27\u0e2cr;\uc000\ud835\udcaaash\u803b\xd8\u40d8i\u016c\u0e37\u0e3cde\u803b\xd5\u40d5es;\u6a37ml\u803b\xd6\u40d6er\u0100BP\u0e4b\u0e60\u0100ar\u0e50\u0e53r;\u603eac\u0100ek\u0e5a\u0e5c;\u63deet;\u63b4arenthesis;\u63dc\u0480acfhilors\u0e7f\u0e87\u0e8a\u0e8f\u0e92\u0e94\u0e9d\u0eb0\u0efcrtialD;\u6202y;\u441fr;\uc000\ud835\udd13i;\u43a6;\u43a0usMinus;\u40b1\u0100ip\u0ea2\u0eadncareplan\xe5\u069df;\u6119\u0200;eio\u0eb9\u0eba\u0ee0\u0ee4\u6abbcedes\u0200;EST\u0ec8\u0ec9\u0ecf\u0eda\u627aqual;\u6aaflantEqual;\u627cilde;\u627eme;\u6033\u0100dp\u0ee9\u0eeeuct;\u620fortion\u0100;a\u0225\u0ef9l;\u621d\u0100ci\u0f01\u0f06r;\uc000\ud835\udcab;\u43a8\u0200Ufos\u0f11\u0f16\u0f1b\u0f1fOT\u803b\"\u4022r;\uc000\ud835\udd14pf;\u611acr;\uc000\ud835\udcac\u0600BEacefhiorsu\u0f3e\u0f43\u0f47\u0f60\u0f73\u0fa7\u0faa\u0fad\u1096\u10a9\u10b4\u10bearr;\u6910G\u803b\xae\u40ae\u0180cnr\u0f4e\u0f53\u0f56ute;\u4154g;\u67ebr\u0100;t\u0f5c\u0f5d\u61a0l;\u6916\u0180aey\u0f67\u0f6c\u0f71ron;\u4158dil;\u4156;\u4420\u0100;v\u0f78\u0f79\u611cerse\u0100EU\u0f82\u0f99\u0100lq\u0f87\u0f8eement;\u620builibrium;\u61cbpEquilibrium;\u696fr\xbb\u0f79o;\u43a1ght\u0400ACDFTUVa\u0fc1\u0feb\u0ff3\u1022\u1028\u105b\u1087\u03d8\u0100nr\u0fc6\u0fd2gleBracket;\u67e9row\u0180;BL\u0fdc\u0fdd\u0fe1\u6192ar;\u61e5eftArrow;\u61c4eiling;\u6309o\u01f5\u0ff9\0\u1005bleBracket;\u67e7n\u01d4\u100a\0\u1014eeVector;\u695dector\u0100;B\u101d\u101e\u61c2ar;\u6955loor;\u630b\u0100er\u102d\u1043e\u0180;AV\u1035\u1036\u103c\u62a2rrow;\u61a6ector;\u695biangle\u0180;BE\u1050\u1051\u1055\u62b3ar;\u69d0qual;\u62b5p\u0180DTV\u1063\u106e\u1078ownVector;\u694feeVector;\u695cector\u0100;B\u1082\u1083\u61bear;\u6954ector\u0100;B\u1091\u1092\u61c0ar;\u6953\u0100pu\u109b\u109ef;\u611dndImplies;\u6970ightarrow;\u61db\u0100ch\u10b9\u10bcr;\u611b;\u61b1leDelayed;\u69f4\u0680HOacfhimoqstu\u10e4\u10f1\u10f7\u10fd\u1119\u111e\u1151\u1156\u1161\u1167\u11b5\u11bb\u11bf\u0100Cc\u10e9\u10eeHcy;\u4429y;\u4428FTcy;\u442ccute;\u415a\u0280;aeiy\u1108\u1109\u110e\u1113\u1117\u6abcron;\u4160dil;\u415erc;\u415c;\u4421r;\uc000\ud835\udd16ort\u0200DLRU\u112a\u1134\u113e\u1149ownArrow\xbb\u041eeftArrow\xbb\u089aightArrow\xbb\u0fddpArrow;\u6191gma;\u43a3allCircle;\u6218pf;\uc000\ud835\udd4a\u0272\u116d\0\0\u1170t;\u621aare\u0200;ISU\u117b\u117c\u1189\u11af\u65a1ntersection;\u6293u\u0100bp\u118f\u119eset\u0100;E\u1197\u1198\u628fqual;\u6291erset\u0100;E\u11a8\u11a9\u6290qual;\u6292nion;\u6294cr;\uc000\ud835\udcaear;\u62c6\u0200bcmp\u11c8\u11db\u1209\u120b\u0100;s\u11cd\u11ce\u62d0et\u0100;E\u11cd\u11d5qual;\u6286\u0100ch\u11e0\u1205eeds\u0200;EST\u11ed\u11ee\u11f4\u11ff\u627bqual;\u6ab0lantEqual;\u627dilde;\u627fTh\xe1\u0f8c;\u6211\u0180;es\u1212\u1213\u1223\u62d1rset\u0100;E\u121c\u121d\u6283qual;\u6287et\xbb\u1213\u0580HRSacfhiors\u123e\u1244\u1249\u1255\u125e\u1271\u1276\u129f\u12c2\u12c8\u12d1ORN\u803b\xde\u40deADE;\u6122\u0100Hc\u124e\u1252cy;\u440by;\u4426\u0100bu\u125a\u125c;\u4009;\u43a4\u0180aey\u1265\u126a\u126fron;\u4164dil;\u4162;\u4422r;\uc000\ud835\udd17\u0100ei\u127b\u1289\u01f2\u1280\0\u1287efore;\u6234a;\u4398\u0100cn\u128e\u1298kSpace;\uc000\u205f\u200aSpace;\u6009lde\u0200;EFT\u12ab\u12ac\u12b2\u12bc\u623cqual;\u6243ullEqual;\u6245ilde;\u6248pf;\uc000\ud835\udd4bipleDot;\u60db\u0100ct\u12d6\u12dbr;\uc000\ud835\udcafrok;\u4166\u0ae1\u12f7\u130e\u131a\u1326\0\u132c\u1331\0\0\0\0\0\u1338\u133d\u1377\u1385\0\u13ff\u1404\u140a\u1410\u0100cr\u12fb\u1301ute\u803b\xda\u40dar\u0100;o\u1307\u1308\u619fcir;\u6949r\u01e3\u1313\0\u1316y;\u440eve;\u416c\u0100iy\u131e\u1323rc\u803b\xdb\u40db;\u4423blac;\u4170r;\uc000\ud835\udd18rave\u803b\xd9\u40d9acr;\u416a\u0100di\u1341\u1369er\u0100BP\u1348\u135d\u0100ar\u134d\u1350r;\u405fac\u0100ek\u1357\u1359;\u63dfet;\u63b5arenthesis;\u63ddon\u0100;P\u1370\u1371\u62c3lus;\u628e\u0100gp\u137b\u137fon;\u4172f;\uc000\ud835\udd4c\u0400ADETadps\u1395\u13ae\u13b8\u13c4\u03e8\u13d2\u13d7\u13f3rrow\u0180;BD\u1150\u13a0\u13a4ar;\u6912ownArrow;\u61c5ownArrow;\u6195quilibrium;\u696eee\u0100;A\u13cb\u13cc\u62a5rrow;\u61a5own\xe1\u03f3er\u0100LR\u13de\u13e8eftArrow;\u6196ightArrow;\u6197i\u0100;l\u13f9\u13fa\u43d2on;\u43a5ing;\u416ecr;\uc000\ud835\udcb0ilde;\u4168ml\u803b\xdc\u40dc\u0480Dbcdefosv\u1427\u142c\u1430\u1433\u143e\u1485\u148a\u1490\u1496ash;\u62abar;\u6aeby;\u4412ash\u0100;l\u143b\u143c\u62a9;\u6ae6\u0100er\u1443\u1445;\u62c1\u0180bty\u144c\u1450\u147aar;\u6016\u0100;i\u144f\u1455cal\u0200BLST\u1461\u1465\u146a\u1474ar;\u6223ine;\u407ceparator;\u6758ilde;\u6240ThinSpace;\u600ar;\uc000\ud835\udd19pf;\uc000\ud835\udd4dcr;\uc000\ud835\udcb1dash;\u62aa\u0280cefos\u14a7\u14ac\u14b1\u14b6\u14bcirc;\u4174dge;\u62c0r;\uc000\ud835\udd1apf;\uc000\ud835\udd4ecr;\uc000\ud835\udcb2\u0200fios\u14cb\u14d0\u14d2\u14d8r;\uc000\ud835\udd1b;\u439epf;\uc000\ud835\udd4fcr;\uc000\ud835\udcb3\u0480AIUacfosu\u14f1\u14f5\u14f9\u14fd\u1504\u150f\u1514\u151a\u1520cy;\u442fcy;\u4407cy;\u442ecute\u803b\xdd\u40dd\u0100iy\u1509\u150drc;\u4176;\u442br;\uc000\ud835\udd1cpf;\uc000\ud835\udd50cr;\uc000\ud835\udcb4ml;\u4178\u0400Hacdefos\u1535\u1539\u153f\u154b\u154f\u155d\u1560\u1564cy;\u4416cute;\u4179\u0100ay\u1544\u1549ron;\u417d;\u4417ot;\u417b\u01f2\u1554\0\u155boWidt\xe8\u0ad9a;\u4396r;\u6128pf;\u6124cr;\uc000\ud835\udcb5\u0be1\u1583\u158a\u1590\0\u15b0\u15b6\u15bf\0\0\0\0\u15c6\u15db\u15eb\u165f\u166d\0\u1695\u169b\u16b2\u16b9\0\u16becute\u803b\xe1\u40e1reve;\u4103\u0300;Ediuy\u159c\u159d\u15a1\u15a3\u15a8\u15ad\u623e;\uc000\u223e\u0333;\u623frc\u803b\xe2\u40e2te\u80bb\xb4\u0306;\u4430lig\u803b\xe6\u40e6\u0100;r\xb2\u15ba;\uc000\ud835\udd1erave\u803b\xe0\u40e0\u0100ep\u15ca\u15d6\u0100fp\u15cf\u15d4sym;\u6135\xe8\u15d3ha;\u43b1\u0100ap\u15dfc\u0100cl\u15e4\u15e7r;\u4101g;\u6a3f\u0264\u15f0\0\0\u160a\u0280;adsv\u15fa\u15fb\u15ff\u1601\u1607\u6227nd;\u6a55;\u6a5clope;\u6a58;\u6a5a\u0380;elmrsz\u1618\u1619\u161b\u161e\u163f\u164f\u1659\u6220;\u69a4e\xbb\u1619sd\u0100;a\u1625\u1626\u6221\u0461\u1630\u1632\u1634\u1636\u1638\u163a\u163c\u163e;\u69a8;\u69a9;\u69aa;\u69ab;\u69ac;\u69ad;\u69ae;\u69aft\u0100;v\u1645\u1646\u621fb\u0100;d\u164c\u164d\u62be;\u699d\u0100pt\u1654\u1657h;\u6222\xbb\xb9arr;\u637c\u0100gp\u1663\u1667on;\u4105f;\uc000\ud835\udd52\u0380;Eaeiop\u12c1\u167b\u167d\u1682\u1684\u1687\u168a;\u6a70cir;\u6a6f;\u624ad;\u624bs;\u4027rox\u0100;e\u12c1\u1692\xf1\u1683ing\u803b\xe5\u40e5\u0180cty\u16a1\u16a6\u16a8r;\uc000\ud835\udcb6;\u402amp\u0100;e\u12c1\u16af\xf1\u0288ilde\u803b\xe3\u40e3ml\u803b\xe4\u40e4\u0100ci\u16c2\u16c8onin\xf4\u0272nt;\u6a11\u0800Nabcdefiklnoprsu\u16ed\u16f1\u1730\u173c\u1743\u1748\u1778\u177d\u17e0\u17e6\u1839\u1850\u170d\u193d\u1948\u1970ot;\u6aed\u0100cr\u16f6\u171ek\u0200ceps\u1700\u1705\u170d\u1713ong;\u624cpsilon;\u43f6rime;\u6035im\u0100;e\u171a\u171b\u623dq;\u62cd\u0176\u1722\u1726ee;\u62bded\u0100;g\u172c\u172d\u6305e\xbb\u172drk\u0100;t\u135c\u1737brk;\u63b6\u0100oy\u1701\u1741;\u4431quo;\u601e\u0280cmprt\u1753\u175b\u1761\u1764\u1768aus\u0100;e\u010a\u0109ptyv;\u69b0s\xe9\u170cno\xf5\u0113\u0180ahw\u176f\u1771\u1773;\u43b2;\u6136een;\u626cr;\uc000\ud835\udd1fg\u0380costuvw\u178d\u179d\u17b3\u17c1\u17d5\u17db\u17de\u0180aiu\u1794\u1796\u179a\xf0\u0760rc;\u65efp\xbb\u1371\u0180dpt\u17a4\u17a8\u17adot;\u6a00lus;\u6a01imes;\u6a02\u0271\u17b9\0\0\u17becup;\u6a06ar;\u6605riangle\u0100du\u17cd\u17d2own;\u65bdp;\u65b3plus;\u6a04e\xe5\u1444\xe5\u14adarow;\u690d\u0180ako\u17ed\u1826\u1835\u0100cn\u17f2\u1823k\u0180lst\u17fa\u05ab\u1802ozenge;\u69ebriangle\u0200;dlr\u1812\u1813\u1818\u181d\u65b4own;\u65beeft;\u65c2ight;\u65b8k;\u6423\u01b1\u182b\0\u1833\u01b2\u182f\0\u1831;\u6592;\u65914;\u6593ck;\u6588\u0100eo\u183e\u184d\u0100;q\u1843\u1846\uc000=\u20e5uiv;\uc000\u2261\u20e5t;\u6310\u0200ptwx\u1859\u185e\u1867\u186cf;\uc000\ud835\udd53\u0100;t\u13cb\u1863om\xbb\u13cctie;\u62c8\u0600DHUVbdhmptuv\u1885\u1896\u18aa\u18bb\u18d7\u18db\u18ec\u18ff\u1905\u190a\u1910\u1921\u0200LRlr\u188e\u1890\u1892\u1894;\u6557;\u6554;\u6556;\u6553\u0280;DUdu\u18a1\u18a2\u18a4\u18a6\u18a8\u6550;\u6566;\u6569;\u6564;\u6567\u0200LRlr\u18b3\u18b5\u18b7\u18b9;\u655d;\u655a;\u655c;\u6559\u0380;HLRhlr\u18ca\u18cb\u18cd\u18cf\u18d1\u18d3\u18d5\u6551;\u656c;\u6563;\u6560;\u656b;\u6562;\u655fox;\u69c9\u0200LRlr\u18e4\u18e6\u18e8\u18ea;\u6555;\u6552;\u6510;\u650c\u0280;DUdu\u06bd\u18f7\u18f9\u18fb\u18fd;\u6565;\u6568;\u652c;\u6534inus;\u629flus;\u629eimes;\u62a0\u0200LRlr\u1919\u191b\u191d\u191f;\u655b;\u6558;\u6518;\u6514\u0380;HLRhlr\u1930\u1931\u1933\u1935\u1937\u1939\u193b\u6502;\u656a;\u6561;\u655e;\u653c;\u6524;\u651c\u0100ev\u0123\u1942bar\u803b\xa6\u40a6\u0200ceio\u1951\u1956\u195a\u1960r;\uc000\ud835\udcb7mi;\u604fm\u0100;e\u171a\u171cl\u0180;bh\u1968\u1969\u196b\u405c;\u69c5sub;\u67c8\u016c\u1974\u197el\u0100;e\u1979\u197a\u6022t\xbb\u197ap\u0180;Ee\u012f\u1985\u1987;\u6aae\u0100;q\u06dc\u06db\u0ce1\u19a7\0\u19e8\u1a11\u1a15\u1a32\0\u1a37\u1a50\0\0\u1ab4\0\0\u1ac1\0\0\u1b21\u1b2e\u1b4d\u1b52\0\u1bfd\0\u1c0c\u0180cpr\u19ad\u19b2\u19ddute;\u4107\u0300;abcds\u19bf\u19c0\u19c4\u19ca\u19d5\u19d9\u6229nd;\u6a44rcup;\u6a49\u0100au\u19cf\u19d2p;\u6a4bp;\u6a47ot;\u6a40;\uc000\u2229\ufe00\u0100eo\u19e2\u19e5t;\u6041\xee\u0693\u0200aeiu\u19f0\u19fb\u1a01\u1a05\u01f0\u19f5\0\u19f8s;\u6a4don;\u410ddil\u803b\xe7\u40e7rc;\u4109ps\u0100;s\u1a0c\u1a0d\u6a4cm;\u6a50ot;\u410b\u0180dmn\u1a1b\u1a20\u1a26il\u80bb\xb8\u01adptyv;\u69b2t\u8100\xa2;e\u1a2d\u1a2e\u40a2r\xe4\u01b2r;\uc000\ud835\udd20\u0180cei\u1a3d\u1a40\u1a4dy;\u4447ck\u0100;m\u1a47\u1a48\u6713ark\xbb\u1a48;\u43c7r\u0380;Ecefms\u1a5f\u1a60\u1a62\u1a6b\u1aa4\u1aaa\u1aae\u65cb;\u69c3\u0180;el\u1a69\u1a6a\u1a6d\u42c6q;\u6257e\u0261\u1a74\0\0\u1a88rrow\u0100lr\u1a7c\u1a81eft;\u61baight;\u61bb\u0280RSacd\u1a92\u1a94\u1a96\u1a9a\u1a9f\xbb\u0f47;\u64c8st;\u629birc;\u629aash;\u629dnint;\u6a10id;\u6aefcir;\u69c2ubs\u0100;u\u1abb\u1abc\u6663it\xbb\u1abc\u02ec\u1ac7\u1ad4\u1afa\0\u1b0aon\u0100;e\u1acd\u1ace\u403a\u0100;q\xc7\xc6\u026d\u1ad9\0\0\u1ae2a\u0100;t\u1ade\u1adf\u402c;\u4040\u0180;fl\u1ae8\u1ae9\u1aeb\u6201\xee\u1160e\u0100mx\u1af1\u1af6ent\xbb\u1ae9e\xf3\u024d\u01e7\u1afe\0\u1b07\u0100;d\u12bb\u1b02ot;\u6a6dn\xf4\u0246\u0180fry\u1b10\u1b14\u1b17;\uc000\ud835\udd54o\xe4\u0254\u8100\xa9;s\u0155\u1b1dr;\u6117\u0100ao\u1b25\u1b29rr;\u61b5ss;\u6717\u0100cu\u1b32\u1b37r;\uc000\ud835\udcb8\u0100bp\u1b3c\u1b44\u0100;e\u1b41\u1b42\u6acf;\u6ad1\u0100;e\u1b49\u1b4a\u6ad0;\u6ad2dot;\u62ef\u0380delprvw\u1b60\u1b6c\u1b77\u1b82\u1bac\u1bd4\u1bf9arr\u0100lr\u1b68\u1b6a;\u6938;\u6935\u0270\u1b72\0\0\u1b75r;\u62dec;\u62dfarr\u0100;p\u1b7f\u1b80\u61b6;\u693d\u0300;bcdos\u1b8f\u1b90\u1b96\u1ba1\u1ba5\u1ba8\u622arcap;\u6a48\u0100au\u1b9b\u1b9ep;\u6a46p;\u6a4aot;\u628dr;\u6a45;\uc000\u222a\ufe00\u0200alrv\u1bb5\u1bbf\u1bde\u1be3rr\u0100;m\u1bbc\u1bbd\u61b7;\u693cy\u0180evw\u1bc7\u1bd4\u1bd8q\u0270\u1bce\0\0\u1bd2re\xe3\u1b73u\xe3\u1b75ee;\u62ceedge;\u62cfen\u803b\xa4\u40a4earrow\u0100lr\u1bee\u1bf3eft\xbb\u1b80ight\xbb\u1bbde\xe4\u1bdd\u0100ci\u1c01\u1c07onin\xf4\u01f7nt;\u6231lcty;\u632d\u0980AHabcdefhijlorstuwz\u1c38\u1c3b\u1c3f\u1c5d\u1c69\u1c75\u1c8a\u1c9e\u1cac\u1cb7\u1cfb\u1cff\u1d0d\u1d7b\u1d91\u1dab\u1dbb\u1dc6\u1dcdr\xf2\u0381ar;\u6965\u0200glrs\u1c48\u1c4d\u1c52\u1c54ger;\u6020eth;\u6138\xf2\u1133h\u0100;v\u1c5a\u1c5b\u6010\xbb\u090a\u016b\u1c61\u1c67arow;\u690fa\xe3\u0315\u0100ay\u1c6e\u1c73ron;\u410f;\u4434\u0180;ao\u0332\u1c7c\u1c84\u0100gr\u02bf\u1c81r;\u61catseq;\u6a77\u0180glm\u1c91\u1c94\u1c98\u803b\xb0\u40b0ta;\u43b4ptyv;\u69b1\u0100ir\u1ca3\u1ca8sht;\u697f;\uc000\ud835\udd21ar\u0100lr\u1cb3\u1cb5\xbb\u08dc\xbb\u101e\u0280aegsv\u1cc2\u0378\u1cd6\u1cdc\u1ce0m\u0180;os\u0326\u1cca\u1cd4nd\u0100;s\u0326\u1cd1uit;\u6666amma;\u43ddin;\u62f2\u0180;io\u1ce7\u1ce8\u1cf8\u40f7de\u8100\xf7;o\u1ce7\u1cf0ntimes;\u62c7n\xf8\u1cf7cy;\u4452c\u026f\u1d06\0\0\u1d0arn;\u631eop;\u630d\u0280lptuw\u1d18\u1d1d\u1d22\u1d49\u1d55lar;\u4024f;\uc000\ud835\udd55\u0280;emps\u030b\u1d2d\u1d37\u1d3d\u1d42q\u0100;d\u0352\u1d33ot;\u6251inus;\u6238lus;\u6214quare;\u62a1blebarwedg\xe5\xfan\u0180adh\u112e\u1d5d\u1d67ownarrow\xf3\u1c83arpoon\u0100lr\u1d72\u1d76ef\xf4\u1cb4igh\xf4\u1cb6\u0162\u1d7f\u1d85karo\xf7\u0f42\u026f\u1d8a\0\0\u1d8ern;\u631fop;\u630c\u0180cot\u1d98\u1da3\u1da6\u0100ry\u1d9d\u1da1;\uc000\ud835\udcb9;\u4455l;\u69f6rok;\u4111\u0100dr\u1db0\u1db4ot;\u62f1i\u0100;f\u1dba\u1816\u65bf\u0100ah\u1dc0\u1dc3r\xf2\u0429a\xf2\u0fa6angle;\u69a6\u0100ci\u1dd2\u1dd5y;\u445fgrarr;\u67ff\u0900Dacdefglmnopqrstux\u1e01\u1e09\u1e19\u1e38\u0578\u1e3c\u1e49\u1e61\u1e7e\u1ea5\u1eaf\u1ebd\u1ee1\u1f2a\u1f37\u1f44\u1f4e\u1f5a\u0100Do\u1e06\u1d34o\xf4\u1c89\u0100cs\u1e0e\u1e14ute\u803b\xe9\u40e9ter;\u6a6e\u0200aioy\u1e22\u1e27\u1e31\u1e36ron;\u411br\u0100;c\u1e2d\u1e2e\u6256\u803b\xea\u40ealon;\u6255;\u444dot;\u4117\u0100Dr\u1e41\u1e45ot;\u6252;\uc000\ud835\udd22\u0180;rs\u1e50\u1e51\u1e57\u6a9aave\u803b\xe8\u40e8\u0100;d\u1e5c\u1e5d\u6a96ot;\u6a98\u0200;ils\u1e6a\u1e6b\u1e72\u1e74\u6a99nters;\u63e7;\u6113\u0100;d\u1e79\u1e7a\u6a95ot;\u6a97\u0180aps\u1e85\u1e89\u1e97cr;\u4113ty\u0180;sv\u1e92\u1e93\u1e95\u6205et\xbb\u1e93p\u01001;\u1e9d\u1ea4\u0133\u1ea1\u1ea3;\u6004;\u6005\u6003\u0100gs\u1eaa\u1eac;\u414bp;\u6002\u0100gp\u1eb4\u1eb8on;\u4119f;\uc000\ud835\udd56\u0180als\u1ec4\u1ece\u1ed2r\u0100;s\u1eca\u1ecb\u62d5l;\u69e3us;\u6a71i\u0180;lv\u1eda\u1edb\u1edf\u43b5on\xbb\u1edb;\u43f5\u0200csuv\u1eea\u1ef3\u1f0b\u1f23\u0100io\u1eef\u1e31rc\xbb\u1e2e\u0269\u1ef9\0\0\u1efb\xed\u0548ant\u0100gl\u1f02\u1f06tr\xbb\u1e5dess\xbb\u1e7a\u0180aei\u1f12\u1f16\u1f1als;\u403dst;\u625fv\u0100;D\u0235\u1f20D;\u6a78parsl;\u69e5\u0100Da\u1f2f\u1f33ot;\u6253rr;\u6971\u0180cdi\u1f3e\u1f41\u1ef8r;\u612fo\xf4\u0352\u0100ah\u1f49\u1f4b;\u43b7\u803b\xf0\u40f0\u0100mr\u1f53\u1f57l\u803b\xeb\u40ebo;\u60ac\u0180cip\u1f61\u1f64\u1f67l;\u4021s\xf4\u056e\u0100eo\u1f6c\u1f74ctatio\xee\u0559nential\xe5\u0579\u09e1\u1f92\0\u1f9e\0\u1fa1\u1fa7\0\0\u1fc6\u1fcc\0\u1fd3\0\u1fe6\u1fea\u2000\0\u2008\u205allingdotse\xf1\u1e44y;\u4444male;\u6640\u0180ilr\u1fad\u1fb3\u1fc1lig;\u8000\ufb03\u0269\u1fb9\0\0\u1fbdg;\u8000\ufb00ig;\u8000\ufb04;\uc000\ud835\udd23lig;\u8000\ufb01lig;\uc000fj\u0180alt\u1fd9\u1fdc\u1fe1t;\u666dig;\u8000\ufb02ns;\u65b1of;\u4192\u01f0\u1fee\0\u1ff3f;\uc000\ud835\udd57\u0100ak\u05bf\u1ff7\u0100;v\u1ffc\u1ffd\u62d4;\u6ad9artint;\u6a0d\u0100ao\u200c\u2055\u0100cs\u2011\u2052\u03b1\u201a\u2030\u2038\u2045\u2048\0\u2050\u03b2\u2022\u2025\u2027\u202a\u202c\0\u202e\u803b\xbd\u40bd;\u6153\u803b\xbc\u40bc;\u6155;\u6159;\u615b\u01b3\u2034\0\u2036;\u6154;\u6156\u02b4\u203e\u2041\0\0\u2043\u803b\xbe\u40be;\u6157;\u615c5;\u6158\u01b6\u204c\0\u204e;\u615a;\u615d8;\u615el;\u6044wn;\u6322cr;\uc000\ud835\udcbb\u0880Eabcdefgijlnorstv\u2082\u2089\u209f\u20a5\u20b0\u20b4\u20f0\u20f5\u20fa\u20ff\u2103\u2112\u2138\u0317\u213e\u2152\u219e\u0100;l\u064d\u2087;\u6a8c\u0180cmp\u2090\u2095\u209dute;\u41f5ma\u0100;d\u209c\u1cda\u43b3;\u6a86reve;\u411f\u0100iy\u20aa\u20aerc;\u411d;\u4433ot;\u4121\u0200;lqs\u063e\u0642\u20bd\u20c9\u0180;qs\u063e\u064c\u20c4lan\xf4\u0665\u0200;cdl\u0665\u20d2\u20d5\u20e5c;\u6aa9ot\u0100;o\u20dc\u20dd\u6a80\u0100;l\u20e2\u20e3\u6a82;\u6a84\u0100;e\u20ea\u20ed\uc000\u22db\ufe00s;\u6a94r;\uc000\ud835\udd24\u0100;g\u0673\u061bmel;\u6137cy;\u4453\u0200;Eaj\u065a\u210c\u210e\u2110;\u6a92;\u6aa5;\u6aa4\u0200Eaes\u211b\u211d\u2129\u2134;\u6269p\u0100;p\u2123\u2124\u6a8arox\xbb\u2124\u0100;q\u212e\u212f\u6a88\u0100;q\u212e\u211bim;\u62e7pf;\uc000\ud835\udd58\u0100ci\u2143\u2146r;\u610am\u0180;el\u066b\u214e\u2150;\u6a8e;\u6a90\u8300>;cdlqr\u05ee\u2160\u216a\u216e\u2173\u2179\u0100ci\u2165\u2167;\u6aa7r;\u6a7aot;\u62d7Par;\u6995uest;\u6a7c\u0280adels\u2184\u216a\u2190\u0656\u219b\u01f0\u2189\0\u218epro\xf8\u209er;\u6978q\u0100lq\u063f\u2196les\xf3\u2088i\xed\u066b\u0100en\u21a3\u21adrtneqq;\uc000\u2269\ufe00\xc5\u21aa\u0500Aabcefkosy\u21c4\u21c7\u21f1\u21f5\u21fa\u2218\u221d\u222f\u2268\u227dr\xf2\u03a0\u0200ilmr\u21d0\u21d4\u21d7\u21dbrs\xf0\u1484f\xbb\u2024il\xf4\u06a9\u0100dr\u21e0\u21e4cy;\u444a\u0180;cw\u08f4\u21eb\u21efir;\u6948;\u61adar;\u610firc;\u4125\u0180alr\u2201\u220e\u2213rts\u0100;u\u2209\u220a\u6665it\xbb\u220alip;\u6026con;\u62b9r;\uc000\ud835\udd25s\u0100ew\u2223\u2229arow;\u6925arow;\u6926\u0280amopr\u223a\u223e\u2243\u225e\u2263rr;\u61fftht;\u623bk\u0100lr\u2249\u2253eftarrow;\u61a9ightarrow;\u61aaf;\uc000\ud835\udd59bar;\u6015\u0180clt\u226f\u2274\u2278r;\uc000\ud835\udcbdas\xe8\u21f4rok;\u4127\u0100bp\u2282\u2287ull;\u6043hen\xbb\u1c5b\u0ae1\u22a3\0\u22aa\0\u22b8\u22c5\u22ce\0\u22d5\u22f3\0\0\u22f8\u2322\u2367\u2362\u237f\0\u2386\u23aa\u23b4cute\u803b\xed\u40ed\u0180;iy\u0771\u22b0\u22b5rc\u803b\xee\u40ee;\u4438\u0100cx\u22bc\u22bfy;\u4435cl\u803b\xa1\u40a1\u0100fr\u039f\u22c9;\uc000\ud835\udd26rave\u803b\xec\u40ec\u0200;ino\u073e\u22dd\u22e9\u22ee\u0100in\u22e2\u22e6nt;\u6a0ct;\u622dfin;\u69dcta;\u6129lig;\u4133\u0180aop\u22fe\u231a\u231d\u0180cgt\u2305\u2308\u2317r;\u412b\u0180elp\u071f\u230f\u2313in\xe5\u078ear\xf4\u0720h;\u4131f;\u62b7ed;\u41b5\u0280;cfot\u04f4\u232c\u2331\u233d\u2341are;\u6105in\u0100;t\u2338\u2339\u621eie;\u69dddo\xf4\u2319\u0280;celp\u0757\u234c\u2350\u235b\u2361al;\u62ba\u0100gr\u2355\u2359er\xf3\u1563\xe3\u234darhk;\u6a17rod;\u6a3c\u0200cgpt\u236f\u2372\u2376\u237by;\u4451on;\u412ff;\uc000\ud835\udd5aa;\u43b9uest\u803b\xbf\u40bf\u0100ci\u238a\u238fr;\uc000\ud835\udcben\u0280;Edsv\u04f4\u239b\u239d\u23a1\u04f3;\u62f9ot;\u62f5\u0100;v\u23a6\u23a7\u62f4;\u62f3\u0100;i\u0777\u23aelde;\u4129\u01eb\u23b8\0\u23bccy;\u4456l\u803b\xef\u40ef\u0300cfmosu\u23cc\u23d7\u23dc\u23e1\u23e7\u23f5\u0100iy\u23d1\u23d5rc;\u4135;\u4439r;\uc000\ud835\udd27ath;\u4237pf;\uc000\ud835\udd5b\u01e3\u23ec\0\u23f1r;\uc000\ud835\udcbfrcy;\u4458kcy;\u4454\u0400acfghjos\u240b\u2416\u2422\u2427\u242d\u2431\u2435\u243bppa\u0100;v\u2413\u2414\u43ba;\u43f0\u0100ey\u241b\u2420dil;\u4137;\u443ar;\uc000\ud835\udd28reen;\u4138cy;\u4445cy;\u445cpf;\uc000\ud835\udd5ccr;\uc000\ud835\udcc0\u0b80ABEHabcdefghjlmnoprstuv\u2470\u2481\u2486\u248d\u2491\u250e\u253d\u255a\u2580\u264e\u265e\u2665\u2679\u267d\u269a\u26b2\u26d8\u275d\u2768\u278b\u27c0\u2801\u2812\u0180art\u2477\u247a\u247cr\xf2\u09c6\xf2\u0395ail;\u691barr;\u690e\u0100;g\u0994\u248b;\u6a8bar;\u6962\u0963\u24a5\0\u24aa\0\u24b1\0\0\0\0\0\u24b5\u24ba\0\u24c6\u24c8\u24cd\0\u24f9ute;\u413amptyv;\u69b4ra\xee\u084cbda;\u43bbg\u0180;dl\u088e\u24c1\u24c3;\u6991\xe5\u088e;\u6a85uo\u803b\xab\u40abr\u0400;bfhlpst\u0899\u24de\u24e6\u24e9\u24eb\u24ee\u24f1\u24f5\u0100;f\u089d\u24e3s;\u691fs;\u691d\xeb\u2252p;\u61abl;\u6939im;\u6973l;\u61a2\u0180;ae\u24ff\u2500\u2504\u6aabil;\u6919\u0100;s\u2509\u250a\u6aad;\uc000\u2aad\ufe00\u0180abr\u2515\u2519\u251drr;\u690crk;\u6772\u0100ak\u2522\u252cc\u0100ek\u2528\u252a;\u407b;\u405b\u0100es\u2531\u2533;\u698bl\u0100du\u2539\u253b;\u698f;\u698d\u0200aeuy\u2546\u254b\u2556\u2558ron;\u413e\u0100di\u2550\u2554il;\u413c\xec\u08b0\xe2\u2529;\u443b\u0200cqrs\u2563\u2566\u256d\u257da;\u6936uo\u0100;r\u0e19\u1746\u0100du\u2572\u2577har;\u6967shar;\u694bh;\u61b2\u0280;fgqs\u258b\u258c\u0989\u25f3\u25ff\u6264t\u0280ahlrt\u2598\u25a4\u25b7\u25c2\u25e8rrow\u0100;t\u0899\u25a1a\xe9\u24f6arpoon\u0100du\u25af\u25b4own\xbb\u045ap\xbb\u0966eftarrows;\u61c7ight\u0180ahs\u25cd\u25d6\u25derrow\u0100;s\u08f4\u08a7arpoon\xf3\u0f98quigarro\xf7\u21f0hreetimes;\u62cb\u0180;qs\u258b\u0993\u25falan\xf4\u09ac\u0280;cdgs\u09ac\u260a\u260d\u261d\u2628c;\u6aa8ot\u0100;o\u2614\u2615\u6a7f\u0100;r\u261a\u261b\u6a81;\u6a83\u0100;e\u2622\u2625\uc000\u22da\ufe00s;\u6a93\u0280adegs\u2633\u2639\u263d\u2649\u264bppro\xf8\u24c6ot;\u62d6q\u0100gq\u2643\u2645\xf4\u0989gt\xf2\u248c\xf4\u099bi\xed\u09b2\u0180ilr\u2655\u08e1\u265asht;\u697c;\uc000\ud835\udd29\u0100;E\u099c\u2663;\u6a91\u0161\u2669\u2676r\u0100du\u25b2\u266e\u0100;l\u0965\u2673;\u696alk;\u6584cy;\u4459\u0280;acht\u0a48\u2688\u268b\u2691\u2696r\xf2\u25c1orne\xf2\u1d08ard;\u696bri;\u65fa\u0100io\u269f\u26a4dot;\u4140ust\u0100;a\u26ac\u26ad\u63b0che\xbb\u26ad\u0200Eaes\u26bb\u26bd\u26c9\u26d4;\u6268p\u0100;p\u26c3\u26c4\u6a89rox\xbb\u26c4\u0100;q\u26ce\u26cf\u6a87\u0100;q\u26ce\u26bbim;\u62e6\u0400abnoptwz\u26e9\u26f4\u26f7\u271a\u272f\u2741\u2747\u2750\u0100nr\u26ee\u26f1g;\u67ecr;\u61fdr\xeb\u08c1g\u0180lmr\u26ff\u270d\u2714eft\u0100ar\u09e6\u2707ight\xe1\u09f2apsto;\u67fcight\xe1\u09fdparrow\u0100lr\u2725\u2729ef\xf4\u24edight;\u61ac\u0180afl\u2736\u2739\u273dr;\u6985;\uc000\ud835\udd5dus;\u6a2dimes;\u6a34\u0161\u274b\u274fst;\u6217\xe1\u134e\u0180;ef\u2757\u2758\u1800\u65cange\xbb\u2758ar\u0100;l\u2764\u2765\u4028t;\u6993\u0280achmt\u2773\u2776\u277c\u2785\u2787r\xf2\u08a8orne\xf2\u1d8car\u0100;d\u0f98\u2783;\u696d;\u600eri;\u62bf\u0300achiqt\u2798\u279d\u0a40\u27a2\u27ae\u27bbquo;\u6039r;\uc000\ud835\udcc1m\u0180;eg\u09b2\u27aa\u27ac;\u6a8d;\u6a8f\u0100bu\u252a\u27b3o\u0100;r\u0e1f\u27b9;\u601arok;\u4142\u8400<;cdhilqr\u082b\u27d2\u2639\u27dc\u27e0\u27e5\u27ea\u27f0\u0100ci\u27d7\u27d9;\u6aa6r;\u6a79re\xe5\u25f2mes;\u62c9arr;\u6976uest;\u6a7b\u0100Pi\u27f5\u27f9ar;\u6996\u0180;ef\u2800\u092d\u181b\u65c3r\u0100du\u2807\u280dshar;\u694ahar;\u6966\u0100en\u2817\u2821rtneqq;\uc000\u2268\ufe00\xc5\u281e\u0700Dacdefhilnopsu\u2840\u2845\u2882\u288e\u2893\u28a0\u28a5\u28a8\u28da\u28e2\u28e4\u0a83\u28f3\u2902Dot;\u623a\u0200clpr\u284e\u2852\u2863\u287dr\u803b\xaf\u40af\u0100et\u2857\u2859;\u6642\u0100;e\u285e\u285f\u6720se\xbb\u285f\u0100;s\u103b\u2868to\u0200;dlu\u103b\u2873\u2877\u287bow\xee\u048cef\xf4\u090f\xf0\u13d1ker;\u65ae\u0100oy\u2887\u288cmma;\u6a29;\u443cash;\u6014asuredangle\xbb\u1626r;\uc000\ud835\udd2ao;\u6127\u0180cdn\u28af\u28b4\u28c9ro\u803b\xb5\u40b5\u0200;acd\u1464\u28bd\u28c0\u28c4s\xf4\u16a7ir;\u6af0ot\u80bb\xb7\u01b5us\u0180;bd\u28d2\u1903\u28d3\u6212\u0100;u\u1d3c\u28d8;\u6a2a\u0163\u28de\u28e1p;\u6adb\xf2\u2212\xf0\u0a81\u0100dp\u28e9\u28eeels;\u62a7f;\uc000\ud835\udd5e\u0100ct\u28f8\u28fdr;\uc000\ud835\udcc2pos\xbb\u159d\u0180;lm\u2909\u290a\u290d\u43bctimap;\u62b8\u0c00GLRVabcdefghijlmoprstuvw\u2942\u2953\u297e\u2989\u2998\u29da\u29e9\u2a15\u2a1a\u2a58\u2a5d\u2a83\u2a95\u2aa4\u2aa8\u2b04\u2b07\u2b44\u2b7f\u2bae\u2c34\u2c67\u2c7c\u2ce9\u0100gt\u2947\u294b;\uc000\u22d9\u0338\u0100;v\u2950\u0bcf\uc000\u226b\u20d2\u0180elt\u295a\u2972\u2976ft\u0100ar\u2961\u2967rrow;\u61cdightarrow;\u61ce;\uc000\u22d8\u0338\u0100;v\u297b\u0c47\uc000\u226a\u20d2ightarrow;\u61cf\u0100Dd\u298e\u2993ash;\u62afash;\u62ae\u0280bcnpt\u29a3\u29a7\u29ac\u29b1\u29ccla\xbb\u02deute;\u4144g;\uc000\u2220\u20d2\u0280;Eiop\u0d84\u29bc\u29c0\u29c5\u29c8;\uc000\u2a70\u0338d;\uc000\u224b\u0338s;\u4149ro\xf8\u0d84ur\u0100;a\u29d3\u29d4\u666el\u0100;s\u29d3\u0b38\u01f3\u29df\0\u29e3p\u80bb\xa0\u0b37mp\u0100;e\u0bf9\u0c00\u0280aeouy\u29f4\u29fe\u2a03\u2a10\u2a13\u01f0\u29f9\0\u29fb;\u6a43on;\u4148dil;\u4146ng\u0100;d\u0d7e\u2a0aot;\uc000\u2a6d\u0338p;\u6a42;\u443dash;\u6013\u0380;Aadqsx\u0b92\u2a29\u2a2d\u2a3b\u2a41\u2a45\u2a50rr;\u61d7r\u0100hr\u2a33\u2a36k;\u6924\u0100;o\u13f2\u13f0ot;\uc000\u2250\u0338ui\xf6\u0b63\u0100ei\u2a4a\u2a4ear;\u6928\xed\u0b98ist\u0100;s\u0ba0\u0b9fr;\uc000\ud835\udd2b\u0200Eest\u0bc5\u2a66\u2a79\u2a7c\u0180;qs\u0bbc\u2a6d\u0be1\u0180;qs\u0bbc\u0bc5\u2a74lan\xf4\u0be2i\xed\u0bea\u0100;r\u0bb6\u2a81\xbb\u0bb7\u0180Aap\u2a8a\u2a8d\u2a91r\xf2\u2971rr;\u61aear;\u6af2\u0180;sv\u0f8d\u2a9c\u0f8c\u0100;d\u2aa1\u2aa2\u62fc;\u62facy;\u445a\u0380AEadest\u2ab7\u2aba\u2abe\u2ac2\u2ac5\u2af6\u2af9r\xf2\u2966;\uc000\u2266\u0338rr;\u619ar;\u6025\u0200;fqs\u0c3b\u2ace\u2ae3\u2aeft\u0100ar\u2ad4\u2ad9rro\xf7\u2ac1ightarro\xf7\u2a90\u0180;qs\u0c3b\u2aba\u2aealan\xf4\u0c55\u0100;s\u0c55\u2af4\xbb\u0c36i\xed\u0c5d\u0100;r\u0c35\u2afei\u0100;e\u0c1a\u0c25i\xe4\u0d90\u0100pt\u2b0c\u2b11f;\uc000\ud835\udd5f\u8180\xac;in\u2b19\u2b1a\u2b36\u40acn\u0200;Edv\u0b89\u2b24\u2b28\u2b2e;\uc000\u22f9\u0338ot;\uc000\u22f5\u0338\u01e1\u0b89\u2b33\u2b35;\u62f7;\u62f6i\u0100;v\u0cb8\u2b3c\u01e1\u0cb8\u2b41\u2b43;\u62fe;\u62fd\u0180aor\u2b4b\u2b63\u2b69r\u0200;ast\u0b7b\u2b55\u2b5a\u2b5flle\xec\u0b7bl;\uc000\u2afd\u20e5;\uc000\u2202\u0338lint;\u6a14\u0180;ce\u0c92\u2b70\u2b73u\xe5\u0ca5\u0100;c\u0c98\u2b78\u0100;e\u0c92\u2b7d\xf1\u0c98\u0200Aait\u2b88\u2b8b\u2b9d\u2ba7r\xf2\u2988rr\u0180;cw\u2b94\u2b95\u2b99\u619b;\uc000\u2933\u0338;\uc000\u219d\u0338ghtarrow\xbb\u2b95ri\u0100;e\u0ccb\u0cd6\u0380chimpqu\u2bbd\u2bcd\u2bd9\u2b04\u0b78\u2be4\u2bef\u0200;cer\u0d32\u2bc6\u0d37\u2bc9u\xe5\u0d45;\uc000\ud835\udcc3ort\u026d\u2b05\0\0\u2bd6ar\xe1\u2b56m\u0100;e\u0d6e\u2bdf\u0100;q\u0d74\u0d73su\u0100bp\u2beb\u2bed\xe5\u0cf8\xe5\u0d0b\u0180bcp\u2bf6\u2c11\u2c19\u0200;Ees\u2bff\u2c00\u0d22\u2c04\u6284;\uc000\u2ac5\u0338et\u0100;e\u0d1b\u2c0bq\u0100;q\u0d23\u2c00c\u0100;e\u0d32\u2c17\xf1\u0d38\u0200;Ees\u2c22\u2c23\u0d5f\u2c27\u6285;\uc000\u2ac6\u0338et\u0100;e\u0d58\u2c2eq\u0100;q\u0d60\u2c23\u0200gilr\u2c3d\u2c3f\u2c45\u2c47\xec\u0bd7lde\u803b\xf1\u40f1\xe7\u0c43iangle\u0100lr\u2c52\u2c5ceft\u0100;e\u0c1a\u2c5a\xf1\u0c26ight\u0100;e\u0ccb\u2c65\xf1\u0cd7\u0100;m\u2c6c\u2c6d\u43bd\u0180;es\u2c74\u2c75\u2c79\u4023ro;\u6116p;\u6007\u0480DHadgilrs\u2c8f\u2c94\u2c99\u2c9e\u2ca3\u2cb0\u2cb6\u2cd3\u2ce3ash;\u62adarr;\u6904p;\uc000\u224d\u20d2ash;\u62ac\u0100et\u2ca8\u2cac;\uc000\u2265\u20d2;\uc000>\u20d2nfin;\u69de\u0180Aet\u2cbd\u2cc1\u2cc5rr;\u6902;\uc000\u2264\u20d2\u0100;r\u2cca\u2ccd\uc000<\u20d2ie;\uc000\u22b4\u20d2\u0100At\u2cd8\u2cdcrr;\u6903rie;\uc000\u22b5\u20d2im;\uc000\u223c\u20d2\u0180Aan\u2cf0\u2cf4\u2d02rr;\u61d6r\u0100hr\u2cfa\u2cfdk;\u6923\u0100;o\u13e7\u13e5ear;\u6927\u1253\u1a95\0\0\0\0\0\0\0\0\0\0\0\0\0\u2d2d\0\u2d38\u2d48\u2d60\u2d65\u2d72\u2d84\u1b07\0\0\u2d8d\u2dab\0\u2dc8\u2dce\0\u2ddc\u2e19\u2e2b\u2e3e\u2e43\u0100cs\u2d31\u1a97ute\u803b\xf3\u40f3\u0100iy\u2d3c\u2d45r\u0100;c\u1a9e\u2d42\u803b\xf4\u40f4;\u443e\u0280abios\u1aa0\u2d52\u2d57\u01c8\u2d5alac;\u4151v;\u6a38old;\u69bclig;\u4153\u0100cr\u2d69\u2d6dir;\u69bf;\uc000\ud835\udd2c\u036f\u2d79\0\0\u2d7c\0\u2d82n;\u42dbave\u803b\xf2\u40f2;\u69c1\u0100bm\u2d88\u0df4ar;\u69b5\u0200acit\u2d95\u2d98\u2da5\u2da8r\xf2\u1a80\u0100ir\u2d9d\u2da0r;\u69beoss;\u69bbn\xe5\u0e52;\u69c0\u0180aei\u2db1\u2db5\u2db9cr;\u414dga;\u43c9\u0180cdn\u2dc0\u2dc5\u01cdron;\u43bf;\u69b6pf;\uc000\ud835\udd60\u0180ael\u2dd4\u2dd7\u01d2r;\u69b7rp;\u69b9\u0380;adiosv\u2dea\u2deb\u2dee\u2e08\u2e0d\u2e10\u2e16\u6228r\xf2\u1a86\u0200;efm\u2df7\u2df8\u2e02\u2e05\u6a5dr\u0100;o\u2dfe\u2dff\u6134f\xbb\u2dff\u803b\xaa\u40aa\u803b\xba\u40bagof;\u62b6r;\u6a56lope;\u6a57;\u6a5b\u0180clo\u2e1f\u2e21\u2e27\xf2\u2e01ash\u803b\xf8\u40f8l;\u6298i\u016c\u2e2f\u2e34de\u803b\xf5\u40f5es\u0100;a\u01db\u2e3as;\u6a36ml\u803b\xf6\u40f6bar;\u633d\u0ae1\u2e5e\0\u2e7d\0\u2e80\u2e9d\0\u2ea2\u2eb9\0\0\u2ecb\u0e9c\0\u2f13\0\0\u2f2b\u2fbc\0\u2fc8r\u0200;ast\u0403\u2e67\u2e72\u0e85\u8100\xb6;l\u2e6d\u2e6e\u40b6le\xec\u0403\u0269\u2e78\0\0\u2e7bm;\u6af3;\u6afdy;\u443fr\u0280cimpt\u2e8b\u2e8f\u2e93\u1865\u2e97nt;\u4025od;\u402eil;\u6030enk;\u6031r;\uc000\ud835\udd2d\u0180imo\u2ea8\u2eb0\u2eb4\u0100;v\u2ead\u2eae\u43c6;\u43d5ma\xf4\u0a76ne;\u660e\u0180;tv\u2ebf\u2ec0\u2ec8\u43c0chfork\xbb\u1ffd;\u43d6\u0100au\u2ecf\u2edfn\u0100ck\u2ed5\u2eddk\u0100;h\u21f4\u2edb;\u610e\xf6\u21f4s\u0480;abcdemst\u2ef3\u2ef4\u1908\u2ef9\u2efd\u2f04\u2f06\u2f0a\u2f0e\u402bcir;\u6a23ir;\u6a22\u0100ou\u1d40\u2f02;\u6a25;\u6a72n\u80bb\xb1\u0e9dim;\u6a26wo;\u6a27\u0180ipu\u2f19\u2f20\u2f25ntint;\u6a15f;\uc000\ud835\udd61nd\u803b\xa3\u40a3\u0500;Eaceinosu\u0ec8\u2f3f\u2f41\u2f44\u2f47\u2f81\u2f89\u2f92\u2f7e\u2fb6;\u6ab3p;\u6ab7u\xe5\u0ed9\u0100;c\u0ece\u2f4c\u0300;acens\u0ec8\u2f59\u2f5f\u2f66\u2f68\u2f7eppro\xf8\u2f43urlye\xf1\u0ed9\xf1\u0ece\u0180aes\u2f6f\u2f76\u2f7approx;\u6ab9qq;\u6ab5im;\u62e8i\xed\u0edfme\u0100;s\u2f88\u0eae\u6032\u0180Eas\u2f78\u2f90\u2f7a\xf0\u2f75\u0180dfp\u0eec\u2f99\u2faf\u0180als\u2fa0\u2fa5\u2faalar;\u632eine;\u6312urf;\u6313\u0100;t\u0efb\u2fb4\xef\u0efbrel;\u62b0\u0100ci\u2fc0\u2fc5r;\uc000\ud835\udcc5;\u43c8ncsp;\u6008\u0300fiopsu\u2fda\u22e2\u2fdf\u2fe5\u2feb\u2ff1r;\uc000\ud835\udd2epf;\uc000\ud835\udd62rime;\u6057cr;\uc000\ud835\udcc6\u0180aeo\u2ff8\u3009\u3013t\u0100ei\u2ffe\u3005rnion\xf3\u06b0nt;\u6a16st\u0100;e\u3010\u3011\u403f\xf1\u1f19\xf4\u0f14\u0a80ABHabcdefhilmnoprstux\u3040\u3051\u3055\u3059\u30e0\u310e\u312b\u3147\u3162\u3172\u318e\u3206\u3215\u3224\u3229\u3258\u326e\u3272\u3290\u32b0\u32b7\u0180art\u3047\u304a\u304cr\xf2\u10b3\xf2\u03ddail;\u691car\xf2\u1c65ar;\u6964\u0380cdenqrt\u3068\u3075\u3078\u307f\u308f\u3094\u30cc\u0100eu\u306d\u3071;\uc000\u223d\u0331te;\u4155i\xe3\u116emptyv;\u69b3g\u0200;del\u0fd1\u3089\u308b\u308d;\u6992;\u69a5\xe5\u0fd1uo\u803b\xbb\u40bbr\u0580;abcfhlpstw\u0fdc\u30ac\u30af\u30b7\u30b9\u30bc\u30be\u30c0\u30c3\u30c7\u30cap;\u6975\u0100;f\u0fe0\u30b4s;\u6920;\u6933s;\u691e\xeb\u225d\xf0\u272el;\u6945im;\u6974l;\u61a3;\u619d\u0100ai\u30d1\u30d5il;\u691ao\u0100;n\u30db\u30dc\u6236al\xf3\u0f1e\u0180abr\u30e7\u30ea\u30eer\xf2\u17e5rk;\u6773\u0100ak\u30f3\u30fdc\u0100ek\u30f9\u30fb;\u407d;\u405d\u0100es\u3102\u3104;\u698cl\u0100du\u310a\u310c;\u698e;\u6990\u0200aeuy\u3117\u311c\u3127\u3129ron;\u4159\u0100di\u3121\u3125il;\u4157\xec\u0ff2\xe2\u30fa;\u4440\u0200clqs\u3134\u3137\u313d\u3144a;\u6937dhar;\u6969uo\u0100;r\u020e\u020dh;\u61b3\u0180acg\u314e\u315f\u0f44l\u0200;ips\u0f78\u3158\u315b\u109cn\xe5\u10bbar\xf4\u0fa9t;\u65ad\u0180ilr\u3169\u1023\u316esht;\u697d;\uc000\ud835\udd2f\u0100ao\u3177\u3186r\u0100du\u317d\u317f\xbb\u047b\u0100;l\u1091\u3184;\u696c\u0100;v\u318b\u318c\u43c1;\u43f1\u0180gns\u3195\u31f9\u31fcht\u0300ahlrst\u31a4\u31b0\u31c2\u31d8\u31e4\u31eerrow\u0100;t\u0fdc\u31ada\xe9\u30c8arpoon\u0100du\u31bb\u31bfow\xee\u317ep\xbb\u1092eft\u0100ah\u31ca\u31d0rrow\xf3\u0feaarpoon\xf3\u0551ightarrows;\u61c9quigarro\xf7\u30cbhreetimes;\u62ccg;\u42daingdotse\xf1\u1f32\u0180ahm\u320d\u3210\u3213r\xf2\u0feaa\xf2\u0551;\u600foust\u0100;a\u321e\u321f\u63b1che\xbb\u321fmid;\u6aee\u0200abpt\u3232\u323d\u3240\u3252\u0100nr\u3237\u323ag;\u67edr;\u61fer\xeb\u1003\u0180afl\u3247\u324a\u324er;\u6986;\uc000\ud835\udd63us;\u6a2eimes;\u6a35\u0100ap\u325d\u3267r\u0100;g\u3263\u3264\u4029t;\u6994olint;\u6a12ar\xf2\u31e3\u0200achq\u327b\u3280\u10bc\u3285quo;\u603ar;\uc000\ud835\udcc7\u0100bu\u30fb\u328ao\u0100;r\u0214\u0213\u0180hir\u3297\u329b\u32a0re\xe5\u31f8mes;\u62cai\u0200;efl\u32aa\u1059\u1821\u32ab\u65b9tri;\u69celuhar;\u6968;\u611e\u0d61\u32d5\u32db\u32df\u332c\u3338\u3371\0\u337a\u33a4\0\0\u33ec\u33f0\0\u3428\u3448\u345a\u34ad\u34b1\u34ca\u34f1\0\u3616\0\0\u3633cute;\u415bqu\xef\u27ba\u0500;Eaceinpsy\u11ed\u32f3\u32f5\u32ff\u3302\u330b\u330f\u331f\u3326\u3329;\u6ab4\u01f0\u32fa\0\u32fc;\u6ab8on;\u4161u\xe5\u11fe\u0100;d\u11f3\u3307il;\u415frc;\u415d\u0180Eas\u3316\u3318\u331b;\u6ab6p;\u6abaim;\u62e9olint;\u6a13i\xed\u1204;\u4441ot\u0180;be\u3334\u1d47\u3335\u62c5;\u6a66\u0380Aacmstx\u3346\u334a\u3357\u335b\u335e\u3363\u336drr;\u61d8r\u0100hr\u3350\u3352\xeb\u2228\u0100;o\u0a36\u0a34t\u803b\xa7\u40a7i;\u403bwar;\u6929m\u0100in\u3369\xf0nu\xf3\xf1t;\u6736r\u0100;o\u3376\u2055\uc000\ud835\udd30\u0200acoy\u3382\u3386\u3391\u33a0rp;\u666f\u0100hy\u338b\u338fcy;\u4449;\u4448rt\u026d\u3399\0\0\u339ci\xe4\u1464ara\xec\u2e6f\u803b\xad\u40ad\u0100gm\u33a8\u33b4ma\u0180;fv\u33b1\u33b2\u33b2\u43c3;\u43c2\u0400;deglnpr\u12ab\u33c5\u33c9\u33ce\u33d6\u33de\u33e1\u33e6ot;\u6a6a\u0100;q\u12b1\u12b0\u0100;E\u33d3\u33d4\u6a9e;\u6aa0\u0100;E\u33db\u33dc\u6a9d;\u6a9fe;\u6246lus;\u6a24arr;\u6972ar\xf2\u113d\u0200aeit\u33f8\u3408\u340f\u3417\u0100ls\u33fd\u3404lsetm\xe9\u336ahp;\u6a33parsl;\u69e4\u0100dl\u1463\u3414e;\u6323\u0100;e\u341c\u341d\u6aaa\u0100;s\u3422\u3423\u6aac;\uc000\u2aac\ufe00\u0180flp\u342e\u3433\u3442tcy;\u444c\u0100;b\u3438\u3439\u402f\u0100;a\u343e\u343f\u69c4r;\u633ff;\uc000\ud835\udd64a\u0100dr\u344d\u0402es\u0100;u\u3454\u3455\u6660it\xbb\u3455\u0180csu\u3460\u3479\u349f\u0100au\u3465\u346fp\u0100;s\u1188\u346b;\uc000\u2293\ufe00p\u0100;s\u11b4\u3475;\uc000\u2294\ufe00u\u0100bp\u347f\u348f\u0180;es\u1197\u119c\u3486et\u0100;e\u1197\u348d\xf1\u119d\u0180;es\u11a8\u11ad\u3496et\u0100;e\u11a8\u349d\xf1\u11ae\u0180;af\u117b\u34a6\u05b0r\u0165\u34ab\u05b1\xbb\u117car\xf2\u1148\u0200cemt\u34b9\u34be\u34c2\u34c5r;\uc000\ud835\udcc8tm\xee\xf1i\xec\u3415ar\xe6\u11be\u0100ar\u34ce\u34d5r\u0100;f\u34d4\u17bf\u6606\u0100an\u34da\u34edight\u0100ep\u34e3\u34eapsilo\xee\u1ee0h\xe9\u2eafs\xbb\u2852\u0280bcmnp\u34fb\u355e\u1209\u358b\u358e\u0480;Edemnprs\u350e\u350f\u3511\u3515\u351e\u3523\u352c\u3531\u3536\u6282;\u6ac5ot;\u6abd\u0100;d\u11da\u351aot;\u6ac3ult;\u6ac1\u0100Ee\u3528\u352a;\u6acb;\u628alus;\u6abfarr;\u6979\u0180eiu\u353d\u3552\u3555t\u0180;en\u350e\u3545\u354bq\u0100;q\u11da\u350feq\u0100;q\u352b\u3528m;\u6ac7\u0100bp\u355a\u355c;\u6ad5;\u6ad3c\u0300;acens\u11ed\u356c\u3572\u3579\u357b\u3326ppro\xf8\u32faurlye\xf1\u11fe\xf1\u11f3\u0180aes\u3582\u3588\u331bppro\xf8\u331aq\xf1\u3317g;\u666a\u0680123;Edehlmnps\u35a9\u35ac\u35af\u121c\u35b2\u35b4\u35c0\u35c9\u35d5\u35da\u35df\u35e8\u35ed\u803b\xb9\u40b9\u803b\xb2\u40b2\u803b\xb3\u40b3;\u6ac6\u0100os\u35b9\u35bct;\u6abeub;\u6ad8\u0100;d\u1222\u35c5ot;\u6ac4s\u0100ou\u35cf\u35d2l;\u67c9b;\u6ad7arr;\u697bult;\u6ac2\u0100Ee\u35e4\u35e6;\u6acc;\u628blus;\u6ac0\u0180eiu\u35f4\u3609\u360ct\u0180;en\u121c\u35fc\u3602q\u0100;q\u1222\u35b2eq\u0100;q\u35e7\u35e4m;\u6ac8\u0100bp\u3611\u3613;\u6ad4;\u6ad6\u0180Aan\u361c\u3620\u362drr;\u61d9r\u0100hr\u3626\u3628\xeb\u222e\u0100;o\u0a2b\u0a29war;\u692alig\u803b\xdf\u40df\u0be1\u3651\u365d\u3660\u12ce\u3673\u3679\0\u367e\u36c2\0\0\0\0\0\u36db\u3703\0\u3709\u376c\0\0\0\u3787\u0272\u3656\0\0\u365bget;\u6316;\u43c4r\xeb\u0e5f\u0180aey\u3666\u366b\u3670ron;\u4165dil;\u4163;\u4442lrec;\u6315r;\uc000\ud835\udd31\u0200eiko\u3686\u369d\u36b5\u36bc\u01f2\u368b\0\u3691e\u01004f\u1284\u1281a\u0180;sv\u3698\u3699\u369b\u43b8ym;\u43d1\u0100cn\u36a2\u36b2k\u0100as\u36a8\u36aeppro\xf8\u12c1im\xbb\u12acs\xf0\u129e\u0100as\u36ba\u36ae\xf0\u12c1rn\u803b\xfe\u40fe\u01ec\u031f\u36c6\u22e7es\u8180\xd7;bd\u36cf\u36d0\u36d8\u40d7\u0100;a\u190f\u36d5r;\u6a31;\u6a30\u0180eps\u36e1\u36e3\u3700\xe1\u2a4d\u0200;bcf\u0486\u36ec\u36f0\u36f4ot;\u6336ir;\u6af1\u0100;o\u36f9\u36fc\uc000\ud835\udd65rk;\u6ada\xe1\u3362rime;\u6034\u0180aip\u370f\u3712\u3764d\xe5\u1248\u0380adempst\u3721\u374d\u3740\u3751\u3757\u375c\u375fngle\u0280;dlqr\u3730\u3731\u3736\u3740\u3742\u65b5own\xbb\u1dbbeft\u0100;e\u2800\u373e\xf1\u092e;\u625cight\u0100;e\u32aa\u374b\xf1\u105aot;\u65ecinus;\u6a3alus;\u6a39b;\u69cdime;\u6a3bezium;\u63e2\u0180cht\u3772\u377d\u3781\u0100ry\u3777\u377b;\uc000\ud835\udcc9;\u4446cy;\u445brok;\u4167\u0100io\u378b\u378ex\xf4\u1777head\u0100lr\u3797\u37a0eftarro\xf7\u084fightarrow\xbb\u0f5d\u0900AHabcdfghlmoprstuw\u37d0\u37d3\u37d7\u37e4\u37f0\u37fc\u380e\u381c\u3823\u3834\u3851\u385d\u386b\u38a9\u38cc\u38d2\u38ea\u38f6r\xf2\u03edar;\u6963\u0100cr\u37dc\u37e2ute\u803b\xfa\u40fa\xf2\u1150r\u01e3\u37ea\0\u37edy;\u445eve;\u416d\u0100iy\u37f5\u37farc\u803b\xfb\u40fb;\u4443\u0180abh\u3803\u3806\u380br\xf2\u13adlac;\u4171a\xf2\u13c3\u0100ir\u3813\u3818sht;\u697e;\uc000\ud835\udd32rave\u803b\xf9\u40f9\u0161\u3827\u3831r\u0100lr\u382c\u382e\xbb\u0957\xbb\u1083lk;\u6580\u0100ct\u3839\u384d\u026f\u383f\0\0\u384arn\u0100;e\u3845\u3846\u631cr\xbb\u3846op;\u630fri;\u65f8\u0100al\u3856\u385acr;\u416b\u80bb\xa8\u0349\u0100gp\u3862\u3866on;\u4173f;\uc000\ud835\udd66\u0300adhlsu\u114b\u3878\u387d\u1372\u3891\u38a0own\xe1\u13b3arpoon\u0100lr\u3888\u388cef\xf4\u382digh\xf4\u382fi\u0180;hl\u3899\u389a\u389c\u43c5\xbb\u13faon\xbb\u389aparrows;\u61c8\u0180cit\u38b0\u38c4\u38c8\u026f\u38b6\0\0\u38c1rn\u0100;e\u38bc\u38bd\u631dr\xbb\u38bdop;\u630eng;\u416fri;\u65f9cr;\uc000\ud835\udcca\u0180dir\u38d9\u38dd\u38e2ot;\u62f0lde;\u4169i\u0100;f\u3730\u38e8\xbb\u1813\u0100am\u38ef\u38f2r\xf2\u38a8l\u803b\xfc\u40fcangle;\u69a7\u0780ABDacdeflnoprsz\u391c\u391f\u3929\u392d\u39b5\u39b8\u39bd\u39df\u39e4\u39e8\u39f3\u39f9\u39fd\u3a01\u3a20r\xf2\u03f7ar\u0100;v\u3926\u3927\u6ae8;\u6ae9as\xe8\u03e1\u0100nr\u3932\u3937grt;\u699c\u0380eknprst\u34e3\u3946\u394b\u3952\u395d\u3964\u3996app\xe1\u2415othin\xe7\u1e96\u0180hir\u34eb\u2ec8\u3959op\xf4\u2fb5\u0100;h\u13b7\u3962\xef\u318d\u0100iu\u3969\u396dgm\xe1\u33b3\u0100bp\u3972\u3984setneq\u0100;q\u397d\u3980\uc000\u228a\ufe00;\uc000\u2acb\ufe00setneq\u0100;q\u398f\u3992\uc000\u228b\ufe00;\uc000\u2acc\ufe00\u0100hr\u399b\u399fet\xe1\u369ciangle\u0100lr\u39aa\u39afeft\xbb\u0925ight\xbb\u1051y;\u4432ash\xbb\u1036\u0180elr\u39c4\u39d2\u39d7\u0180;be\u2dea\u39cb\u39cfar;\u62bbq;\u625alip;\u62ee\u0100bt\u39dc\u1468a\xf2\u1469r;\uc000\ud835\udd33tr\xe9\u39aesu\u0100bp\u39ef\u39f1\xbb\u0d1c\xbb\u0d59pf;\uc000\ud835\udd67ro\xf0\u0efbtr\xe9\u39b4\u0100cu\u3a06\u3a0br;\uc000\ud835\udccb\u0100bp\u3a10\u3a18n\u0100Ee\u3980\u3a16\xbb\u397en\u0100Ee\u3992\u3a1e\xbb\u3990igzag;\u699a\u0380cefoprs\u3a36\u3a3b\u3a56\u3a5b\u3a54\u3a61\u3a6airc;\u4175\u0100di\u3a40\u3a51\u0100bg\u3a45\u3a49ar;\u6a5fe\u0100;q\u15fa\u3a4f;\u6259erp;\u6118r;\uc000\ud835\udd34pf;\uc000\ud835\udd68\u0100;e\u1479\u3a66at\xe8\u1479cr;\uc000\ud835\udccc\u0ae3\u178e\u3a87\0\u3a8b\0\u3a90\u3a9b\0\0\u3a9d\u3aa8\u3aab\u3aaf\0\0\u3ac3\u3ace\0\u3ad8\u17dc\u17dftr\xe9\u17d1r;\uc000\ud835\udd35\u0100Aa\u3a94\u3a97r\xf2\u03c3r\xf2\u09f6;\u43be\u0100Aa\u3aa1\u3aa4r\xf2\u03b8r\xf2\u09eba\xf0\u2713is;\u62fb\u0180dpt\u17a4\u3ab5\u3abe\u0100fl\u3aba\u17a9;\uc000\ud835\udd69im\xe5\u17b2\u0100Aa\u3ac7\u3acar\xf2\u03cer\xf2\u0a01\u0100cq\u3ad2\u17b8r;\uc000\ud835\udccd\u0100pt\u17d6\u3adcr\xe9\u17d4\u0400acefiosu\u3af0\u3afd\u3b08\u3b0c\u3b11\u3b15\u3b1b\u3b21c\u0100uy\u3af6\u3afbte\u803b\xfd\u40fd;\u444f\u0100iy\u3b02\u3b06rc;\u4177;\u444bn\u803b\xa5\u40a5r;\uc000\ud835\udd36cy;\u4457pf;\uc000\ud835\udd6acr;\uc000\ud835\udcce\u0100cm\u3b26\u3b29y;\u444el\u803b\xff\u40ff\u0500acdefhiosw\u3b42\u3b48\u3b54\u3b58\u3b64\u3b69\u3b6d\u3b74\u3b7a\u3b80cute;\u417a\u0100ay\u3b4d\u3b52ron;\u417e;\u4437ot;\u417c\u0100et\u3b5d\u3b61tr\xe6\u155fa;\u43b6r;\uc000\ud835\udd37cy;\u4436grarr;\u61ddpf;\uc000\ud835\udd6bcr;\uc000\ud835\udccf\u0100jn\u3b85\u3b87;\u600dj;\u600c"
	    .split("")
	    .map(function (c) { return c.charCodeAt(0); }));
	
	return decodeDataHtml;
}

var decodeDataXml = {};

var hasRequiredDecodeDataXml;

function requireDecodeDataXml () {
	if (hasRequiredDecodeDataXml) return decodeDataXml;
	hasRequiredDecodeDataXml = 1;
	// Generated using scripts/write-decode-map.ts
	Object.defineProperty(decodeDataXml, "__esModule", { value: true });
	decodeDataXml.default = new Uint16Array(
	// prettier-ignore
	"\u0200aglq\t\x15\x18\x1b\u026d\x0f\0\0\x12p;\u4026os;\u4027t;\u403et;\u403cuot;\u4022"
	    .split("")
	    .map(function (c) { return c.charCodeAt(0); }));
	
	return decodeDataXml;
}

var decode_codepoint = {};

var hasRequiredDecode_codepoint;

function requireDecode_codepoint () {
	if (hasRequiredDecode_codepoint) return decode_codepoint;
	hasRequiredDecode_codepoint = 1;
	(function (exports) {
		// Adapted from https://github.com/mathiasbynens/he/blob/36afe179392226cf1b6ccdb16ebbb7a5a844d93a/src/he.js#L106-L134
		var _a;
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.replaceCodePoint = exports.fromCodePoint = void 0;
		var decodeMap = new Map([
		    [0, 65533],
		    // C1 Unicode control character reference replacements
		    [128, 8364],
		    [130, 8218],
		    [131, 402],
		    [132, 8222],
		    [133, 8230],
		    [134, 8224],
		    [135, 8225],
		    [136, 710],
		    [137, 8240],
		    [138, 352],
		    [139, 8249],
		    [140, 338],
		    [142, 381],
		    [145, 8216],
		    [146, 8217],
		    [147, 8220],
		    [148, 8221],
		    [149, 8226],
		    [150, 8211],
		    [151, 8212],
		    [152, 732],
		    [153, 8482],
		    [154, 353],
		    [155, 8250],
		    [156, 339],
		    [158, 382],
		    [159, 376],
		]);
		/**
		 * Polyfill for `String.fromCodePoint`. It is used to create a string from a Unicode code point.
		 */
		exports.fromCodePoint = 
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, node/no-unsupported-features/es-builtins
		(_a = String.fromCodePoint) !== null && _a !== void 0 ? _a : function (codePoint) {
		    var output = "";
		    if (codePoint > 0xffff) {
		        codePoint -= 0x10000;
		        output += String.fromCharCode(((codePoint >>> 10) & 0x3ff) | 0xd800);
		        codePoint = 0xdc00 | (codePoint & 0x3ff);
		    }
		    output += String.fromCharCode(codePoint);
		    return output;
		};
		/**
		 * Replace the given code point with a replacement character if it is a
		 * surrogate or is outside the valid range. Otherwise return the code
		 * point unchanged.
		 */
		function replaceCodePoint(codePoint) {
		    var _a;
		    if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || codePoint > 0x10ffff) {
		        return 0xfffd;
		    }
		    return (_a = decodeMap.get(codePoint)) !== null && _a !== void 0 ? _a : codePoint;
		}
		exports.replaceCodePoint = replaceCodePoint;
		/**
		 * Replace the code point if relevant, then convert it to a string.
		 *
		 * @deprecated Use `fromCodePoint(replaceCodePoint(codePoint))` instead.
		 * @param codePoint The code point to decode.
		 * @returns The decoded code point.
		 */
		function decodeCodePoint(codePoint) {
		    return (0, exports.fromCodePoint)(replaceCodePoint(codePoint));
		}
		exports.default = decodeCodePoint;
		
	} (decode_codepoint));
	return decode_codepoint;
}

var hasRequiredDecode;

function requireDecode () {
	if (hasRequiredDecode) return decode;
	hasRequiredDecode = 1;
	(function (exports) {
		var __createBinding = (decode && decode.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (decode && decode.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (decode && decode.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		var __importDefault = (decode && decode.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.decodeXML = exports.decodeHTMLStrict = exports.decodeHTMLAttribute = exports.decodeHTML = exports.determineBranch = exports.EntityDecoder = exports.DecodingMode = exports.BinTrieFlags = exports.fromCodePoint = exports.replaceCodePoint = exports.decodeCodePoint = exports.xmlDecodeTree = exports.htmlDecodeTree = void 0;
		var decode_data_html_js_1 = __importDefault(/*@__PURE__*/ requireDecodeDataHtml());
		exports.htmlDecodeTree = decode_data_html_js_1.default;
		var decode_data_xml_js_1 = __importDefault(/*@__PURE__*/ requireDecodeDataXml());
		exports.xmlDecodeTree = decode_data_xml_js_1.default;
		var decode_codepoint_js_1 = __importStar(/*@__PURE__*/ requireDecode_codepoint());
		exports.decodeCodePoint = decode_codepoint_js_1.default;
		var decode_codepoint_js_2 = /*@__PURE__*/ requireDecode_codepoint();
		Object.defineProperty(exports, "replaceCodePoint", { enumerable: true, get: function () { return decode_codepoint_js_2.replaceCodePoint; } });
		Object.defineProperty(exports, "fromCodePoint", { enumerable: true, get: function () { return decode_codepoint_js_2.fromCodePoint; } });
		var CharCodes;
		(function (CharCodes) {
		    CharCodes[CharCodes["NUM"] = 35] = "NUM";
		    CharCodes[CharCodes["SEMI"] = 59] = "SEMI";
		    CharCodes[CharCodes["EQUALS"] = 61] = "EQUALS";
		    CharCodes[CharCodes["ZERO"] = 48] = "ZERO";
		    CharCodes[CharCodes["NINE"] = 57] = "NINE";
		    CharCodes[CharCodes["LOWER_A"] = 97] = "LOWER_A";
		    CharCodes[CharCodes["LOWER_F"] = 102] = "LOWER_F";
		    CharCodes[CharCodes["LOWER_X"] = 120] = "LOWER_X";
		    CharCodes[CharCodes["LOWER_Z"] = 122] = "LOWER_Z";
		    CharCodes[CharCodes["UPPER_A"] = 65] = "UPPER_A";
		    CharCodes[CharCodes["UPPER_F"] = 70] = "UPPER_F";
		    CharCodes[CharCodes["UPPER_Z"] = 90] = "UPPER_Z";
		})(CharCodes || (CharCodes = {}));
		/** Bit that needs to be set to convert an upper case ASCII character to lower case */
		var TO_LOWER_BIT = 32;
		var BinTrieFlags;
		(function (BinTrieFlags) {
		    BinTrieFlags[BinTrieFlags["VALUE_LENGTH"] = 49152] = "VALUE_LENGTH";
		    BinTrieFlags[BinTrieFlags["BRANCH_LENGTH"] = 16256] = "BRANCH_LENGTH";
		    BinTrieFlags[BinTrieFlags["JUMP_TABLE"] = 127] = "JUMP_TABLE";
		})(BinTrieFlags = exports.BinTrieFlags || (exports.BinTrieFlags = {}));
		function isNumber(code) {
		    return code >= CharCodes.ZERO && code <= CharCodes.NINE;
		}
		function isHexadecimalCharacter(code) {
		    return ((code >= CharCodes.UPPER_A && code <= CharCodes.UPPER_F) ||
		        (code >= CharCodes.LOWER_A && code <= CharCodes.LOWER_F));
		}
		function isAsciiAlphaNumeric(code) {
		    return ((code >= CharCodes.UPPER_A && code <= CharCodes.UPPER_Z) ||
		        (code >= CharCodes.LOWER_A && code <= CharCodes.LOWER_Z) ||
		        isNumber(code));
		}
		/**
		 * Checks if the given character is a valid end character for an entity in an attribute.
		 *
		 * Attribute values that aren't terminated properly aren't parsed, and shouldn't lead to a parser error.
		 * See the example in https://html.spec.whatwg.org/multipage/parsing.html#named-character-reference-state
		 */
		function isEntityInAttributeInvalidEnd(code) {
		    return code === CharCodes.EQUALS || isAsciiAlphaNumeric(code);
		}
		var EntityDecoderState;
		(function (EntityDecoderState) {
		    EntityDecoderState[EntityDecoderState["EntityStart"] = 0] = "EntityStart";
		    EntityDecoderState[EntityDecoderState["NumericStart"] = 1] = "NumericStart";
		    EntityDecoderState[EntityDecoderState["NumericDecimal"] = 2] = "NumericDecimal";
		    EntityDecoderState[EntityDecoderState["NumericHex"] = 3] = "NumericHex";
		    EntityDecoderState[EntityDecoderState["NamedEntity"] = 4] = "NamedEntity";
		})(EntityDecoderState || (EntityDecoderState = {}));
		var DecodingMode;
		(function (DecodingMode) {
		    /** Entities in text nodes that can end with any character. */
		    DecodingMode[DecodingMode["Legacy"] = 0] = "Legacy";
		    /** Only allow entities terminated with a semicolon. */
		    DecodingMode[DecodingMode["Strict"] = 1] = "Strict";
		    /** Entities in attributes have limitations on ending characters. */
		    DecodingMode[DecodingMode["Attribute"] = 2] = "Attribute";
		})(DecodingMode = exports.DecodingMode || (exports.DecodingMode = {}));
		/**
		 * Token decoder with support of writing partial entities.
		 */
		var EntityDecoder = /** @class */ (function () {
		    function EntityDecoder(
		    /** The tree used to decode entities. */
		    decodeTree, 
		    /**
		     * The function that is called when a codepoint is decoded.
		     *
		     * For multi-byte named entities, this will be called multiple times,
		     * with the second codepoint, and the same `consumed` value.
		     *
		     * @param codepoint The decoded codepoint.
		     * @param consumed The number of bytes consumed by the decoder.
		     */
		    emitCodePoint, 
		    /** An object that is used to produce errors. */
		    errors) {
		        this.decodeTree = decodeTree;
		        this.emitCodePoint = emitCodePoint;
		        this.errors = errors;
		        /** The current state of the decoder. */
		        this.state = EntityDecoderState.EntityStart;
		        /** Characters that were consumed while parsing an entity. */
		        this.consumed = 1;
		        /**
		         * The result of the entity.
		         *
		         * Either the result index of a numeric entity, or the codepoint of a
		         * numeric entity.
		         */
		        this.result = 0;
		        /** The current index in the decode tree. */
		        this.treeIndex = 0;
		        /** The number of characters that were consumed in excess. */
		        this.excess = 1;
		        /** The mode in which the decoder is operating. */
		        this.decodeMode = DecodingMode.Strict;
		    }
		    /** Resets the instance to make it reusable. */
		    EntityDecoder.prototype.startEntity = function (decodeMode) {
		        this.decodeMode = decodeMode;
		        this.state = EntityDecoderState.EntityStart;
		        this.result = 0;
		        this.treeIndex = 0;
		        this.excess = 1;
		        this.consumed = 1;
		    };
		    /**
		     * Write an entity to the decoder. This can be called multiple times with partial entities.
		     * If the entity is incomplete, the decoder will return -1.
		     *
		     * Mirrors the implementation of `getDecoder`, but with the ability to stop decoding if the
		     * entity is incomplete, and resume when the next string is written.
		     *
		     * @param string The string containing the entity (or a continuation of the entity).
		     * @param offset The offset at which the entity begins. Should be 0 if this is not the first call.
		     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
		     */
		    EntityDecoder.prototype.write = function (str, offset) {
		        switch (this.state) {
		            case EntityDecoderState.EntityStart: {
		                if (str.charCodeAt(offset) === CharCodes.NUM) {
		                    this.state = EntityDecoderState.NumericStart;
		                    this.consumed += 1;
		                    return this.stateNumericStart(str, offset + 1);
		                }
		                this.state = EntityDecoderState.NamedEntity;
		                return this.stateNamedEntity(str, offset);
		            }
		            case EntityDecoderState.NumericStart: {
		                return this.stateNumericStart(str, offset);
		            }
		            case EntityDecoderState.NumericDecimal: {
		                return this.stateNumericDecimal(str, offset);
		            }
		            case EntityDecoderState.NumericHex: {
		                return this.stateNumericHex(str, offset);
		            }
		            case EntityDecoderState.NamedEntity: {
		                return this.stateNamedEntity(str, offset);
		            }
		        }
		    };
		    /**
		     * Switches between the numeric decimal and hexadecimal states.
		     *
		     * Equivalent to the `Numeric character reference state` in the HTML spec.
		     *
		     * @param str The string containing the entity (or a continuation of the entity).
		     * @param offset The current offset.
		     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
		     */
		    EntityDecoder.prototype.stateNumericStart = function (str, offset) {
		        if (offset >= str.length) {
		            return -1;
		        }
		        if ((str.charCodeAt(offset) | TO_LOWER_BIT) === CharCodes.LOWER_X) {
		            this.state = EntityDecoderState.NumericHex;
		            this.consumed += 1;
		            return this.stateNumericHex(str, offset + 1);
		        }
		        this.state = EntityDecoderState.NumericDecimal;
		        return this.stateNumericDecimal(str, offset);
		    };
		    EntityDecoder.prototype.addToNumericResult = function (str, start, end, base) {
		        if (start !== end) {
		            var digitCount = end - start;
		            this.result =
		                this.result * Math.pow(base, digitCount) +
		                    parseInt(str.substr(start, digitCount), base);
		            this.consumed += digitCount;
		        }
		    };
		    /**
		     * Parses a hexadecimal numeric entity.
		     *
		     * Equivalent to the `Hexademical character reference state` in the HTML spec.
		     *
		     * @param str The string containing the entity (or a continuation of the entity).
		     * @param offset The current offset.
		     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
		     */
		    EntityDecoder.prototype.stateNumericHex = function (str, offset) {
		        var startIdx = offset;
		        while (offset < str.length) {
		            var char = str.charCodeAt(offset);
		            if (isNumber(char) || isHexadecimalCharacter(char)) {
		                offset += 1;
		            }
		            else {
		                this.addToNumericResult(str, startIdx, offset, 16);
		                return this.emitNumericEntity(char, 3);
		            }
		        }
		        this.addToNumericResult(str, startIdx, offset, 16);
		        return -1;
		    };
		    /**
		     * Parses a decimal numeric entity.
		     *
		     * Equivalent to the `Decimal character reference state` in the HTML spec.
		     *
		     * @param str The string containing the entity (or a continuation of the entity).
		     * @param offset The current offset.
		     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
		     */
		    EntityDecoder.prototype.stateNumericDecimal = function (str, offset) {
		        var startIdx = offset;
		        while (offset < str.length) {
		            var char = str.charCodeAt(offset);
		            if (isNumber(char)) {
		                offset += 1;
		            }
		            else {
		                this.addToNumericResult(str, startIdx, offset, 10);
		                return this.emitNumericEntity(char, 2);
		            }
		        }
		        this.addToNumericResult(str, startIdx, offset, 10);
		        return -1;
		    };
		    /**
		     * Validate and emit a numeric entity.
		     *
		     * Implements the logic from the `Hexademical character reference start
		     * state` and `Numeric character reference end state` in the HTML spec.
		     *
		     * @param lastCp The last code point of the entity. Used to see if the
		     *               entity was terminated with a semicolon.
		     * @param expectedLength The minimum number of characters that should be
		     *                       consumed. Used to validate that at least one digit
		     *                       was consumed.
		     * @returns The number of characters that were consumed.
		     */
		    EntityDecoder.prototype.emitNumericEntity = function (lastCp, expectedLength) {
		        var _a;
		        // Ensure we consumed at least one digit.
		        if (this.consumed <= expectedLength) {
		            (_a = this.errors) === null || _a === void 0 ? void 0 : _a.absenceOfDigitsInNumericCharacterReference(this.consumed);
		            return 0;
		        }
		        // Figure out if this is a legit end of the entity
		        if (lastCp === CharCodes.SEMI) {
		            this.consumed += 1;
		        }
		        else if (this.decodeMode === DecodingMode.Strict) {
		            return 0;
		        }
		        this.emitCodePoint((0, decode_codepoint_js_1.replaceCodePoint)(this.result), this.consumed);
		        if (this.errors) {
		            if (lastCp !== CharCodes.SEMI) {
		                this.errors.missingSemicolonAfterCharacterReference();
		            }
		            this.errors.validateNumericCharacterReference(this.result);
		        }
		        return this.consumed;
		    };
		    /**
		     * Parses a named entity.
		     *
		     * Equivalent to the `Named character reference state` in the HTML spec.
		     *
		     * @param str The string containing the entity (or a continuation of the entity).
		     * @param offset The current offset.
		     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
		     */
		    EntityDecoder.prototype.stateNamedEntity = function (str, offset) {
		        var decodeTree = this.decodeTree;
		        var current = decodeTree[this.treeIndex];
		        // The mask is the number of bytes of the value, including the current byte.
		        var valueLength = (current & BinTrieFlags.VALUE_LENGTH) >> 14;
		        for (; offset < str.length; offset++, this.excess++) {
		            var char = str.charCodeAt(offset);
		            this.treeIndex = determineBranch(decodeTree, current, this.treeIndex + Math.max(1, valueLength), char);
		            if (this.treeIndex < 0) {
		                return this.result === 0 ||
		                    // If we are parsing an attribute
		                    (this.decodeMode === DecodingMode.Attribute &&
		                        // We shouldn't have consumed any characters after the entity,
		                        (valueLength === 0 ||
		                            // And there should be no invalid characters.
		                            isEntityInAttributeInvalidEnd(char)))
		                    ? 0
		                    : this.emitNotTerminatedNamedEntity();
		            }
		            current = decodeTree[this.treeIndex];
		            valueLength = (current & BinTrieFlags.VALUE_LENGTH) >> 14;
		            // If the branch is a value, store it and continue
		            if (valueLength !== 0) {
		                // If the entity is terminated by a semicolon, we are done.
		                if (char === CharCodes.SEMI) {
		                    return this.emitNamedEntityData(this.treeIndex, valueLength, this.consumed + this.excess);
		                }
		                // If we encounter a non-terminated (legacy) entity while parsing strictly, then ignore it.
		                if (this.decodeMode !== DecodingMode.Strict) {
		                    this.result = this.treeIndex;
		                    this.consumed += this.excess;
		                    this.excess = 0;
		                }
		            }
		        }
		        return -1;
		    };
		    /**
		     * Emit a named entity that was not terminated with a semicolon.
		     *
		     * @returns The number of characters consumed.
		     */
		    EntityDecoder.prototype.emitNotTerminatedNamedEntity = function () {
		        var _a;
		        var _b = this, result = _b.result, decodeTree = _b.decodeTree;
		        var valueLength = (decodeTree[result] & BinTrieFlags.VALUE_LENGTH) >> 14;
		        this.emitNamedEntityData(result, valueLength, this.consumed);
		        (_a = this.errors) === null || _a === void 0 ? void 0 : _a.missingSemicolonAfterCharacterReference();
		        return this.consumed;
		    };
		    /**
		     * Emit a named entity.
		     *
		     * @param result The index of the entity in the decode tree.
		     * @param valueLength The number of bytes in the entity.
		     * @param consumed The number of characters consumed.
		     *
		     * @returns The number of characters consumed.
		     */
		    EntityDecoder.prototype.emitNamedEntityData = function (result, valueLength, consumed) {
		        var decodeTree = this.decodeTree;
		        this.emitCodePoint(valueLength === 1
		            ? decodeTree[result] & ~BinTrieFlags.VALUE_LENGTH
		            : decodeTree[result + 1], consumed);
		        if (valueLength === 3) {
		            // For multi-byte values, we need to emit the second byte.
		            this.emitCodePoint(decodeTree[result + 2], consumed);
		        }
		        return consumed;
		    };
		    /**
		     * Signal to the parser that the end of the input was reached.
		     *
		     * Remaining data will be emitted and relevant errors will be produced.
		     *
		     * @returns The number of characters consumed.
		     */
		    EntityDecoder.prototype.end = function () {
		        var _a;
		        switch (this.state) {
		            case EntityDecoderState.NamedEntity: {
		                // Emit a named entity if we have one.
		                return this.result !== 0 &&
		                    (this.decodeMode !== DecodingMode.Attribute ||
		                        this.result === this.treeIndex)
		                    ? this.emitNotTerminatedNamedEntity()
		                    : 0;
		            }
		            // Otherwise, emit a numeric entity if we have one.
		            case EntityDecoderState.NumericDecimal: {
		                return this.emitNumericEntity(0, 2);
		            }
		            case EntityDecoderState.NumericHex: {
		                return this.emitNumericEntity(0, 3);
		            }
		            case EntityDecoderState.NumericStart: {
		                (_a = this.errors) === null || _a === void 0 ? void 0 : _a.absenceOfDigitsInNumericCharacterReference(this.consumed);
		                return 0;
		            }
		            case EntityDecoderState.EntityStart: {
		                // Return 0 if we have no entity.
		                return 0;
		            }
		        }
		    };
		    return EntityDecoder;
		}());
		exports.EntityDecoder = EntityDecoder;
		/**
		 * Creates a function that decodes entities in a string.
		 *
		 * @param decodeTree The decode tree.
		 * @returns A function that decodes entities in a string.
		 */
		function getDecoder(decodeTree) {
		    var ret = "";
		    var decoder = new EntityDecoder(decodeTree, function (str) { return (ret += (0, decode_codepoint_js_1.fromCodePoint)(str)); });
		    return function decodeWithTrie(str, decodeMode) {
		        var lastIndex = 0;
		        var offset = 0;
		        while ((offset = str.indexOf("&", offset)) >= 0) {
		            ret += str.slice(lastIndex, offset);
		            decoder.startEntity(decodeMode);
		            var len = decoder.write(str, 
		            // Skip the "&"
		            offset + 1);
		            if (len < 0) {
		                lastIndex = offset + decoder.end();
		                break;
		            }
		            lastIndex = offset + len;
		            // If `len` is 0, skip the current `&` and continue.
		            offset = len === 0 ? lastIndex + 1 : lastIndex;
		        }
		        var result = ret + str.slice(lastIndex);
		        // Make sure we don't keep a reference to the final string.
		        ret = "";
		        return result;
		    };
		}
		/**
		 * Determines the branch of the current node that is taken given the current
		 * character. This function is used to traverse the trie.
		 *
		 * @param decodeTree The trie.
		 * @param current The current node.
		 * @param nodeIdx The index right after the current node and its value.
		 * @param char The current character.
		 * @returns The index of the next node, or -1 if no branch is taken.
		 */
		function determineBranch(decodeTree, current, nodeIdx, char) {
		    var branchCount = (current & BinTrieFlags.BRANCH_LENGTH) >> 7;
		    var jumpOffset = current & BinTrieFlags.JUMP_TABLE;
		    // Case 1: Single branch encoded in jump offset
		    if (branchCount === 0) {
		        return jumpOffset !== 0 && char === jumpOffset ? nodeIdx : -1;
		    }
		    // Case 2: Multiple branches encoded in jump table
		    if (jumpOffset) {
		        var value = char - jumpOffset;
		        return value < 0 || value >= branchCount
		            ? -1
		            : decodeTree[nodeIdx + value] - 1;
		    }
		    // Case 3: Multiple branches encoded in dictionary
		    // Binary search for the character.
		    var lo = nodeIdx;
		    var hi = lo + branchCount - 1;
		    while (lo <= hi) {
		        var mid = (lo + hi) >>> 1;
		        var midVal = decodeTree[mid];
		        if (midVal < char) {
		            lo = mid + 1;
		        }
		        else if (midVal > char) {
		            hi = mid - 1;
		        }
		        else {
		            return decodeTree[mid + branchCount];
		        }
		    }
		    return -1;
		}
		exports.determineBranch = determineBranch;
		var htmlDecoder = getDecoder(decode_data_html_js_1.default);
		var xmlDecoder = getDecoder(decode_data_xml_js_1.default);
		/**
		 * Decodes an HTML string.
		 *
		 * @param str The string to decode.
		 * @param mode The decoding mode.
		 * @returns The decoded string.
		 */
		function decodeHTML(str, mode) {
		    if (mode === void 0) { mode = DecodingMode.Legacy; }
		    return htmlDecoder(str, mode);
		}
		exports.decodeHTML = decodeHTML;
		/**
		 * Decodes an HTML string in an attribute.
		 *
		 * @param str The string to decode.
		 * @returns The decoded string.
		 */
		function decodeHTMLAttribute(str) {
		    return htmlDecoder(str, DecodingMode.Attribute);
		}
		exports.decodeHTMLAttribute = decodeHTMLAttribute;
		/**
		 * Decodes an HTML string, requiring all entities to be terminated by a semicolon.
		 *
		 * @param str The string to decode.
		 * @returns The decoded string.
		 */
		function decodeHTMLStrict(str) {
		    return htmlDecoder(str, DecodingMode.Strict);
		}
		exports.decodeHTMLStrict = decodeHTMLStrict;
		/**
		 * Decodes an XML string, requiring all entities to be terminated by a semicolon.
		 *
		 * @param str The string to decode.
		 * @returns The decoded string.
		 */
		function decodeXML(str) {
		    return xmlDecoder(str, DecodingMode.Strict);
		}
		exports.decodeXML = decodeXML;
		
	} (decode));
	return decode;
}

var hasRequiredTokenizer;

function requireTokenizer () {
	if (hasRequiredTokenizer) return Tokenizer;
	hasRequiredTokenizer = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.QuoteType = void 0;
		var decode_js_1 = /*@__PURE__*/ requireDecode();
		var CharCodes;
		(function (CharCodes) {
		    CharCodes[CharCodes["Tab"] = 9] = "Tab";
		    CharCodes[CharCodes["NewLine"] = 10] = "NewLine";
		    CharCodes[CharCodes["FormFeed"] = 12] = "FormFeed";
		    CharCodes[CharCodes["CarriageReturn"] = 13] = "CarriageReturn";
		    CharCodes[CharCodes["Space"] = 32] = "Space";
		    CharCodes[CharCodes["ExclamationMark"] = 33] = "ExclamationMark";
		    CharCodes[CharCodes["Number"] = 35] = "Number";
		    CharCodes[CharCodes["Amp"] = 38] = "Amp";
		    CharCodes[CharCodes["SingleQuote"] = 39] = "SingleQuote";
		    CharCodes[CharCodes["DoubleQuote"] = 34] = "DoubleQuote";
		    CharCodes[CharCodes["Dash"] = 45] = "Dash";
		    CharCodes[CharCodes["Slash"] = 47] = "Slash";
		    CharCodes[CharCodes["Zero"] = 48] = "Zero";
		    CharCodes[CharCodes["Nine"] = 57] = "Nine";
		    CharCodes[CharCodes["Semi"] = 59] = "Semi";
		    CharCodes[CharCodes["Lt"] = 60] = "Lt";
		    CharCodes[CharCodes["Eq"] = 61] = "Eq";
		    CharCodes[CharCodes["Gt"] = 62] = "Gt";
		    CharCodes[CharCodes["Questionmark"] = 63] = "Questionmark";
		    CharCodes[CharCodes["UpperA"] = 65] = "UpperA";
		    CharCodes[CharCodes["LowerA"] = 97] = "LowerA";
		    CharCodes[CharCodes["UpperF"] = 70] = "UpperF";
		    CharCodes[CharCodes["LowerF"] = 102] = "LowerF";
		    CharCodes[CharCodes["UpperZ"] = 90] = "UpperZ";
		    CharCodes[CharCodes["LowerZ"] = 122] = "LowerZ";
		    CharCodes[CharCodes["LowerX"] = 120] = "LowerX";
		    CharCodes[CharCodes["OpeningSquareBracket"] = 91] = "OpeningSquareBracket";
		})(CharCodes || (CharCodes = {}));
		/** All the states the tokenizer can be in. */
		var State;
		(function (State) {
		    State[State["Text"] = 1] = "Text";
		    State[State["BeforeTagName"] = 2] = "BeforeTagName";
		    State[State["InTagName"] = 3] = "InTagName";
		    State[State["InSelfClosingTag"] = 4] = "InSelfClosingTag";
		    State[State["BeforeClosingTagName"] = 5] = "BeforeClosingTagName";
		    State[State["InClosingTagName"] = 6] = "InClosingTagName";
		    State[State["AfterClosingTagName"] = 7] = "AfterClosingTagName";
		    // Attributes
		    State[State["BeforeAttributeName"] = 8] = "BeforeAttributeName";
		    State[State["InAttributeName"] = 9] = "InAttributeName";
		    State[State["AfterAttributeName"] = 10] = "AfterAttributeName";
		    State[State["BeforeAttributeValue"] = 11] = "BeforeAttributeValue";
		    State[State["InAttributeValueDq"] = 12] = "InAttributeValueDq";
		    State[State["InAttributeValueSq"] = 13] = "InAttributeValueSq";
		    State[State["InAttributeValueNq"] = 14] = "InAttributeValueNq";
		    // Declarations
		    State[State["BeforeDeclaration"] = 15] = "BeforeDeclaration";
		    State[State["InDeclaration"] = 16] = "InDeclaration";
		    // Processing instructions
		    State[State["InProcessingInstruction"] = 17] = "InProcessingInstruction";
		    // Comments & CDATA
		    State[State["BeforeComment"] = 18] = "BeforeComment";
		    State[State["CDATASequence"] = 19] = "CDATASequence";
		    State[State["InSpecialComment"] = 20] = "InSpecialComment";
		    State[State["InCommentLike"] = 21] = "InCommentLike";
		    // Special tags
		    State[State["BeforeSpecialS"] = 22] = "BeforeSpecialS";
		    State[State["SpecialStartSequence"] = 23] = "SpecialStartSequence";
		    State[State["InSpecialTag"] = 24] = "InSpecialTag";
		    State[State["BeforeEntity"] = 25] = "BeforeEntity";
		    State[State["BeforeNumericEntity"] = 26] = "BeforeNumericEntity";
		    State[State["InNamedEntity"] = 27] = "InNamedEntity";
		    State[State["InNumericEntity"] = 28] = "InNumericEntity";
		    State[State["InHexEntity"] = 29] = "InHexEntity";
		})(State || (State = {}));
		function isWhitespace(c) {
		    return (c === CharCodes.Space ||
		        c === CharCodes.NewLine ||
		        c === CharCodes.Tab ||
		        c === CharCodes.FormFeed ||
		        c === CharCodes.CarriageReturn);
		}
		function isEndOfTagSection(c) {
		    return c === CharCodes.Slash || c === CharCodes.Gt || isWhitespace(c);
		}
		function isNumber(c) {
		    return c >= CharCodes.Zero && c <= CharCodes.Nine;
		}
		function isASCIIAlpha(c) {
		    return ((c >= CharCodes.LowerA && c <= CharCodes.LowerZ) ||
		        (c >= CharCodes.UpperA && c <= CharCodes.UpperZ));
		}
		function isHexDigit(c) {
		    return ((c >= CharCodes.UpperA && c <= CharCodes.UpperF) ||
		        (c >= CharCodes.LowerA && c <= CharCodes.LowerF));
		}
		var QuoteType;
		(function (QuoteType) {
		    QuoteType[QuoteType["NoValue"] = 0] = "NoValue";
		    QuoteType[QuoteType["Unquoted"] = 1] = "Unquoted";
		    QuoteType[QuoteType["Single"] = 2] = "Single";
		    QuoteType[QuoteType["Double"] = 3] = "Double";
		})(QuoteType = exports.QuoteType || (exports.QuoteType = {}));
		/**
		 * Sequences used to match longer strings.
		 *
		 * We don't have `Script`, `Style`, or `Title` here. Instead, we re-use the *End
		 * sequences with an increased offset.
		 */
		var Sequences = {
		    Cdata: new Uint8Array([0x43, 0x44, 0x41, 0x54, 0x41, 0x5b]),
		    CdataEnd: new Uint8Array([0x5d, 0x5d, 0x3e]),
		    CommentEnd: new Uint8Array([0x2d, 0x2d, 0x3e]),
		    ScriptEnd: new Uint8Array([0x3c, 0x2f, 0x73, 0x63, 0x72, 0x69, 0x70, 0x74]),
		    StyleEnd: new Uint8Array([0x3c, 0x2f, 0x73, 0x74, 0x79, 0x6c, 0x65]),
		    TitleEnd: new Uint8Array([0x3c, 0x2f, 0x74, 0x69, 0x74, 0x6c, 0x65]), // `</title`
		};
		var Tokenizer = /** @class */ (function () {
		    function Tokenizer(_a, cbs) {
		        var _b = _a.xmlMode, xmlMode = _b === void 0 ? false : _b, _c = _a.decodeEntities, decodeEntities = _c === void 0 ? true : _c;
		        this.cbs = cbs;
		        /** The current state the tokenizer is in. */
		        this.state = State.Text;
		        /** The read buffer. */
		        this.buffer = "";
		        /** The beginning of the section that is currently being read. */
		        this.sectionStart = 0;
		        /** The index within the buffer that we are currently looking at. */
		        this.index = 0;
		        /** Some behavior, eg. when decoding entities, is done while we are in another state. This keeps track of the other state type. */
		        this.baseState = State.Text;
		        /** For special parsing behavior inside of script and style tags. */
		        this.isSpecial = false;
		        /** Indicates whether the tokenizer has been paused. */
		        this.running = true;
		        /** The offset of the current buffer. */
		        this.offset = 0;
		        this.currentSequence = undefined;
		        this.sequenceIndex = 0;
		        this.trieIndex = 0;
		        this.trieCurrent = 0;
		        /** For named entities, the index of the value. For numeric entities, the code point. */
		        this.entityResult = 0;
		        this.entityExcess = 0;
		        this.xmlMode = xmlMode;
		        this.decodeEntities = decodeEntities;
		        this.entityTrie = xmlMode ? decode_js_1.xmlDecodeTree : decode_js_1.htmlDecodeTree;
		    }
		    Tokenizer.prototype.reset = function () {
		        this.state = State.Text;
		        this.buffer = "";
		        this.sectionStart = 0;
		        this.index = 0;
		        this.baseState = State.Text;
		        this.currentSequence = undefined;
		        this.running = true;
		        this.offset = 0;
		    };
		    Tokenizer.prototype.write = function (chunk) {
		        this.offset += this.buffer.length;
		        this.buffer = chunk;
		        this.parse();
		    };
		    Tokenizer.prototype.end = function () {
		        if (this.running)
		            this.finish();
		    };
		    Tokenizer.prototype.pause = function () {
		        this.running = false;
		    };
		    Tokenizer.prototype.resume = function () {
		        this.running = true;
		        if (this.index < this.buffer.length + this.offset) {
		            this.parse();
		        }
		    };
		    /**
		     * The current index within all of the written data.
		     */
		    Tokenizer.prototype.getIndex = function () {
		        return this.index;
		    };
		    /**
		     * The start of the current section.
		     */
		    Tokenizer.prototype.getSectionStart = function () {
		        return this.sectionStart;
		    };
		    Tokenizer.prototype.stateText = function (c) {
		        if (c === CharCodes.Lt ||
		            (!this.decodeEntities && this.fastForwardTo(CharCodes.Lt))) {
		            if (this.index > this.sectionStart) {
		                this.cbs.ontext(this.sectionStart, this.index);
		            }
		            this.state = State.BeforeTagName;
		            this.sectionStart = this.index;
		        }
		        else if (this.decodeEntities && c === CharCodes.Amp) {
		            this.state = State.BeforeEntity;
		        }
		    };
		    Tokenizer.prototype.stateSpecialStartSequence = function (c) {
		        var isEnd = this.sequenceIndex === this.currentSequence.length;
		        var isMatch = isEnd
		            ? // If we are at the end of the sequence, make sure the tag name has ended
		                isEndOfTagSection(c)
		            : // Otherwise, do a case-insensitive comparison
		                (c | 0x20) === this.currentSequence[this.sequenceIndex];
		        if (!isMatch) {
		            this.isSpecial = false;
		        }
		        else if (!isEnd) {
		            this.sequenceIndex++;
		            return;
		        }
		        this.sequenceIndex = 0;
		        this.state = State.InTagName;
		        this.stateInTagName(c);
		    };
		    /** Look for an end tag. For <title> tags, also decode entities. */
		    Tokenizer.prototype.stateInSpecialTag = function (c) {
		        if (this.sequenceIndex === this.currentSequence.length) {
		            if (c === CharCodes.Gt || isWhitespace(c)) {
		                var endOfText = this.index - this.currentSequence.length;
		                if (this.sectionStart < endOfText) {
		                    // Spoof the index so that reported locations match up.
		                    var actualIndex = this.index;
		                    this.index = endOfText;
		                    this.cbs.ontext(this.sectionStart, endOfText);
		                    this.index = actualIndex;
		                }
		                this.isSpecial = false;
		                this.sectionStart = endOfText + 2; // Skip over the `</`
		                this.stateInClosingTagName(c);
		                return; // We are done; skip the rest of the function.
		            }
		            this.sequenceIndex = 0;
		        }
		        if ((c | 0x20) === this.currentSequence[this.sequenceIndex]) {
		            this.sequenceIndex += 1;
		        }
		        else if (this.sequenceIndex === 0) {
		            if (this.currentSequence === Sequences.TitleEnd) {
		                // We have to parse entities in <title> tags.
		                if (this.decodeEntities && c === CharCodes.Amp) {
		                    this.state = State.BeforeEntity;
		                }
		            }
		            else if (this.fastForwardTo(CharCodes.Lt)) {
		                // Outside of <title> tags, we can fast-forward.
		                this.sequenceIndex = 1;
		            }
		        }
		        else {
		            // If we see a `<`, set the sequence index to 1; useful for eg. `<</script>`.
		            this.sequenceIndex = Number(c === CharCodes.Lt);
		        }
		    };
		    Tokenizer.prototype.stateCDATASequence = function (c) {
		        if (c === Sequences.Cdata[this.sequenceIndex]) {
		            if (++this.sequenceIndex === Sequences.Cdata.length) {
		                this.state = State.InCommentLike;
		                this.currentSequence = Sequences.CdataEnd;
		                this.sequenceIndex = 0;
		                this.sectionStart = this.index + 1;
		            }
		        }
		        else {
		            this.sequenceIndex = 0;
		            this.state = State.InDeclaration;
		            this.stateInDeclaration(c); // Reconsume the character
		        }
		    };
		    /**
		     * When we wait for one specific character, we can speed things up
		     * by skipping through the buffer until we find it.
		     *
		     * @returns Whether the character was found.
		     */
		    Tokenizer.prototype.fastForwardTo = function (c) {
		        while (++this.index < this.buffer.length + this.offset) {
		            if (this.buffer.charCodeAt(this.index - this.offset) === c) {
		                return true;
		            }
		        }
		        /*
		         * We increment the index at the end of the `parse` loop,
		         * so set it to `buffer.length - 1` here.
		         *
		         * TODO: Refactor `parse` to increment index before calling states.
		         */
		        this.index = this.buffer.length + this.offset - 1;
		        return false;
		    };
		    /**
		     * Comments and CDATA end with `-->` and `]]>`.
		     *
		     * Their common qualities are:
		     * - Their end sequences have a distinct character they start with.
		     * - That character is then repeated, so we have to check multiple repeats.
		     * - All characters but the start character of the sequence can be skipped.
		     */
		    Tokenizer.prototype.stateInCommentLike = function (c) {
		        if (c === this.currentSequence[this.sequenceIndex]) {
		            if (++this.sequenceIndex === this.currentSequence.length) {
		                if (this.currentSequence === Sequences.CdataEnd) {
		                    this.cbs.oncdata(this.sectionStart, this.index, 2);
		                }
		                else {
		                    this.cbs.oncomment(this.sectionStart, this.index, 2);
		                }
		                this.sequenceIndex = 0;
		                this.sectionStart = this.index + 1;
		                this.state = State.Text;
		            }
		        }
		        else if (this.sequenceIndex === 0) {
		            // Fast-forward to the first character of the sequence
		            if (this.fastForwardTo(this.currentSequence[0])) {
		                this.sequenceIndex = 1;
		            }
		        }
		        else if (c !== this.currentSequence[this.sequenceIndex - 1]) {
		            // Allow long sequences, eg. --->, ]]]>
		            this.sequenceIndex = 0;
		        }
		    };
		    /**
		     * HTML only allows ASCII alpha characters (a-z and A-Z) at the beginning of a tag name.
		     *
		     * XML allows a lot more characters here (@see https://www.w3.org/TR/REC-xml/#NT-NameStartChar).
		     * We allow anything that wouldn't end the tag.
		     */
		    Tokenizer.prototype.isTagStartChar = function (c) {
		        return this.xmlMode ? !isEndOfTagSection(c) : isASCIIAlpha(c);
		    };
		    Tokenizer.prototype.startSpecial = function (sequence, offset) {
		        this.isSpecial = true;
		        this.currentSequence = sequence;
		        this.sequenceIndex = offset;
		        this.state = State.SpecialStartSequence;
		    };
		    Tokenizer.prototype.stateBeforeTagName = function (c) {
		        if (c === CharCodes.ExclamationMark) {
		            this.state = State.BeforeDeclaration;
		            this.sectionStart = this.index + 1;
		        }
		        else if (c === CharCodes.Questionmark) {
		            this.state = State.InProcessingInstruction;
		            this.sectionStart = this.index + 1;
		        }
		        else if (this.isTagStartChar(c)) {
		            var lower = c | 0x20;
		            this.sectionStart = this.index;
		            if (!this.xmlMode && lower === Sequences.TitleEnd[2]) {
		                this.startSpecial(Sequences.TitleEnd, 3);
		            }
		            else {
		                this.state =
		                    !this.xmlMode && lower === Sequences.ScriptEnd[2]
		                        ? State.BeforeSpecialS
		                        : State.InTagName;
		            }
		        }
		        else if (c === CharCodes.Slash) {
		            this.state = State.BeforeClosingTagName;
		        }
		        else {
		            this.state = State.Text;
		            this.stateText(c);
		        }
		    };
		    Tokenizer.prototype.stateInTagName = function (c) {
		        if (isEndOfTagSection(c)) {
		            this.cbs.onopentagname(this.sectionStart, this.index);
		            this.sectionStart = -1;
		            this.state = State.BeforeAttributeName;
		            this.stateBeforeAttributeName(c);
		        }
		    };
		    Tokenizer.prototype.stateBeforeClosingTagName = function (c) {
		        if (isWhitespace(c)) ;
		        else if (c === CharCodes.Gt) {
		            this.state = State.Text;
		        }
		        else {
		            this.state = this.isTagStartChar(c)
		                ? State.InClosingTagName
		                : State.InSpecialComment;
		            this.sectionStart = this.index;
		        }
		    };
		    Tokenizer.prototype.stateInClosingTagName = function (c) {
		        if (c === CharCodes.Gt || isWhitespace(c)) {
		            this.cbs.onclosetag(this.sectionStart, this.index);
		            this.sectionStart = -1;
		            this.state = State.AfterClosingTagName;
		            this.stateAfterClosingTagName(c);
		        }
		    };
		    Tokenizer.prototype.stateAfterClosingTagName = function (c) {
		        // Skip everything until ">"
		        if (c === CharCodes.Gt || this.fastForwardTo(CharCodes.Gt)) {
		            this.state = State.Text;
		            this.baseState = State.Text;
		            this.sectionStart = this.index + 1;
		        }
		    };
		    Tokenizer.prototype.stateBeforeAttributeName = function (c) {
		        if (c === CharCodes.Gt) {
		            this.cbs.onopentagend(this.index);
		            if (this.isSpecial) {
		                this.state = State.InSpecialTag;
		                this.sequenceIndex = 0;
		            }
		            else {
		                this.state = State.Text;
		            }
		            this.baseState = this.state;
		            this.sectionStart = this.index + 1;
		        }
		        else if (c === CharCodes.Slash) {
		            this.state = State.InSelfClosingTag;
		        }
		        else if (!isWhitespace(c)) {
		            this.state = State.InAttributeName;
		            this.sectionStart = this.index;
		        }
		    };
		    Tokenizer.prototype.stateInSelfClosingTag = function (c) {
		        if (c === CharCodes.Gt) {
		            this.cbs.onselfclosingtag(this.index);
		            this.state = State.Text;
		            this.baseState = State.Text;
		            this.sectionStart = this.index + 1;
		            this.isSpecial = false; // Reset special state, in case of self-closing special tags
		        }
		        else if (!isWhitespace(c)) {
		            this.state = State.BeforeAttributeName;
		            this.stateBeforeAttributeName(c);
		        }
		    };
		    Tokenizer.prototype.stateInAttributeName = function (c) {
		        if (c === CharCodes.Eq || isEndOfTagSection(c)) {
		            this.cbs.onattribname(this.sectionStart, this.index);
		            this.sectionStart = -1;
		            this.state = State.AfterAttributeName;
		            this.stateAfterAttributeName(c);
		        }
		    };
		    Tokenizer.prototype.stateAfterAttributeName = function (c) {
		        if (c === CharCodes.Eq) {
		            this.state = State.BeforeAttributeValue;
		        }
		        else if (c === CharCodes.Slash || c === CharCodes.Gt) {
		            this.cbs.onattribend(QuoteType.NoValue, this.index);
		            this.state = State.BeforeAttributeName;
		            this.stateBeforeAttributeName(c);
		        }
		        else if (!isWhitespace(c)) {
		            this.cbs.onattribend(QuoteType.NoValue, this.index);
		            this.state = State.InAttributeName;
		            this.sectionStart = this.index;
		        }
		    };
		    Tokenizer.prototype.stateBeforeAttributeValue = function (c) {
		        if (c === CharCodes.DoubleQuote) {
		            this.state = State.InAttributeValueDq;
		            this.sectionStart = this.index + 1;
		        }
		        else if (c === CharCodes.SingleQuote) {
		            this.state = State.InAttributeValueSq;
		            this.sectionStart = this.index + 1;
		        }
		        else if (!isWhitespace(c)) {
		            this.sectionStart = this.index;
		            this.state = State.InAttributeValueNq;
		            this.stateInAttributeValueNoQuotes(c); // Reconsume token
		        }
		    };
		    Tokenizer.prototype.handleInAttributeValue = function (c, quote) {
		        if (c === quote ||
		            (!this.decodeEntities && this.fastForwardTo(quote))) {
		            this.cbs.onattribdata(this.sectionStart, this.index);
		            this.sectionStart = -1;
		            this.cbs.onattribend(quote === CharCodes.DoubleQuote
		                ? QuoteType.Double
		                : QuoteType.Single, this.index);
		            this.state = State.BeforeAttributeName;
		        }
		        else if (this.decodeEntities && c === CharCodes.Amp) {
		            this.baseState = this.state;
		            this.state = State.BeforeEntity;
		        }
		    };
		    Tokenizer.prototype.stateInAttributeValueDoubleQuotes = function (c) {
		        this.handleInAttributeValue(c, CharCodes.DoubleQuote);
		    };
		    Tokenizer.prototype.stateInAttributeValueSingleQuotes = function (c) {
		        this.handleInAttributeValue(c, CharCodes.SingleQuote);
		    };
		    Tokenizer.prototype.stateInAttributeValueNoQuotes = function (c) {
		        if (isWhitespace(c) || c === CharCodes.Gt) {
		            this.cbs.onattribdata(this.sectionStart, this.index);
		            this.sectionStart = -1;
		            this.cbs.onattribend(QuoteType.Unquoted, this.index);
		            this.state = State.BeforeAttributeName;
		            this.stateBeforeAttributeName(c);
		        }
		        else if (this.decodeEntities && c === CharCodes.Amp) {
		            this.baseState = this.state;
		            this.state = State.BeforeEntity;
		        }
		    };
		    Tokenizer.prototype.stateBeforeDeclaration = function (c) {
		        if (c === CharCodes.OpeningSquareBracket) {
		            this.state = State.CDATASequence;
		            this.sequenceIndex = 0;
		        }
		        else {
		            this.state =
		                c === CharCodes.Dash
		                    ? State.BeforeComment
		                    : State.InDeclaration;
		        }
		    };
		    Tokenizer.prototype.stateInDeclaration = function (c) {
		        if (c === CharCodes.Gt || this.fastForwardTo(CharCodes.Gt)) {
		            this.cbs.ondeclaration(this.sectionStart, this.index);
		            this.state = State.Text;
		            this.sectionStart = this.index + 1;
		        }
		    };
		    Tokenizer.prototype.stateInProcessingInstruction = function (c) {
		        if (c === CharCodes.Gt || this.fastForwardTo(CharCodes.Gt)) {
		            this.cbs.onprocessinginstruction(this.sectionStart, this.index);
		            this.state = State.Text;
		            this.sectionStart = this.index + 1;
		        }
		    };
		    Tokenizer.prototype.stateBeforeComment = function (c) {
		        if (c === CharCodes.Dash) {
		            this.state = State.InCommentLike;
		            this.currentSequence = Sequences.CommentEnd;
		            // Allow short comments (eg. <!-->)
		            this.sequenceIndex = 2;
		            this.sectionStart = this.index + 1;
		        }
		        else {
		            this.state = State.InDeclaration;
		        }
		    };
		    Tokenizer.prototype.stateInSpecialComment = function (c) {
		        if (c === CharCodes.Gt || this.fastForwardTo(CharCodes.Gt)) {
		            this.cbs.oncomment(this.sectionStart, this.index, 0);
		            this.state = State.Text;
		            this.sectionStart = this.index + 1;
		        }
		    };
		    Tokenizer.prototype.stateBeforeSpecialS = function (c) {
		        var lower = c | 0x20;
		        if (lower === Sequences.ScriptEnd[3]) {
		            this.startSpecial(Sequences.ScriptEnd, 4);
		        }
		        else if (lower === Sequences.StyleEnd[3]) {
		            this.startSpecial(Sequences.StyleEnd, 4);
		        }
		        else {
		            this.state = State.InTagName;
		            this.stateInTagName(c); // Consume the token again
		        }
		    };
		    Tokenizer.prototype.stateBeforeEntity = function (c) {
		        // Start excess with 1 to include the '&'
		        this.entityExcess = 1;
		        this.entityResult = 0;
		        if (c === CharCodes.Number) {
		            this.state = State.BeforeNumericEntity;
		        }
		        else if (c === CharCodes.Amp) ;
		        else {
		            this.trieIndex = 0;
		            this.trieCurrent = this.entityTrie[0];
		            this.state = State.InNamedEntity;
		            this.stateInNamedEntity(c);
		        }
		    };
		    Tokenizer.prototype.stateInNamedEntity = function (c) {
		        this.entityExcess += 1;
		        this.trieIndex = (0, decode_js_1.determineBranch)(this.entityTrie, this.trieCurrent, this.trieIndex + 1, c);
		        if (this.trieIndex < 0) {
		            this.emitNamedEntity();
		            this.index--;
		            return;
		        }
		        this.trieCurrent = this.entityTrie[this.trieIndex];
		        var masked = this.trieCurrent & decode_js_1.BinTrieFlags.VALUE_LENGTH;
		        // If the branch is a value, store it and continue
		        if (masked) {
		            // The mask is the number of bytes of the value, including the current byte.
		            var valueLength = (masked >> 14) - 1;
		            // If we have a legacy entity while parsing strictly, just skip the number of bytes
		            if (!this.allowLegacyEntity() && c !== CharCodes.Semi) {
		                this.trieIndex += valueLength;
		            }
		            else {
		                // Add 1 as we have already incremented the excess
		                var entityStart = this.index - this.entityExcess + 1;
		                if (entityStart > this.sectionStart) {
		                    this.emitPartial(this.sectionStart, entityStart);
		                }
		                // If this is a surrogate pair, consume the next two bytes
		                this.entityResult = this.trieIndex;
		                this.trieIndex += valueLength;
		                this.entityExcess = 0;
		                this.sectionStart = this.index + 1;
		                if (valueLength === 0) {
		                    this.emitNamedEntity();
		                }
		            }
		        }
		    };
		    Tokenizer.prototype.emitNamedEntity = function () {
		        this.state = this.baseState;
		        if (this.entityResult === 0) {
		            return;
		        }
		        var valueLength = (this.entityTrie[this.entityResult] & decode_js_1.BinTrieFlags.VALUE_LENGTH) >>
		            14;
		        switch (valueLength) {
		            case 1: {
		                this.emitCodePoint(this.entityTrie[this.entityResult] &
		                    ~decode_js_1.BinTrieFlags.VALUE_LENGTH);
		                break;
		            }
		            case 2: {
		                this.emitCodePoint(this.entityTrie[this.entityResult + 1]);
		                break;
		            }
		            case 3: {
		                this.emitCodePoint(this.entityTrie[this.entityResult + 1]);
		                this.emitCodePoint(this.entityTrie[this.entityResult + 2]);
		            }
		        }
		    };
		    Tokenizer.prototype.stateBeforeNumericEntity = function (c) {
		        if ((c | 0x20) === CharCodes.LowerX) {
		            this.entityExcess++;
		            this.state = State.InHexEntity;
		        }
		        else {
		            this.state = State.InNumericEntity;
		            this.stateInNumericEntity(c);
		        }
		    };
		    Tokenizer.prototype.emitNumericEntity = function (strict) {
		        var entityStart = this.index - this.entityExcess - 1;
		        var numberStart = entityStart + 2 + Number(this.state === State.InHexEntity);
		        if (numberStart !== this.index) {
		            // Emit leading data if any
		            if (entityStart > this.sectionStart) {
		                this.emitPartial(this.sectionStart, entityStart);
		            }
		            this.sectionStart = this.index + Number(strict);
		            this.emitCodePoint((0, decode_js_1.replaceCodePoint)(this.entityResult));
		        }
		        this.state = this.baseState;
		    };
		    Tokenizer.prototype.stateInNumericEntity = function (c) {
		        if (c === CharCodes.Semi) {
		            this.emitNumericEntity(true);
		        }
		        else if (isNumber(c)) {
		            this.entityResult = this.entityResult * 10 + (c - CharCodes.Zero);
		            this.entityExcess++;
		        }
		        else {
		            if (this.allowLegacyEntity()) {
		                this.emitNumericEntity(false);
		            }
		            else {
		                this.state = this.baseState;
		            }
		            this.index--;
		        }
		    };
		    Tokenizer.prototype.stateInHexEntity = function (c) {
		        if (c === CharCodes.Semi) {
		            this.emitNumericEntity(true);
		        }
		        else if (isNumber(c)) {
		            this.entityResult = this.entityResult * 16 + (c - CharCodes.Zero);
		            this.entityExcess++;
		        }
		        else if (isHexDigit(c)) {
		            this.entityResult =
		                this.entityResult * 16 + ((c | 0x20) - CharCodes.LowerA + 10);
		            this.entityExcess++;
		        }
		        else {
		            if (this.allowLegacyEntity()) {
		                this.emitNumericEntity(false);
		            }
		            else {
		                this.state = this.baseState;
		            }
		            this.index--;
		        }
		    };
		    Tokenizer.prototype.allowLegacyEntity = function () {
		        return (!this.xmlMode &&
		            (this.baseState === State.Text ||
		                this.baseState === State.InSpecialTag));
		    };
		    /**
		     * Remove data that has already been consumed from the buffer.
		     */
		    Tokenizer.prototype.cleanup = function () {
		        // If we are inside of text or attributes, emit what we already have.
		        if (this.running && this.sectionStart !== this.index) {
		            if (this.state === State.Text ||
		                (this.state === State.InSpecialTag && this.sequenceIndex === 0)) {
		                this.cbs.ontext(this.sectionStart, this.index);
		                this.sectionStart = this.index;
		            }
		            else if (this.state === State.InAttributeValueDq ||
		                this.state === State.InAttributeValueSq ||
		                this.state === State.InAttributeValueNq) {
		                this.cbs.onattribdata(this.sectionStart, this.index);
		                this.sectionStart = this.index;
		            }
		        }
		    };
		    Tokenizer.prototype.shouldContinue = function () {
		        return this.index < this.buffer.length + this.offset && this.running;
		    };
		    /**
		     * Iterates through the buffer, calling the function corresponding to the current state.
		     *
		     * States that are more likely to be hit are higher up, as a performance improvement.
		     */
		    Tokenizer.prototype.parse = function () {
		        while (this.shouldContinue()) {
		            var c = this.buffer.charCodeAt(this.index - this.offset);
		            switch (this.state) {
		                case State.Text: {
		                    this.stateText(c);
		                    break;
		                }
		                case State.SpecialStartSequence: {
		                    this.stateSpecialStartSequence(c);
		                    break;
		                }
		                case State.InSpecialTag: {
		                    this.stateInSpecialTag(c);
		                    break;
		                }
		                case State.CDATASequence: {
		                    this.stateCDATASequence(c);
		                    break;
		                }
		                case State.InAttributeValueDq: {
		                    this.stateInAttributeValueDoubleQuotes(c);
		                    break;
		                }
		                case State.InAttributeName: {
		                    this.stateInAttributeName(c);
		                    break;
		                }
		                case State.InCommentLike: {
		                    this.stateInCommentLike(c);
		                    break;
		                }
		                case State.InSpecialComment: {
		                    this.stateInSpecialComment(c);
		                    break;
		                }
		                case State.BeforeAttributeName: {
		                    this.stateBeforeAttributeName(c);
		                    break;
		                }
		                case State.InTagName: {
		                    this.stateInTagName(c);
		                    break;
		                }
		                case State.InClosingTagName: {
		                    this.stateInClosingTagName(c);
		                    break;
		                }
		                case State.BeforeTagName: {
		                    this.stateBeforeTagName(c);
		                    break;
		                }
		                case State.AfterAttributeName: {
		                    this.stateAfterAttributeName(c);
		                    break;
		                }
		                case State.InAttributeValueSq: {
		                    this.stateInAttributeValueSingleQuotes(c);
		                    break;
		                }
		                case State.BeforeAttributeValue: {
		                    this.stateBeforeAttributeValue(c);
		                    break;
		                }
		                case State.BeforeClosingTagName: {
		                    this.stateBeforeClosingTagName(c);
		                    break;
		                }
		                case State.AfterClosingTagName: {
		                    this.stateAfterClosingTagName(c);
		                    break;
		                }
		                case State.BeforeSpecialS: {
		                    this.stateBeforeSpecialS(c);
		                    break;
		                }
		                case State.InAttributeValueNq: {
		                    this.stateInAttributeValueNoQuotes(c);
		                    break;
		                }
		                case State.InSelfClosingTag: {
		                    this.stateInSelfClosingTag(c);
		                    break;
		                }
		                case State.InDeclaration: {
		                    this.stateInDeclaration(c);
		                    break;
		                }
		                case State.BeforeDeclaration: {
		                    this.stateBeforeDeclaration(c);
		                    break;
		                }
		                case State.BeforeComment: {
		                    this.stateBeforeComment(c);
		                    break;
		                }
		                case State.InProcessingInstruction: {
		                    this.stateInProcessingInstruction(c);
		                    break;
		                }
		                case State.InNamedEntity: {
		                    this.stateInNamedEntity(c);
		                    break;
		                }
		                case State.BeforeEntity: {
		                    this.stateBeforeEntity(c);
		                    break;
		                }
		                case State.InHexEntity: {
		                    this.stateInHexEntity(c);
		                    break;
		                }
		                case State.InNumericEntity: {
		                    this.stateInNumericEntity(c);
		                    break;
		                }
		                default: {
		                    // `this._state === State.BeforeNumericEntity`
		                    this.stateBeforeNumericEntity(c);
		                }
		            }
		            this.index++;
		        }
		        this.cleanup();
		    };
		    Tokenizer.prototype.finish = function () {
		        if (this.state === State.InNamedEntity) {
		            this.emitNamedEntity();
		        }
		        // If there is remaining data, emit it in a reasonable way
		        if (this.sectionStart < this.index) {
		            this.handleTrailingData();
		        }
		        this.cbs.onend();
		    };
		    /** Handle any trailing data. */
		    Tokenizer.prototype.handleTrailingData = function () {
		        var endIndex = this.buffer.length + this.offset;
		        if (this.state === State.InCommentLike) {
		            if (this.currentSequence === Sequences.CdataEnd) {
		                this.cbs.oncdata(this.sectionStart, endIndex, 0);
		            }
		            else {
		                this.cbs.oncomment(this.sectionStart, endIndex, 0);
		            }
		        }
		        else if (this.state === State.InNumericEntity &&
		            this.allowLegacyEntity()) {
		            this.emitNumericEntity(false);
		            // All trailing data will have been consumed
		        }
		        else if (this.state === State.InHexEntity &&
		            this.allowLegacyEntity()) {
		            this.emitNumericEntity(false);
		            // All trailing data will have been consumed
		        }
		        else if (this.state === State.InTagName ||
		            this.state === State.BeforeAttributeName ||
		            this.state === State.BeforeAttributeValue ||
		            this.state === State.AfterAttributeName ||
		            this.state === State.InAttributeName ||
		            this.state === State.InAttributeValueSq ||
		            this.state === State.InAttributeValueDq ||
		            this.state === State.InAttributeValueNq ||
		            this.state === State.InClosingTagName) ;
		        else {
		            this.cbs.ontext(this.sectionStart, endIndex);
		        }
		    };
		    Tokenizer.prototype.emitPartial = function (start, endIndex) {
		        if (this.baseState !== State.Text &&
		            this.baseState !== State.InSpecialTag) {
		            this.cbs.onattribdata(start, endIndex);
		        }
		        else {
		            this.cbs.ontext(start, endIndex);
		        }
		    };
		    Tokenizer.prototype.emitCodePoint = function (cp) {
		        if (this.baseState !== State.Text &&
		            this.baseState !== State.InSpecialTag) {
		            this.cbs.onattribentity(cp);
		        }
		        else {
		            this.cbs.ontextentity(cp);
		        }
		    };
		    return Tokenizer;
		}());
		exports.default = Tokenizer;
		
	} (Tokenizer));
	return Tokenizer;
}

var hasRequiredParser$1;

function requireParser$1 () {
	if (hasRequiredParser$1) return Parser;
	hasRequiredParser$1 = 1;
	var __createBinding = (Parser && Parser.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (Parser && Parser.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (Parser && Parser.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(Parser, "__esModule", { value: true });
	Parser.Parser = void 0;
	var Tokenizer_js_1 = __importStar(/*@__PURE__*/ requireTokenizer());
	var decode_js_1 = /*@__PURE__*/ requireDecode();
	var formTags = new Set([
	    "input",
	    "option",
	    "optgroup",
	    "select",
	    "button",
	    "datalist",
	    "textarea",
	]);
	var pTag = new Set(["p"]);
	var tableSectionTags = new Set(["thead", "tbody"]);
	var ddtTags = new Set(["dd", "dt"]);
	var rtpTags = new Set(["rt", "rp"]);
	var openImpliesClose = new Map([
	    ["tr", new Set(["tr", "th", "td"])],
	    ["th", new Set(["th"])],
	    ["td", new Set(["thead", "th", "td"])],
	    ["body", new Set(["head", "link", "script"])],
	    ["li", new Set(["li"])],
	    ["p", pTag],
	    ["h1", pTag],
	    ["h2", pTag],
	    ["h3", pTag],
	    ["h4", pTag],
	    ["h5", pTag],
	    ["h6", pTag],
	    ["select", formTags],
	    ["input", formTags],
	    ["output", formTags],
	    ["button", formTags],
	    ["datalist", formTags],
	    ["textarea", formTags],
	    ["option", new Set(["option"])],
	    ["optgroup", new Set(["optgroup", "option"])],
	    ["dd", ddtTags],
	    ["dt", ddtTags],
	    ["address", pTag],
	    ["article", pTag],
	    ["aside", pTag],
	    ["blockquote", pTag],
	    ["details", pTag],
	    ["div", pTag],
	    ["dl", pTag],
	    ["fieldset", pTag],
	    ["figcaption", pTag],
	    ["figure", pTag],
	    ["footer", pTag],
	    ["form", pTag],
	    ["header", pTag],
	    ["hr", pTag],
	    ["main", pTag],
	    ["nav", pTag],
	    ["ol", pTag],
	    ["pre", pTag],
	    ["section", pTag],
	    ["table", pTag],
	    ["ul", pTag],
	    ["rt", rtpTags],
	    ["rp", rtpTags],
	    ["tbody", tableSectionTags],
	    ["tfoot", tableSectionTags],
	]);
	var voidElements = new Set([
	    "area",
	    "base",
	    "basefont",
	    "br",
	    "col",
	    "command",
	    "embed",
	    "frame",
	    "hr",
	    "img",
	    "input",
	    "isindex",
	    "keygen",
	    "link",
	    "meta",
	    "param",
	    "source",
	    "track",
	    "wbr",
	]);
	var foreignContextElements = new Set(["math", "svg"]);
	var htmlIntegrationElements = new Set([
	    "mi",
	    "mo",
	    "mn",
	    "ms",
	    "mtext",
	    "annotation-xml",
	    "foreignobject",
	    "desc",
	    "title",
	]);
	var reNameEnd = /\s|\//;
	var Parser$1 = /** @class */ (function () {
	    function Parser(cbs, options) {
	        if (options === void 0) { options = {}; }
	        var _a, _b, _c, _d, _e;
	        this.options = options;
	        /** The start index of the last event. */
	        this.startIndex = 0;
	        /** The end index of the last event. */
	        this.endIndex = 0;
	        /**
	         * Store the start index of the current open tag,
	         * so we can update the start index for attributes.
	         */
	        this.openTagStart = 0;
	        this.tagname = "";
	        this.attribname = "";
	        this.attribvalue = "";
	        this.attribs = null;
	        this.stack = [];
	        this.foreignContext = [];
	        this.buffers = [];
	        this.bufferOffset = 0;
	        /** The index of the last written buffer. Used when resuming after a `pause()`. */
	        this.writeIndex = 0;
	        /** Indicates whether the parser has finished running / `.end` has been called. */
	        this.ended = false;
	        this.cbs = cbs !== null && cbs !== void 0 ? cbs : {};
	        this.lowerCaseTagNames = (_a = options.lowerCaseTags) !== null && _a !== void 0 ? _a : !options.xmlMode;
	        this.lowerCaseAttributeNames =
	            (_b = options.lowerCaseAttributeNames) !== null && _b !== void 0 ? _b : !options.xmlMode;
	        this.tokenizer = new ((_c = options.Tokenizer) !== null && _c !== void 0 ? _c : Tokenizer_js_1.default)(this.options, this);
	        (_e = (_d = this.cbs).onparserinit) === null || _e === void 0 ? void 0 : _e.call(_d, this);
	    }
	    // Tokenizer event handlers
	    /** @internal */
	    Parser.prototype.ontext = function (start, endIndex) {
	        var _a, _b;
	        var data = this.getSlice(start, endIndex);
	        this.endIndex = endIndex - 1;
	        (_b = (_a = this.cbs).ontext) === null || _b === void 0 ? void 0 : _b.call(_a, data);
	        this.startIndex = endIndex;
	    };
	    /** @internal */
	    Parser.prototype.ontextentity = function (cp) {
	        var _a, _b;
	        /*
	         * Entities can be emitted on the character, or directly after.
	         * We use the section start here to get accurate indices.
	         */
	        var index = this.tokenizer.getSectionStart();
	        this.endIndex = index - 1;
	        (_b = (_a = this.cbs).ontext) === null || _b === void 0 ? void 0 : _b.call(_a, (0, decode_js_1.fromCodePoint)(cp));
	        this.startIndex = index;
	    };
	    Parser.prototype.isVoidElement = function (name) {
	        return !this.options.xmlMode && voidElements.has(name);
	    };
	    /** @internal */
	    Parser.prototype.onopentagname = function (start, endIndex) {
	        this.endIndex = endIndex;
	        var name = this.getSlice(start, endIndex);
	        if (this.lowerCaseTagNames) {
	            name = name.toLowerCase();
	        }
	        this.emitOpenTag(name);
	    };
	    Parser.prototype.emitOpenTag = function (name) {
	        var _a, _b, _c, _d;
	        this.openTagStart = this.startIndex;
	        this.tagname = name;
	        var impliesClose = !this.options.xmlMode && openImpliesClose.get(name);
	        if (impliesClose) {
	            while (this.stack.length > 0 &&
	                impliesClose.has(this.stack[this.stack.length - 1])) {
	                var element = this.stack.pop();
	                (_b = (_a = this.cbs).onclosetag) === null || _b === void 0 ? void 0 : _b.call(_a, element, true);
	            }
	        }
	        if (!this.isVoidElement(name)) {
	            this.stack.push(name);
	            if (foreignContextElements.has(name)) {
	                this.foreignContext.push(true);
	            }
	            else if (htmlIntegrationElements.has(name)) {
	                this.foreignContext.push(false);
	            }
	        }
	        (_d = (_c = this.cbs).onopentagname) === null || _d === void 0 ? void 0 : _d.call(_c, name);
	        if (this.cbs.onopentag)
	            this.attribs = {};
	    };
	    Parser.prototype.endOpenTag = function (isImplied) {
	        var _a, _b;
	        this.startIndex = this.openTagStart;
	        if (this.attribs) {
	            (_b = (_a = this.cbs).onopentag) === null || _b === void 0 ? void 0 : _b.call(_a, this.tagname, this.attribs, isImplied);
	            this.attribs = null;
	        }
	        if (this.cbs.onclosetag && this.isVoidElement(this.tagname)) {
	            this.cbs.onclosetag(this.tagname, true);
	        }
	        this.tagname = "";
	    };
	    /** @internal */
	    Parser.prototype.onopentagend = function (endIndex) {
	        this.endIndex = endIndex;
	        this.endOpenTag(false);
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.onclosetag = function (start, endIndex) {
	        var _a, _b, _c, _d, _e, _f;
	        this.endIndex = endIndex;
	        var name = this.getSlice(start, endIndex);
	        if (this.lowerCaseTagNames) {
	            name = name.toLowerCase();
	        }
	        if (foreignContextElements.has(name) ||
	            htmlIntegrationElements.has(name)) {
	            this.foreignContext.pop();
	        }
	        if (!this.isVoidElement(name)) {
	            var pos = this.stack.lastIndexOf(name);
	            if (pos !== -1) {
	                if (this.cbs.onclosetag) {
	                    var count = this.stack.length - pos;
	                    while (count--) {
	                        // We know the stack has sufficient elements.
	                        this.cbs.onclosetag(this.stack.pop(), count !== 0);
	                    }
	                }
	                else
	                    this.stack.length = pos;
	            }
	            else if (!this.options.xmlMode && name === "p") {
	                // Implicit open before close
	                this.emitOpenTag("p");
	                this.closeCurrentTag(true);
	            }
	        }
	        else if (!this.options.xmlMode && name === "br") {
	            // We can't use `emitOpenTag` for implicit open, as `br` would be implicitly closed.
	            (_b = (_a = this.cbs).onopentagname) === null || _b === void 0 ? void 0 : _b.call(_a, "br");
	            (_d = (_c = this.cbs).onopentag) === null || _d === void 0 ? void 0 : _d.call(_c, "br", {}, true);
	            (_f = (_e = this.cbs).onclosetag) === null || _f === void 0 ? void 0 : _f.call(_e, "br", false);
	        }
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.onselfclosingtag = function (endIndex) {
	        this.endIndex = endIndex;
	        if (this.options.xmlMode ||
	            this.options.recognizeSelfClosing ||
	            this.foreignContext[this.foreignContext.length - 1]) {
	            this.closeCurrentTag(false);
	            // Set `startIndex` for next node
	            this.startIndex = endIndex + 1;
	        }
	        else {
	            // Ignore the fact that the tag is self-closing.
	            this.onopentagend(endIndex);
	        }
	    };
	    Parser.prototype.closeCurrentTag = function (isOpenImplied) {
	        var _a, _b;
	        var name = this.tagname;
	        this.endOpenTag(isOpenImplied);
	        // Self-closing tags will be on the top of the stack
	        if (this.stack[this.stack.length - 1] === name) {
	            // If the opening tag isn't implied, the closing tag has to be implied.
	            (_b = (_a = this.cbs).onclosetag) === null || _b === void 0 ? void 0 : _b.call(_a, name, !isOpenImplied);
	            this.stack.pop();
	        }
	    };
	    /** @internal */
	    Parser.prototype.onattribname = function (start, endIndex) {
	        this.startIndex = start;
	        var name = this.getSlice(start, endIndex);
	        this.attribname = this.lowerCaseAttributeNames
	            ? name.toLowerCase()
	            : name;
	    };
	    /** @internal */
	    Parser.prototype.onattribdata = function (start, endIndex) {
	        this.attribvalue += this.getSlice(start, endIndex);
	    };
	    /** @internal */
	    Parser.prototype.onattribentity = function (cp) {
	        this.attribvalue += (0, decode_js_1.fromCodePoint)(cp);
	    };
	    /** @internal */
	    Parser.prototype.onattribend = function (quote, endIndex) {
	        var _a, _b;
	        this.endIndex = endIndex;
	        (_b = (_a = this.cbs).onattribute) === null || _b === void 0 ? void 0 : _b.call(_a, this.attribname, this.attribvalue, quote === Tokenizer_js_1.QuoteType.Double
	            ? '"'
	            : quote === Tokenizer_js_1.QuoteType.Single
	                ? "'"
	                : quote === Tokenizer_js_1.QuoteType.NoValue
	                    ? undefined
	                    : null);
	        if (this.attribs &&
	            !Object.prototype.hasOwnProperty.call(this.attribs, this.attribname)) {
	            this.attribs[this.attribname] = this.attribvalue;
	        }
	        this.attribvalue = "";
	    };
	    Parser.prototype.getInstructionName = function (value) {
	        var index = value.search(reNameEnd);
	        var name = index < 0 ? value : value.substr(0, index);
	        if (this.lowerCaseTagNames) {
	            name = name.toLowerCase();
	        }
	        return name;
	    };
	    /** @internal */
	    Parser.prototype.ondeclaration = function (start, endIndex) {
	        this.endIndex = endIndex;
	        var value = this.getSlice(start, endIndex);
	        if (this.cbs.onprocessinginstruction) {
	            var name = this.getInstructionName(value);
	            this.cbs.onprocessinginstruction("!".concat(name), "!".concat(value));
	        }
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.onprocessinginstruction = function (start, endIndex) {
	        this.endIndex = endIndex;
	        var value = this.getSlice(start, endIndex);
	        if (this.cbs.onprocessinginstruction) {
	            var name = this.getInstructionName(value);
	            this.cbs.onprocessinginstruction("?".concat(name), "?".concat(value));
	        }
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.oncomment = function (start, endIndex, offset) {
	        var _a, _b, _c, _d;
	        this.endIndex = endIndex;
	        (_b = (_a = this.cbs).oncomment) === null || _b === void 0 ? void 0 : _b.call(_a, this.getSlice(start, endIndex - offset));
	        (_d = (_c = this.cbs).oncommentend) === null || _d === void 0 ? void 0 : _d.call(_c);
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.oncdata = function (start, endIndex, offset) {
	        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
	        this.endIndex = endIndex;
	        var value = this.getSlice(start, endIndex - offset);
	        if (this.options.xmlMode || this.options.recognizeCDATA) {
	            (_b = (_a = this.cbs).oncdatastart) === null || _b === void 0 ? void 0 : _b.call(_a);
	            (_d = (_c = this.cbs).ontext) === null || _d === void 0 ? void 0 : _d.call(_c, value);
	            (_f = (_e = this.cbs).oncdataend) === null || _f === void 0 ? void 0 : _f.call(_e);
	        }
	        else {
	            (_h = (_g = this.cbs).oncomment) === null || _h === void 0 ? void 0 : _h.call(_g, "[CDATA[".concat(value, "]]"));
	            (_k = (_j = this.cbs).oncommentend) === null || _k === void 0 ? void 0 : _k.call(_j);
	        }
	        // Set `startIndex` for next node
	        this.startIndex = endIndex + 1;
	    };
	    /** @internal */
	    Parser.prototype.onend = function () {
	        var _a, _b;
	        if (this.cbs.onclosetag) {
	            // Set the end index for all remaining tags
	            this.endIndex = this.startIndex;
	            for (var index = this.stack.length; index > 0; this.cbs.onclosetag(this.stack[--index], true))
	                ;
	        }
	        (_b = (_a = this.cbs).onend) === null || _b === void 0 ? void 0 : _b.call(_a);
	    };
	    /**
	     * Resets the parser to a blank state, ready to parse a new HTML document
	     */
	    Parser.prototype.reset = function () {
	        var _a, _b, _c, _d;
	        (_b = (_a = this.cbs).onreset) === null || _b === void 0 ? void 0 : _b.call(_a);
	        this.tokenizer.reset();
	        this.tagname = "";
	        this.attribname = "";
	        this.attribs = null;
	        this.stack.length = 0;
	        this.startIndex = 0;
	        this.endIndex = 0;
	        (_d = (_c = this.cbs).onparserinit) === null || _d === void 0 ? void 0 : _d.call(_c, this);
	        this.buffers.length = 0;
	        this.bufferOffset = 0;
	        this.writeIndex = 0;
	        this.ended = false;
	    };
	    /**
	     * Resets the parser, then parses a complete document and
	     * pushes it to the handler.
	     *
	     * @param data Document to parse.
	     */
	    Parser.prototype.parseComplete = function (data) {
	        this.reset();
	        this.end(data);
	    };
	    Parser.prototype.getSlice = function (start, end) {
	        while (start - this.bufferOffset >= this.buffers[0].length) {
	            this.shiftBuffer();
	        }
	        var slice = this.buffers[0].slice(start - this.bufferOffset, end - this.bufferOffset);
	        while (end - this.bufferOffset > this.buffers[0].length) {
	            this.shiftBuffer();
	            slice += this.buffers[0].slice(0, end - this.bufferOffset);
	        }
	        return slice;
	    };
	    Parser.prototype.shiftBuffer = function () {
	        this.bufferOffset += this.buffers[0].length;
	        this.writeIndex--;
	        this.buffers.shift();
	    };
	    /**
	     * Parses a chunk of data and calls the corresponding callbacks.
	     *
	     * @param chunk Chunk to parse.
	     */
	    Parser.prototype.write = function (chunk) {
	        var _a, _b;
	        if (this.ended) {
	            (_b = (_a = this.cbs).onerror) === null || _b === void 0 ? void 0 : _b.call(_a, new Error(".write() after done!"));
	            return;
	        }
	        this.buffers.push(chunk);
	        if (this.tokenizer.running) {
	            this.tokenizer.write(chunk);
	            this.writeIndex++;
	        }
	    };
	    /**
	     * Parses the end of the buffer and clears the stack, calls onend.
	     *
	     * @param chunk Optional final chunk to parse.
	     */
	    Parser.prototype.end = function (chunk) {
	        var _a, _b;
	        if (this.ended) {
	            (_b = (_a = this.cbs).onerror) === null || _b === void 0 ? void 0 : _b.call(_a, new Error(".end() after done!"));
	            return;
	        }
	        if (chunk)
	            this.write(chunk);
	        this.ended = true;
	        this.tokenizer.end();
	    };
	    /**
	     * Pauses parsing. The parser won't emit events until `resume` is called.
	     */
	    Parser.prototype.pause = function () {
	        this.tokenizer.pause();
	    };
	    /**
	     * Resumes parsing after `pause` was called.
	     */
	    Parser.prototype.resume = function () {
	        this.tokenizer.resume();
	        while (this.tokenizer.running &&
	            this.writeIndex < this.buffers.length) {
	            this.tokenizer.write(this.buffers[this.writeIndex++]);
	        }
	        if (this.ended)
	            this.tokenizer.end();
	    };
	    /**
	     * Alias of `write`, for backwards compatibility.
	     *
	     * @param chunk Chunk to parse.
	     * @deprecated
	     */
	    Parser.prototype.parseChunk = function (chunk) {
	        this.write(chunk);
	    };
	    /**
	     * Alias of `end`, for backwards compatibility.
	     *
	     * @param chunk Optional final chunk to parse.
	     * @deprecated
	     */
	    Parser.prototype.done = function (chunk) {
	        this.end(chunk);
	    };
	    return Parser;
	}());
	Parser.Parser = Parser$1;
	
	return Parser;
}

var lib$4 = {};

var lib$3 = {};

var hasRequiredLib$5;

function requireLib$5 () {
	if (hasRequiredLib$5) return lib$3;
	hasRequiredLib$5 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.Doctype = exports.CDATA = exports.Tag = exports.Style = exports.Script = exports.Comment = exports.Directive = exports.Text = exports.Root = exports.isTag = exports.ElementType = void 0;
		/** Types of elements found in htmlparser2's DOM */
		var ElementType;
		(function (ElementType) {
		    /** Type for the root element of a document */
		    ElementType["Root"] = "root";
		    /** Type for Text */
		    ElementType["Text"] = "text";
		    /** Type for <? ... ?> */
		    ElementType["Directive"] = "directive";
		    /** Type for <!-- ... --> */
		    ElementType["Comment"] = "comment";
		    /** Type for <script> tags */
		    ElementType["Script"] = "script";
		    /** Type for <style> tags */
		    ElementType["Style"] = "style";
		    /** Type for Any tag */
		    ElementType["Tag"] = "tag";
		    /** Type for <![CDATA[ ... ]]> */
		    ElementType["CDATA"] = "cdata";
		    /** Type for <!doctype ...> */
		    ElementType["Doctype"] = "doctype";
		})(ElementType = exports.ElementType || (exports.ElementType = {}));
		/**
		 * Tests whether an element is a tag or not.
		 *
		 * @param elem Element to test
		 */
		function isTag(elem) {
		    return (elem.type === ElementType.Tag ||
		        elem.type === ElementType.Script ||
		        elem.type === ElementType.Style);
		}
		exports.isTag = isTag;
		// Exports for backwards compatibility
		/** Type for the root element of a document */
		exports.Root = ElementType.Root;
		/** Type for Text */
		exports.Text = ElementType.Text;
		/** Type for <? ... ?> */
		exports.Directive = ElementType.Directive;
		/** Type for <!-- ... --> */
		exports.Comment = ElementType.Comment;
		/** Type for <script> tags */
		exports.Script = ElementType.Script;
		/** Type for <style> tags */
		exports.Style = ElementType.Style;
		/** Type for Any tag */
		exports.Tag = ElementType.Tag;
		/** Type for <![CDATA[ ... ]]> */
		exports.CDATA = ElementType.CDATA;
		/** Type for <!doctype ...> */
		exports.Doctype = ElementType.Doctype; 
	} (lib$3));
	return lib$3;
}

var node$1 = {};

var hasRequiredNode$1;

function requireNode$1 () {
	if (hasRequiredNode$1) return node$1;
	hasRequiredNode$1 = 1;
	var __extends = (node$1 && node$1.__extends) || (function () {
	    var extendStatics = function (d, b) {
	        extendStatics = Object.setPrototypeOf ||
	            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
	            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
	        return extendStatics(d, b);
	    };
	    return function (d, b) {
	        if (typeof b !== "function" && b !== null)
	            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
	        extendStatics(d, b);
	        function __() { this.constructor = d; }
	        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
	    };
	})();
	var __assign = (node$1 && node$1.__assign) || function () {
	    __assign = Object.assign || function(t) {
	        for (var s, i = 1, n = arguments.length; i < n; i++) {
	            s = arguments[i];
	            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
	                t[p] = s[p];
	        }
	        return t;
	    };
	    return __assign.apply(this, arguments);
	};
	Object.defineProperty(node$1, "__esModule", { value: true });
	node$1.cloneNode = node$1.hasChildren = node$1.isDocument = node$1.isDirective = node$1.isComment = node$1.isText = node$1.isCDATA = node$1.isTag = node$1.Element = node$1.Document = node$1.CDATA = node$1.NodeWithChildren = node$1.ProcessingInstruction = node$1.Comment = node$1.Text = node$1.DataNode = node$1.Node = void 0;
	var domelementtype_1 = /*@__PURE__*/ requireLib$5();
	/**
	 * This object will be used as the prototype for Nodes when creating a
	 * DOM-Level-1-compliant structure.
	 */
	var Node = /** @class */ (function () {
	    function Node() {
	        /** Parent of the node */
	        this.parent = null;
	        /** Previous sibling */
	        this.prev = null;
	        /** Next sibling */
	        this.next = null;
	        /** The start index of the node. Requires `withStartIndices` on the handler to be `true. */
	        this.startIndex = null;
	        /** The end index of the node. Requires `withEndIndices` on the handler to be `true. */
	        this.endIndex = null;
	    }
	    Object.defineProperty(Node.prototype, "parentNode", {
	        // Read-write aliases for properties
	        /**
	         * Same as {@link parent}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.parent;
	        },
	        set: function (parent) {
	            this.parent = parent;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(Node.prototype, "previousSibling", {
	        /**
	         * Same as {@link prev}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.prev;
	        },
	        set: function (prev) {
	            this.prev = prev;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(Node.prototype, "nextSibling", {
	        /**
	         * Same as {@link next}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.next;
	        },
	        set: function (next) {
	            this.next = next;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    /**
	     * Clone this node, and optionally its children.
	     *
	     * @param recursive Clone child nodes as well.
	     * @returns A clone of the node.
	     */
	    Node.prototype.cloneNode = function (recursive) {
	        if (recursive === void 0) { recursive = false; }
	        return cloneNode(this, recursive);
	    };
	    return Node;
	}());
	node$1.Node = Node;
	/**
	 * A node that contains some data.
	 */
	var DataNode = /** @class */ (function (_super) {
	    __extends(DataNode, _super);
	    /**
	     * @param data The content of the data node
	     */
	    function DataNode(data) {
	        var _this = _super.call(this) || this;
	        _this.data = data;
	        return _this;
	    }
	    Object.defineProperty(DataNode.prototype, "nodeValue", {
	        /**
	         * Same as {@link data}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.data;
	        },
	        set: function (data) {
	            this.data = data;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return DataNode;
	}(Node));
	node$1.DataNode = DataNode;
	/**
	 * Text within the document.
	 */
	var Text = /** @class */ (function (_super) {
	    __extends(Text, _super);
	    function Text() {
	        var _this = _super !== null && _super.apply(this, arguments) || this;
	        _this.type = domelementtype_1.ElementType.Text;
	        return _this;
	    }
	    Object.defineProperty(Text.prototype, "nodeType", {
	        get: function () {
	            return 3;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return Text;
	}(DataNode));
	node$1.Text = Text;
	/**
	 * Comments within the document.
	 */
	var Comment = /** @class */ (function (_super) {
	    __extends(Comment, _super);
	    function Comment() {
	        var _this = _super !== null && _super.apply(this, arguments) || this;
	        _this.type = domelementtype_1.ElementType.Comment;
	        return _this;
	    }
	    Object.defineProperty(Comment.prototype, "nodeType", {
	        get: function () {
	            return 8;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return Comment;
	}(DataNode));
	node$1.Comment = Comment;
	/**
	 * Processing instructions, including doc types.
	 */
	var ProcessingInstruction = /** @class */ (function (_super) {
	    __extends(ProcessingInstruction, _super);
	    function ProcessingInstruction(name, data) {
	        var _this = _super.call(this, data) || this;
	        _this.name = name;
	        _this.type = domelementtype_1.ElementType.Directive;
	        return _this;
	    }
	    Object.defineProperty(ProcessingInstruction.prototype, "nodeType", {
	        get: function () {
	            return 1;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return ProcessingInstruction;
	}(DataNode));
	node$1.ProcessingInstruction = ProcessingInstruction;
	/**
	 * A `Node` that can have children.
	 */
	var NodeWithChildren = /** @class */ (function (_super) {
	    __extends(NodeWithChildren, _super);
	    /**
	     * @param children Children of the node. Only certain node types can have children.
	     */
	    function NodeWithChildren(children) {
	        var _this = _super.call(this) || this;
	        _this.children = children;
	        return _this;
	    }
	    Object.defineProperty(NodeWithChildren.prototype, "firstChild", {
	        // Aliases
	        /** First child of the node. */
	        get: function () {
	            var _a;
	            return (_a = this.children[0]) !== null && _a !== void 0 ? _a : null;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(NodeWithChildren.prototype, "lastChild", {
	        /** Last child of the node. */
	        get: function () {
	            return this.children.length > 0
	                ? this.children[this.children.length - 1]
	                : null;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(NodeWithChildren.prototype, "childNodes", {
	        /**
	         * Same as {@link children}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.children;
	        },
	        set: function (children) {
	            this.children = children;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return NodeWithChildren;
	}(Node));
	node$1.NodeWithChildren = NodeWithChildren;
	var CDATA = /** @class */ (function (_super) {
	    __extends(CDATA, _super);
	    function CDATA() {
	        var _this = _super !== null && _super.apply(this, arguments) || this;
	        _this.type = domelementtype_1.ElementType.CDATA;
	        return _this;
	    }
	    Object.defineProperty(CDATA.prototype, "nodeType", {
	        get: function () {
	            return 4;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return CDATA;
	}(NodeWithChildren));
	node$1.CDATA = CDATA;
	/**
	 * The root node of the document.
	 */
	var Document = /** @class */ (function (_super) {
	    __extends(Document, _super);
	    function Document() {
	        var _this = _super !== null && _super.apply(this, arguments) || this;
	        _this.type = domelementtype_1.ElementType.Root;
	        return _this;
	    }
	    Object.defineProperty(Document.prototype, "nodeType", {
	        get: function () {
	            return 9;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return Document;
	}(NodeWithChildren));
	node$1.Document = Document;
	/**
	 * An element within the DOM.
	 */
	var Element = /** @class */ (function (_super) {
	    __extends(Element, _super);
	    /**
	     * @param name Name of the tag, eg. `div`, `span`.
	     * @param attribs Object mapping attribute names to attribute values.
	     * @param children Children of the node.
	     */
	    function Element(name, attribs, children, type) {
	        if (children === void 0) { children = []; }
	        if (type === void 0) { type = name === "script"
	            ? domelementtype_1.ElementType.Script
	            : name === "style"
	                ? domelementtype_1.ElementType.Style
	                : domelementtype_1.ElementType.Tag; }
	        var _this = _super.call(this, children) || this;
	        _this.name = name;
	        _this.attribs = attribs;
	        _this.type = type;
	        return _this;
	    }
	    Object.defineProperty(Element.prototype, "nodeType", {
	        get: function () {
	            return 1;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(Element.prototype, "tagName", {
	        // DOM Level 1 aliases
	        /**
	         * Same as {@link name}.
	         * [DOM spec](https://dom.spec.whatwg.org)-compatible alias.
	         */
	        get: function () {
	            return this.name;
	        },
	        set: function (name) {
	            this.name = name;
	        },
	        enumerable: false,
	        configurable: true
	    });
	    Object.defineProperty(Element.prototype, "attributes", {
	        get: function () {
	            var _this = this;
	            return Object.keys(this.attribs).map(function (name) {
	                var _a, _b;
	                return ({
	                    name: name,
	                    value: _this.attribs[name],
	                    namespace: (_a = _this["x-attribsNamespace"]) === null || _a === void 0 ? void 0 : _a[name],
	                    prefix: (_b = _this["x-attribsPrefix"]) === null || _b === void 0 ? void 0 : _b[name],
	                });
	            });
	        },
	        enumerable: false,
	        configurable: true
	    });
	    return Element;
	}(NodeWithChildren));
	node$1.Element = Element;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node is a `Element`, `false` otherwise.
	 */
	function isTag(node) {
	    return (0, domelementtype_1.isTag)(node);
	}
	node$1.isTag = isTag;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has the type `CDATA`, `false` otherwise.
	 */
	function isCDATA(node) {
	    return node.type === domelementtype_1.ElementType.CDATA;
	}
	node$1.isCDATA = isCDATA;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has the type `Text`, `false` otherwise.
	 */
	function isText(node) {
	    return node.type === domelementtype_1.ElementType.Text;
	}
	node$1.isText = isText;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has the type `Comment`, `false` otherwise.
	 */
	function isComment(node) {
	    return node.type === domelementtype_1.ElementType.Comment;
	}
	node$1.isComment = isComment;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has the type `ProcessingInstruction`, `false` otherwise.
	 */
	function isDirective(node) {
	    return node.type === domelementtype_1.ElementType.Directive;
	}
	node$1.isDirective = isDirective;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has the type `ProcessingInstruction`, `false` otherwise.
	 */
	function isDocument(node) {
	    return node.type === domelementtype_1.ElementType.Root;
	}
	node$1.isDocument = isDocument;
	/**
	 * @param node Node to check.
	 * @returns `true` if the node has children, `false` otherwise.
	 */
	function hasChildren(node) {
	    return Object.prototype.hasOwnProperty.call(node, "children");
	}
	node$1.hasChildren = hasChildren;
	/**
	 * Clone a node, and optionally its children.
	 *
	 * @param recursive Clone child nodes as well.
	 * @returns A clone of the node.
	 */
	function cloneNode(node, recursive) {
	    if (recursive === void 0) { recursive = false; }
	    var result;
	    if (isText(node)) {
	        result = new Text(node.data);
	    }
	    else if (isComment(node)) {
	        result = new Comment(node.data);
	    }
	    else if (isTag(node)) {
	        var children = recursive ? cloneChildren(node.children) : [];
	        var clone_1 = new Element(node.name, __assign({}, node.attribs), children);
	        children.forEach(function (child) { return (child.parent = clone_1); });
	        if (node.namespace != null) {
	            clone_1.namespace = node.namespace;
	        }
	        if (node["x-attribsNamespace"]) {
	            clone_1["x-attribsNamespace"] = __assign({}, node["x-attribsNamespace"]);
	        }
	        if (node["x-attribsPrefix"]) {
	            clone_1["x-attribsPrefix"] = __assign({}, node["x-attribsPrefix"]);
	        }
	        result = clone_1;
	    }
	    else if (isCDATA(node)) {
	        var children = recursive ? cloneChildren(node.children) : [];
	        var clone_2 = new CDATA(children);
	        children.forEach(function (child) { return (child.parent = clone_2); });
	        result = clone_2;
	    }
	    else if (isDocument(node)) {
	        var children = recursive ? cloneChildren(node.children) : [];
	        var clone_3 = new Document(children);
	        children.forEach(function (child) { return (child.parent = clone_3); });
	        if (node["x-mode"]) {
	            clone_3["x-mode"] = node["x-mode"];
	        }
	        result = clone_3;
	    }
	    else if (isDirective(node)) {
	        var instruction = new ProcessingInstruction(node.name, node.data);
	        if (node["x-name"] != null) {
	            instruction["x-name"] = node["x-name"];
	            instruction["x-publicId"] = node["x-publicId"];
	            instruction["x-systemId"] = node["x-systemId"];
	        }
	        result = instruction;
	    }
	    else {
	        throw new Error("Not implemented yet: ".concat(node.type));
	    }
	    result.startIndex = node.startIndex;
	    result.endIndex = node.endIndex;
	    if (node.sourceCodeLocation != null) {
	        result.sourceCodeLocation = node.sourceCodeLocation;
	    }
	    return result;
	}
	node$1.cloneNode = cloneNode;
	function cloneChildren(childs) {
	    var children = childs.map(function (child) { return cloneNode(child, true); });
	    for (var i = 1; i < children.length; i++) {
	        children[i].prev = children[i - 1];
	        children[i - 1].next = children[i];
	    }
	    return children;
	}
	return node$1;
}

var hasRequiredLib$4;

function requireLib$4 () {
	if (hasRequiredLib$4) return lib$4;
	hasRequiredLib$4 = 1;
	(function (exports) {
		var __createBinding = (lib$4 && lib$4.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (lib$4 && lib$4.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DomHandler = void 0;
		var domelementtype_1 = /*@__PURE__*/ requireLib$5();
		var node_js_1 = /*@__PURE__*/ requireNode$1();
		__exportStar(/*@__PURE__*/ requireNode$1(), exports);
		// Default options
		var defaultOpts = {
		    withStartIndices: false,
		    withEndIndices: false,
		    xmlMode: false,
		};
		var DomHandler = /** @class */ (function () {
		    /**
		     * @param callback Called once parsing has completed.
		     * @param options Settings for the handler.
		     * @param elementCB Callback whenever a tag is closed.
		     */
		    function DomHandler(callback, options, elementCB) {
		        /** The elements of the DOM */
		        this.dom = [];
		        /** The root element for the DOM */
		        this.root = new node_js_1.Document(this.dom);
		        /** Indicated whether parsing has been completed. */
		        this.done = false;
		        /** Stack of open tags. */
		        this.tagStack = [this.root];
		        /** A data node that is still being written to. */
		        this.lastNode = null;
		        /** Reference to the parser instance. Used for location information. */
		        this.parser = null;
		        // Make it possible to skip arguments, for backwards-compatibility
		        if (typeof options === "function") {
		            elementCB = options;
		            options = defaultOpts;
		        }
		        if (typeof callback === "object") {
		            options = callback;
		            callback = undefined;
		        }
		        this.callback = callback !== null && callback !== void 0 ? callback : null;
		        this.options = options !== null && options !== void 0 ? options : defaultOpts;
		        this.elementCB = elementCB !== null && elementCB !== void 0 ? elementCB : null;
		    }
		    DomHandler.prototype.onparserinit = function (parser) {
		        this.parser = parser;
		    };
		    // Resets the handler back to starting state
		    DomHandler.prototype.onreset = function () {
		        this.dom = [];
		        this.root = new node_js_1.Document(this.dom);
		        this.done = false;
		        this.tagStack = [this.root];
		        this.lastNode = null;
		        this.parser = null;
		    };
		    // Signals the handler that parsing is done
		    DomHandler.prototype.onend = function () {
		        if (this.done)
		            return;
		        this.done = true;
		        this.parser = null;
		        this.handleCallback(null);
		    };
		    DomHandler.prototype.onerror = function (error) {
		        this.handleCallback(error);
		    };
		    DomHandler.prototype.onclosetag = function () {
		        this.lastNode = null;
		        var elem = this.tagStack.pop();
		        if (this.options.withEndIndices) {
		            elem.endIndex = this.parser.endIndex;
		        }
		        if (this.elementCB)
		            this.elementCB(elem);
		    };
		    DomHandler.prototype.onopentag = function (name, attribs) {
		        var type = this.options.xmlMode ? domelementtype_1.ElementType.Tag : undefined;
		        var element = new node_js_1.Element(name, attribs, undefined, type);
		        this.addNode(element);
		        this.tagStack.push(element);
		    };
		    DomHandler.prototype.ontext = function (data) {
		        var lastNode = this.lastNode;
		        if (lastNode && lastNode.type === domelementtype_1.ElementType.Text) {
		            lastNode.data += data;
		            if (this.options.withEndIndices) {
		                lastNode.endIndex = this.parser.endIndex;
		            }
		        }
		        else {
		            var node = new node_js_1.Text(data);
		            this.addNode(node);
		            this.lastNode = node;
		        }
		    };
		    DomHandler.prototype.oncomment = function (data) {
		        if (this.lastNode && this.lastNode.type === domelementtype_1.ElementType.Comment) {
		            this.lastNode.data += data;
		            return;
		        }
		        var node = new node_js_1.Comment(data);
		        this.addNode(node);
		        this.lastNode = node;
		    };
		    DomHandler.prototype.oncommentend = function () {
		        this.lastNode = null;
		    };
		    DomHandler.prototype.oncdatastart = function () {
		        var text = new node_js_1.Text("");
		        var node = new node_js_1.CDATA([text]);
		        this.addNode(node);
		        text.parent = node;
		        this.lastNode = text;
		    };
		    DomHandler.prototype.oncdataend = function () {
		        this.lastNode = null;
		    };
		    DomHandler.prototype.onprocessinginstruction = function (name, data) {
		        var node = new node_js_1.ProcessingInstruction(name, data);
		        this.addNode(node);
		    };
		    DomHandler.prototype.handleCallback = function (error) {
		        if (typeof this.callback === "function") {
		            this.callback(error, this.dom);
		        }
		        else if (error) {
		            throw error;
		        }
		    };
		    DomHandler.prototype.addNode = function (node) {
		        var parent = this.tagStack[this.tagStack.length - 1];
		        var previousSibling = parent.children[parent.children.length - 1];
		        if (this.options.withStartIndices) {
		            node.startIndex = this.parser.startIndex;
		        }
		        if (this.options.withEndIndices) {
		            node.endIndex = this.parser.endIndex;
		        }
		        parent.children.push(node);
		        if (previousSibling) {
		            node.prev = previousSibling;
		            previousSibling.next = node;
		        }
		        node.parent = parent;
		        this.lastNode = null;
		    };
		    return DomHandler;
		}());
		exports.DomHandler = DomHandler;
		exports.default = DomHandler; 
	} (lib$4));
	return lib$4;
}

var lib$2 = {};

var stringify = {};

var lib$1 = {};

var lib = {};

var encode = {};

var encodeHtml = {};

var hasRequiredEncodeHtml;

function requireEncodeHtml () {
	if (hasRequiredEncodeHtml) return encodeHtml;
	hasRequiredEncodeHtml = 1;
	// Generated using scripts/write-encode-map.ts
	Object.defineProperty(encodeHtml, "__esModule", { value: true });
	function restoreDiff(arr) {
	    for (var i = 1; i < arr.length; i++) {
	        arr[i][0] += arr[i - 1][0] + 1;
	    }
	    return arr;
	}
	// prettier-ignore
	encodeHtml.default = new Map(/* #__PURE__ */ restoreDiff([[9, "&Tab;"], [0, "&NewLine;"], [22, "&excl;"], [0, "&quot;"], [0, "&num;"], [0, "&dollar;"], [0, "&percnt;"], [0, "&amp;"], [0, "&apos;"], [0, "&lpar;"], [0, "&rpar;"], [0, "&ast;"], [0, "&plus;"], [0, "&comma;"], [1, "&period;"], [0, "&sol;"], [10, "&colon;"], [0, "&semi;"], [0, { v: "&lt;", n: 8402, o: "&nvlt;" }], [0, { v: "&equals;", n: 8421, o: "&bne;" }], [0, { v: "&gt;", n: 8402, o: "&nvgt;" }], [0, "&quest;"], [0, "&commat;"], [26, "&lbrack;"], [0, "&bsol;"], [0, "&rbrack;"], [0, "&Hat;"], [0, "&lowbar;"], [0, "&DiacriticalGrave;"], [5, { n: 106, o: "&fjlig;" }], [20, "&lbrace;"], [0, "&verbar;"], [0, "&rbrace;"], [34, "&nbsp;"], [0, "&iexcl;"], [0, "&cent;"], [0, "&pound;"], [0, "&curren;"], [0, "&yen;"], [0, "&brvbar;"], [0, "&sect;"], [0, "&die;"], [0, "&copy;"], [0, "&ordf;"], [0, "&laquo;"], [0, "&not;"], [0, "&shy;"], [0, "&circledR;"], [0, "&macr;"], [0, "&deg;"], [0, "&PlusMinus;"], [0, "&sup2;"], [0, "&sup3;"], [0, "&acute;"], [0, "&micro;"], [0, "&para;"], [0, "&centerdot;"], [0, "&cedil;"], [0, "&sup1;"], [0, "&ordm;"], [0, "&raquo;"], [0, "&frac14;"], [0, "&frac12;"], [0, "&frac34;"], [0, "&iquest;"], [0, "&Agrave;"], [0, "&Aacute;"], [0, "&Acirc;"], [0, "&Atilde;"], [0, "&Auml;"], [0, "&angst;"], [0, "&AElig;"], [0, "&Ccedil;"], [0, "&Egrave;"], [0, "&Eacute;"], [0, "&Ecirc;"], [0, "&Euml;"], [0, "&Igrave;"], [0, "&Iacute;"], [0, "&Icirc;"], [0, "&Iuml;"], [0, "&ETH;"], [0, "&Ntilde;"], [0, "&Ograve;"], [0, "&Oacute;"], [0, "&Ocirc;"], [0, "&Otilde;"], [0, "&Ouml;"], [0, "&times;"], [0, "&Oslash;"], [0, "&Ugrave;"], [0, "&Uacute;"], [0, "&Ucirc;"], [0, "&Uuml;"], [0, "&Yacute;"], [0, "&THORN;"], [0, "&szlig;"], [0, "&agrave;"], [0, "&aacute;"], [0, "&acirc;"], [0, "&atilde;"], [0, "&auml;"], [0, "&aring;"], [0, "&aelig;"], [0, "&ccedil;"], [0, "&egrave;"], [0, "&eacute;"], [0, "&ecirc;"], [0, "&euml;"], [0, "&igrave;"], [0, "&iacute;"], [0, "&icirc;"], [0, "&iuml;"], [0, "&eth;"], [0, "&ntilde;"], [0, "&ograve;"], [0, "&oacute;"], [0, "&ocirc;"], [0, "&otilde;"], [0, "&ouml;"], [0, "&div;"], [0, "&oslash;"], [0, "&ugrave;"], [0, "&uacute;"], [0, "&ucirc;"], [0, "&uuml;"], [0, "&yacute;"], [0, "&thorn;"], [0, "&yuml;"], [0, "&Amacr;"], [0, "&amacr;"], [0, "&Abreve;"], [0, "&abreve;"], [0, "&Aogon;"], [0, "&aogon;"], [0, "&Cacute;"], [0, "&cacute;"], [0, "&Ccirc;"], [0, "&ccirc;"], [0, "&Cdot;"], [0, "&cdot;"], [0, "&Ccaron;"], [0, "&ccaron;"], [0, "&Dcaron;"], [0, "&dcaron;"], [0, "&Dstrok;"], [0, "&dstrok;"], [0, "&Emacr;"], [0, "&emacr;"], [2, "&Edot;"], [0, "&edot;"], [0, "&Eogon;"], [0, "&eogon;"], [0, "&Ecaron;"], [0, "&ecaron;"], [0, "&Gcirc;"], [0, "&gcirc;"], [0, "&Gbreve;"], [0, "&gbreve;"], [0, "&Gdot;"], [0, "&gdot;"], [0, "&Gcedil;"], [1, "&Hcirc;"], [0, "&hcirc;"], [0, "&Hstrok;"], [0, "&hstrok;"], [0, "&Itilde;"], [0, "&itilde;"], [0, "&Imacr;"], [0, "&imacr;"], [2, "&Iogon;"], [0, "&iogon;"], [0, "&Idot;"], [0, "&imath;"], [0, "&IJlig;"], [0, "&ijlig;"], [0, "&Jcirc;"], [0, "&jcirc;"], [0, "&Kcedil;"], [0, "&kcedil;"], [0, "&kgreen;"], [0, "&Lacute;"], [0, "&lacute;"], [0, "&Lcedil;"], [0, "&lcedil;"], [0, "&Lcaron;"], [0, "&lcaron;"], [0, "&Lmidot;"], [0, "&lmidot;"], [0, "&Lstrok;"], [0, "&lstrok;"], [0, "&Nacute;"], [0, "&nacute;"], [0, "&Ncedil;"], [0, "&ncedil;"], [0, "&Ncaron;"], [0, "&ncaron;"], [0, "&napos;"], [0, "&ENG;"], [0, "&eng;"], [0, "&Omacr;"], [0, "&omacr;"], [2, "&Odblac;"], [0, "&odblac;"], [0, "&OElig;"], [0, "&oelig;"], [0, "&Racute;"], [0, "&racute;"], [0, "&Rcedil;"], [0, "&rcedil;"], [0, "&Rcaron;"], [0, "&rcaron;"], [0, "&Sacute;"], [0, "&sacute;"], [0, "&Scirc;"], [0, "&scirc;"], [0, "&Scedil;"], [0, "&scedil;"], [0, "&Scaron;"], [0, "&scaron;"], [0, "&Tcedil;"], [0, "&tcedil;"], [0, "&Tcaron;"], [0, "&tcaron;"], [0, "&Tstrok;"], [0, "&tstrok;"], [0, "&Utilde;"], [0, "&utilde;"], [0, "&Umacr;"], [0, "&umacr;"], [0, "&Ubreve;"], [0, "&ubreve;"], [0, "&Uring;"], [0, "&uring;"], [0, "&Udblac;"], [0, "&udblac;"], [0, "&Uogon;"], [0, "&uogon;"], [0, "&Wcirc;"], [0, "&wcirc;"], [0, "&Ycirc;"], [0, "&ycirc;"], [0, "&Yuml;"], [0, "&Zacute;"], [0, "&zacute;"], [0, "&Zdot;"], [0, "&zdot;"], [0, "&Zcaron;"], [0, "&zcaron;"], [19, "&fnof;"], [34, "&imped;"], [63, "&gacute;"], [65, "&jmath;"], [142, "&circ;"], [0, "&caron;"], [16, "&breve;"], [0, "&DiacriticalDot;"], [0, "&ring;"], [0, "&ogon;"], [0, "&DiacriticalTilde;"], [0, "&dblac;"], [51, "&DownBreve;"], [127, "&Alpha;"], [0, "&Beta;"], [0, "&Gamma;"], [0, "&Delta;"], [0, "&Epsilon;"], [0, "&Zeta;"], [0, "&Eta;"], [0, "&Theta;"], [0, "&Iota;"], [0, "&Kappa;"], [0, "&Lambda;"], [0, "&Mu;"], [0, "&Nu;"], [0, "&Xi;"], [0, "&Omicron;"], [0, "&Pi;"], [0, "&Rho;"], [1, "&Sigma;"], [0, "&Tau;"], [0, "&Upsilon;"], [0, "&Phi;"], [0, "&Chi;"], [0, "&Psi;"], [0, "&ohm;"], [7, "&alpha;"], [0, "&beta;"], [0, "&gamma;"], [0, "&delta;"], [0, "&epsi;"], [0, "&zeta;"], [0, "&eta;"], [0, "&theta;"], [0, "&iota;"], [0, "&kappa;"], [0, "&lambda;"], [0, "&mu;"], [0, "&nu;"], [0, "&xi;"], [0, "&omicron;"], [0, "&pi;"], [0, "&rho;"], [0, "&sigmaf;"], [0, "&sigma;"], [0, "&tau;"], [0, "&upsi;"], [0, "&phi;"], [0, "&chi;"], [0, "&psi;"], [0, "&omega;"], [7, "&thetasym;"], [0, "&Upsi;"], [2, "&phiv;"], [0, "&piv;"], [5, "&Gammad;"], [0, "&digamma;"], [18, "&kappav;"], [0, "&rhov;"], [3, "&epsiv;"], [0, "&backepsilon;"], [10, "&IOcy;"], [0, "&DJcy;"], [0, "&GJcy;"], [0, "&Jukcy;"], [0, "&DScy;"], [0, "&Iukcy;"], [0, "&YIcy;"], [0, "&Jsercy;"], [0, "&LJcy;"], [0, "&NJcy;"], [0, "&TSHcy;"], [0, "&KJcy;"], [1, "&Ubrcy;"], [0, "&DZcy;"], [0, "&Acy;"], [0, "&Bcy;"], [0, "&Vcy;"], [0, "&Gcy;"], [0, "&Dcy;"], [0, "&IEcy;"], [0, "&ZHcy;"], [0, "&Zcy;"], [0, "&Icy;"], [0, "&Jcy;"], [0, "&Kcy;"], [0, "&Lcy;"], [0, "&Mcy;"], [0, "&Ncy;"], [0, "&Ocy;"], [0, "&Pcy;"], [0, "&Rcy;"], [0, "&Scy;"], [0, "&Tcy;"], [0, "&Ucy;"], [0, "&Fcy;"], [0, "&KHcy;"], [0, "&TScy;"], [0, "&CHcy;"], [0, "&SHcy;"], [0, "&SHCHcy;"], [0, "&HARDcy;"], [0, "&Ycy;"], [0, "&SOFTcy;"], [0, "&Ecy;"], [0, "&YUcy;"], [0, "&YAcy;"], [0, "&acy;"], [0, "&bcy;"], [0, "&vcy;"], [0, "&gcy;"], [0, "&dcy;"], [0, "&iecy;"], [0, "&zhcy;"], [0, "&zcy;"], [0, "&icy;"], [0, "&jcy;"], [0, "&kcy;"], [0, "&lcy;"], [0, "&mcy;"], [0, "&ncy;"], [0, "&ocy;"], [0, "&pcy;"], [0, "&rcy;"], [0, "&scy;"], [0, "&tcy;"], [0, "&ucy;"], [0, "&fcy;"], [0, "&khcy;"], [0, "&tscy;"], [0, "&chcy;"], [0, "&shcy;"], [0, "&shchcy;"], [0, "&hardcy;"], [0, "&ycy;"], [0, "&softcy;"], [0, "&ecy;"], [0, "&yucy;"], [0, "&yacy;"], [1, "&iocy;"], [0, "&djcy;"], [0, "&gjcy;"], [0, "&jukcy;"], [0, "&dscy;"], [0, "&iukcy;"], [0, "&yicy;"], [0, "&jsercy;"], [0, "&ljcy;"], [0, "&njcy;"], [0, "&tshcy;"], [0, "&kjcy;"], [1, "&ubrcy;"], [0, "&dzcy;"], [7074, "&ensp;"], [0, "&emsp;"], [0, "&emsp13;"], [0, "&emsp14;"], [1, "&numsp;"], [0, "&puncsp;"], [0, "&ThinSpace;"], [0, "&hairsp;"], [0, "&NegativeMediumSpace;"], [0, "&zwnj;"], [0, "&zwj;"], [0, "&lrm;"], [0, "&rlm;"], [0, "&dash;"], [2, "&ndash;"], [0, "&mdash;"], [0, "&horbar;"], [0, "&Verbar;"], [1, "&lsquo;"], [0, "&CloseCurlyQuote;"], [0, "&lsquor;"], [1, "&ldquo;"], [0, "&CloseCurlyDoubleQuote;"], [0, "&bdquo;"], [1, "&dagger;"], [0, "&Dagger;"], [0, "&bull;"], [2, "&nldr;"], [0, "&hellip;"], [9, "&permil;"], [0, "&pertenk;"], [0, "&prime;"], [0, "&Prime;"], [0, "&tprime;"], [0, "&backprime;"], [3, "&lsaquo;"], [0, "&rsaquo;"], [3, "&oline;"], [2, "&caret;"], [1, "&hybull;"], [0, "&frasl;"], [10, "&bsemi;"], [7, "&qprime;"], [7, { v: "&MediumSpace;", n: 8202, o: "&ThickSpace;" }], [0, "&NoBreak;"], [0, "&af;"], [0, "&InvisibleTimes;"], [0, "&ic;"], [72, "&euro;"], [46, "&tdot;"], [0, "&DotDot;"], [37, "&complexes;"], [2, "&incare;"], [4, "&gscr;"], [0, "&hamilt;"], [0, "&Hfr;"], [0, "&Hopf;"], [0, "&planckh;"], [0, "&hbar;"], [0, "&imagline;"], [0, "&Ifr;"], [0, "&lagran;"], [0, "&ell;"], [1, "&naturals;"], [0, "&numero;"], [0, "&copysr;"], [0, "&weierp;"], [0, "&Popf;"], [0, "&Qopf;"], [0, "&realine;"], [0, "&real;"], [0, "&reals;"], [0, "&rx;"], [3, "&trade;"], [1, "&integers;"], [2, "&mho;"], [0, "&zeetrf;"], [0, "&iiota;"], [2, "&bernou;"], [0, "&Cayleys;"], [1, "&escr;"], [0, "&Escr;"], [0, "&Fouriertrf;"], [1, "&Mellintrf;"], [0, "&order;"], [0, "&alefsym;"], [0, "&beth;"], [0, "&gimel;"], [0, "&daleth;"], [12, "&CapitalDifferentialD;"], [0, "&dd;"], [0, "&ee;"], [0, "&ii;"], [10, "&frac13;"], [0, "&frac23;"], [0, "&frac15;"], [0, "&frac25;"], [0, "&frac35;"], [0, "&frac45;"], [0, "&frac16;"], [0, "&frac56;"], [0, "&frac18;"], [0, "&frac38;"], [0, "&frac58;"], [0, "&frac78;"], [49, "&larr;"], [0, "&ShortUpArrow;"], [0, "&rarr;"], [0, "&darr;"], [0, "&harr;"], [0, "&updownarrow;"], [0, "&nwarr;"], [0, "&nearr;"], [0, "&LowerRightArrow;"], [0, "&LowerLeftArrow;"], [0, "&nlarr;"], [0, "&nrarr;"], [1, { v: "&rarrw;", n: 824, o: "&nrarrw;" }], [0, "&Larr;"], [0, "&Uarr;"], [0, "&Rarr;"], [0, "&Darr;"], [0, "&larrtl;"], [0, "&rarrtl;"], [0, "&LeftTeeArrow;"], [0, "&mapstoup;"], [0, "&map;"], [0, "&DownTeeArrow;"], [1, "&hookleftarrow;"], [0, "&hookrightarrow;"], [0, "&larrlp;"], [0, "&looparrowright;"], [0, "&harrw;"], [0, "&nharr;"], [1, "&lsh;"], [0, "&rsh;"], [0, "&ldsh;"], [0, "&rdsh;"], [1, "&crarr;"], [0, "&cularr;"], [0, "&curarr;"], [2, "&circlearrowleft;"], [0, "&circlearrowright;"], [0, "&leftharpoonup;"], [0, "&DownLeftVector;"], [0, "&RightUpVector;"], [0, "&LeftUpVector;"], [0, "&rharu;"], [0, "&DownRightVector;"], [0, "&dharr;"], [0, "&dharl;"], [0, "&RightArrowLeftArrow;"], [0, "&udarr;"], [0, "&LeftArrowRightArrow;"], [0, "&leftleftarrows;"], [0, "&upuparrows;"], [0, "&rightrightarrows;"], [0, "&ddarr;"], [0, "&leftrightharpoons;"], [0, "&Equilibrium;"], [0, "&nlArr;"], [0, "&nhArr;"], [0, "&nrArr;"], [0, "&DoubleLeftArrow;"], [0, "&DoubleUpArrow;"], [0, "&DoubleRightArrow;"], [0, "&dArr;"], [0, "&DoubleLeftRightArrow;"], [0, "&DoubleUpDownArrow;"], [0, "&nwArr;"], [0, "&neArr;"], [0, "&seArr;"], [0, "&swArr;"], [0, "&lAarr;"], [0, "&rAarr;"], [1, "&zigrarr;"], [6, "&larrb;"], [0, "&rarrb;"], [15, "&DownArrowUpArrow;"], [7, "&loarr;"], [0, "&roarr;"], [0, "&hoarr;"], [0, "&forall;"], [0, "&comp;"], [0, { v: "&part;", n: 824, o: "&npart;" }], [0, "&exist;"], [0, "&nexist;"], [0, "&empty;"], [1, "&Del;"], [0, "&Element;"], [0, "&NotElement;"], [1, "&ni;"], [0, "&notni;"], [2, "&prod;"], [0, "&coprod;"], [0, "&sum;"], [0, "&minus;"], [0, "&MinusPlus;"], [0, "&dotplus;"], [1, "&Backslash;"], [0, "&lowast;"], [0, "&compfn;"], [1, "&radic;"], [2, "&prop;"], [0, "&infin;"], [0, "&angrt;"], [0, { v: "&ang;", n: 8402, o: "&nang;" }], [0, "&angmsd;"], [0, "&angsph;"], [0, "&mid;"], [0, "&nmid;"], [0, "&DoubleVerticalBar;"], [0, "&NotDoubleVerticalBar;"], [0, "&and;"], [0, "&or;"], [0, { v: "&cap;", n: 65024, o: "&caps;" }], [0, { v: "&cup;", n: 65024, o: "&cups;" }], [0, "&int;"], [0, "&Int;"], [0, "&iiint;"], [0, "&conint;"], [0, "&Conint;"], [0, "&Cconint;"], [0, "&cwint;"], [0, "&ClockwiseContourIntegral;"], [0, "&awconint;"], [0, "&there4;"], [0, "&becaus;"], [0, "&ratio;"], [0, "&Colon;"], [0, "&dotminus;"], [1, "&mDDot;"], [0, "&homtht;"], [0, { v: "&sim;", n: 8402, o: "&nvsim;" }], [0, { v: "&backsim;", n: 817, o: "&race;" }], [0, { v: "&ac;", n: 819, o: "&acE;" }], [0, "&acd;"], [0, "&VerticalTilde;"], [0, "&NotTilde;"], [0, { v: "&eqsim;", n: 824, o: "&nesim;" }], [0, "&sime;"], [0, "&NotTildeEqual;"], [0, "&cong;"], [0, "&simne;"], [0, "&ncong;"], [0, "&ap;"], [0, "&nap;"], [0, "&ape;"], [0, { v: "&apid;", n: 824, o: "&napid;" }], [0, "&backcong;"], [0, { v: "&asympeq;", n: 8402, o: "&nvap;" }], [0, { v: "&bump;", n: 824, o: "&nbump;" }], [0, { v: "&bumpe;", n: 824, o: "&nbumpe;" }], [0, { v: "&doteq;", n: 824, o: "&nedot;" }], [0, "&doteqdot;"], [0, "&efDot;"], [0, "&erDot;"], [0, "&Assign;"], [0, "&ecolon;"], [0, "&ecir;"], [0, "&circeq;"], [1, "&wedgeq;"], [0, "&veeeq;"], [1, "&triangleq;"], [2, "&equest;"], [0, "&ne;"], [0, { v: "&Congruent;", n: 8421, o: "&bnequiv;" }], [0, "&nequiv;"], [1, { v: "&le;", n: 8402, o: "&nvle;" }], [0, { v: "&ge;", n: 8402, o: "&nvge;" }], [0, { v: "&lE;", n: 824, o: "&nlE;" }], [0, { v: "&gE;", n: 824, o: "&ngE;" }], [0, { v: "&lnE;", n: 65024, o: "&lvertneqq;" }], [0, { v: "&gnE;", n: 65024, o: "&gvertneqq;" }], [0, { v: "&ll;", n: new Map(/* #__PURE__ */ restoreDiff([[824, "&nLtv;"], [7577, "&nLt;"]])) }], [0, { v: "&gg;", n: new Map(/* #__PURE__ */ restoreDiff([[824, "&nGtv;"], [7577, "&nGt;"]])) }], [0, "&between;"], [0, "&NotCupCap;"], [0, "&nless;"], [0, "&ngt;"], [0, "&nle;"], [0, "&nge;"], [0, "&lesssim;"], [0, "&GreaterTilde;"], [0, "&nlsim;"], [0, "&ngsim;"], [0, "&LessGreater;"], [0, "&gl;"], [0, "&NotLessGreater;"], [0, "&NotGreaterLess;"], [0, "&pr;"], [0, "&sc;"], [0, "&prcue;"], [0, "&sccue;"], [0, "&PrecedesTilde;"], [0, { v: "&scsim;", n: 824, o: "&NotSucceedsTilde;" }], [0, "&NotPrecedes;"], [0, "&NotSucceeds;"], [0, { v: "&sub;", n: 8402, o: "&NotSubset;" }], [0, { v: "&sup;", n: 8402, o: "&NotSuperset;" }], [0, "&nsub;"], [0, "&nsup;"], [0, "&sube;"], [0, "&supe;"], [0, "&NotSubsetEqual;"], [0, "&NotSupersetEqual;"], [0, { v: "&subne;", n: 65024, o: "&varsubsetneq;" }], [0, { v: "&supne;", n: 65024, o: "&varsupsetneq;" }], [1, "&cupdot;"], [0, "&UnionPlus;"], [0, { v: "&sqsub;", n: 824, o: "&NotSquareSubset;" }], [0, { v: "&sqsup;", n: 824, o: "&NotSquareSuperset;" }], [0, "&sqsube;"], [0, "&sqsupe;"], [0, { v: "&sqcap;", n: 65024, o: "&sqcaps;" }], [0, { v: "&sqcup;", n: 65024, o: "&sqcups;" }], [0, "&CirclePlus;"], [0, "&CircleMinus;"], [0, "&CircleTimes;"], [0, "&osol;"], [0, "&CircleDot;"], [0, "&circledcirc;"], [0, "&circledast;"], [1, "&circleddash;"], [0, "&boxplus;"], [0, "&boxminus;"], [0, "&boxtimes;"], [0, "&dotsquare;"], [0, "&RightTee;"], [0, "&dashv;"], [0, "&DownTee;"], [0, "&bot;"], [1, "&models;"], [0, "&DoubleRightTee;"], [0, "&Vdash;"], [0, "&Vvdash;"], [0, "&VDash;"], [0, "&nvdash;"], [0, "&nvDash;"], [0, "&nVdash;"], [0, "&nVDash;"], [0, "&prurel;"], [1, "&LeftTriangle;"], [0, "&RightTriangle;"], [0, { v: "&LeftTriangleEqual;", n: 8402, o: "&nvltrie;" }], [0, { v: "&RightTriangleEqual;", n: 8402, o: "&nvrtrie;" }], [0, "&origof;"], [0, "&imof;"], [0, "&multimap;"], [0, "&hercon;"], [0, "&intcal;"], [0, "&veebar;"], [1, "&barvee;"], [0, "&angrtvb;"], [0, "&lrtri;"], [0, "&bigwedge;"], [0, "&bigvee;"], [0, "&bigcap;"], [0, "&bigcup;"], [0, "&diam;"], [0, "&sdot;"], [0, "&sstarf;"], [0, "&divideontimes;"], [0, "&bowtie;"], [0, "&ltimes;"], [0, "&rtimes;"], [0, "&leftthreetimes;"], [0, "&rightthreetimes;"], [0, "&backsimeq;"], [0, "&curlyvee;"], [0, "&curlywedge;"], [0, "&Sub;"], [0, "&Sup;"], [0, "&Cap;"], [0, "&Cup;"], [0, "&fork;"], [0, "&epar;"], [0, "&lessdot;"], [0, "&gtdot;"], [0, { v: "&Ll;", n: 824, o: "&nLl;" }], [0, { v: "&Gg;", n: 824, o: "&nGg;" }], [0, { v: "&leg;", n: 65024, o: "&lesg;" }], [0, { v: "&gel;", n: 65024, o: "&gesl;" }], [2, "&cuepr;"], [0, "&cuesc;"], [0, "&NotPrecedesSlantEqual;"], [0, "&NotSucceedsSlantEqual;"], [0, "&NotSquareSubsetEqual;"], [0, "&NotSquareSupersetEqual;"], [2, "&lnsim;"], [0, "&gnsim;"], [0, "&precnsim;"], [0, "&scnsim;"], [0, "&nltri;"], [0, "&NotRightTriangle;"], [0, "&nltrie;"], [0, "&NotRightTriangleEqual;"], [0, "&vellip;"], [0, "&ctdot;"], [0, "&utdot;"], [0, "&dtdot;"], [0, "&disin;"], [0, "&isinsv;"], [0, "&isins;"], [0, { v: "&isindot;", n: 824, o: "&notindot;" }], [0, "&notinvc;"], [0, "&notinvb;"], [1, { v: "&isinE;", n: 824, o: "&notinE;" }], [0, "&nisd;"], [0, "&xnis;"], [0, "&nis;"], [0, "&notnivc;"], [0, "&notnivb;"], [6, "&barwed;"], [0, "&Barwed;"], [1, "&lceil;"], [0, "&rceil;"], [0, "&LeftFloor;"], [0, "&rfloor;"], [0, "&drcrop;"], [0, "&dlcrop;"], [0, "&urcrop;"], [0, "&ulcrop;"], [0, "&bnot;"], [1, "&profline;"], [0, "&profsurf;"], [1, "&telrec;"], [0, "&target;"], [5, "&ulcorn;"], [0, "&urcorn;"], [0, "&dlcorn;"], [0, "&drcorn;"], [2, "&frown;"], [0, "&smile;"], [9, "&cylcty;"], [0, "&profalar;"], [7, "&topbot;"], [6, "&ovbar;"], [1, "&solbar;"], [60, "&angzarr;"], [51, "&lmoustache;"], [0, "&rmoustache;"], [2, "&OverBracket;"], [0, "&bbrk;"], [0, "&bbrktbrk;"], [37, "&OverParenthesis;"], [0, "&UnderParenthesis;"], [0, "&OverBrace;"], [0, "&UnderBrace;"], [2, "&trpezium;"], [4, "&elinters;"], [59, "&blank;"], [164, "&circledS;"], [55, "&boxh;"], [1, "&boxv;"], [9, "&boxdr;"], [3, "&boxdl;"], [3, "&boxur;"], [3, "&boxul;"], [3, "&boxvr;"], [7, "&boxvl;"], [7, "&boxhd;"], [7, "&boxhu;"], [7, "&boxvh;"], [19, "&boxH;"], [0, "&boxV;"], [0, "&boxdR;"], [0, "&boxDr;"], [0, "&boxDR;"], [0, "&boxdL;"], [0, "&boxDl;"], [0, "&boxDL;"], [0, "&boxuR;"], [0, "&boxUr;"], [0, "&boxUR;"], [0, "&boxuL;"], [0, "&boxUl;"], [0, "&boxUL;"], [0, "&boxvR;"], [0, "&boxVr;"], [0, "&boxVR;"], [0, "&boxvL;"], [0, "&boxVl;"], [0, "&boxVL;"], [0, "&boxHd;"], [0, "&boxhD;"], [0, "&boxHD;"], [0, "&boxHu;"], [0, "&boxhU;"], [0, "&boxHU;"], [0, "&boxvH;"], [0, "&boxVh;"], [0, "&boxVH;"], [19, "&uhblk;"], [3, "&lhblk;"], [3, "&block;"], [8, "&blk14;"], [0, "&blk12;"], [0, "&blk34;"], [13, "&square;"], [8, "&blacksquare;"], [0, "&EmptyVerySmallSquare;"], [1, "&rect;"], [0, "&marker;"], [2, "&fltns;"], [1, "&bigtriangleup;"], [0, "&blacktriangle;"], [0, "&triangle;"], [2, "&blacktriangleright;"], [0, "&rtri;"], [3, "&bigtriangledown;"], [0, "&blacktriangledown;"], [0, "&dtri;"], [2, "&blacktriangleleft;"], [0, "&ltri;"], [6, "&loz;"], [0, "&cir;"], [32, "&tridot;"], [2, "&bigcirc;"], [8, "&ultri;"], [0, "&urtri;"], [0, "&lltri;"], [0, "&EmptySmallSquare;"], [0, "&FilledSmallSquare;"], [8, "&bigstar;"], [0, "&star;"], [7, "&phone;"], [49, "&female;"], [1, "&male;"], [29, "&spades;"], [2, "&clubs;"], [1, "&hearts;"], [0, "&diamondsuit;"], [3, "&sung;"], [2, "&flat;"], [0, "&natural;"], [0, "&sharp;"], [163, "&check;"], [3, "&cross;"], [8, "&malt;"], [21, "&sext;"], [33, "&VerticalSeparator;"], [25, "&lbbrk;"], [0, "&rbbrk;"], [84, "&bsolhsub;"], [0, "&suphsol;"], [28, "&LeftDoubleBracket;"], [0, "&RightDoubleBracket;"], [0, "&lang;"], [0, "&rang;"], [0, "&Lang;"], [0, "&Rang;"], [0, "&loang;"], [0, "&roang;"], [7, "&longleftarrow;"], [0, "&longrightarrow;"], [0, "&longleftrightarrow;"], [0, "&DoubleLongLeftArrow;"], [0, "&DoubleLongRightArrow;"], [0, "&DoubleLongLeftRightArrow;"], [1, "&longmapsto;"], [2, "&dzigrarr;"], [258, "&nvlArr;"], [0, "&nvrArr;"], [0, "&nvHarr;"], [0, "&Map;"], [6, "&lbarr;"], [0, "&bkarow;"], [0, "&lBarr;"], [0, "&dbkarow;"], [0, "&drbkarow;"], [0, "&DDotrahd;"], [0, "&UpArrowBar;"], [0, "&DownArrowBar;"], [2, "&Rarrtl;"], [2, "&latail;"], [0, "&ratail;"], [0, "&lAtail;"], [0, "&rAtail;"], [0, "&larrfs;"], [0, "&rarrfs;"], [0, "&larrbfs;"], [0, "&rarrbfs;"], [2, "&nwarhk;"], [0, "&nearhk;"], [0, "&hksearow;"], [0, "&hkswarow;"], [0, "&nwnear;"], [0, "&nesear;"], [0, "&seswar;"], [0, "&swnwar;"], [8, { v: "&rarrc;", n: 824, o: "&nrarrc;" }], [1, "&cudarrr;"], [0, "&ldca;"], [0, "&rdca;"], [0, "&cudarrl;"], [0, "&larrpl;"], [2, "&curarrm;"], [0, "&cularrp;"], [7, "&rarrpl;"], [2, "&harrcir;"], [0, "&Uarrocir;"], [0, "&lurdshar;"], [0, "&ldrushar;"], [2, "&LeftRightVector;"], [0, "&RightUpDownVector;"], [0, "&DownLeftRightVector;"], [0, "&LeftUpDownVector;"], [0, "&LeftVectorBar;"], [0, "&RightVectorBar;"], [0, "&RightUpVectorBar;"], [0, "&RightDownVectorBar;"], [0, "&DownLeftVectorBar;"], [0, "&DownRightVectorBar;"], [0, "&LeftUpVectorBar;"], [0, "&LeftDownVectorBar;"], [0, "&LeftTeeVector;"], [0, "&RightTeeVector;"], [0, "&RightUpTeeVector;"], [0, "&RightDownTeeVector;"], [0, "&DownLeftTeeVector;"], [0, "&DownRightTeeVector;"], [0, "&LeftUpTeeVector;"], [0, "&LeftDownTeeVector;"], [0, "&lHar;"], [0, "&uHar;"], [0, "&rHar;"], [0, "&dHar;"], [0, "&luruhar;"], [0, "&ldrdhar;"], [0, "&ruluhar;"], [0, "&rdldhar;"], [0, "&lharul;"], [0, "&llhard;"], [0, "&rharul;"], [0, "&lrhard;"], [0, "&udhar;"], [0, "&duhar;"], [0, "&RoundImplies;"], [0, "&erarr;"], [0, "&simrarr;"], [0, "&larrsim;"], [0, "&rarrsim;"], [0, "&rarrap;"], [0, "&ltlarr;"], [1, "&gtrarr;"], [0, "&subrarr;"], [1, "&suplarr;"], [0, "&lfisht;"], [0, "&rfisht;"], [0, "&ufisht;"], [0, "&dfisht;"], [5, "&lopar;"], [0, "&ropar;"], [4, "&lbrke;"], [0, "&rbrke;"], [0, "&lbrkslu;"], [0, "&rbrksld;"], [0, "&lbrksld;"], [0, "&rbrkslu;"], [0, "&langd;"], [0, "&rangd;"], [0, "&lparlt;"], [0, "&rpargt;"], [0, "&gtlPar;"], [0, "&ltrPar;"], [3, "&vzigzag;"], [1, "&vangrt;"], [0, "&angrtvbd;"], [6, "&ange;"], [0, "&range;"], [0, "&dwangle;"], [0, "&uwangle;"], [0, "&angmsdaa;"], [0, "&angmsdab;"], [0, "&angmsdac;"], [0, "&angmsdad;"], [0, "&angmsdae;"], [0, "&angmsdaf;"], [0, "&angmsdag;"], [0, "&angmsdah;"], [0, "&bemptyv;"], [0, "&demptyv;"], [0, "&cemptyv;"], [0, "&raemptyv;"], [0, "&laemptyv;"], [0, "&ohbar;"], [0, "&omid;"], [0, "&opar;"], [1, "&operp;"], [1, "&olcross;"], [0, "&odsold;"], [1, "&olcir;"], [0, "&ofcir;"], [0, "&olt;"], [0, "&ogt;"], [0, "&cirscir;"], [0, "&cirE;"], [0, "&solb;"], [0, "&bsolb;"], [3, "&boxbox;"], [3, "&trisb;"], [0, "&rtriltri;"], [0, { v: "&LeftTriangleBar;", n: 824, o: "&NotLeftTriangleBar;" }], [0, { v: "&RightTriangleBar;", n: 824, o: "&NotRightTriangleBar;" }], [11, "&iinfin;"], [0, "&infintie;"], [0, "&nvinfin;"], [4, "&eparsl;"], [0, "&smeparsl;"], [0, "&eqvparsl;"], [5, "&blacklozenge;"], [8, "&RuleDelayed;"], [1, "&dsol;"], [9, "&bigodot;"], [0, "&bigoplus;"], [0, "&bigotimes;"], [1, "&biguplus;"], [1, "&bigsqcup;"], [5, "&iiiint;"], [0, "&fpartint;"], [2, "&cirfnint;"], [0, "&awint;"], [0, "&rppolint;"], [0, "&scpolint;"], [0, "&npolint;"], [0, "&pointint;"], [0, "&quatint;"], [0, "&intlarhk;"], [10, "&pluscir;"], [0, "&plusacir;"], [0, "&simplus;"], [0, "&plusdu;"], [0, "&plussim;"], [0, "&plustwo;"], [1, "&mcomma;"], [0, "&minusdu;"], [2, "&loplus;"], [0, "&roplus;"], [0, "&Cross;"], [0, "&timesd;"], [0, "&timesbar;"], [1, "&smashp;"], [0, "&lotimes;"], [0, "&rotimes;"], [0, "&otimesas;"], [0, "&Otimes;"], [0, "&odiv;"], [0, "&triplus;"], [0, "&triminus;"], [0, "&tritime;"], [0, "&intprod;"], [2, "&amalg;"], [0, "&capdot;"], [1, "&ncup;"], [0, "&ncap;"], [0, "&capand;"], [0, "&cupor;"], [0, "&cupcap;"], [0, "&capcup;"], [0, "&cupbrcap;"], [0, "&capbrcup;"], [0, "&cupcup;"], [0, "&capcap;"], [0, "&ccups;"], [0, "&ccaps;"], [2, "&ccupssm;"], [2, "&And;"], [0, "&Or;"], [0, "&andand;"], [0, "&oror;"], [0, "&orslope;"], [0, "&andslope;"], [1, "&andv;"], [0, "&orv;"], [0, "&andd;"], [0, "&ord;"], [1, "&wedbar;"], [6, "&sdote;"], [3, "&simdot;"], [2, { v: "&congdot;", n: 824, o: "&ncongdot;" }], [0, "&easter;"], [0, "&apacir;"], [0, { v: "&apE;", n: 824, o: "&napE;" }], [0, "&eplus;"], [0, "&pluse;"], [0, "&Esim;"], [0, "&Colone;"], [0, "&Equal;"], [1, "&ddotseq;"], [0, "&equivDD;"], [0, "&ltcir;"], [0, "&gtcir;"], [0, "&ltquest;"], [0, "&gtquest;"], [0, { v: "&leqslant;", n: 824, o: "&nleqslant;" }], [0, { v: "&geqslant;", n: 824, o: "&ngeqslant;" }], [0, "&lesdot;"], [0, "&gesdot;"], [0, "&lesdoto;"], [0, "&gesdoto;"], [0, "&lesdotor;"], [0, "&gesdotol;"], [0, "&lap;"], [0, "&gap;"], [0, "&lne;"], [0, "&gne;"], [0, "&lnap;"], [0, "&gnap;"], [0, "&lEg;"], [0, "&gEl;"], [0, "&lsime;"], [0, "&gsime;"], [0, "&lsimg;"], [0, "&gsiml;"], [0, "&lgE;"], [0, "&glE;"], [0, "&lesges;"], [0, "&gesles;"], [0, "&els;"], [0, "&egs;"], [0, "&elsdot;"], [0, "&egsdot;"], [0, "&el;"], [0, "&eg;"], [2, "&siml;"], [0, "&simg;"], [0, "&simlE;"], [0, "&simgE;"], [0, { v: "&LessLess;", n: 824, o: "&NotNestedLessLess;" }], [0, { v: "&GreaterGreater;", n: 824, o: "&NotNestedGreaterGreater;" }], [1, "&glj;"], [0, "&gla;"], [0, "&ltcc;"], [0, "&gtcc;"], [0, "&lescc;"], [0, "&gescc;"], [0, "&smt;"], [0, "&lat;"], [0, { v: "&smte;", n: 65024, o: "&smtes;" }], [0, { v: "&late;", n: 65024, o: "&lates;" }], [0, "&bumpE;"], [0, { v: "&PrecedesEqual;", n: 824, o: "&NotPrecedesEqual;" }], [0, { v: "&sce;", n: 824, o: "&NotSucceedsEqual;" }], [2, "&prE;"], [0, "&scE;"], [0, "&precneqq;"], [0, "&scnE;"], [0, "&prap;"], [0, "&scap;"], [0, "&precnapprox;"], [0, "&scnap;"], [0, "&Pr;"], [0, "&Sc;"], [0, "&subdot;"], [0, "&supdot;"], [0, "&subplus;"], [0, "&supplus;"], [0, "&submult;"], [0, "&supmult;"], [0, "&subedot;"], [0, "&supedot;"], [0, { v: "&subE;", n: 824, o: "&nsubE;" }], [0, { v: "&supE;", n: 824, o: "&nsupE;" }], [0, "&subsim;"], [0, "&supsim;"], [2, { v: "&subnE;", n: 65024, o: "&varsubsetneqq;" }], [0, { v: "&supnE;", n: 65024, o: "&varsupsetneqq;" }], [2, "&csub;"], [0, "&csup;"], [0, "&csube;"], [0, "&csupe;"], [0, "&subsup;"], [0, "&supsub;"], [0, "&subsub;"], [0, "&supsup;"], [0, "&suphsub;"], [0, "&supdsub;"], [0, "&forkv;"], [0, "&topfork;"], [0, "&mlcp;"], [8, "&Dashv;"], [1, "&Vdashl;"], [0, "&Barv;"], [0, "&vBar;"], [0, "&vBarv;"], [1, "&Vbar;"], [0, "&Not;"], [0, "&bNot;"], [0, "&rnmid;"], [0, "&cirmid;"], [0, "&midcir;"], [0, "&topcir;"], [0, "&nhpar;"], [0, "&parsim;"], [9, { v: "&parsl;", n: 8421, o: "&nparsl;" }], [44343, { n: new Map(/* #__PURE__ */ restoreDiff([[56476, "&Ascr;"], [1, "&Cscr;"], [0, "&Dscr;"], [2, "&Gscr;"], [2, "&Jscr;"], [0, "&Kscr;"], [2, "&Nscr;"], [0, "&Oscr;"], [0, "&Pscr;"], [0, "&Qscr;"], [1, "&Sscr;"], [0, "&Tscr;"], [0, "&Uscr;"], [0, "&Vscr;"], [0, "&Wscr;"], [0, "&Xscr;"], [0, "&Yscr;"], [0, "&Zscr;"], [0, "&ascr;"], [0, "&bscr;"], [0, "&cscr;"], [0, "&dscr;"], [1, "&fscr;"], [1, "&hscr;"], [0, "&iscr;"], [0, "&jscr;"], [0, "&kscr;"], [0, "&lscr;"], [0, "&mscr;"], [0, "&nscr;"], [1, "&pscr;"], [0, "&qscr;"], [0, "&rscr;"], [0, "&sscr;"], [0, "&tscr;"], [0, "&uscr;"], [0, "&vscr;"], [0, "&wscr;"], [0, "&xscr;"], [0, "&yscr;"], [0, "&zscr;"], [52, "&Afr;"], [0, "&Bfr;"], [1, "&Dfr;"], [0, "&Efr;"], [0, "&Ffr;"], [0, "&Gfr;"], [2, "&Jfr;"], [0, "&Kfr;"], [0, "&Lfr;"], [0, "&Mfr;"], [0, "&Nfr;"], [0, "&Ofr;"], [0, "&Pfr;"], [0, "&Qfr;"], [1, "&Sfr;"], [0, "&Tfr;"], [0, "&Ufr;"], [0, "&Vfr;"], [0, "&Wfr;"], [0, "&Xfr;"], [0, "&Yfr;"], [1, "&afr;"], [0, "&bfr;"], [0, "&cfr;"], [0, "&dfr;"], [0, "&efr;"], [0, "&ffr;"], [0, "&gfr;"], [0, "&hfr;"], [0, "&ifr;"], [0, "&jfr;"], [0, "&kfr;"], [0, "&lfr;"], [0, "&mfr;"], [0, "&nfr;"], [0, "&ofr;"], [0, "&pfr;"], [0, "&qfr;"], [0, "&rfr;"], [0, "&sfr;"], [0, "&tfr;"], [0, "&ufr;"], [0, "&vfr;"], [0, "&wfr;"], [0, "&xfr;"], [0, "&yfr;"], [0, "&zfr;"], [0, "&Aopf;"], [0, "&Bopf;"], [1, "&Dopf;"], [0, "&Eopf;"], [0, "&Fopf;"], [0, "&Gopf;"], [1, "&Iopf;"], [0, "&Jopf;"], [0, "&Kopf;"], [0, "&Lopf;"], [0, "&Mopf;"], [1, "&Oopf;"], [3, "&Sopf;"], [0, "&Topf;"], [0, "&Uopf;"], [0, "&Vopf;"], [0, "&Wopf;"], [0, "&Xopf;"], [0, "&Yopf;"], [1, "&aopf;"], [0, "&bopf;"], [0, "&copf;"], [0, "&dopf;"], [0, "&eopf;"], [0, "&fopf;"], [0, "&gopf;"], [0, "&hopf;"], [0, "&iopf;"], [0, "&jopf;"], [0, "&kopf;"], [0, "&lopf;"], [0, "&mopf;"], [0, "&nopf;"], [0, "&oopf;"], [0, "&popf;"], [0, "&qopf;"], [0, "&ropf;"], [0, "&sopf;"], [0, "&topf;"], [0, "&uopf;"], [0, "&vopf;"], [0, "&wopf;"], [0, "&xopf;"], [0, "&yopf;"], [0, "&zopf;"]])) }], [8906, "&fflig;"], [0, "&filig;"], [0, "&fllig;"], [0, "&ffilig;"], [0, "&ffllig;"]]));
	
	return encodeHtml;
}

var _escape = {};

var hasRequired_escape;

function require_escape () {
	if (hasRequired_escape) return _escape;
	hasRequired_escape = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.escapeText = exports.escapeAttribute = exports.escapeUTF8 = exports.escape = exports.encodeXML = exports.getCodePoint = exports.xmlReplacer = void 0;
		exports.xmlReplacer = /["&'<>$\x80-\uFFFF]/g;
		var xmlCodeMap = new Map([
		    [34, "&quot;"],
		    [38, "&amp;"],
		    [39, "&apos;"],
		    [60, "&lt;"],
		    [62, "&gt;"],
		]);
		// For compatibility with node < 4, we wrap `codePointAt`
		exports.getCodePoint = 
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		String.prototype.codePointAt != null
		    ? function (str, index) { return str.codePointAt(index); }
		    : // http://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
		        function (c, index) {
		            return (c.charCodeAt(index) & 0xfc00) === 0xd800
		                ? (c.charCodeAt(index) - 0xd800) * 0x400 +
		                    c.charCodeAt(index + 1) -
		                    0xdc00 +
		                    0x10000
		                : c.charCodeAt(index);
		        };
		/**
		 * Encodes all non-ASCII characters, as well as characters not valid in XML
		 * documents using XML entities.
		 *
		 * If a character has no equivalent entity, a
		 * numeric hexadecimal reference (eg. `&#xfc;`) will be used.
		 */
		function encodeXML(str) {
		    var ret = "";
		    var lastIdx = 0;
		    var match;
		    while ((match = exports.xmlReplacer.exec(str)) !== null) {
		        var i = match.index;
		        var char = str.charCodeAt(i);
		        var next = xmlCodeMap.get(char);
		        if (next !== undefined) {
		            ret += str.substring(lastIdx, i) + next;
		            lastIdx = i + 1;
		        }
		        else {
		            ret += "".concat(str.substring(lastIdx, i), "&#x").concat((0, exports.getCodePoint)(str, i).toString(16), ";");
		            // Increase by 1 if we have a surrogate pair
		            lastIdx = exports.xmlReplacer.lastIndex += Number((char & 0xfc00) === 0xd800);
		        }
		    }
		    return ret + str.substr(lastIdx);
		}
		exports.encodeXML = encodeXML;
		/**
		 * Encodes all non-ASCII characters, as well as characters not valid in XML
		 * documents using numeric hexadecimal reference (eg. `&#xfc;`).
		 *
		 * Have a look at `escapeUTF8` if you want a more concise output at the expense
		 * of reduced transportability.
		 *
		 * @param data String to escape.
		 */
		exports.escape = encodeXML;
		/**
		 * Creates a function that escapes all characters matched by the given regular
		 * expression using the given map of characters to escape to their entities.
		 *
		 * @param regex Regular expression to match characters to escape.
		 * @param map Map of characters to escape to their entities.
		 *
		 * @returns Function that escapes all characters matched by the given regular
		 * expression using the given map of characters to escape to their entities.
		 */
		function getEscaper(regex, map) {
		    return function escape(data) {
		        var match;
		        var lastIdx = 0;
		        var result = "";
		        while ((match = regex.exec(data))) {
		            if (lastIdx !== match.index) {
		                result += data.substring(lastIdx, match.index);
		            }
		            // We know that this character will be in the map.
		            result += map.get(match[0].charCodeAt(0));
		            // Every match will be of length 1
		            lastIdx = match.index + 1;
		        }
		        return result + data.substring(lastIdx);
		    };
		}
		/**
		 * Encodes all characters not valid in XML documents using XML entities.
		 *
		 * Note that the output will be character-set dependent.
		 *
		 * @param data String to escape.
		 */
		exports.escapeUTF8 = getEscaper(/[&<>'"]/g, xmlCodeMap);
		/**
		 * Encodes all characters that have to be escaped in HTML attributes,
		 * following {@link https://html.spec.whatwg.org/multipage/parsing.html#escapingString}.
		 *
		 * @param data String to escape.
		 */
		exports.escapeAttribute = getEscaper(/["&\u00A0]/g, new Map([
		    [34, "&quot;"],
		    [38, "&amp;"],
		    [160, "&nbsp;"],
		]));
		/**
		 * Encodes all characters that have to be escaped in HTML text,
		 * following {@link https://html.spec.whatwg.org/multipage/parsing.html#escapingString}.
		 *
		 * @param data String to escape.
		 */
		exports.escapeText = getEscaper(/[&<>\u00A0]/g, new Map([
		    [38, "&amp;"],
		    [60, "&lt;"],
		    [62, "&gt;"],
		    [160, "&nbsp;"],
		]));
		
	} (_escape));
	return _escape;
}

var hasRequiredEncode;

function requireEncode () {
	if (hasRequiredEncode) return encode;
	hasRequiredEncode = 1;
	var __importDefault = (encode && encode.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(encode, "__esModule", { value: true });
	encode.encodeNonAsciiHTML = encode.encodeHTML = void 0;
	var encode_html_js_1 = __importDefault(/*@__PURE__*/ requireEncodeHtml());
	var escape_js_1 = /*@__PURE__*/ require_escape();
	var htmlReplacer = /[\t\n!-,./:-@[-`\f{-}$\x80-\uFFFF]/g;
	/**
	 * Encodes all characters in the input using HTML entities. This includes
	 * characters that are valid ASCII characters in HTML documents, such as `#`.
	 *
	 * To get a more compact output, consider using the `encodeNonAsciiHTML`
	 * function, which will only encode characters that are not valid in HTML
	 * documents, as well as non-ASCII characters.
	 *
	 * If a character has no equivalent entity, a numeric hexadecimal reference
	 * (eg. `&#xfc;`) will be used.
	 */
	function encodeHTML(data) {
	    return encodeHTMLTrieRe(htmlReplacer, data);
	}
	encode.encodeHTML = encodeHTML;
	/**
	 * Encodes all non-ASCII characters, as well as characters not valid in HTML
	 * documents using HTML entities. This function will not encode characters that
	 * are valid in HTML documents, such as `#`.
	 *
	 * If a character has no equivalent entity, a numeric hexadecimal reference
	 * (eg. `&#xfc;`) will be used.
	 */
	function encodeNonAsciiHTML(data) {
	    return encodeHTMLTrieRe(escape_js_1.xmlReplacer, data);
	}
	encode.encodeNonAsciiHTML = encodeNonAsciiHTML;
	function encodeHTMLTrieRe(regExp, str) {
	    var ret = "";
	    var lastIdx = 0;
	    var match;
	    while ((match = regExp.exec(str)) !== null) {
	        var i = match.index;
	        ret += str.substring(lastIdx, i);
	        var char = str.charCodeAt(i);
	        var next = encode_html_js_1.default.get(char);
	        if (typeof next === "object") {
	            // We are in a branch. Try to match the next char.
	            if (i + 1 < str.length) {
	                var nextChar = str.charCodeAt(i + 1);
	                var value = typeof next.n === "number"
	                    ? next.n === nextChar
	                        ? next.o
	                        : undefined
	                    : next.n.get(nextChar);
	                if (value !== undefined) {
	                    ret += value;
	                    lastIdx = regExp.lastIndex += 1;
	                    continue;
	                }
	            }
	            next = next.v;
	        }
	        // We might have a tree node without a value; skip and use a numeric entity.
	        if (next !== undefined) {
	            ret += next;
	            lastIdx = i + 1;
	        }
	        else {
	            var cp = (0, escape_js_1.getCodePoint)(str, i);
	            ret += "&#x".concat(cp.toString(16), ";");
	            // Increase by 1 if we have a surrogate pair
	            lastIdx = regExp.lastIndex += Number(cp !== char);
	        }
	    }
	    return ret + str.substr(lastIdx);
	}
	
	return encode;
}

var hasRequiredLib$3;

function requireLib$3 () {
	if (hasRequiredLib$3) return lib;
	hasRequiredLib$3 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.decodeXMLStrict = exports.decodeHTML5Strict = exports.decodeHTML4Strict = exports.decodeHTML5 = exports.decodeHTML4 = exports.decodeHTMLAttribute = exports.decodeHTMLStrict = exports.decodeHTML = exports.decodeXML = exports.DecodingMode = exports.EntityDecoder = exports.encodeHTML5 = exports.encodeHTML4 = exports.encodeNonAsciiHTML = exports.encodeHTML = exports.escapeText = exports.escapeAttribute = exports.escapeUTF8 = exports.escape = exports.encodeXML = exports.encode = exports.decodeStrict = exports.decode = exports.EncodingMode = exports.EntityLevel = void 0;
		var decode_js_1 = /*@__PURE__*/ requireDecode();
		var encode_js_1 = /*@__PURE__*/ requireEncode();
		var escape_js_1 = /*@__PURE__*/ require_escape();
		/** The level of entities to support. */
		var EntityLevel;
		(function (EntityLevel) {
		    /** Support only XML entities. */
		    EntityLevel[EntityLevel["XML"] = 0] = "XML";
		    /** Support HTML entities, which are a superset of XML entities. */
		    EntityLevel[EntityLevel["HTML"] = 1] = "HTML";
		})(EntityLevel = exports.EntityLevel || (exports.EntityLevel = {}));
		var EncodingMode;
		(function (EncodingMode) {
		    /**
		     * The output is UTF-8 encoded. Only characters that need escaping within
		     * XML will be escaped.
		     */
		    EncodingMode[EncodingMode["UTF8"] = 0] = "UTF8";
		    /**
		     * The output consists only of ASCII characters. Characters that need
		     * escaping within HTML, and characters that aren't ASCII characters will
		     * be escaped.
		     */
		    EncodingMode[EncodingMode["ASCII"] = 1] = "ASCII";
		    /**
		     * Encode all characters that have an equivalent entity, as well as all
		     * characters that are not ASCII characters.
		     */
		    EncodingMode[EncodingMode["Extensive"] = 2] = "Extensive";
		    /**
		     * Encode all characters that have to be escaped in HTML attributes,
		     * following {@link https://html.spec.whatwg.org/multipage/parsing.html#escapingString}.
		     */
		    EncodingMode[EncodingMode["Attribute"] = 3] = "Attribute";
		    /**
		     * Encode all characters that have to be escaped in HTML text,
		     * following {@link https://html.spec.whatwg.org/multipage/parsing.html#escapingString}.
		     */
		    EncodingMode[EncodingMode["Text"] = 4] = "Text";
		})(EncodingMode = exports.EncodingMode || (exports.EncodingMode = {}));
		/**
		 * Decodes a string with entities.
		 *
		 * @param data String to decode.
		 * @param options Decoding options.
		 */
		function decode(data, options) {
		    if (options === void 0) { options = EntityLevel.XML; }
		    var level = typeof options === "number" ? options : options.level;
		    if (level === EntityLevel.HTML) {
		        var mode = typeof options === "object" ? options.mode : undefined;
		        return (0, decode_js_1.decodeHTML)(data, mode);
		    }
		    return (0, decode_js_1.decodeXML)(data);
		}
		exports.decode = decode;
		/**
		 * Decodes a string with entities. Does not allow missing trailing semicolons for entities.
		 *
		 * @param data String to decode.
		 * @param options Decoding options.
		 * @deprecated Use `decode` with the `mode` set to `Strict`.
		 */
		function decodeStrict(data, options) {
		    var _a;
		    if (options === void 0) { options = EntityLevel.XML; }
		    var opts = typeof options === "number" ? { level: options } : options;
		    (_a = opts.mode) !== null && _a !== void 0 ? _a : (opts.mode = decode_js_1.DecodingMode.Strict);
		    return decode(data, opts);
		}
		exports.decodeStrict = decodeStrict;
		/**
		 * Encodes a string with entities.
		 *
		 * @param data String to encode.
		 * @param options Encoding options.
		 */
		function encode(data, options) {
		    if (options === void 0) { options = EntityLevel.XML; }
		    var opts = typeof options === "number" ? { level: options } : options;
		    // Mode `UTF8` just escapes XML entities
		    if (opts.mode === EncodingMode.UTF8)
		        return (0, escape_js_1.escapeUTF8)(data);
		    if (opts.mode === EncodingMode.Attribute)
		        return (0, escape_js_1.escapeAttribute)(data);
		    if (opts.mode === EncodingMode.Text)
		        return (0, escape_js_1.escapeText)(data);
		    if (opts.level === EntityLevel.HTML) {
		        if (opts.mode === EncodingMode.ASCII) {
		            return (0, encode_js_1.encodeNonAsciiHTML)(data);
		        }
		        return (0, encode_js_1.encodeHTML)(data);
		    }
		    // ASCII and Extensive are equivalent
		    return (0, escape_js_1.encodeXML)(data);
		}
		exports.encode = encode;
		var escape_js_2 = /*@__PURE__*/ require_escape();
		Object.defineProperty(exports, "encodeXML", { enumerable: true, get: function () { return escape_js_2.encodeXML; } });
		Object.defineProperty(exports, "escape", { enumerable: true, get: function () { return escape_js_2.escape; } });
		Object.defineProperty(exports, "escapeUTF8", { enumerable: true, get: function () { return escape_js_2.escapeUTF8; } });
		Object.defineProperty(exports, "escapeAttribute", { enumerable: true, get: function () { return escape_js_2.escapeAttribute; } });
		Object.defineProperty(exports, "escapeText", { enumerable: true, get: function () { return escape_js_2.escapeText; } });
		var encode_js_2 = /*@__PURE__*/ requireEncode();
		Object.defineProperty(exports, "encodeHTML", { enumerable: true, get: function () { return encode_js_2.encodeHTML; } });
		Object.defineProperty(exports, "encodeNonAsciiHTML", { enumerable: true, get: function () { return encode_js_2.encodeNonAsciiHTML; } });
		// Legacy aliases (deprecated)
		Object.defineProperty(exports, "encodeHTML4", { enumerable: true, get: function () { return encode_js_2.encodeHTML; } });
		Object.defineProperty(exports, "encodeHTML5", { enumerable: true, get: function () { return encode_js_2.encodeHTML; } });
		var decode_js_2 = /*@__PURE__*/ requireDecode();
		Object.defineProperty(exports, "EntityDecoder", { enumerable: true, get: function () { return decode_js_2.EntityDecoder; } });
		Object.defineProperty(exports, "DecodingMode", { enumerable: true, get: function () { return decode_js_2.DecodingMode; } });
		Object.defineProperty(exports, "decodeXML", { enumerable: true, get: function () { return decode_js_2.decodeXML; } });
		Object.defineProperty(exports, "decodeHTML", { enumerable: true, get: function () { return decode_js_2.decodeHTML; } });
		Object.defineProperty(exports, "decodeHTMLStrict", { enumerable: true, get: function () { return decode_js_2.decodeHTMLStrict; } });
		Object.defineProperty(exports, "decodeHTMLAttribute", { enumerable: true, get: function () { return decode_js_2.decodeHTMLAttribute; } });
		// Legacy aliases (deprecated)
		Object.defineProperty(exports, "decodeHTML4", { enumerable: true, get: function () { return decode_js_2.decodeHTML; } });
		Object.defineProperty(exports, "decodeHTML5", { enumerable: true, get: function () { return decode_js_2.decodeHTML; } });
		Object.defineProperty(exports, "decodeHTML4Strict", { enumerable: true, get: function () { return decode_js_2.decodeHTMLStrict; } });
		Object.defineProperty(exports, "decodeHTML5Strict", { enumerable: true, get: function () { return decode_js_2.decodeHTMLStrict; } });
		Object.defineProperty(exports, "decodeXMLStrict", { enumerable: true, get: function () { return decode_js_2.decodeXML; } });
		
	} (lib));
	return lib;
}

var foreignNames = {};

var hasRequiredForeignNames;

function requireForeignNames () {
	if (hasRequiredForeignNames) return foreignNames;
	hasRequiredForeignNames = 1;
	Object.defineProperty(foreignNames, "__esModule", { value: true });
	foreignNames.attributeNames = foreignNames.elementNames = void 0;
	foreignNames.elementNames = new Map([
	    "altGlyph",
	    "altGlyphDef",
	    "altGlyphItem",
	    "animateColor",
	    "animateMotion",
	    "animateTransform",
	    "clipPath",
	    "feBlend",
	    "feColorMatrix",
	    "feComponentTransfer",
	    "feComposite",
	    "feConvolveMatrix",
	    "feDiffuseLighting",
	    "feDisplacementMap",
	    "feDistantLight",
	    "feDropShadow",
	    "feFlood",
	    "feFuncA",
	    "feFuncB",
	    "feFuncG",
	    "feFuncR",
	    "feGaussianBlur",
	    "feImage",
	    "feMerge",
	    "feMergeNode",
	    "feMorphology",
	    "feOffset",
	    "fePointLight",
	    "feSpecularLighting",
	    "feSpotLight",
	    "feTile",
	    "feTurbulence",
	    "foreignObject",
	    "glyphRef",
	    "linearGradient",
	    "radialGradient",
	    "textPath",
	].map(function (val) { return [val.toLowerCase(), val]; }));
	foreignNames.attributeNames = new Map([
	    "definitionURL",
	    "attributeName",
	    "attributeType",
	    "baseFrequency",
	    "baseProfile",
	    "calcMode",
	    "clipPathUnits",
	    "diffuseConstant",
	    "edgeMode",
	    "filterUnits",
	    "glyphRef",
	    "gradientTransform",
	    "gradientUnits",
	    "kernelMatrix",
	    "kernelUnitLength",
	    "keyPoints",
	    "keySplines",
	    "keyTimes",
	    "lengthAdjust",
	    "limitingConeAngle",
	    "markerHeight",
	    "markerUnits",
	    "markerWidth",
	    "maskContentUnits",
	    "maskUnits",
	    "numOctaves",
	    "pathLength",
	    "patternContentUnits",
	    "patternTransform",
	    "patternUnits",
	    "pointsAtX",
	    "pointsAtY",
	    "pointsAtZ",
	    "preserveAlpha",
	    "preserveAspectRatio",
	    "primitiveUnits",
	    "refX",
	    "refY",
	    "repeatCount",
	    "repeatDur",
	    "requiredExtensions",
	    "requiredFeatures",
	    "specularConstant",
	    "specularExponent",
	    "spreadMethod",
	    "startOffset",
	    "stdDeviation",
	    "stitchTiles",
	    "surfaceScale",
	    "systemLanguage",
	    "tableValues",
	    "targetX",
	    "targetY",
	    "textLength",
	    "viewBox",
	    "viewTarget",
	    "xChannelSelector",
	    "yChannelSelector",
	    "zoomAndPan",
	].map(function (val) { return [val.toLowerCase(), val]; }));
	return foreignNames;
}

var hasRequiredLib$2;

function requireLib$2 () {
	if (hasRequiredLib$2) return lib$1;
	hasRequiredLib$2 = 1;
	var __assign = (lib$1 && lib$1.__assign) || function () {
	    __assign = Object.assign || function(t) {
	        for (var s, i = 1, n = arguments.length; i < n; i++) {
	            s = arguments[i];
	            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
	                t[p] = s[p];
	        }
	        return t;
	    };
	    return __assign.apply(this, arguments);
	};
	var __createBinding = (lib$1 && lib$1.__createBinding) || (Object.create ? (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    var desc = Object.getOwnPropertyDescriptor(m, k);
	    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
	      desc = { enumerable: true, get: function() { return m[k]; } };
	    }
	    Object.defineProperty(o, k2, desc);
	}) : (function(o, m, k, k2) {
	    if (k2 === undefined) k2 = k;
	    o[k2] = m[k];
	}));
	var __setModuleDefault = (lib$1 && lib$1.__setModuleDefault) || (Object.create ? (function(o, v) {
	    Object.defineProperty(o, "default", { enumerable: true, value: v });
	}) : function(o, v) {
	    o["default"] = v;
	});
	var __importStar = (lib$1 && lib$1.__importStar) || function (mod) {
	    if (mod && mod.__esModule) return mod;
	    var result = {};
	    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
	    __setModuleDefault(result, mod);
	    return result;
	};
	Object.defineProperty(lib$1, "__esModule", { value: true });
	lib$1.render = void 0;
	/*
	 * Module dependencies
	 */
	var ElementType = __importStar(/*@__PURE__*/ requireLib$5());
	var entities_1 = /*@__PURE__*/ requireLib$3();
	/**
	 * Mixed-case SVG and MathML tags & attributes
	 * recognized by the HTML parser.
	 *
	 * @see https://html.spec.whatwg.org/multipage/parsing.html#parsing-main-inforeign
	 */
	var foreignNames_js_1 = /*@__PURE__*/ requireForeignNames();
	var unencodedElements = new Set([
	    "style",
	    "script",
	    "xmp",
	    "iframe",
	    "noembed",
	    "noframes",
	    "plaintext",
	    "noscript",
	]);
	function replaceQuotes(value) {
	    return value.replace(/"/g, "&quot;");
	}
	/**
	 * Format attributes
	 */
	function formatAttributes(attributes, opts) {
	    var _a;
	    if (!attributes)
	        return;
	    var encode = ((_a = opts.encodeEntities) !== null && _a !== void 0 ? _a : opts.decodeEntities) === false
	        ? replaceQuotes
	        : opts.xmlMode || opts.encodeEntities !== "utf8"
	            ? entities_1.encodeXML
	            : entities_1.escapeAttribute;
	    return Object.keys(attributes)
	        .map(function (key) {
	        var _a, _b;
	        var value = (_a = attributes[key]) !== null && _a !== void 0 ? _a : "";
	        if (opts.xmlMode === "foreign") {
	            /* Fix up mixed-case attribute names */
	            key = (_b = foreignNames_js_1.attributeNames.get(key)) !== null && _b !== void 0 ? _b : key;
	        }
	        if (!opts.emptyAttrs && !opts.xmlMode && value === "") {
	            return key;
	        }
	        return "".concat(key, "=\"").concat(encode(value), "\"");
	    })
	        .join(" ");
	}
	/**
	 * Self-enclosing tags
	 */
	var singleTag = new Set([
	    "area",
	    "base",
	    "basefont",
	    "br",
	    "col",
	    "command",
	    "embed",
	    "frame",
	    "hr",
	    "img",
	    "input",
	    "isindex",
	    "keygen",
	    "link",
	    "meta",
	    "param",
	    "source",
	    "track",
	    "wbr",
	]);
	/**
	 * Renders a DOM node or an array of DOM nodes to a string.
	 *
	 * Can be thought of as the equivalent of the `outerHTML` of the passed node(s).
	 *
	 * @param node Node to be rendered.
	 * @param options Changes serialization behavior
	 */
	function render(node, options) {
	    if (options === void 0) { options = {}; }
	    var nodes = "length" in node ? node : [node];
	    var output = "";
	    for (var i = 0; i < nodes.length; i++) {
	        output += renderNode(nodes[i], options);
	    }
	    return output;
	}
	lib$1.render = render;
	lib$1.default = render;
	function renderNode(node, options) {
	    switch (node.type) {
	        case ElementType.Root:
	            return render(node.children, options);
	        // @ts-expect-error We don't use `Doctype` yet
	        case ElementType.Doctype:
	        case ElementType.Directive:
	            return renderDirective(node);
	        case ElementType.Comment:
	            return renderComment(node);
	        case ElementType.CDATA:
	            return renderCdata(node);
	        case ElementType.Script:
	        case ElementType.Style:
	        case ElementType.Tag:
	            return renderTag(node, options);
	        case ElementType.Text:
	            return renderText(node, options);
	    }
	}
	var foreignModeIntegrationPoints = new Set([
	    "mi",
	    "mo",
	    "mn",
	    "ms",
	    "mtext",
	    "annotation-xml",
	    "foreignObject",
	    "desc",
	    "title",
	]);
	var foreignElements = new Set(["svg", "math"]);
	function renderTag(elem, opts) {
	    var _a;
	    // Handle SVG / MathML in HTML
	    if (opts.xmlMode === "foreign") {
	        /* Fix up mixed-case element names */
	        elem.name = (_a = foreignNames_js_1.elementNames.get(elem.name)) !== null && _a !== void 0 ? _a : elem.name;
	        /* Exit foreign mode at integration points */
	        if (elem.parent &&
	            foreignModeIntegrationPoints.has(elem.parent.name)) {
	            opts = __assign(__assign({}, opts), { xmlMode: false });
	        }
	    }
	    if (!opts.xmlMode && foreignElements.has(elem.name)) {
	        opts = __assign(__assign({}, opts), { xmlMode: "foreign" });
	    }
	    var tag = "<".concat(elem.name);
	    var attribs = formatAttributes(elem.attribs, opts);
	    if (attribs) {
	        tag += " ".concat(attribs);
	    }
	    if (elem.children.length === 0 &&
	        (opts.xmlMode
	            ? // In XML mode or foreign mode, and user hasn't explicitly turned off self-closing tags
	                opts.selfClosingTags !== false
	            : // User explicitly asked for self-closing tags, even in HTML mode
	                opts.selfClosingTags && singleTag.has(elem.name))) {
	        if (!opts.xmlMode)
	            tag += " ";
	        tag += "/>";
	    }
	    else {
	        tag += ">";
	        if (elem.children.length > 0) {
	            tag += render(elem.children, opts);
	        }
	        if (opts.xmlMode || !singleTag.has(elem.name)) {
	            tag += "</".concat(elem.name, ">");
	        }
	    }
	    return tag;
	}
	function renderDirective(elem) {
	    return "<".concat(elem.data, ">");
	}
	function renderText(elem, opts) {
	    var _a;
	    var data = elem.data || "";
	    // If entities weren't decoded, no need to encode them back
	    if (((_a = opts.encodeEntities) !== null && _a !== void 0 ? _a : opts.decodeEntities) !== false &&
	        !(!opts.xmlMode &&
	            elem.parent &&
	            unencodedElements.has(elem.parent.name))) {
	        data =
	            opts.xmlMode || opts.encodeEntities !== "utf8"
	                ? (0, entities_1.encodeXML)(data)
	                : (0, entities_1.escapeText)(data);
	    }
	    return data;
	}
	function renderCdata(elem) {
	    return "<![CDATA[".concat(elem.children[0].data, "]]>");
	}
	function renderComment(elem) {
	    return "<!--".concat(elem.data, "-->");
	}
	return lib$1;
}

var hasRequiredStringify$1;

function requireStringify$1 () {
	if (hasRequiredStringify$1) return stringify;
	hasRequiredStringify$1 = 1;
	var __importDefault = (stringify && stringify.__importDefault) || function (mod) {
	    return (mod && mod.__esModule) ? mod : { "default": mod };
	};
	Object.defineProperty(stringify, "__esModule", { value: true });
	stringify.getOuterHTML = getOuterHTML;
	stringify.getInnerHTML = getInnerHTML;
	stringify.getText = getText;
	stringify.textContent = textContent;
	stringify.innerText = innerText;
	var domhandler_1 = /*@__PURE__*/ requireLib$4();
	var dom_serializer_1 = __importDefault(/*@__PURE__*/ requireLib$2());
	var domelementtype_1 = /*@__PURE__*/ requireLib$5();
	/**
	 * @category Stringify
	 * @deprecated Use the `dom-serializer` module directly.
	 * @param node Node to get the outer HTML of.
	 * @param options Options for serialization.
	 * @returns `node`'s outer HTML.
	 */
	function getOuterHTML(node, options) {
	    return (0, dom_serializer_1.default)(node, options);
	}
	/**
	 * @category Stringify
	 * @deprecated Use the `dom-serializer` module directly.
	 * @param node Node to get the inner HTML of.
	 * @param options Options for serialization.
	 * @returns `node`'s inner HTML.
	 */
	function getInnerHTML(node, options) {
	    return (0, domhandler_1.hasChildren)(node)
	        ? node.children.map(function (node) { return getOuterHTML(node, options); }).join("")
	        : "";
	}
	/**
	 * Get a node's inner text. Same as `textContent`, but inserts newlines for `<br>` tags. Ignores comments.
	 *
	 * @category Stringify
	 * @deprecated Use `textContent` instead.
	 * @param node Node to get the inner text of.
	 * @returns `node`'s inner text.
	 */
	function getText(node) {
	    if (Array.isArray(node))
	        return node.map(getText).join("");
	    if ((0, domhandler_1.isTag)(node))
	        return node.name === "br" ? "\n" : getText(node.children);
	    if ((0, domhandler_1.isCDATA)(node))
	        return getText(node.children);
	    if ((0, domhandler_1.isText)(node))
	        return node.data;
	    return "";
	}
	/**
	 * Get a node's text content. Ignores comments.
	 *
	 * @category Stringify
	 * @param node Node to get the text content of.
	 * @returns `node`'s text content.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Node/textContent}
	 */
	function textContent(node) {
	    if (Array.isArray(node))
	        return node.map(textContent).join("");
	    if ((0, domhandler_1.hasChildren)(node) && !(0, domhandler_1.isComment)(node)) {
	        return textContent(node.children);
	    }
	    if ((0, domhandler_1.isText)(node))
	        return node.data;
	    return "";
	}
	/**
	 * Get a node's inner text, ignoring `<script>` and `<style>` tags. Ignores comments.
	 *
	 * @category Stringify
	 * @param node Node to get the inner text of.
	 * @returns `node`'s inner text.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Node/innerText}
	 */
	function innerText(node) {
	    if (Array.isArray(node))
	        return node.map(innerText).join("");
	    if ((0, domhandler_1.hasChildren)(node) && (node.type === domelementtype_1.ElementType.Tag || (0, domhandler_1.isCDATA)(node))) {
	        return innerText(node.children);
	    }
	    if ((0, domhandler_1.isText)(node))
	        return node.data;
	    return "";
	}
	
	return stringify;
}

var traversal = {};

var hasRequiredTraversal;

function requireTraversal () {
	if (hasRequiredTraversal) return traversal;
	hasRequiredTraversal = 1;
	Object.defineProperty(traversal, "__esModule", { value: true });
	traversal.getChildren = getChildren;
	traversal.getParent = getParent;
	traversal.getSiblings = getSiblings;
	traversal.getAttributeValue = getAttributeValue;
	traversal.hasAttrib = hasAttrib;
	traversal.getName = getName;
	traversal.nextElementSibling = nextElementSibling;
	traversal.prevElementSibling = prevElementSibling;
	var domhandler_1 = /*@__PURE__*/ requireLib$4();
	/**
	 * Get a node's children.
	 *
	 * @category Traversal
	 * @param elem Node to get the children of.
	 * @returns `elem`'s children, or an empty array.
	 */
	function getChildren(elem) {
	    return (0, domhandler_1.hasChildren)(elem) ? elem.children : [];
	}
	/**
	 * Get a node's parent.
	 *
	 * @category Traversal
	 * @param elem Node to get the parent of.
	 * @returns `elem`'s parent node, or `null` if `elem` is a root node.
	 */
	function getParent(elem) {
	    return elem.parent || null;
	}
	/**
	 * Gets an elements siblings, including the element itself.
	 *
	 * Attempts to get the children through the element's parent first. If we don't
	 * have a parent (the element is a root node), we walk the element's `prev` &
	 * `next` to get all remaining nodes.
	 *
	 * @category Traversal
	 * @param elem Element to get the siblings of.
	 * @returns `elem`'s siblings, including `elem`.
	 */
	function getSiblings(elem) {
	    var _a, _b;
	    var parent = getParent(elem);
	    if (parent != null)
	        return getChildren(parent);
	    var siblings = [elem];
	    var prev = elem.prev, next = elem.next;
	    while (prev != null) {
	        siblings.unshift(prev);
	        (_a = prev, prev = _a.prev);
	    }
	    while (next != null) {
	        siblings.push(next);
	        (_b = next, next = _b.next);
	    }
	    return siblings;
	}
	/**
	 * Gets an attribute from an element.
	 *
	 * @category Traversal
	 * @param elem Element to check.
	 * @param name Attribute name to retrieve.
	 * @returns The element's attribute value, or `undefined`.
	 */
	function getAttributeValue(elem, name) {
	    var _a;
	    return (_a = elem.attribs) === null || _a === void 0 ? void 0 : _a[name];
	}
	/**
	 * Checks whether an element has an attribute.
	 *
	 * @category Traversal
	 * @param elem Element to check.
	 * @param name Attribute name to look for.
	 * @returns Returns whether `elem` has the attribute `name`.
	 */
	function hasAttrib(elem, name) {
	    return (elem.attribs != null &&
	        Object.prototype.hasOwnProperty.call(elem.attribs, name) &&
	        elem.attribs[name] != null);
	}
	/**
	 * Get the tag name of an element.
	 *
	 * @category Traversal
	 * @param elem The element to get the name for.
	 * @returns The tag name of `elem`.
	 */
	function getName(elem) {
	    return elem.name;
	}
	/**
	 * Returns the next element sibling of a node.
	 *
	 * @category Traversal
	 * @param elem The element to get the next sibling of.
	 * @returns `elem`'s next sibling that is a tag, or `null` if there is no next
	 * sibling.
	 */
	function nextElementSibling(elem) {
	    var _a;
	    var next = elem.next;
	    while (next !== null && !(0, domhandler_1.isTag)(next))
	        (_a = next, next = _a.next);
	    return next;
	}
	/**
	 * Returns the previous element sibling of a node.
	 *
	 * @category Traversal
	 * @param elem The element to get the previous sibling of.
	 * @returns `elem`'s previous sibling that is a tag, or `null` if there is no
	 * previous sibling.
	 */
	function prevElementSibling(elem) {
	    var _a;
	    var prev = elem.prev;
	    while (prev !== null && !(0, domhandler_1.isTag)(prev))
	        (_a = prev, prev = _a.prev);
	    return prev;
	}
	
	return traversal;
}

var manipulation = {};

var hasRequiredManipulation;

function requireManipulation () {
	if (hasRequiredManipulation) return manipulation;
	hasRequiredManipulation = 1;
	Object.defineProperty(manipulation, "__esModule", { value: true });
	manipulation.removeElement = removeElement;
	manipulation.replaceElement = replaceElement;
	manipulation.appendChild = appendChild;
	manipulation.append = append;
	manipulation.prependChild = prependChild;
	manipulation.prepend = prepend;
	/**
	 * Remove an element from the dom
	 *
	 * @category Manipulation
	 * @param elem The element to be removed
	 */
	function removeElement(elem) {
	    if (elem.prev)
	        elem.prev.next = elem.next;
	    if (elem.next)
	        elem.next.prev = elem.prev;
	    if (elem.parent) {
	        var childs = elem.parent.children;
	        var childsIndex = childs.lastIndexOf(elem);
	        if (childsIndex >= 0) {
	            childs.splice(childsIndex, 1);
	        }
	    }
	    elem.next = null;
	    elem.prev = null;
	    elem.parent = null;
	}
	/**
	 * Replace an element in the dom
	 *
	 * @category Manipulation
	 * @param elem The element to be replaced
	 * @param replacement The element to be added
	 */
	function replaceElement(elem, replacement) {
	    var prev = (replacement.prev = elem.prev);
	    if (prev) {
	        prev.next = replacement;
	    }
	    var next = (replacement.next = elem.next);
	    if (next) {
	        next.prev = replacement;
	    }
	    var parent = (replacement.parent = elem.parent);
	    if (parent) {
	        var childs = parent.children;
	        childs[childs.lastIndexOf(elem)] = replacement;
	        elem.parent = null;
	    }
	}
	/**
	 * Append a child to an element.
	 *
	 * @category Manipulation
	 * @param parent The element to append to.
	 * @param child The element to be added as a child.
	 */
	function appendChild(parent, child) {
	    removeElement(child);
	    child.next = null;
	    child.parent = parent;
	    if (parent.children.push(child) > 1) {
	        var sibling = parent.children[parent.children.length - 2];
	        sibling.next = child;
	        child.prev = sibling;
	    }
	    else {
	        child.prev = null;
	    }
	}
	/**
	 * Append an element after another.
	 *
	 * @category Manipulation
	 * @param elem The element to append after.
	 * @param next The element be added.
	 */
	function append(elem, next) {
	    removeElement(next);
	    var parent = elem.parent;
	    var currNext = elem.next;
	    next.next = currNext;
	    next.prev = elem;
	    elem.next = next;
	    next.parent = parent;
	    if (currNext) {
	        currNext.prev = next;
	        if (parent) {
	            var childs = parent.children;
	            childs.splice(childs.lastIndexOf(currNext), 0, next);
	        }
	    }
	    else if (parent) {
	        parent.children.push(next);
	    }
	}
	/**
	 * Prepend a child to an element.
	 *
	 * @category Manipulation
	 * @param parent The element to prepend before.
	 * @param child The element to be added as a child.
	 */
	function prependChild(parent, child) {
	    removeElement(child);
	    child.parent = parent;
	    child.prev = null;
	    if (parent.children.unshift(child) !== 1) {
	        var sibling = parent.children[1];
	        sibling.prev = child;
	        child.next = sibling;
	    }
	    else {
	        child.next = null;
	    }
	}
	/**
	 * Prepend an element before another.
	 *
	 * @category Manipulation
	 * @param elem The element to prepend before.
	 * @param prev The element be added.
	 */
	function prepend(elem, prev) {
	    removeElement(prev);
	    var parent = elem.parent;
	    if (parent) {
	        var childs = parent.children;
	        childs.splice(childs.indexOf(elem), 0, prev);
	    }
	    if (elem.prev) {
	        elem.prev.next = prev;
	    }
	    prev.parent = parent;
	    prev.prev = elem.prev;
	    prev.next = elem;
	    elem.prev = prev;
	}
	
	return manipulation;
}

var querying = {};

var hasRequiredQuerying;

function requireQuerying () {
	if (hasRequiredQuerying) return querying;
	hasRequiredQuerying = 1;
	Object.defineProperty(querying, "__esModule", { value: true });
	querying.filter = filter;
	querying.find = find;
	querying.findOneChild = findOneChild;
	querying.findOne = findOne;
	querying.existsOne = existsOne;
	querying.findAll = findAll;
	var domhandler_1 = /*@__PURE__*/ requireLib$4();
	/**
	 * Search a node and its children for nodes passing a test function. If `node` is not an array, it will be wrapped in one.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param node Node to search. Will be included in the result set if it matches.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes passing `test`.
	 */
	function filter(test, node, recurse, limit) {
	    if (recurse === void 0) { recurse = true; }
	    if (limit === void 0) { limit = Infinity; }
	    return find(test, Array.isArray(node) ? node : [node], recurse, limit);
	}
	/**
	 * Search an array of nodes and their children for nodes passing a test function.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param nodes Array of nodes to search.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes passing `test`.
	 */
	function find(test, nodes, recurse, limit) {
	    var result = [];
	    /** Stack of the arrays we are looking at. */
	    var nodeStack = [Array.isArray(nodes) ? nodes : [nodes]];
	    /** Stack of the indices within the arrays. */
	    var indexStack = [0];
	    for (;;) {
	        // First, check if the current array has any more elements to look at.
	        if (indexStack[0] >= nodeStack[0].length) {
	            // If we have no more arrays to look at, we are done.
	            if (indexStack.length === 1) {
	                return result;
	            }
	            // Otherwise, remove the current array from the stack.
	            nodeStack.shift();
	            indexStack.shift();
	            // Loop back to the start to continue with the next array.
	            continue;
	        }
	        var elem = nodeStack[0][indexStack[0]++];
	        if (test(elem)) {
	            result.push(elem);
	            if (--limit <= 0)
	                return result;
	        }
	        if (recurse && (0, domhandler_1.hasChildren)(elem) && elem.children.length > 0) {
	            /*
	             * Add the children to the stack. We are depth-first, so this is
	             * the next array we look at.
	             */
	            indexStack.unshift(0);
	            nodeStack.unshift(elem.children);
	        }
	    }
	}
	/**
	 * Finds the first element inside of an array that matches a test function. This is an alias for `Array.prototype.find`.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param nodes Array of nodes to search.
	 * @returns The first node in the array that passes `test`.
	 * @deprecated Use `Array.prototype.find` directly.
	 */
	function findOneChild(test, nodes) {
	    return nodes.find(test);
	}
	/**
	 * Finds one element in a tree that passes a test.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param nodes Node or array of nodes to search.
	 * @param recurse Also consider child nodes.
	 * @returns The first node that passes `test`.
	 */
	function findOne(test, nodes, recurse) {
	    if (recurse === void 0) { recurse = true; }
	    var searchedNodes = Array.isArray(nodes) ? nodes : [nodes];
	    for (var i = 0; i < searchedNodes.length; i++) {
	        var node = searchedNodes[i];
	        if ((0, domhandler_1.isTag)(node) && test(node)) {
	            return node;
	        }
	        if (recurse && (0, domhandler_1.hasChildren)(node) && node.children.length > 0) {
	            var found = findOne(test, node.children, true);
	            if (found)
	                return found;
	        }
	    }
	    return null;
	}
	/**
	 * Checks if a tree of nodes contains at least one node passing a test.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param nodes Array of nodes to search.
	 * @returns Whether a tree of nodes contains at least one node passing the test.
	 */
	function existsOne(test, nodes) {
	    return (Array.isArray(nodes) ? nodes : [nodes]).some(function (node) {
	        return ((0, domhandler_1.isTag)(node) && test(node)) ||
	            ((0, domhandler_1.hasChildren)(node) && existsOne(test, node.children));
	    });
	}
	/**
	 * Search an array of nodes and their children for elements passing a test function.
	 *
	 * Same as `find`, but limited to elements and with less options, leading to reduced complexity.
	 *
	 * @category Querying
	 * @param test Function to test nodes on.
	 * @param nodes Array of nodes to search.
	 * @returns All nodes passing `test`.
	 */
	function findAll(test, nodes) {
	    var result = [];
	    var nodeStack = [Array.isArray(nodes) ? nodes : [nodes]];
	    var indexStack = [0];
	    for (;;) {
	        if (indexStack[0] >= nodeStack[0].length) {
	            if (nodeStack.length === 1) {
	                return result;
	            }
	            // Otherwise, remove the current array from the stack.
	            nodeStack.shift();
	            indexStack.shift();
	            // Loop back to the start to continue with the next array.
	            continue;
	        }
	        var elem = nodeStack[0][indexStack[0]++];
	        if ((0, domhandler_1.isTag)(elem) && test(elem))
	            result.push(elem);
	        if ((0, domhandler_1.hasChildren)(elem) && elem.children.length > 0) {
	            indexStack.unshift(0);
	            nodeStack.unshift(elem.children);
	        }
	    }
	}
	
	return querying;
}

var legacy = {};

var hasRequiredLegacy;

function requireLegacy () {
	if (hasRequiredLegacy) return legacy;
	hasRequiredLegacy = 1;
	Object.defineProperty(legacy, "__esModule", { value: true });
	legacy.testElement = testElement;
	legacy.getElements = getElements;
	legacy.getElementById = getElementById;
	legacy.getElementsByTagName = getElementsByTagName;
	legacy.getElementsByClassName = getElementsByClassName;
	legacy.getElementsByTagType = getElementsByTagType;
	var domhandler_1 = /*@__PURE__*/ requireLib$4();
	var querying_js_1 = /*@__PURE__*/ requireQuerying();
	/**
	 * A map of functions to check nodes against.
	 */
	var Checks = {
	    tag_name: function (name) {
	        if (typeof name === "function") {
	            return function (elem) { return (0, domhandler_1.isTag)(elem) && name(elem.name); };
	        }
	        else if (name === "*") {
	            return domhandler_1.isTag;
	        }
	        return function (elem) { return (0, domhandler_1.isTag)(elem) && elem.name === name; };
	    },
	    tag_type: function (type) {
	        if (typeof type === "function") {
	            return function (elem) { return type(elem.type); };
	        }
	        return function (elem) { return elem.type === type; };
	    },
	    tag_contains: function (data) {
	        if (typeof data === "function") {
	            return function (elem) { return (0, domhandler_1.isText)(elem) && data(elem.data); };
	        }
	        return function (elem) { return (0, domhandler_1.isText)(elem) && elem.data === data; };
	    },
	};
	/**
	 * Returns a function to check whether a node has an attribute with a particular
	 * value.
	 *
	 * @param attrib Attribute to check.
	 * @param value Attribute value to look for.
	 * @returns A function to check whether the a node has an attribute with a
	 *   particular value.
	 */
	function getAttribCheck(attrib, value) {
	    if (typeof value === "function") {
	        return function (elem) { return (0, domhandler_1.isTag)(elem) && value(elem.attribs[attrib]); };
	    }
	    return function (elem) { return (0, domhandler_1.isTag)(elem) && elem.attribs[attrib] === value; };
	}
	/**
	 * Returns a function that returns `true` if either of the input functions
	 * returns `true` for a node.
	 *
	 * @param a First function to combine.
	 * @param b Second function to combine.
	 * @returns A function taking a node and returning `true` if either of the input
	 *   functions returns `true` for the node.
	 */
	function combineFuncs(a, b) {
	    return function (elem) { return a(elem) || b(elem); };
	}
	/**
	 * Returns a function that executes all checks in `options` and returns `true`
	 * if any of them match a node.
	 *
	 * @param options An object describing nodes to look for.
	 * @returns A function that executes all checks in `options` and returns `true`
	 *   if any of them match a node.
	 */
	function compileTest(options) {
	    var funcs = Object.keys(options).map(function (key) {
	        var value = options[key];
	        return Object.prototype.hasOwnProperty.call(Checks, key)
	            ? Checks[key](value)
	            : getAttribCheck(key, value);
	    });
	    return funcs.length === 0 ? null : funcs.reduce(combineFuncs);
	}
	/**
	 * Checks whether a node matches the description in `options`.
	 *
	 * @category Legacy Query Functions
	 * @param options An object describing nodes to look for.
	 * @param node The element to test.
	 * @returns Whether the element matches the description in `options`.
	 */
	function testElement(options, node) {
	    var test = compileTest(options);
	    return test ? test(node) : true;
	}
	/**
	 * Returns all nodes that match `options`.
	 *
	 * @category Legacy Query Functions
	 * @param options An object describing nodes to look for.
	 * @param nodes Nodes to search through.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes that match `options`.
	 */
	function getElements(options, nodes, recurse, limit) {
	    if (limit === void 0) { limit = Infinity; }
	    var test = compileTest(options);
	    return test ? (0, querying_js_1.filter)(test, nodes, recurse, limit) : [];
	}
	/**
	 * Returns the node with the supplied ID.
	 *
	 * @category Legacy Query Functions
	 * @param id The unique ID attribute value to look for.
	 * @param nodes Nodes to search through.
	 * @param recurse Also consider child nodes.
	 * @returns The node with the supplied ID.
	 */
	function getElementById(id, nodes, recurse) {
	    if (recurse === void 0) { recurse = true; }
	    if (!Array.isArray(nodes))
	        nodes = [nodes];
	    return (0, querying_js_1.findOne)(getAttribCheck("id", id), nodes, recurse);
	}
	/**
	 * Returns all nodes with the supplied `tagName`.
	 *
	 * @category Legacy Query Functions
	 * @param tagName Tag name to search for.
	 * @param nodes Nodes to search through.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes with the supplied `tagName`.
	 */
	function getElementsByTagName(tagName, nodes, recurse, limit) {
	    if (recurse === void 0) { recurse = true; }
	    if (limit === void 0) { limit = Infinity; }
	    return (0, querying_js_1.filter)(Checks["tag_name"](tagName), nodes, recurse, limit);
	}
	/**
	 * Returns all nodes with the supplied `className`.
	 *
	 * @category Legacy Query Functions
	 * @param className Class name to search for.
	 * @param nodes Nodes to search through.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes with the supplied `className`.
	 */
	function getElementsByClassName(className, nodes, recurse, limit) {
	    if (recurse === void 0) { recurse = true; }
	    if (limit === void 0) { limit = Infinity; }
	    return (0, querying_js_1.filter)(getAttribCheck("class", className), nodes, recurse, limit);
	}
	/**
	 * Returns all nodes with the supplied `type`.
	 *
	 * @category Legacy Query Functions
	 * @param type Element type to look for.
	 * @param nodes Nodes to search through.
	 * @param recurse Also consider child nodes.
	 * @param limit Maximum number of nodes to return.
	 * @returns All nodes with the supplied `type`.
	 */
	function getElementsByTagType(type, nodes, recurse, limit) {
	    if (recurse === void 0) { recurse = true; }
	    if (limit === void 0) { limit = Infinity; }
	    return (0, querying_js_1.filter)(Checks["tag_type"](type), nodes, recurse, limit);
	}
	
	return legacy;
}

var helpers = {};

var hasRequiredHelpers;

function requireHelpers () {
	if (hasRequiredHelpers) return helpers;
	hasRequiredHelpers = 1;
	Object.defineProperty(helpers, "__esModule", { value: true });
	helpers.DocumentPosition = void 0;
	helpers.removeSubsets = removeSubsets;
	helpers.compareDocumentPosition = compareDocumentPosition;
	helpers.uniqueSort = uniqueSort;
	var domhandler_1 = /*@__PURE__*/ requireLib$4();
	/**
	 * Given an array of nodes, remove any member that is contained by another
	 * member.
	 *
	 * @category Helpers
	 * @param nodes Nodes to filter.
	 * @returns Remaining nodes that aren't contained by other nodes.
	 */
	function removeSubsets(nodes) {
	    var idx = nodes.length;
	    /*
	     * Check if each node (or one of its ancestors) is already contained in the
	     * array.
	     */
	    while (--idx >= 0) {
	        var node = nodes[idx];
	        /*
	         * Remove the node if it is not unique.
	         * We are going through the array from the end, so we only
	         * have to check nodes that preceed the node under consideration in the array.
	         */
	        if (idx > 0 && nodes.lastIndexOf(node, idx - 1) >= 0) {
	            nodes.splice(idx, 1);
	            continue;
	        }
	        for (var ancestor = node.parent; ancestor; ancestor = ancestor.parent) {
	            if (nodes.includes(ancestor)) {
	                nodes.splice(idx, 1);
	                break;
	            }
	        }
	    }
	    return nodes;
	}
	/**
	 * @category Helpers
	 * @see {@link http://dom.spec.whatwg.org/#dom-node-comparedocumentposition}
	 */
	var DocumentPosition;
	(function (DocumentPosition) {
	    DocumentPosition[DocumentPosition["DISCONNECTED"] = 1] = "DISCONNECTED";
	    DocumentPosition[DocumentPosition["PRECEDING"] = 2] = "PRECEDING";
	    DocumentPosition[DocumentPosition["FOLLOWING"] = 4] = "FOLLOWING";
	    DocumentPosition[DocumentPosition["CONTAINS"] = 8] = "CONTAINS";
	    DocumentPosition[DocumentPosition["CONTAINED_BY"] = 16] = "CONTAINED_BY";
	})(DocumentPosition || (helpers.DocumentPosition = DocumentPosition = {}));
	/**
	 * Compare the position of one node against another node in any other document,
	 * returning a bitmask with the values from {@link DocumentPosition}.
	 *
	 * Document order:
	 * > There is an ordering, document order, defined on all the nodes in the
	 * > document corresponding to the order in which the first character of the
	 * > XML representation of each node occurs in the XML representation of the
	 * > document after expansion of general entities. Thus, the document element
	 * > node will be the first node. Element nodes occur before their children.
	 * > Thus, document order orders element nodes in order of the occurrence of
	 * > their start-tag in the XML (after expansion of entities). The attribute
	 * > nodes of an element occur after the element and before its children. The
	 * > relative order of attribute nodes is implementation-dependent.
	 *
	 * Source:
	 * http://www.w3.org/TR/DOM-Level-3-Core/glossary.html#dt-document-order
	 *
	 * @category Helpers
	 * @param nodeA The first node to use in the comparison
	 * @param nodeB The second node to use in the comparison
	 * @returns A bitmask describing the input nodes' relative position.
	 *
	 * See http://dom.spec.whatwg.org/#dom-node-comparedocumentposition for
	 * a description of these values.
	 */
	function compareDocumentPosition(nodeA, nodeB) {
	    var aParents = [];
	    var bParents = [];
	    if (nodeA === nodeB) {
	        return 0;
	    }
	    var current = (0, domhandler_1.hasChildren)(nodeA) ? nodeA : nodeA.parent;
	    while (current) {
	        aParents.unshift(current);
	        current = current.parent;
	    }
	    current = (0, domhandler_1.hasChildren)(nodeB) ? nodeB : nodeB.parent;
	    while (current) {
	        bParents.unshift(current);
	        current = current.parent;
	    }
	    var maxIdx = Math.min(aParents.length, bParents.length);
	    var idx = 0;
	    while (idx < maxIdx && aParents[idx] === bParents[idx]) {
	        idx++;
	    }
	    if (idx === 0) {
	        return DocumentPosition.DISCONNECTED;
	    }
	    var sharedParent = aParents[idx - 1];
	    var siblings = sharedParent.children;
	    var aSibling = aParents[idx];
	    var bSibling = bParents[idx];
	    if (siblings.indexOf(aSibling) > siblings.indexOf(bSibling)) {
	        if (sharedParent === nodeB) {
	            return DocumentPosition.FOLLOWING | DocumentPosition.CONTAINED_BY;
	        }
	        return DocumentPosition.FOLLOWING;
	    }
	    if (sharedParent === nodeA) {
	        return DocumentPosition.PRECEDING | DocumentPosition.CONTAINS;
	    }
	    return DocumentPosition.PRECEDING;
	}
	/**
	 * Sort an array of nodes based on their relative position in the document,
	 * removing any duplicate nodes. If the array contains nodes that do not belong
	 * to the same document, sort order is unspecified.
	 *
	 * @category Helpers
	 * @param nodes Array of DOM nodes.
	 * @returns Collection of unique nodes, sorted in document order.
	 */
	function uniqueSort(nodes) {
	    nodes = nodes.filter(function (node, i, arr) { return !arr.includes(node, i + 1); });
	    nodes.sort(function (a, b) {
	        var relative = compareDocumentPosition(a, b);
	        if (relative & DocumentPosition.PRECEDING) {
	            return -1;
	        }
	        else if (relative & DocumentPosition.FOLLOWING) {
	            return 1;
	        }
	        return 0;
	    });
	    return nodes;
	}
	
	return helpers;
}

var feeds = {};

var hasRequiredFeeds;

function requireFeeds () {
	if (hasRequiredFeeds) return feeds;
	hasRequiredFeeds = 1;
	Object.defineProperty(feeds, "__esModule", { value: true });
	feeds.getFeed = getFeed;
	var stringify_js_1 = /*@__PURE__*/ requireStringify$1();
	var legacy_js_1 = /*@__PURE__*/ requireLegacy();
	/**
	 * Get the feed object from the root of a DOM tree.
	 *
	 * @category Feeds
	 * @param doc - The DOM to to extract the feed from.
	 * @returns The feed.
	 */
	function getFeed(doc) {
	    var feedRoot = getOneElement(isValidFeed, doc);
	    return !feedRoot
	        ? null
	        : feedRoot.name === "feed"
	            ? getAtomFeed(feedRoot)
	            : getRssFeed(feedRoot);
	}
	/**
	 * Parse an Atom feed.
	 *
	 * @param feedRoot The root of the feed.
	 * @returns The parsed feed.
	 */
	function getAtomFeed(feedRoot) {
	    var _a;
	    var childs = feedRoot.children;
	    var feed = {
	        type: "atom",
	        items: (0, legacy_js_1.getElementsByTagName)("entry", childs).map(function (item) {
	            var _a;
	            var children = item.children;
	            var entry = { media: getMediaElements(children) };
	            addConditionally(entry, "id", "id", children);
	            addConditionally(entry, "title", "title", children);
	            var href = (_a = getOneElement("link", children)) === null || _a === void 0 ? void 0 : _a.attribs["href"];
	            if (href) {
	                entry.link = href;
	            }
	            var description = fetch("summary", children) || fetch("content", children);
	            if (description) {
	                entry.description = description;
	            }
	            var pubDate = fetch("updated", children);
	            if (pubDate) {
	                entry.pubDate = new Date(pubDate);
	            }
	            return entry;
	        }),
	    };
	    addConditionally(feed, "id", "id", childs);
	    addConditionally(feed, "title", "title", childs);
	    var href = (_a = getOneElement("link", childs)) === null || _a === void 0 ? void 0 : _a.attribs["href"];
	    if (href) {
	        feed.link = href;
	    }
	    addConditionally(feed, "description", "subtitle", childs);
	    var updated = fetch("updated", childs);
	    if (updated) {
	        feed.updated = new Date(updated);
	    }
	    addConditionally(feed, "author", "email", childs, true);
	    return feed;
	}
	/**
	 * Parse a RSS feed.
	 *
	 * @param feedRoot The root of the feed.
	 * @returns The parsed feed.
	 */
	function getRssFeed(feedRoot) {
	    var _a, _b;
	    var childs = (_b = (_a = getOneElement("channel", feedRoot.children)) === null || _a === void 0 ? void 0 : _a.children) !== null && _b !== void 0 ? _b : [];
	    var feed = {
	        type: feedRoot.name.substr(0, 3),
	        id: "",
	        items: (0, legacy_js_1.getElementsByTagName)("item", feedRoot.children).map(function (item) {
	            var children = item.children;
	            var entry = { media: getMediaElements(children) };
	            addConditionally(entry, "id", "guid", children);
	            addConditionally(entry, "title", "title", children);
	            addConditionally(entry, "link", "link", children);
	            addConditionally(entry, "description", "description", children);
	            var pubDate = fetch("pubDate", children) || fetch("dc:date", children);
	            if (pubDate)
	                entry.pubDate = new Date(pubDate);
	            return entry;
	        }),
	    };
	    addConditionally(feed, "title", "title", childs);
	    addConditionally(feed, "link", "link", childs);
	    addConditionally(feed, "description", "description", childs);
	    var updated = fetch("lastBuildDate", childs);
	    if (updated) {
	        feed.updated = new Date(updated);
	    }
	    addConditionally(feed, "author", "managingEditor", childs, true);
	    return feed;
	}
	var MEDIA_KEYS_STRING = ["url", "type", "lang"];
	var MEDIA_KEYS_INT = [
	    "fileSize",
	    "bitrate",
	    "framerate",
	    "samplingrate",
	    "channels",
	    "duration",
	    "height",
	    "width",
	];
	/**
	 * Get all media elements of a feed item.
	 *
	 * @param where Nodes to search in.
	 * @returns Media elements.
	 */
	function getMediaElements(where) {
	    return (0, legacy_js_1.getElementsByTagName)("media:content", where).map(function (elem) {
	        var attribs = elem.attribs;
	        var media = {
	            medium: attribs["medium"],
	            isDefault: !!attribs["isDefault"],
	        };
	        for (var _i = 0, MEDIA_KEYS_STRING_1 = MEDIA_KEYS_STRING; _i < MEDIA_KEYS_STRING_1.length; _i++) {
	            var attrib = MEDIA_KEYS_STRING_1[_i];
	            if (attribs[attrib]) {
	                media[attrib] = attribs[attrib];
	            }
	        }
	        for (var _a = 0, MEDIA_KEYS_INT_1 = MEDIA_KEYS_INT; _a < MEDIA_KEYS_INT_1.length; _a++) {
	            var attrib = MEDIA_KEYS_INT_1[_a];
	            if (attribs[attrib]) {
	                media[attrib] = parseInt(attribs[attrib], 10);
	            }
	        }
	        if (attribs["expression"]) {
	            media.expression = attribs["expression"];
	        }
	        return media;
	    });
	}
	/**
	 * Get one element by tag name.
	 *
	 * @param tagName Tag name to look for
	 * @param node Node to search in
	 * @returns The element or null
	 */
	function getOneElement(tagName, node) {
	    return (0, legacy_js_1.getElementsByTagName)(tagName, node, true, 1)[0];
	}
	/**
	 * Get the text content of an element with a certain tag name.
	 *
	 * @param tagName Tag name to look for.
	 * @param where Node to search in.
	 * @param recurse Whether to recurse into child nodes.
	 * @returns The text content of the element.
	 */
	function fetch(tagName, where, recurse) {
	    if (recurse === void 0) { recurse = false; }
	    return (0, stringify_js_1.textContent)((0, legacy_js_1.getElementsByTagName)(tagName, where, recurse, 1)).trim();
	}
	/**
	 * Adds a property to an object if it has a value.
	 *
	 * @param obj Object to be extended
	 * @param prop Property name
	 * @param tagName Tag name that contains the conditionally added property
	 * @param where Element to search for the property
	 * @param recurse Whether to recurse into child nodes.
	 */
	function addConditionally(obj, prop, tagName, where, recurse) {
	    if (recurse === void 0) { recurse = false; }
	    var val = fetch(tagName, where, recurse);
	    if (val)
	        obj[prop] = val;
	}
	/**
	 * Checks if an element is a feed root node.
	 *
	 * @param value The name of the element to check.
	 * @returns Whether an element is a feed root node.
	 */
	function isValidFeed(value) {
	    return value === "rss" || value === "feed" || value === "rdf:RDF";
	}
	
	return feeds;
}

var hasRequiredLib$1;

function requireLib$1 () {
	if (hasRequiredLib$1) return lib$2;
	hasRequiredLib$1 = 1;
	(function (exports) {
		var __createBinding = (lib$2 && lib$2.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __exportStar = (lib$2 && lib$2.__exportStar) || function(m, exports) {
		    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.hasChildren = exports.isDocument = exports.isComment = exports.isText = exports.isCDATA = exports.isTag = void 0;
		__exportStar(/*@__PURE__*/ requireStringify$1(), exports);
		__exportStar(/*@__PURE__*/ requireTraversal(), exports);
		__exportStar(/*@__PURE__*/ requireManipulation(), exports);
		__exportStar(/*@__PURE__*/ requireQuerying(), exports);
		__exportStar(/*@__PURE__*/ requireLegacy(), exports);
		__exportStar(/*@__PURE__*/ requireHelpers(), exports);
		__exportStar(/*@__PURE__*/ requireFeeds(), exports);
		/** @deprecated Use these methods from `domhandler` directly. */
		var domhandler_1 = /*@__PURE__*/ requireLib$4();
		Object.defineProperty(exports, "isTag", { enumerable: true, get: function () { return domhandler_1.isTag; } });
		Object.defineProperty(exports, "isCDATA", { enumerable: true, get: function () { return domhandler_1.isCDATA; } });
		Object.defineProperty(exports, "isText", { enumerable: true, get: function () { return domhandler_1.isText; } });
		Object.defineProperty(exports, "isComment", { enumerable: true, get: function () { return domhandler_1.isComment; } });
		Object.defineProperty(exports, "isDocument", { enumerable: true, get: function () { return domhandler_1.isDocument; } });
		Object.defineProperty(exports, "hasChildren", { enumerable: true, get: function () { return domhandler_1.hasChildren; } });
		
	} (lib$2));
	return lib$2;
}

var hasRequiredLib;

function requireLib () {
	if (hasRequiredLib) return lib$5;
	hasRequiredLib = 1;
	(function (exports) {
		var __createBinding = (lib$5 && lib$5.__createBinding) || (Object.create ? (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    var desc = Object.getOwnPropertyDescriptor(m, k);
		    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
		      desc = { enumerable: true, get: function() { return m[k]; } };
		    }
		    Object.defineProperty(o, k2, desc);
		}) : (function(o, m, k, k2) {
		    if (k2 === undefined) k2 = k;
		    o[k2] = m[k];
		}));
		var __setModuleDefault = (lib$5 && lib$5.__setModuleDefault) || (Object.create ? (function(o, v) {
		    Object.defineProperty(o, "default", { enumerable: true, value: v });
		}) : function(o, v) {
		    o["default"] = v;
		});
		var __importStar = (lib$5 && lib$5.__importStar) || function (mod) {
		    if (mod && mod.__esModule) return mod;
		    var result = {};
		    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
		    __setModuleDefault(result, mod);
		    return result;
		};
		var __importDefault = (lib$5 && lib$5.__importDefault) || function (mod) {
		    return (mod && mod.__esModule) ? mod : { "default": mod };
		};
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.DomUtils = exports.parseFeed = exports.getFeed = exports.ElementType = exports.Tokenizer = exports.createDomStream = exports.parseDOM = exports.parseDocument = exports.DefaultHandler = exports.DomHandler = exports.Parser = void 0;
		var Parser_js_1 = /*@__PURE__*/ requireParser$1();
		var Parser_js_2 = /*@__PURE__*/ requireParser$1();
		Object.defineProperty(exports, "Parser", { enumerable: true, get: function () { return Parser_js_2.Parser; } });
		var domhandler_1 = /*@__PURE__*/ requireLib$4();
		var domhandler_2 = /*@__PURE__*/ requireLib$4();
		Object.defineProperty(exports, "DomHandler", { enumerable: true, get: function () { return domhandler_2.DomHandler; } });
		// Old name for DomHandler
		Object.defineProperty(exports, "DefaultHandler", { enumerable: true, get: function () { return domhandler_2.DomHandler; } });
		// Helper methods
		/**
		 * Parses the data, returns the resulting document.
		 *
		 * @param data The data that should be parsed.
		 * @param options Optional options for the parser and DOM builder.
		 */
		function parseDocument(data, options) {
		    var handler = new domhandler_1.DomHandler(undefined, options);
		    new Parser_js_1.Parser(handler, options).end(data);
		    return handler.root;
		}
		exports.parseDocument = parseDocument;
		/**
		 * Parses data, returns an array of the root nodes.
		 *
		 * Note that the root nodes still have a `Document` node as their parent.
		 * Use `parseDocument` to get the `Document` node instead.
		 *
		 * @param data The data that should be parsed.
		 * @param options Optional options for the parser and DOM builder.
		 * @deprecated Use `parseDocument` instead.
		 */
		function parseDOM(data, options) {
		    return parseDocument(data, options).children;
		}
		exports.parseDOM = parseDOM;
		/**
		 * Creates a parser instance, with an attached DOM handler.
		 *
		 * @param callback A callback that will be called once parsing has been completed.
		 * @param options Optional options for the parser and DOM builder.
		 * @param elementCallback An optional callback that will be called every time a tag has been completed inside of the DOM.
		 */
		function createDomStream(callback, options, elementCallback) {
		    var handler = new domhandler_1.DomHandler(callback, options, elementCallback);
		    return new Parser_js_1.Parser(handler, options);
		}
		exports.createDomStream = createDomStream;
		var Tokenizer_js_1 = /*@__PURE__*/ requireTokenizer();
		Object.defineProperty(exports, "Tokenizer", { enumerable: true, get: function () { return __importDefault(Tokenizer_js_1).default; } });
		/*
		 * All of the following exports exist for backwards-compatibility.
		 * They should probably be removed eventually.
		 */
		exports.ElementType = __importStar(/*@__PURE__*/ requireLib$5());
		var domutils_1 = /*@__PURE__*/ requireLib$1();
		var domutils_2 = /*@__PURE__*/ requireLib$1();
		Object.defineProperty(exports, "getFeed", { enumerable: true, get: function () { return domutils_2.getFeed; } });
		var parseFeedDefaultOptions = { xmlMode: true };
		/**
		 * Parse a feed.
		 *
		 * @param feed The feed that should be parsed, as a string.
		 * @param options Optionally, options for parsing. When using this, you should set `xmlMode` to `true`.
		 */
		function parseFeed(feed, options) {
		    if (options === void 0) { options = parseFeedDefaultOptions; }
		    return (0, domutils_1.getFeed)(parseDOM(feed, options));
		}
		exports.parseFeed = parseFeed;
		exports.DomUtils = __importStar(/*@__PURE__*/ requireLib$1());
		
	} (lib$5));
	return lib$5;
}

var escapeStringRegexp;
var hasRequiredEscapeStringRegexp;

function requireEscapeStringRegexp () {
	if (hasRequiredEscapeStringRegexp) return escapeStringRegexp;
	hasRequiredEscapeStringRegexp = 1;

	escapeStringRegexp = string => {
		if (typeof string !== 'string') {
			throw new TypeError('Expected a string');
		}

		// Escape characters with special meaning either inside or outside character sets.
		// Use a simple backslash escape when it’s always valid, and a \unnnn escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
		return string
			.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')
			.replace(/-/g, '\\x2d');
	};
	return escapeStringRegexp;
}

var isPlainObject = {};

var hasRequiredIsPlainObject;

function requireIsPlainObject () {
	if (hasRequiredIsPlainObject) return isPlainObject;
	hasRequiredIsPlainObject = 1;

	Object.defineProperty(isPlainObject, '__esModule', { value: true });

	/*!
	 * is-plain-object <https://github.com/jonschlinkert/is-plain-object>
	 *
	 * Copyright (c) 2014-2017, Jon Schlinkert.
	 * Released under the MIT License.
	 */

	function isObject(o) {
	  return Object.prototype.toString.call(o) === '[object Object]';
	}

	function isPlainObject$1(o) {
	  var ctor,prot;

	  if (isObject(o) === false) return false;

	  // If has modified constructor
	  ctor = o.constructor;
	  if (ctor === undefined) return true;

	  // If has modified prototype
	  prot = ctor.prototype;
	  if (isObject(prot) === false) return false;

	  // If constructor does not have an Object-specific method
	  if (prot.hasOwnProperty('isPrototypeOf') === false) {
	    return false;
	  }

	  // Most likely a plain Object
	  return true;
	}

	isPlainObject.isPlainObject = isPlainObject$1;
	return isPlainObject;
}

var cjs;
var hasRequiredCjs;

function requireCjs () {
	if (hasRequiredCjs) return cjs;
	hasRequiredCjs = 1;

	var isMergeableObject = function isMergeableObject(value) {
		return isNonNullObject(value)
			&& !isSpecial(value)
	};

	function isNonNullObject(value) {
		return !!value && typeof value === 'object'
	}

	function isSpecial(value) {
		var stringValue = Object.prototype.toString.call(value);

		return stringValue === '[object RegExp]'
			|| stringValue === '[object Date]'
			|| isReactElement(value)
	}

	// see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
	var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

	function isReactElement(value) {
		return value.$$typeof === REACT_ELEMENT_TYPE
	}

	function emptyTarget(val) {
		return Array.isArray(val) ? [] : {}
	}

	function cloneUnlessOtherwiseSpecified(value, options) {
		return (options.clone !== false && options.isMergeableObject(value))
			? deepmerge(emptyTarget(value), value, options)
			: value
	}

	function defaultArrayMerge(target, source, options) {
		return target.concat(source).map(function(element) {
			return cloneUnlessOtherwiseSpecified(element, options)
		})
	}

	function getMergeFunction(key, options) {
		if (!options.customMerge) {
			return deepmerge
		}
		var customMerge = options.customMerge(key);
		return typeof customMerge === 'function' ? customMerge : deepmerge
	}

	function getEnumerableOwnPropertySymbols(target) {
		return Object.getOwnPropertySymbols
			? Object.getOwnPropertySymbols(target).filter(function(symbol) {
				return Object.propertyIsEnumerable.call(target, symbol)
			})
			: []
	}

	function getKeys(target) {
		return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target))
	}

	function propertyIsOnObject(object, property) {
		try {
			return property in object
		} catch(_) {
			return false
		}
	}

	// Protects from prototype poisoning and unexpected merging up the prototype chain.
	function propertyIsUnsafe(target, key) {
		return propertyIsOnObject(target, key) // Properties are safe to merge if they don't exist in the target yet,
			&& !(Object.hasOwnProperty.call(target, key) // unsafe if they exist up the prototype chain,
				&& Object.propertyIsEnumerable.call(target, key)) // and also unsafe if they're nonenumerable.
	}

	function mergeObject(target, source, options) {
		var destination = {};
		if (options.isMergeableObject(target)) {
			getKeys(target).forEach(function(key) {
				destination[key] = cloneUnlessOtherwiseSpecified(target[key], options);
			});
		}
		getKeys(source).forEach(function(key) {
			if (propertyIsUnsafe(target, key)) {
				return
			}

			if (propertyIsOnObject(target, key) && options.isMergeableObject(source[key])) {
				destination[key] = getMergeFunction(key, options)(target[key], source[key], options);
			} else {
				destination[key] = cloneUnlessOtherwiseSpecified(source[key], options);
			}
		});
		return destination
	}

	function deepmerge(target, source, options) {
		options = options || {};
		options.arrayMerge = options.arrayMerge || defaultArrayMerge;
		options.isMergeableObject = options.isMergeableObject || isMergeableObject;
		// cloneUnlessOtherwiseSpecified is added to `options` so that custom arrayMerge()
		// implementations can use it. The caller may not replace it.
		options.cloneUnlessOtherwiseSpecified = cloneUnlessOtherwiseSpecified;

		var sourceIsArray = Array.isArray(source);
		var targetIsArray = Array.isArray(target);
		var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

		if (!sourceAndTargetTypesMatch) {
			return cloneUnlessOtherwiseSpecified(source, options)
		} else if (sourceIsArray) {
			return options.arrayMerge(target, source, options)
		} else {
			return mergeObject(target, source, options)
		}
	}

	deepmerge.all = function deepmergeAll(array, options) {
		if (!Array.isArray(array)) {
			throw new Error('first argument should be an array')
		}

		return array.reduce(function(prev, next) {
			return deepmerge(prev, next, options)
		}, {})
	};

	var deepmerge_1 = deepmerge;

	cjs = deepmerge_1;
	return cjs;
}

var parseSrcset$1 = {exports: {}};

/**
 * Srcset Parser
 *
 * By Alex Bell |  MIT License
 *
 * JS Parser for the string value that appears in markup <img srcset="here">
 *
 * @returns Array [{url: _, d: _, w: _, h:_}, ...]
 *
 * Based super duper closely on the reference algorithm at:
 * https://html.spec.whatwg.org/multipage/embedded-content.html#parse-a-srcset-attribute
 *
 * Most comments are copied in directly from the spec
 * (except for comments in parens).
 */
var parseSrcset = parseSrcset$1.exports;

var hasRequiredParseSrcset;

function requireParseSrcset () {
	if (hasRequiredParseSrcset) return parseSrcset$1.exports;
	hasRequiredParseSrcset = 1;
	(function (module) {
		(function (root, factory) {
			if (module.exports) {
				// Node. Does not work with strict CommonJS, but
				// only CommonJS-like environments that support module.exports,
				// like Node.
				module.exports = factory();
			} else {
				// Browser globals (root is window)
				root.parseSrcset = factory();
			}
		}(parseSrcset, function () {

			// 1. Let input be the value passed to this algorithm.
			return function (input) {

				// UTILITY FUNCTIONS

				// Manual is faster than RegEx
				// http://bjorn.tipling.com/state-and-regular-expressions-in-javascript
				// http://jsperf.com/whitespace-character/5
				function isSpace(c) {
					return (c === "\u0020" || // space
					c === "\u0009" || // horizontal tab
					c === "\u000A" || // new line
					c === "\u000C" || // form feed
					c === "\u000D");  // carriage return
				}

				function collectCharacters(regEx) {
					var chars,
						match = regEx.exec(input.substring(pos));
					if (match) {
						chars = match[ 0 ];
						pos += chars.length;
						return chars;
					}
				}

				var inputLength = input.length,

					// (Don't use \s, to avoid matching non-breaking space)
					regexLeadingSpaces = /^[ \t\n\r\u000c]+/,
					regexLeadingCommasOrSpaces = /^[, \t\n\r\u000c]+/,
					regexLeadingNotSpaces = /^[^ \t\n\r\u000c]+/,
					regexTrailingCommas = /[,]+$/,
					regexNonNegativeInteger = /^\d+$/,

					// ( Positive or negative or unsigned integers or decimals, without or without exponents.
					// Must include at least one digit.
					// According to spec tests any decimal point must be followed by a digit.
					// No leading plus sign is allowed.)
					// https://html.spec.whatwg.org/multipage/infrastructure.html#valid-floating-point-number
					regexFloatingPoint = /^-?(?:[0-9]+|[0-9]*\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/,

					url,
					descriptors,
					currentDescriptor,
					state,
					c,

					// 2. Let position be a pointer into input, initially pointing at the start
					//    of the string.
					pos = 0,

					// 3. Let candidates be an initially empty source set.
					candidates = [];

				// 4. Splitting loop: Collect a sequence of characters that are space
				//    characters or U+002C COMMA characters. If any U+002C COMMA characters
				//    were collected, that is a parse error.
				while (true) {
					collectCharacters(regexLeadingCommasOrSpaces);

					// 5. If position is past the end of input, return candidates and abort these steps.
					if (pos >= inputLength) {
						return candidates; // (we're done, this is the sole return path)
					}

					// 6. Collect a sequence of characters that are not space characters,
					//    and let that be url.
					url = collectCharacters(regexLeadingNotSpaces);

					// 7. Let descriptors be a new empty list.
					descriptors = [];

					// 8. If url ends with a U+002C COMMA character (,), follow these substeps:
					//		(1). Remove all trailing U+002C COMMA characters from url. If this removed
					//         more than one character, that is a parse error.
					if (url.slice(-1) === ",") {
						url = url.replace(regexTrailingCommas, "");
						// (Jump ahead to step 9 to skip tokenization and just push the candidate).
						parseDescriptors();

						//	Otherwise, follow these substeps:
					} else {
						tokenize();
					} // (close else of step 8)

					// 16. Return to the step labeled splitting loop.
				} // (Close of big while loop.)

				/**
				 * Tokenizes descriptor properties prior to parsing
				 * Returns undefined.
				 */
				function tokenize() {

					// 8.1. Descriptor tokeniser: Skip whitespace
					collectCharacters(regexLeadingSpaces);

					// 8.2. Let current descriptor be the empty string.
					currentDescriptor = "";

					// 8.3. Let state be in descriptor.
					state = "in descriptor";

					while (true) {

						// 8.4. Let c be the character at position.
						c = input.charAt(pos);

						//  Do the following depending on the value of state.
						//  For the purpose of this step, "EOF" is a special character representing
						//  that position is past the end of input.

						// In descriptor
						if (state === "in descriptor") {
							// Do the following, depending on the value of c:

							// Space character
							// If current descriptor is not empty, append current descriptor to
							// descriptors and let current descriptor be the empty string.
							// Set state to after descriptor.
							if (isSpace(c)) {
								if (currentDescriptor) {
									descriptors.push(currentDescriptor);
									currentDescriptor = "";
									state = "after descriptor";
								}

								// U+002C COMMA (,)
								// Advance position to the next character in input. If current descriptor
								// is not empty, append current descriptor to descriptors. Jump to the step
								// labeled descriptor parser.
							} else if (c === ",") {
								pos += 1;
								if (currentDescriptor) {
									descriptors.push(currentDescriptor);
								}
								parseDescriptors();
								return;

								// U+0028 LEFT PARENTHESIS (()
								// Append c to current descriptor. Set state to in parens.
							} else if (c === "\u0028") {
								currentDescriptor = currentDescriptor + c;
								state = "in parens";

								// EOF
								// If current descriptor is not empty, append current descriptor to
								// descriptors. Jump to the step labeled descriptor parser.
							} else if (c === "") {
								if (currentDescriptor) {
									descriptors.push(currentDescriptor);
								}
								parseDescriptors();
								return;

								// Anything else
								// Append c to current descriptor.
							} else {
								currentDescriptor = currentDescriptor + c;
							}
							// (end "in descriptor"

							// In parens
						} else if (state === "in parens") {

							// U+0029 RIGHT PARENTHESIS ())
							// Append c to current descriptor. Set state to in descriptor.
							if (c === ")") {
								currentDescriptor = currentDescriptor + c;
								state = "in descriptor";

								// EOF
								// Append current descriptor to descriptors. Jump to the step labeled
								// descriptor parser.
							} else if (c === "") {
								descriptors.push(currentDescriptor);
								parseDescriptors();
								return;

								// Anything else
								// Append c to current descriptor.
							} else {
								currentDescriptor = currentDescriptor + c;
							}

							// After descriptor
						} else if (state === "after descriptor") {

							// Do the following, depending on the value of c:
							// Space character: Stay in this state.
							if (isSpace(c)) ; else if (c === "") {
								parseDescriptors();
								return;

								// Anything else
								// Set state to in descriptor. Set position to the previous character in input.
							} else {
								state = "in descriptor";
								pos -= 1;

							}
						}

						// Advance position to the next character in input.
						pos += 1;

						// Repeat this step.
					} // (close while true loop)
				}

				/**
				 * Adds descriptor properties to a candidate, pushes to the candidates array
				 * @return undefined
				 */
				// Declared outside of the while loop so that it's only created once.
				function parseDescriptors() {

					// 9. Descriptor parser: Let error be no.
					var pError = false,

						// 10. Let width be absent.
						// 11. Let density be absent.
						// 12. Let future-compat-h be absent. (We're implementing it now as h)
						w, d, h, i,
						candidate = {},
						desc, lastChar, value, intVal, floatVal;

					// 13. For each descriptor in descriptors, run the appropriate set of steps
					// from the following list:
					for (i = 0 ; i < descriptors.length; i++) {
						desc = descriptors[ i ];

						lastChar = desc[ desc.length - 1 ];
						value = desc.substring(0, desc.length - 1);
						intVal = parseInt(value, 10);
						floatVal = parseFloat(value);

						// If the descriptor consists of a valid non-negative integer followed by
						// a U+0077 LATIN SMALL LETTER W character
						if (regexNonNegativeInteger.test(value) && (lastChar === "w")) {

							// If width and density are not both absent, then let error be yes.
							if (w || d) {pError = true;}

							// Apply the rules for parsing non-negative integers to the descriptor.
							// If the result is zero, let error be yes.
							// Otherwise, let width be the result.
							if (intVal === 0) {pError = true;} else {w = intVal;}

							// If the descriptor consists of a valid floating-point number followed by
							// a U+0078 LATIN SMALL LETTER X character
						} else if (regexFloatingPoint.test(value) && (lastChar === "x")) {

							// If width, density and future-compat-h are not all absent, then let error
							// be yes.
							if (w || d || h) {pError = true;}

							// Apply the rules for parsing floating-point number values to the descriptor.
							// If the result is less than zero, let error be yes. Otherwise, let density
							// be the result.
							if (floatVal < 0) {pError = true;} else {d = floatVal;}

							// If the descriptor consists of a valid non-negative integer followed by
							// a U+0068 LATIN SMALL LETTER H character
						} else if (regexNonNegativeInteger.test(value) && (lastChar === "h")) {

							// If height and density are not both absent, then let error be yes.
							if (h || d) {pError = true;}

							// Apply the rules for parsing non-negative integers to the descriptor.
							// If the result is zero, let error be yes. Otherwise, let future-compat-h
							// be the result.
							if (intVal === 0) {pError = true;} else {h = intVal;}

							// Anything else, Let error be yes.
						} else {pError = true;}
					} // (close step 13 for loop)

					// 15. If error is still no, then append a new image source to candidates whose
					// URL is url, associated with a width width if not absent and a pixel
					// density density if not absent. Otherwise, there is a parse error.
					if (!pError) {
						candidate.url = url;
						if (w) { candidate.w = w;}
						if (d) { candidate.d = d;}
						if (h) { candidate.h = h;}
						candidates.push(candidate);
					} else if (console && console.log) {
						console.log("Invalid srcset descriptor found in '" +
							input + "' at '" + desc + "'.");
					}
				} // (close parseDescriptors fn)

			}
		})); 
	} (parseSrcset$1));
	return parseSrcset$1.exports;
}

var picocolors_browser = {exports: {}};

var hasRequiredPicocolors_browser;

function requirePicocolors_browser () {
	if (hasRequiredPicocolors_browser) return picocolors_browser.exports;
	hasRequiredPicocolors_browser = 1;
	var x=String;
	var create=function() {return {isColorSupported:false,reset:x,bold:x,dim:x,italic:x,underline:x,inverse:x,hidden:x,strikethrough:x,black:x,red:x,green:x,yellow:x,blue:x,magenta:x,cyan:x,white:x,gray:x,bgBlack:x,bgRed:x,bgGreen:x,bgYellow:x,bgBlue:x,bgMagenta:x,bgCyan:x,bgWhite:x,blackBright:x,redBright:x,greenBright:x,yellowBright:x,blueBright:x,magentaBright:x,cyanBright:x,whiteBright:x,bgBlackBright:x,bgRedBright:x,bgGreenBright:x,bgYellowBright:x,bgBlueBright:x,bgMagentaBright:x,bgCyanBright:x,bgWhiteBright:x}};
	picocolors_browser.exports=create();
	picocolors_browser.exports.createColors = create;
	return picocolors_browser.exports;
}

const __viteBrowserExternal = {};

const __viteBrowserExternal$1 = /*#__PURE__*/Object.freeze(/*#__PURE__*/Object.defineProperty({
    __proto__: null,
    default: __viteBrowserExternal
}, Symbol.toStringTag, { value: 'Module' }));

const require$$2 = /*@__PURE__*/getAugmentedNamespace(__viteBrowserExternal$1);

var cssSyntaxError;
var hasRequiredCssSyntaxError;

function requireCssSyntaxError () {
	if (hasRequiredCssSyntaxError) return cssSyntaxError;
	hasRequiredCssSyntaxError = 1;

	let pico = /*@__PURE__*/ requirePicocolors_browser();

	let terminalHighlight = require$$2;

	class CssSyntaxError extends Error {
	  constructor(message, line, column, source, file, plugin) {
	    super(message);
	    this.name = 'CssSyntaxError';
	    this.reason = message;

	    if (file) {
	      this.file = file;
	    }
	    if (source) {
	      this.source = source;
	    }
	    if (plugin) {
	      this.plugin = plugin;
	    }
	    if (typeof line !== 'undefined' && typeof column !== 'undefined') {
	      if (typeof line === 'number') {
	        this.line = line;
	        this.column = column;
	      } else {
	        this.line = line.line;
	        this.column = line.column;
	        this.endLine = column.line;
	        this.endColumn = column.column;
	      }
	    }

	    this.setMessage();

	    if (Error.captureStackTrace) {
	      Error.captureStackTrace(this, CssSyntaxError);
	    }
	  }

	  setMessage() {
	    this.message = this.plugin ? this.plugin + ': ' : '';
	    this.message += this.file ? this.file : '<css input>';
	    if (typeof this.line !== 'undefined') {
	      this.message += ':' + this.line + ':' + this.column;
	    }
	    this.message += ': ' + this.reason;
	  }

	  showSourceCode(color) {
	    if (!this.source) return ''

	    let css = this.source;
	    if (color == null) color = pico.isColorSupported;

	    let aside = text => text;
	    let mark = text => text;
	    let highlight = text => text;
	    if (color) {
	      let { bold, gray, red } = pico.createColors(true);
	      mark = text => bold(red(text));
	      aside = text => gray(text);
	      if (terminalHighlight) {
	        highlight = text => terminalHighlight(text);
	      }
	    }

	    let lines = css.split(/\r?\n/);
	    let start = Math.max(this.line - 3, 0);
	    let end = Math.min(this.line + 2, lines.length);
	    let maxWidth = String(end).length;

	    return lines
	      .slice(start, end)
	      .map((line, index) => {
	        let number = start + 1 + index;
	        let gutter = ' ' + (' ' + number).slice(-maxWidth) + ' | ';
	        if (number === this.line) {
	          if (line.length > 160) {
	            let padding = 20;
	            let subLineStart = Math.max(0, this.column - padding);
	            let subLineEnd = Math.max(
	              this.column + padding,
	              this.endColumn + padding
	            );
	            let subLine = line.slice(subLineStart, subLineEnd);

	            let spacing =
	              aside(gutter.replace(/\d/g, ' ')) +
	              line
	                .slice(0, Math.min(this.column - 1, padding - 1))
	                .replace(/[^\t]/g, ' ');

	            return (
	              mark('>') +
	              aside(gutter) +
	              highlight(subLine) +
	              '\n ' +
	              spacing +
	              mark('^')
	            )
	          }

	          let spacing =
	            aside(gutter.replace(/\d/g, ' ')) +
	            line.slice(0, this.column - 1).replace(/[^\t]/g, ' ');

	          return (
	            mark('>') +
	            aside(gutter) +
	            highlight(line) +
	            '\n ' +
	            spacing +
	            mark('^')
	          )
	        }

	        return ' ' + aside(gutter) + highlight(line)
	      })
	      .join('\n')
	  }

	  toString() {
	    let code = this.showSourceCode();
	    if (code) {
	      code = '\n\n' + code + '\n';
	    }
	    return this.name + ': ' + this.message + code
	  }
	}

	cssSyntaxError = CssSyntaxError;
	CssSyntaxError.default = CssSyntaxError;
	return cssSyntaxError;
}

var stringifier;
var hasRequiredStringifier;

function requireStringifier () {
	if (hasRequiredStringifier) return stringifier;
	hasRequiredStringifier = 1;

	const DEFAULT_RAW = {
	  after: '\n',
	  beforeClose: '\n',
	  beforeComment: '\n',
	  beforeDecl: '\n',
	  beforeOpen: ' ',
	  beforeRule: '\n',
	  colon: ': ',
	  commentLeft: ' ',
	  commentRight: ' ',
	  emptyBody: '',
	  indent: '    ',
	  semicolon: false
	};

	function capitalize(str) {
	  return str[0].toUpperCase() + str.slice(1)
	}

	class Stringifier {
	  constructor(builder) {
	    this.builder = builder;
	  }

	  atrule(node, semicolon) {
	    let name = '@' + node.name;
	    let params = node.params ? this.rawValue(node, 'params') : '';

	    if (typeof node.raws.afterName !== 'undefined') {
	      name += node.raws.afterName;
	    } else if (params) {
	      name += ' ';
	    }

	    if (node.nodes) {
	      this.block(node, name + params);
	    } else {
	      let end = (node.raws.between || '') + (semicolon ? ';' : '');
	      this.builder(name + params + end, node);
	    }
	  }

	  beforeAfter(node, detect) {
	    let value;
	    if (node.type === 'decl') {
	      value = this.raw(node, null, 'beforeDecl');
	    } else if (node.type === 'comment') {
	      value = this.raw(node, null, 'beforeComment');
	    } else if (detect === 'before') {
	      value = this.raw(node, null, 'beforeRule');
	    } else {
	      value = this.raw(node, null, 'beforeClose');
	    }

	    let buf = node.parent;
	    let depth = 0;
	    while (buf && buf.type !== 'root') {
	      depth += 1;
	      buf = buf.parent;
	    }

	    if (value.includes('\n')) {
	      let indent = this.raw(node, null, 'indent');
	      if (indent.length) {
	        for (let step = 0; step < depth; step++) value += indent;
	      }
	    }

	    return value
	  }

	  block(node, start) {
	    let between = this.raw(node, 'between', 'beforeOpen');
	    this.builder(start + between + '{', node, 'start');

	    let after;
	    if (node.nodes && node.nodes.length) {
	      this.body(node);
	      after = this.raw(node, 'after');
	    } else {
	      after = this.raw(node, 'after', 'emptyBody');
	    }

	    if (after) this.builder(after);
	    this.builder('}', node, 'end');
	  }

	  body(node) {
	    let last = node.nodes.length - 1;
	    while (last > 0) {
	      if (node.nodes[last].type !== 'comment') break
	      last -= 1;
	    }

	    let semicolon = this.raw(node, 'semicolon');
	    for (let i = 0; i < node.nodes.length; i++) {
	      let child = node.nodes[i];
	      let before = this.raw(child, 'before');
	      if (before) this.builder(before);
	      this.stringify(child, last !== i || semicolon);
	    }
	  }

	  comment(node) {
	    let left = this.raw(node, 'left', 'commentLeft');
	    let right = this.raw(node, 'right', 'commentRight');
	    this.builder('/*' + left + node.text + right + '*/', node);
	  }

	  decl(node, semicolon) {
	    let between = this.raw(node, 'between', 'colon');
	    let string = node.prop + between + this.rawValue(node, 'value');

	    if (node.important) {
	      string += node.raws.important || ' !important';
	    }

	    if (semicolon) string += ';';
	    this.builder(string, node);
	  }

	  document(node) {
	    this.body(node);
	  }

	  raw(node, own, detect) {
	    let value;
	    if (!detect) detect = own;

	    // Already had
	    if (own) {
	      value = node.raws[own];
	      if (typeof value !== 'undefined') return value
	    }

	    let parent = node.parent;

	    if (detect === 'before') {
	      // Hack for first rule in CSS
	      if (!parent || (parent.type === 'root' && parent.first === node)) {
	        return ''
	      }

	      // `root` nodes in `document` should use only their own raws
	      if (parent && parent.type === 'document') {
	        return ''
	      }
	    }

	    // Floating child without parent
	    if (!parent) return DEFAULT_RAW[detect]

	    // Detect style by other nodes
	    let root = node.root();
	    if (!root.rawCache) root.rawCache = {};
	    if (typeof root.rawCache[detect] !== 'undefined') {
	      return root.rawCache[detect]
	    }

	    if (detect === 'before' || detect === 'after') {
	      return this.beforeAfter(node, detect)
	    } else {
	      let method = 'raw' + capitalize(detect);
	      if (this[method]) {
	        value = this[method](root, node);
	      } else {
	        root.walk(i => {
	          value = i.raws[own];
	          if (typeof value !== 'undefined') return false
	        });
	      }
	    }

	    if (typeof value === 'undefined') value = DEFAULT_RAW[detect];

	    root.rawCache[detect] = value;
	    return value
	  }

	  rawBeforeClose(root) {
	    let value;
	    root.walk(i => {
	      if (i.nodes && i.nodes.length > 0) {
	        if (typeof i.raws.after !== 'undefined') {
	          value = i.raws.after;
	          if (value.includes('\n')) {
	            value = value.replace(/[^\n]+$/, '');
	          }
	          return false
	        }
	      }
	    });
	    if (value) value = value.replace(/\S/g, '');
	    return value
	  }

	  rawBeforeComment(root, node) {
	    let value;
	    root.walkComments(i => {
	      if (typeof i.raws.before !== 'undefined') {
	        value = i.raws.before;
	        if (value.includes('\n')) {
	          value = value.replace(/[^\n]+$/, '');
	        }
	        return false
	      }
	    });
	    if (typeof value === 'undefined') {
	      value = this.raw(node, null, 'beforeDecl');
	    } else if (value) {
	      value = value.replace(/\S/g, '');
	    }
	    return value
	  }

	  rawBeforeDecl(root, node) {
	    let value;
	    root.walkDecls(i => {
	      if (typeof i.raws.before !== 'undefined') {
	        value = i.raws.before;
	        if (value.includes('\n')) {
	          value = value.replace(/[^\n]+$/, '');
	        }
	        return false
	      }
	    });
	    if (typeof value === 'undefined') {
	      value = this.raw(node, null, 'beforeRule');
	    } else if (value) {
	      value = value.replace(/\S/g, '');
	    }
	    return value
	  }

	  rawBeforeOpen(root) {
	    let value;
	    root.walk(i => {
	      if (i.type !== 'decl') {
	        value = i.raws.between;
	        if (typeof value !== 'undefined') return false
	      }
	    });
	    return value
	  }

	  rawBeforeRule(root) {
	    let value;
	    root.walk(i => {
	      if (i.nodes && (i.parent !== root || root.first !== i)) {
	        if (typeof i.raws.before !== 'undefined') {
	          value = i.raws.before;
	          if (value.includes('\n')) {
	            value = value.replace(/[^\n]+$/, '');
	          }
	          return false
	        }
	      }
	    });
	    if (value) value = value.replace(/\S/g, '');
	    return value
	  }

	  rawColon(root) {
	    let value;
	    root.walkDecls(i => {
	      if (typeof i.raws.between !== 'undefined') {
	        value = i.raws.between.replace(/[^\s:]/g, '');
	        return false
	      }
	    });
	    return value
	  }

	  rawEmptyBody(root) {
	    let value;
	    root.walk(i => {
	      if (i.nodes && i.nodes.length === 0) {
	        value = i.raws.after;
	        if (typeof value !== 'undefined') return false
	      }
	    });
	    return value
	  }

	  rawIndent(root) {
	    if (root.raws.indent) return root.raws.indent
	    let value;
	    root.walk(i => {
	      let p = i.parent;
	      if (p && p !== root && p.parent && p.parent === root) {
	        if (typeof i.raws.before !== 'undefined') {
	          let parts = i.raws.before.split('\n');
	          value = parts[parts.length - 1];
	          value = value.replace(/\S/g, '');
	          return false
	        }
	      }
	    });
	    return value
	  }

	  rawSemicolon(root) {
	    let value;
	    root.walk(i => {
	      if (i.nodes && i.nodes.length && i.last.type === 'decl') {
	        value = i.raws.semicolon;
	        if (typeof value !== 'undefined') return false
	      }
	    });
	    return value
	  }

	  rawValue(node, prop) {
	    let value = node[prop];
	    let raw = node.raws[prop];
	    if (raw && raw.value === value) {
	      return raw.raw
	    }

	    return value
	  }

	  root(node) {
	    this.body(node);
	    if (node.raws.after) this.builder(node.raws.after);
	  }

	  rule(node) {
	    this.block(node, this.rawValue(node, 'selector'));
	    if (node.raws.ownSemicolon) {
	      this.builder(node.raws.ownSemicolon, node, 'end');
	    }
	  }

	  stringify(node, semicolon) {
	    /* c8 ignore start */
	    if (!this[node.type]) {
	      throw new Error(
	        'Unknown AST node type ' +
	          node.type +
	          '. ' +
	          'Maybe you need to change PostCSS stringifier.'
	      )
	    }
	    /* c8 ignore stop */
	    this[node.type](node, semicolon);
	  }
	}

	stringifier = Stringifier;
	Stringifier.default = Stringifier;
	return stringifier;
}

var stringify_1;
var hasRequiredStringify;

function requireStringify () {
	if (hasRequiredStringify) return stringify_1;
	hasRequiredStringify = 1;

	let Stringifier = requireStringifier();

	function stringify(node, builder) {
	  let str = new Stringifier(builder);
	  str.stringify(node);
	}

	stringify_1 = stringify;
	stringify.default = stringify;
	return stringify_1;
}

var symbols = {};

var hasRequiredSymbols;

function requireSymbols () {
	if (hasRequiredSymbols) return symbols;
	hasRequiredSymbols = 1;

	symbols.isClean = Symbol('isClean');

	symbols.my = Symbol('my');
	return symbols;
}

var node;
var hasRequiredNode;

function requireNode () {
	if (hasRequiredNode) return node;
	hasRequiredNode = 1;

	let CssSyntaxError = requireCssSyntaxError();
	let Stringifier = requireStringifier();
	let stringify = requireStringify();
	let { isClean, my } = requireSymbols();

	function cloneNode(obj, parent) {
	  let cloned = new obj.constructor();

	  for (let i in obj) {
	    if (!Object.prototype.hasOwnProperty.call(obj, i)) {
	      /* c8 ignore next 2 */
	      continue
	    }
	    if (i === 'proxyCache') continue
	    let value = obj[i];
	    let type = typeof value;

	    if (i === 'parent' && type === 'object') {
	      if (parent) cloned[i] = parent;
	    } else if (i === 'source') {
	      cloned[i] = value;
	    } else if (Array.isArray(value)) {
	      cloned[i] = value.map(j => cloneNode(j, cloned));
	    } else {
	      if (type === 'object' && value !== null) value = cloneNode(value);
	      cloned[i] = value;
	    }
	  }

	  return cloned
	}

	function sourceOffset(inputCSS, position) {
	  // Not all custom syntaxes support `offset` in `source.start` and `source.end`
	  if (
	    position &&
	    typeof position.offset !== 'undefined'
	  ) {
	    return position.offset;
	  }

	  let column = 1;
	  let line = 1;
	  let offset = 0;

	  for (let i = 0; i < inputCSS.length; i++) {
	    if (line === position.line && column === position.column) {
	      offset = i;
	      break
	    }

	    if (inputCSS[i] === '\n') {
	      column = 1;
	      line += 1;
	    } else {
	      column += 1;
	    }
	  }

	  return offset
	}

	class Node {
	  get proxyOf() {
	    return this
	  }

	  constructor(defaults = {}) {
	    this.raws = {};
	    this[isClean] = false;
	    this[my] = true;

	    for (let name in defaults) {
	      if (name === 'nodes') {
	        this.nodes = [];
	        for (let node of defaults[name]) {
	          if (typeof node.clone === 'function') {
	            this.append(node.clone());
	          } else {
	            this.append(node);
	          }
	        }
	      } else {
	        this[name] = defaults[name];
	      }
	    }
	  }

	  addToError(error) {
	    error.postcssNode = this;
	    if (error.stack && this.source && /\n\s{4}at /.test(error.stack)) {
	      let s = this.source;
	      error.stack = error.stack.replace(
	        /\n\s{4}at /,
	        `$&${s.input.from}:${s.start.line}:${s.start.column}$&`
	      );
	    }
	    return error
	  }

	  after(add) {
	    this.parent.insertAfter(this, add);
	    return this
	  }

	  assign(overrides = {}) {
	    for (let name in overrides) {
	      this[name] = overrides[name];
	    }
	    return this
	  }

	  before(add) {
	    this.parent.insertBefore(this, add);
	    return this
	  }

	  cleanRaws(keepBetween) {
	    delete this.raws.before;
	    delete this.raws.after;
	    if (!keepBetween) delete this.raws.between;
	  }

	  clone(overrides = {}) {
	    let cloned = cloneNode(this);
	    for (let name in overrides) {
	      cloned[name] = overrides[name];
	    }
	    return cloned
	  }

	  cloneAfter(overrides = {}) {
	    let cloned = this.clone(overrides);
	    this.parent.insertAfter(this, cloned);
	    return cloned
	  }

	  cloneBefore(overrides = {}) {
	    let cloned = this.clone(overrides);
	    this.parent.insertBefore(this, cloned);
	    return cloned
	  }

	  error(message, opts = {}) {
	    if (this.source) {
	      let { end, start } = this.rangeBy(opts);
	      return this.source.input.error(
	        message,
	        { column: start.column, line: start.line },
	        { column: end.column, line: end.line },
	        opts
	      )
	    }
	    return new CssSyntaxError(message)
	  }

	  getProxyProcessor() {
	    return {
	      get(node, prop) {
	        if (prop === 'proxyOf') {
	          return node
	        } else if (prop === 'root') {
	          return () => node.root().toProxy()
	        } else {
	          return node[prop]
	        }
	      },

	      set(node, prop, value) {
	        if (node[prop] === value) return true
	        node[prop] = value;
	        if (
	          prop === 'prop' ||
	          prop === 'value' ||
	          prop === 'name' ||
	          prop === 'params' ||
	          prop === 'important' ||
	          /* c8 ignore next */
	          prop === 'text'
	        ) {
	          node.markDirty();
	        }
	        return true
	      }
	    }
	  }

	  /* c8 ignore next 3 */
	  markClean() {
	    this[isClean] = true;
	  }

	  markDirty() {
	    if (this[isClean]) {
	      this[isClean] = false;
	      let next = this;
	      while ((next = next.parent)) {
	        next[isClean] = false;
	      }
	    }
	  }

	  next() {
	    if (!this.parent) return undefined
	    let index = this.parent.index(this);
	    return this.parent.nodes[index + 1]
	  }

	  positionBy(opts) {
	    let pos = this.source.start;
	    if (opts.index) {
	      pos = this.positionInside(opts.index);
	    } else if (opts.word) {
	      let inputString = ('document' in this.source.input)
	        ? this.source.input.document
	        : this.source.input.css;
	      let stringRepresentation = inputString.slice(
	        sourceOffset(inputString, this.source.start),
	        sourceOffset(inputString, this.source.end)
	      );
	      let index = stringRepresentation.indexOf(opts.word);
	      if (index !== -1) pos = this.positionInside(index);
	    }
	    return pos
	  }

	  positionInside(index) {
	    let column = this.source.start.column;
	    let line = this.source.start.line;
	    let inputString = ('document' in this.source.input)
	      ? this.source.input.document
	      : this.source.input.css;
	    let offset = sourceOffset(inputString, this.source.start);
	    let end = offset + index;

	    for (let i = offset; i < end; i++) {
	      if (inputString[i] === '\n') {
	        column = 1;
	        line += 1;
	      } else {
	        column += 1;
	      }
	    }

	    return { column, line }
	  }

	  prev() {
	    if (!this.parent) return undefined
	    let index = this.parent.index(this);
	    return this.parent.nodes[index - 1]
	  }

	  rangeBy(opts) {
	    let start = {
	      column: this.source.start.column,
	      line: this.source.start.line
	    };
	    let end = this.source.end
	      ? {
	          column: this.source.end.column + 1,
	          line: this.source.end.line
	        }
	      : {
	          column: start.column + 1,
	          line: start.line
	        };

	    if (opts.word) {
	      let inputString = ('document' in this.source.input)
	        ? this.source.input.document
	        : this.source.input.css;
	      let stringRepresentation = inputString.slice(
	        sourceOffset(inputString, this.source.start),
	        sourceOffset(inputString, this.source.end)
	      );
	      let index = stringRepresentation.indexOf(opts.word);
	      if (index !== -1) {
	        start = this.positionInside(index);
	        end = this.positionInside(
	          index + opts.word.length,
	        );
	      }
	    } else {
	      if (opts.start) {
	        start = {
	          column: opts.start.column,
	          line: opts.start.line
	        };
	      } else if (opts.index) {
	        start = this.positionInside(opts.index);
	      }

	      if (opts.end) {
	        end = {
	          column: opts.end.column,
	          line: opts.end.line
	        };
	      } else if (typeof opts.endIndex === 'number') {
	        end = this.positionInside(opts.endIndex);
	      } else if (opts.index) {
	        end = this.positionInside(opts.index + 1);
	      }
	    }

	    if (
	      end.line < start.line ||
	      (end.line === start.line && end.column <= start.column)
	    ) {
	      end = { column: start.column + 1, line: start.line };
	    }

	    return { end, start }
	  }

	  raw(prop, defaultType) {
	    let str = new Stringifier();
	    return str.raw(this, prop, defaultType)
	  }

	  remove() {
	    if (this.parent) {
	      this.parent.removeChild(this);
	    }
	    this.parent = undefined;
	    return this
	  }

	  replaceWith(...nodes) {
	    if (this.parent) {
	      let bookmark = this;
	      let foundSelf = false;
	      for (let node of nodes) {
	        if (node === this) {
	          foundSelf = true;
	        } else if (foundSelf) {
	          this.parent.insertAfter(bookmark, node);
	          bookmark = node;
	        } else {
	          this.parent.insertBefore(bookmark, node);
	        }
	      }

	      if (!foundSelf) {
	        this.remove();
	      }
	    }

	    return this
	  }

	  root() {
	    let result = this;
	    while (result.parent && result.parent.type !== 'document') {
	      result = result.parent;
	    }
	    return result
	  }

	  toJSON(_, inputs) {
	    let fixed = {};
	    let emitInputs = inputs == null;
	    inputs = inputs || new Map();
	    let inputsNextIndex = 0;

	    for (let name in this) {
	      if (!Object.prototype.hasOwnProperty.call(this, name)) {
	        /* c8 ignore next 2 */
	        continue
	      }
	      if (name === 'parent' || name === 'proxyCache') continue
	      let value = this[name];

	      if (Array.isArray(value)) {
	        fixed[name] = value.map(i => {
	          if (typeof i === 'object' && i.toJSON) {
	            return i.toJSON(null, inputs)
	          } else {
	            return i
	          }
	        });
	      } else if (typeof value === 'object' && value.toJSON) {
	        fixed[name] = value.toJSON(null, inputs);
	      } else if (name === 'source') {
	        let inputId = inputs.get(value.input);
	        if (inputId == null) {
	          inputId = inputsNextIndex;
	          inputs.set(value.input, inputsNextIndex);
	          inputsNextIndex++;
	        }
	        fixed[name] = {
	          end: value.end,
	          inputId,
	          start: value.start
	        };
	      } else {
	        fixed[name] = value;
	      }
	    }

	    if (emitInputs) {
	      fixed.inputs = [...inputs.keys()].map(input => input.toJSON());
	    }

	    return fixed
	  }

	  toProxy() {
	    if (!this.proxyCache) {
	      this.proxyCache = new Proxy(this, this.getProxyProcessor());
	    }
	    return this.proxyCache
	  }

	  toString(stringifier = stringify) {
	    if (stringifier.stringify) stringifier = stringifier.stringify;
	    let result = '';
	    stringifier(this, i => {
	      result += i;
	    });
	    return result
	  }

	  warn(result, text, opts) {
	    let data = { node: this };
	    for (let i in opts) data[i] = opts[i];
	    return result.warn(text, data)
	  }
	}

	node = Node;
	Node.default = Node;
	return node;
}

var comment;
var hasRequiredComment;

function requireComment () {
	if (hasRequiredComment) return comment;
	hasRequiredComment = 1;

	let Node = requireNode();

	class Comment extends Node {
	  constructor(defaults) {
	    super(defaults);
	    this.type = 'comment';
	  }
	}

	comment = Comment;
	Comment.default = Comment;
	return comment;
}

var declaration;
var hasRequiredDeclaration;

function requireDeclaration () {
	if (hasRequiredDeclaration) return declaration;
	hasRequiredDeclaration = 1;

	let Node = requireNode();

	class Declaration extends Node {
	  get variable() {
	    return this.prop.startsWith('--') || this.prop[0] === '$'
	  }

	  constructor(defaults) {
	    if (
	      defaults &&
	      typeof defaults.value !== 'undefined' &&
	      typeof defaults.value !== 'string'
	    ) {
	      defaults = { ...defaults, value: String(defaults.value) };
	    }
	    super(defaults);
	    this.type = 'decl';
	  }
	}

	declaration = Declaration;
	Declaration.default = Declaration;
	return declaration;
}

var container;
var hasRequiredContainer;

function requireContainer () {
	if (hasRequiredContainer) return container;
	hasRequiredContainer = 1;

	let Comment = requireComment();
	let Declaration = requireDeclaration();
	let Node = requireNode();
	let { isClean, my } = requireSymbols();

	let AtRule, parse, Root, Rule;

	function cleanSource(nodes) {
	  return nodes.map(i => {
	    if (i.nodes) i.nodes = cleanSource(i.nodes);
	    delete i.source;
	    return i
	  })
	}

	function markTreeDirty(node) {
	  node[isClean] = false;
	  if (node.proxyOf.nodes) {
	    for (let i of node.proxyOf.nodes) {
	      markTreeDirty(i);
	    }
	  }
	}

	class Container extends Node {
	  get first() {
	    if (!this.proxyOf.nodes) return undefined
	    return this.proxyOf.nodes[0]
	  }

	  get last() {
	    if (!this.proxyOf.nodes) return undefined
	    return this.proxyOf.nodes[this.proxyOf.nodes.length - 1]
	  }

	  append(...children) {
	    for (let child of children) {
	      let nodes = this.normalize(child, this.last);
	      for (let node of nodes) this.proxyOf.nodes.push(node);
	    }

	    this.markDirty();

	    return this
	  }

	  cleanRaws(keepBetween) {
	    super.cleanRaws(keepBetween);
	    if (this.nodes) {
	      for (let node of this.nodes) node.cleanRaws(keepBetween);
	    }
	  }

	  each(callback) {
	    if (!this.proxyOf.nodes) return undefined
	    let iterator = this.getIterator();

	    let index, result;
	    while (this.indexes[iterator] < this.proxyOf.nodes.length) {
	      index = this.indexes[iterator];
	      result = callback(this.proxyOf.nodes[index], index);
	      if (result === false) break

	      this.indexes[iterator] += 1;
	    }

	    delete this.indexes[iterator];
	    return result
	  }

	  every(condition) {
	    return this.nodes.every(condition)
	  }

	  getIterator() {
	    if (!this.lastEach) this.lastEach = 0;
	    if (!this.indexes) this.indexes = {};

	    this.lastEach += 1;
	    let iterator = this.lastEach;
	    this.indexes[iterator] = 0;

	    return iterator
	  }

	  getProxyProcessor() {
	    return {
	      get(node, prop) {
	        if (prop === 'proxyOf') {
	          return node
	        } else if (!node[prop]) {
	          return node[prop]
	        } else if (
	          prop === 'each' ||
	          (typeof prop === 'string' && prop.startsWith('walk'))
	        ) {
	          return (...args) => {
	            return node[prop](
	              ...args.map(i => {
	                if (typeof i === 'function') {
	                  return (child, index) => i(child.toProxy(), index)
	                } else {
	                  return i
	                }
	              })
	            )
	          }
	        } else if (prop === 'every' || prop === 'some') {
	          return cb => {
	            return node[prop]((child, ...other) =>
	              cb(child.toProxy(), ...other)
	            )
	          }
	        } else if (prop === 'root') {
	          return () => node.root().toProxy()
	        } else if (prop === 'nodes') {
	          return node.nodes.map(i => i.toProxy())
	        } else if (prop === 'first' || prop === 'last') {
	          return node[prop].toProxy()
	        } else {
	          return node[prop]
	        }
	      },

	      set(node, prop, value) {
	        if (node[prop] === value) return true
	        node[prop] = value;
	        if (prop === 'name' || prop === 'params' || prop === 'selector') {
	          node.markDirty();
	        }
	        return true
	      }
	    }
	  }

	  index(child) {
	    if (typeof child === 'number') return child
	    if (child.proxyOf) child = child.proxyOf;
	    return this.proxyOf.nodes.indexOf(child)
	  }

	  insertAfter(exist, add) {
	    let existIndex = this.index(exist);
	    let nodes = this.normalize(add, this.proxyOf.nodes[existIndex]).reverse();
	    existIndex = this.index(exist);
	    for (let node of nodes) this.proxyOf.nodes.splice(existIndex + 1, 0, node);

	    let index;
	    for (let id in this.indexes) {
	      index = this.indexes[id];
	      if (existIndex < index) {
	        this.indexes[id] = index + nodes.length;
	      }
	    }

	    this.markDirty();

	    return this
	  }

	  insertBefore(exist, add) {
	    let existIndex = this.index(exist);
	    let type = existIndex === 0 ? 'prepend' : false;
	    let nodes = this.normalize(
	      add,
	      this.proxyOf.nodes[existIndex],
	      type
	    ).reverse();
	    existIndex = this.index(exist);
	    for (let node of nodes) this.proxyOf.nodes.splice(existIndex, 0, node);

	    let index;
	    for (let id in this.indexes) {
	      index = this.indexes[id];
	      if (existIndex <= index) {
	        this.indexes[id] = index + nodes.length;
	      }
	    }

	    this.markDirty();

	    return this
	  }

	  normalize(nodes, sample) {
	    if (typeof nodes === 'string') {
	      nodes = cleanSource(parse(nodes).nodes);
	    } else if (typeof nodes === 'undefined') {
	      nodes = [];
	    } else if (Array.isArray(nodes)) {
	      nodes = nodes.slice(0);
	      for (let i of nodes) {
	        if (i.parent) i.parent.removeChild(i, 'ignore');
	      }
	    } else if (nodes.type === 'root' && this.type !== 'document') {
	      nodes = nodes.nodes.slice(0);
	      for (let i of nodes) {
	        if (i.parent) i.parent.removeChild(i, 'ignore');
	      }
	    } else if (nodes.type) {
	      nodes = [nodes];
	    } else if (nodes.prop) {
	      if (typeof nodes.value === 'undefined') {
	        throw new Error('Value field is missed in node creation')
	      } else if (typeof nodes.value !== 'string') {
	        nodes.value = String(nodes.value);
	      }
	      nodes = [new Declaration(nodes)];
	    } else if (nodes.selector || nodes.selectors) {
	      nodes = [new Rule(nodes)];
	    } else if (nodes.name) {
	      nodes = [new AtRule(nodes)];
	    } else if (nodes.text) {
	      nodes = [new Comment(nodes)];
	    } else {
	      throw new Error('Unknown node type in node creation')
	    }

	    let processed = nodes.map(i => {
	      /* c8 ignore next */
	      if (!i[my]) Container.rebuild(i);
	      i = i.proxyOf;
	      if (i.parent) i.parent.removeChild(i);
	      if (i[isClean]) markTreeDirty(i);

	      if (!i.raws) i.raws = {};
	      if (typeof i.raws.before === 'undefined') {
	        if (sample && typeof sample.raws.before !== 'undefined') {
	          i.raws.before = sample.raws.before.replace(/\S/g, '');
	        }
	      }
	      i.parent = this.proxyOf;
	      return i
	    });

	    return processed
	  }

	  prepend(...children) {
	    children = children.reverse();
	    for (let child of children) {
	      let nodes = this.normalize(child, this.first, 'prepend').reverse();
	      for (let node of nodes) this.proxyOf.nodes.unshift(node);
	      for (let id in this.indexes) {
	        this.indexes[id] = this.indexes[id] + nodes.length;
	      }
	    }

	    this.markDirty();

	    return this
	  }

	  push(child) {
	    child.parent = this;
	    this.proxyOf.nodes.push(child);
	    return this
	  }

	  removeAll() {
	    for (let node of this.proxyOf.nodes) node.parent = undefined;
	    this.proxyOf.nodes = [];

	    this.markDirty();

	    return this
	  }

	  removeChild(child) {
	    child = this.index(child);
	    this.proxyOf.nodes[child].parent = undefined;
	    this.proxyOf.nodes.splice(child, 1);

	    let index;
	    for (let id in this.indexes) {
	      index = this.indexes[id];
	      if (index >= child) {
	        this.indexes[id] = index - 1;
	      }
	    }

	    this.markDirty();

	    return this
	  }

	  replaceValues(pattern, opts, callback) {
	    if (!callback) {
	      callback = opts;
	      opts = {};
	    }

	    this.walkDecls(decl => {
	      if (opts.props && !opts.props.includes(decl.prop)) return
	      if (opts.fast && !decl.value.includes(opts.fast)) return

	      decl.value = decl.value.replace(pattern, callback);
	    });

	    this.markDirty();

	    return this
	  }

	  some(condition) {
	    return this.nodes.some(condition)
	  }

	  walk(callback) {
	    return this.each((child, i) => {
	      let result;
	      try {
	        result = callback(child, i);
	      } catch (e) {
	        throw child.addToError(e)
	      }
	      if (result !== false && child.walk) {
	        result = child.walk(callback);
	      }

	      return result
	    })
	  }

	  walkAtRules(name, callback) {
	    if (!callback) {
	      callback = name;
	      return this.walk((child, i) => {
	        if (child.type === 'atrule') {
	          return callback(child, i)
	        }
	      })
	    }
	    if (name instanceof RegExp) {
	      return this.walk((child, i) => {
	        if (child.type === 'atrule' && name.test(child.name)) {
	          return callback(child, i)
	        }
	      })
	    }
	    return this.walk((child, i) => {
	      if (child.type === 'atrule' && child.name === name) {
	        return callback(child, i)
	      }
	    })
	  }

	  walkComments(callback) {
	    return this.walk((child, i) => {
	      if (child.type === 'comment') {
	        return callback(child, i)
	      }
	    })
	  }

	  walkDecls(prop, callback) {
	    if (!callback) {
	      callback = prop;
	      return this.walk((child, i) => {
	        if (child.type === 'decl') {
	          return callback(child, i)
	        }
	      })
	    }
	    if (prop instanceof RegExp) {
	      return this.walk((child, i) => {
	        if (child.type === 'decl' && prop.test(child.prop)) {
	          return callback(child, i)
	        }
	      })
	    }
	    return this.walk((child, i) => {
	      if (child.type === 'decl' && child.prop === prop) {
	        return callback(child, i)
	      }
	    })
	  }

	  walkRules(selector, callback) {
	    if (!callback) {
	      callback = selector;

	      return this.walk((child, i) => {
	        if (child.type === 'rule') {
	          return callback(child, i)
	        }
	      })
	    }
	    if (selector instanceof RegExp) {
	      return this.walk((child, i) => {
	        if (child.type === 'rule' && selector.test(child.selector)) {
	          return callback(child, i)
	        }
	      })
	    }
	    return this.walk((child, i) => {
	      if (child.type === 'rule' && child.selector === selector) {
	        return callback(child, i)
	      }
	    })
	  }
	}

	Container.registerParse = dependant => {
	  parse = dependant;
	};

	Container.registerRule = dependant => {
	  Rule = dependant;
	};

	Container.registerAtRule = dependant => {
	  AtRule = dependant;
	};

	Container.registerRoot = dependant => {
	  Root = dependant;
	};

	container = Container;
	Container.default = Container;

	/* c8 ignore start */
	Container.rebuild = node => {
	  if (node.type === 'atrule') {
	    Object.setPrototypeOf(node, AtRule.prototype);
	  } else if (node.type === 'rule') {
	    Object.setPrototypeOf(node, Rule.prototype);
	  } else if (node.type === 'decl') {
	    Object.setPrototypeOf(node, Declaration.prototype);
	  } else if (node.type === 'comment') {
	    Object.setPrototypeOf(node, Comment.prototype);
	  } else if (node.type === 'root') {
	    Object.setPrototypeOf(node, Root.prototype);
	  }

	  node[my] = true;

	  if (node.nodes) {
	    node.nodes.forEach(child => {
	      Container.rebuild(child);
	    });
	  }
	};
	/* c8 ignore stop */
	return container;
}

var atRule;
var hasRequiredAtRule;

function requireAtRule () {
	if (hasRequiredAtRule) return atRule;
	hasRequiredAtRule = 1;

	let Container = requireContainer();

	class AtRule extends Container {
	  constructor(defaults) {
	    super(defaults);
	    this.type = 'atrule';
	  }

	  append(...children) {
	    if (!this.proxyOf.nodes) this.nodes = [];
	    return super.append(...children)
	  }

	  prepend(...children) {
	    if (!this.proxyOf.nodes) this.nodes = [];
	    return super.prepend(...children)
	  }
	}

	atRule = AtRule;
	AtRule.default = AtRule;

	Container.registerAtRule(AtRule);
	return atRule;
}

var document$1;
var hasRequiredDocument;

function requireDocument () {
	if (hasRequiredDocument) return document$1;
	hasRequiredDocument = 1;

	let Container = requireContainer();

	let LazyResult, Processor;

	class Document extends Container {
	  constructor(defaults) {
	    // type needs to be passed to super, otherwise child roots won't be normalized correctly
	    super({ type: 'document', ...defaults });

	    if (!this.nodes) {
	      this.nodes = [];
	    }
	  }

	  toResult(opts = {}) {
	    let lazy = new LazyResult(new Processor(), this, opts);

	    return lazy.stringify()
	  }
	}

	Document.registerLazyResult = dependant => {
	  LazyResult = dependant;
	};

	Document.registerProcessor = dependant => {
	  Processor = dependant;
	};

	document$1 = Document;
	Document.default = Document;
	return document$1;
}

var nonSecure;
var hasRequiredNonSecure;

function requireNonSecure () {
	if (hasRequiredNonSecure) return nonSecure;
	hasRequiredNonSecure = 1;
	// This alphabet uses `A-Za-z0-9_-` symbols.
	// The order of characters is optimized for better gzip and brotli compression.
	// References to the same file (works both for gzip and brotli):
	// `'use`, `andom`, and `rict'`
	// References to the brotli default dictionary:
	// `-26T`, `1983`, `40px`, `75px`, `bush`, `jack`, `mind`, `very`, and `wolf`
	let urlAlphabet =
	  'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';

	let customAlphabet = (alphabet, defaultSize = 21) => {
	  return (size = defaultSize) => {
	    let id = '';
	    // A compact alternative for `for (var i = 0; i < step; i++)`.
	    let i = size | 0;
	    while (i--) {
	      // `| 0` is more compact and faster than `Math.floor()`.
	      id += alphabet[(Math.random() * alphabet.length) | 0];
	    }
	    return id
	  }
	};

	let nanoid = (size = 21) => {
	  let id = '';
	  // A compact alternative for `for (var i = 0; i < step; i++)`.
	  let i = size | 0;
	  while (i--) {
	    // `| 0` is more compact and faster than `Math.floor()`.
	    id += urlAlphabet[(Math.random() * 64) | 0];
	  }
	  return id
	};

	nonSecure = { nanoid, customAlphabet };
	return nonSecure;
}

var previousMap;
var hasRequiredPreviousMap;

function requirePreviousMap () {
	if (hasRequiredPreviousMap) return previousMap;
	hasRequiredPreviousMap = 1;

	let { existsSync, readFileSync } = require$$2;
	let { dirname, join } = require$$2;
	let { SourceMapConsumer, SourceMapGenerator } = require$$2;

	function fromBase64(str) {
	  if (Buffer) {
	    return Buffer.from(str, 'base64').toString()
	  } else {
	    /* c8 ignore next 2 */
	    return window.atob(str)
	  }
	}

	class PreviousMap {
	  constructor(css, opts) {
	    if (opts.map === false) return
	    this.loadAnnotation(css);
	    this.inline = this.startWith(this.annotation, 'data:');

	    let prev = opts.map ? opts.map.prev : undefined;
	    let text = this.loadMap(opts.from, prev);
	    if (!this.mapFile && opts.from) {
	      this.mapFile = opts.from;
	    }
	    if (this.mapFile) this.root = dirname(this.mapFile);
	    if (text) this.text = text;
	  }

	  consumer() {
	    if (!this.consumerCache) {
	      this.consumerCache = new SourceMapConsumer(this.text);
	    }
	    return this.consumerCache
	  }

	  decodeInline(text) {
	    let baseCharsetUri = /^data:application\/json;charset=utf-?8;base64,/;
	    let baseUri = /^data:application\/json;base64,/;
	    let charsetUri = /^data:application\/json;charset=utf-?8,/;
	    let uri = /^data:application\/json,/;

	    let uriMatch = text.match(charsetUri) || text.match(uri);
	    if (uriMatch) {
	      return decodeURIComponent(text.substr(uriMatch[0].length))
	    }

	    let baseUriMatch = text.match(baseCharsetUri) || text.match(baseUri);
	    if (baseUriMatch) {
	      return fromBase64(text.substr(baseUriMatch[0].length))
	    }

	    let encoding = text.match(/data:application\/json;([^,]+),/)[1];
	    throw new Error('Unsupported source map encoding ' + encoding)
	  }

	  getAnnotationURL(sourceMapString) {
	    return sourceMapString.replace(/^\/\*\s*# sourceMappingURL=/, '').trim()
	  }

	  isMap(map) {
	    if (typeof map !== 'object') return false
	    return (
	      typeof map.mappings === 'string' ||
	      typeof map._mappings === 'string' ||
	      Array.isArray(map.sections)
	    )
	  }

	  loadAnnotation(css) {
	    let comments = css.match(/\/\*\s*# sourceMappingURL=/g);
	    if (!comments) return

	    // sourceMappingURLs from comments, strings, etc.
	    let start = css.lastIndexOf(comments.pop());
	    let end = css.indexOf('*/', start);

	    if (start > -1 && end > -1) {
	      // Locate the last sourceMappingURL to avoid pickin
	      this.annotation = this.getAnnotationURL(css.substring(start, end));
	    }
	  }

	  loadFile(path) {
	    this.root = dirname(path);
	    if (existsSync(path)) {
	      this.mapFile = path;
	      return readFileSync(path, 'utf-8').toString().trim()
	    }
	  }

	  loadMap(file, prev) {
	    if (prev === false) return false

	    if (prev) {
	      if (typeof prev === 'string') {
	        return prev
	      } else if (typeof prev === 'function') {
	        let prevPath = prev(file);
	        if (prevPath) {
	          let map = this.loadFile(prevPath);
	          if (!map) {
	            throw new Error(
	              'Unable to load previous source map: ' + prevPath.toString()
	            )
	          }
	          return map
	        }
	      } else if (prev instanceof SourceMapConsumer) {
	        return SourceMapGenerator.fromSourceMap(prev).toString()
	      } else if (prev instanceof SourceMapGenerator) {
	        return prev.toString()
	      } else if (this.isMap(prev)) {
	        return JSON.stringify(prev)
	      } else {
	        throw new Error(
	          'Unsupported previous source map format: ' + prev.toString()
	        )
	      }
	    } else if (this.inline) {
	      return this.decodeInline(this.annotation)
	    } else if (this.annotation) {
	      let map = this.annotation;
	      if (file) map = join(dirname(file), map);
	      return this.loadFile(map)
	    }
	  }

	  startWith(string, start) {
	    if (!string) return false
	    return string.substr(0, start.length) === start
	  }

	  withContent() {
	    return !!(
	      this.consumer().sourcesContent &&
	      this.consumer().sourcesContent.length > 0
	    )
	  }
	}

	previousMap = PreviousMap;
	PreviousMap.default = PreviousMap;
	return previousMap;
}

var input;
var hasRequiredInput;

function requireInput () {
	if (hasRequiredInput) return input;
	hasRequiredInput = 1;

	let { nanoid } = /*@__PURE__*/ requireNonSecure();
	let { isAbsolute, resolve } = require$$2;
	let { SourceMapConsumer, SourceMapGenerator } = require$$2;
	let { fileURLToPath, pathToFileURL } = require$$2;

	let CssSyntaxError = requireCssSyntaxError();
	let PreviousMap = requirePreviousMap();
	let terminalHighlight = require$$2;

	let fromOffsetCache = Symbol('fromOffsetCache');

	let sourceMapAvailable = Boolean(SourceMapConsumer && SourceMapGenerator);
	let pathAvailable = Boolean(resolve && isAbsolute);

	class Input {
	  get from() {
	    return this.file || this.id
	  }

	  constructor(css, opts = {}) {
	    if (
	      css === null ||
	      typeof css === 'undefined' ||
	      (typeof css === 'object' && !css.toString)
	    ) {
	      throw new Error(`PostCSS received ${css} instead of CSS string`)
	    }

	    this.css = css.toString();

	    if (this.css[0] === '\uFEFF' || this.css[0] === '\uFFFE') {
	      this.hasBOM = true;
	      this.css = this.css.slice(1);
	    } else {
	      this.hasBOM = false;
	    }

	    this.document = this.css;
	    if (opts.document) this.document = opts.document.toString();

	    if (opts.from) {
	      if (
	        !pathAvailable ||
	        /^\w+:\/\//.test(opts.from) ||
	        isAbsolute(opts.from)
	      ) {
	        this.file = opts.from;
	      } else {
	        this.file = resolve(opts.from);
	      }
	    }

	    if (pathAvailable && sourceMapAvailable) {
	      let map = new PreviousMap(this.css, opts);
	      if (map.text) {
	        this.map = map;
	        let file = map.consumer().file;
	        if (!this.file && file) this.file = this.mapResolve(file);
	      }
	    }

	    if (!this.file) {
	      this.id = '<input css ' + nanoid(6) + '>';
	    }
	    if (this.map) this.map.file = this.from;
	  }

	  error(message, line, column, opts = {}) {
	    let endColumn, endLine, result;

	    if (line && typeof line === 'object') {
	      let start = line;
	      let end = column;
	      if (typeof start.offset === 'number') {
	        let pos = this.fromOffset(start.offset);
	        line = pos.line;
	        column = pos.col;
	      } else {
	        line = start.line;
	        column = start.column;
	      }
	      if (typeof end.offset === 'number') {
	        let pos = this.fromOffset(end.offset);
	        endLine = pos.line;
	        endColumn = pos.col;
	      } else {
	        endLine = end.line;
	        endColumn = end.column;
	      }
	    } else if (!column) {
	      let pos = this.fromOffset(line);
	      line = pos.line;
	      column = pos.col;
	    }

	    let origin = this.origin(line, column, endLine, endColumn);
	    if (origin) {
	      result = new CssSyntaxError(
	        message,
	        origin.endLine === undefined
	          ? origin.line
	          : { column: origin.column, line: origin.line },
	        origin.endLine === undefined
	          ? origin.column
	          : { column: origin.endColumn, line: origin.endLine },
	        origin.source,
	        origin.file,
	        opts.plugin
	      );
	    } else {
	      result = new CssSyntaxError(
	        message,
	        endLine === undefined ? line : { column, line },
	        endLine === undefined ? column : { column: endColumn, line: endLine },
	        this.css,
	        this.file,
	        opts.plugin
	      );
	    }

	    result.input = { column, endColumn, endLine, line, source: this.css };
	    if (this.file) {
	      if (pathToFileURL) {
	        result.input.url = pathToFileURL(this.file).toString();
	      }
	      result.input.file = this.file;
	    }

	    return result
	  }

	  fromOffset(offset) {
	    let lastLine, lineToIndex;
	    if (!this[fromOffsetCache]) {
	      let lines = this.css.split('\n');
	      lineToIndex = new Array(lines.length);
	      let prevIndex = 0;

	      for (let i = 0, l = lines.length; i < l; i++) {
	        lineToIndex[i] = prevIndex;
	        prevIndex += lines[i].length + 1;
	      }

	      this[fromOffsetCache] = lineToIndex;
	    } else {
	      lineToIndex = this[fromOffsetCache];
	    }
	    lastLine = lineToIndex[lineToIndex.length - 1];

	    let min = 0;
	    if (offset >= lastLine) {
	      min = lineToIndex.length - 1;
	    } else {
	      let max = lineToIndex.length - 2;
	      let mid;
	      while (min < max) {
	        mid = min + ((max - min) >> 1);
	        if (offset < lineToIndex[mid]) {
	          max = mid - 1;
	        } else if (offset >= lineToIndex[mid + 1]) {
	          min = mid + 1;
	        } else {
	          min = mid;
	          break
	        }
	      }
	    }
	    return {
	      col: offset - lineToIndex[min] + 1,
	      line: min + 1
	    }
	  }

	  mapResolve(file) {
	    if (/^\w+:\/\//.test(file)) {
	      return file
	    }
	    return resolve(this.map.consumer().sourceRoot || this.map.root || '.', file)
	  }

	  origin(line, column, endLine, endColumn) {
	    if (!this.map) return false
	    let consumer = this.map.consumer();

	    let from = consumer.originalPositionFor({ column, line });
	    if (!from.source) return false

	    let to;
	    if (typeof endLine === 'number') {
	      to = consumer.originalPositionFor({ column: endColumn, line: endLine });
	    }

	    let fromUrl;

	    if (isAbsolute(from.source)) {
	      fromUrl = pathToFileURL(from.source);
	    } else {
	      fromUrl = new URL(
	        from.source,
	        this.map.consumer().sourceRoot || pathToFileURL(this.map.mapFile)
	      );
	    }

	    let result = {
	      column: from.column,
	      endColumn: to && to.column,
	      endLine: to && to.line,
	      line: from.line,
	      url: fromUrl.toString()
	    };

	    if (fromUrl.protocol === 'file:') {
	      if (fileURLToPath) {
	        result.file = fileURLToPath(fromUrl);
	      } else {
	        /* c8 ignore next 2 */
	        throw new Error(`file: protocol is not available in this PostCSS build`)
	      }
	    }

	    let source = consumer.sourceContentFor(from.source);
	    if (source) result.source = source;

	    return result
	  }

	  toJSON() {
	    let json = {};
	    for (let name of ['hasBOM', 'css', 'file', 'id']) {
	      if (this[name] != null) {
	        json[name] = this[name];
	      }
	    }
	    if (this.map) {
	      json.map = { ...this.map };
	      if (json.map.consumerCache) {
	        json.map.consumerCache = undefined;
	      }
	    }
	    return json
	  }
	}

	input = Input;
	Input.default = Input;

	if (terminalHighlight && terminalHighlight.registerInput) {
	  terminalHighlight.registerInput(Input);
	}
	return input;
}

var root;
var hasRequiredRoot;

function requireRoot () {
	if (hasRequiredRoot) return root;
	hasRequiredRoot = 1;

	let Container = requireContainer();

	let LazyResult, Processor;

	class Root extends Container {
	  constructor(defaults) {
	    super(defaults);
	    this.type = 'root';
	    if (!this.nodes) this.nodes = [];
	  }

	  normalize(child, sample, type) {
	    let nodes = super.normalize(child);

	    if (sample) {
	      if (type === 'prepend') {
	        if (this.nodes.length > 1) {
	          sample.raws.before = this.nodes[1].raws.before;
	        } else {
	          delete sample.raws.before;
	        }
	      } else if (this.first !== sample) {
	        for (let node of nodes) {
	          node.raws.before = sample.raws.before;
	        }
	      }
	    }

	    return nodes
	  }

	  removeChild(child, ignore) {
	    let index = this.index(child);

	    if (!ignore && index === 0 && this.nodes.length > 1) {
	      this.nodes[1].raws.before = this.nodes[index].raws.before;
	    }

	    return super.removeChild(child)
	  }

	  toResult(opts = {}) {
	    let lazy = new LazyResult(new Processor(), this, opts);
	    return lazy.stringify()
	  }
	}

	Root.registerLazyResult = dependant => {
	  LazyResult = dependant;
	};

	Root.registerProcessor = dependant => {
	  Processor = dependant;
	};

	root = Root;
	Root.default = Root;

	Container.registerRoot(Root);
	return root;
}

var list_1;
var hasRequiredList;

function requireList () {
	if (hasRequiredList) return list_1;
	hasRequiredList = 1;

	let list = {
	  comma(string) {
	    return list.split(string, [','], true)
	  },

	  space(string) {
	    let spaces = [' ', '\n', '\t'];
	    return list.split(string, spaces)
	  },

	  split(string, separators, last) {
	    let array = [];
	    let current = '';
	    let split = false;

	    let func = 0;
	    let inQuote = false;
	    let prevQuote = '';
	    let escape = false;

	    for (let letter of string) {
	      if (escape) {
	        escape = false;
	      } else if (letter === '\\') {
	        escape = true;
	      } else if (inQuote) {
	        if (letter === prevQuote) {
	          inQuote = false;
	        }
	      } else if (letter === '"' || letter === "'") {
	        inQuote = true;
	        prevQuote = letter;
	      } else if (letter === '(') {
	        func += 1;
	      } else if (letter === ')') {
	        if (func > 0) func -= 1;
	      } else if (func === 0) {
	        if (separators.includes(letter)) split = true;
	      }

	      if (split) {
	        if (current !== '') array.push(current.trim());
	        current = '';
	        split = false;
	      } else {
	        current += letter;
	      }
	    }

	    if (last || current !== '') array.push(current.trim());
	    return array
	  }
	};

	list_1 = list;
	list.default = list;
	return list_1;
}

var rule;
var hasRequiredRule;

function requireRule () {
	if (hasRequiredRule) return rule;
	hasRequiredRule = 1;

	let Container = requireContainer();
	let list = requireList();

	class Rule extends Container {
	  get selectors() {
	    return list.comma(this.selector)
	  }

	  set selectors(values) {
	    let match = this.selector ? this.selector.match(/,\s*/) : null;
	    let sep = match ? match[0] : ',' + this.raw('between', 'beforeOpen');
	    this.selector = values.join(sep);
	  }

	  constructor(defaults) {
	    super(defaults);
	    this.type = 'rule';
	    if (!this.nodes) this.nodes = [];
	  }
	}

	rule = Rule;
	Rule.default = Rule;

	Container.registerRule(Rule);
	return rule;
}

var fromJSON_1;
var hasRequiredFromJSON;

function requireFromJSON () {
	if (hasRequiredFromJSON) return fromJSON_1;
	hasRequiredFromJSON = 1;

	let AtRule = requireAtRule();
	let Comment = requireComment();
	let Declaration = requireDeclaration();
	let Input = requireInput();
	let PreviousMap = requirePreviousMap();
	let Root = requireRoot();
	let Rule = requireRule();

	function fromJSON(json, inputs) {
	  if (Array.isArray(json)) return json.map(n => fromJSON(n))

	  let { inputs: ownInputs, ...defaults } = json;
	  if (ownInputs) {
	    inputs = [];
	    for (let input of ownInputs) {
	      let inputHydrated = { ...input, __proto__: Input.prototype };
	      if (inputHydrated.map) {
	        inputHydrated.map = {
	          ...inputHydrated.map,
	          __proto__: PreviousMap.prototype
	        };
	      }
	      inputs.push(inputHydrated);
	    }
	  }
	  if (defaults.nodes) {
	    defaults.nodes = json.nodes.map(n => fromJSON(n, inputs));
	  }
	  if (defaults.source) {
	    let { inputId, ...source } = defaults.source;
	    defaults.source = source;
	    if (inputId != null) {
	      defaults.source.input = inputs[inputId];
	    }
	  }
	  if (defaults.type === 'root') {
	    return new Root(defaults)
	  } else if (defaults.type === 'decl') {
	    return new Declaration(defaults)
	  } else if (defaults.type === 'rule') {
	    return new Rule(defaults)
	  } else if (defaults.type === 'comment') {
	    return new Comment(defaults)
	  } else if (defaults.type === 'atrule') {
	    return new AtRule(defaults)
	  } else {
	    throw new Error('Unknown node type: ' + json.type)
	  }
	}

	fromJSON_1 = fromJSON;
	fromJSON.default = fromJSON;
	return fromJSON_1;
}

var mapGenerator;
var hasRequiredMapGenerator;

function requireMapGenerator () {
	if (hasRequiredMapGenerator) return mapGenerator;
	hasRequiredMapGenerator = 1;

	let { dirname, relative, resolve, sep } = require$$2;
	let { SourceMapConsumer, SourceMapGenerator } = require$$2;
	let { pathToFileURL } = require$$2;

	let Input = requireInput();

	let sourceMapAvailable = Boolean(SourceMapConsumer && SourceMapGenerator);
	let pathAvailable = Boolean(dirname && resolve && relative && sep);

	class MapGenerator {
	  constructor(stringify, root, opts, cssString) {
	    this.stringify = stringify;
	    this.mapOpts = opts.map || {};
	    this.root = root;
	    this.opts = opts;
	    this.css = cssString;
	    this.originalCSS = cssString;
	    this.usesFileUrls = !this.mapOpts.from && this.mapOpts.absolute;

	    this.memoizedFileURLs = new Map();
	    this.memoizedPaths = new Map();
	    this.memoizedURLs = new Map();
	  }

	  addAnnotation() {
	    let content;

	    if (this.isInline()) {
	      content =
	        'data:application/json;base64,' + this.toBase64(this.map.toString());
	    } else if (typeof this.mapOpts.annotation === 'string') {
	      content = this.mapOpts.annotation;
	    } else if (typeof this.mapOpts.annotation === 'function') {
	      content = this.mapOpts.annotation(this.opts.to, this.root);
	    } else {
	      content = this.outputFile() + '.map';
	    }
	    let eol = '\n';
	    if (this.css.includes('\r\n')) eol = '\r\n';

	    this.css += eol + '/*# sourceMappingURL=' + content + ' */';
	  }

	  applyPrevMaps() {
	    for (let prev of this.previous()) {
	      let from = this.toUrl(this.path(prev.file));
	      let root = prev.root || dirname(prev.file);
	      let map;

	      if (this.mapOpts.sourcesContent === false) {
	        map = new SourceMapConsumer(prev.text);
	        if (map.sourcesContent) {
	          map.sourcesContent = null;
	        }
	      } else {
	        map = prev.consumer();
	      }

	      this.map.applySourceMap(map, from, this.toUrl(this.path(root)));
	    }
	  }

	  clearAnnotation() {
	    if (this.mapOpts.annotation === false) return

	    if (this.root) {
	      let node;
	      for (let i = this.root.nodes.length - 1; i >= 0; i--) {
	        node = this.root.nodes[i];
	        if (node.type !== 'comment') continue
	        if (node.text.startsWith('# sourceMappingURL=')) {
	          this.root.removeChild(i);
	        }
	      }
	    } else if (this.css) {
	      this.css = this.css.replace(/\n*\/\*#[\S\s]*?\*\/$/gm, '');
	    }
	  }

	  generate() {
	    this.clearAnnotation();
	    if (pathAvailable && sourceMapAvailable && this.isMap()) {
	      return this.generateMap()
	    } else {
	      let result = '';
	      this.stringify(this.root, i => {
	        result += i;
	      });
	      return [result]
	    }
	  }

	  generateMap() {
	    if (this.root) {
	      this.generateString();
	    } else if (this.previous().length === 1) {
	      let prev = this.previous()[0].consumer();
	      prev.file = this.outputFile();
	      this.map = SourceMapGenerator.fromSourceMap(prev, {
	        ignoreInvalidMapping: true
	      });
	    } else {
	      this.map = new SourceMapGenerator({
	        file: this.outputFile(),
	        ignoreInvalidMapping: true
	      });
	      this.map.addMapping({
	        generated: { column: 0, line: 1 },
	        original: { column: 0, line: 1 },
	        source: this.opts.from
	          ? this.toUrl(this.path(this.opts.from))
	          : '<no source>'
	      });
	    }

	    if (this.isSourcesContent()) this.setSourcesContent();
	    if (this.root && this.previous().length > 0) this.applyPrevMaps();
	    if (this.isAnnotation()) this.addAnnotation();

	    if (this.isInline()) {
	      return [this.css]
	    } else {
	      return [this.css, this.map]
	    }
	  }

	  generateString() {
	    this.css = '';
	    this.map = new SourceMapGenerator({
	      file: this.outputFile(),
	      ignoreInvalidMapping: true
	    });

	    let line = 1;
	    let column = 1;

	    let noSource = '<no source>';
	    let mapping = {
	      generated: { column: 0, line: 0 },
	      original: { column: 0, line: 0 },
	      source: ''
	    };

	    let last, lines;
	    this.stringify(this.root, (str, node, type) => {
	      this.css += str;

	      if (node && type !== 'end') {
	        mapping.generated.line = line;
	        mapping.generated.column = column - 1;
	        if (node.source && node.source.start) {
	          mapping.source = this.sourcePath(node);
	          mapping.original.line = node.source.start.line;
	          mapping.original.column = node.source.start.column - 1;
	          this.map.addMapping(mapping);
	        } else {
	          mapping.source = noSource;
	          mapping.original.line = 1;
	          mapping.original.column = 0;
	          this.map.addMapping(mapping);
	        }
	      }

	      lines = str.match(/\n/g);
	      if (lines) {
	        line += lines.length;
	        last = str.lastIndexOf('\n');
	        column = str.length - last;
	      } else {
	        column += str.length;
	      }

	      if (node && type !== 'start') {
	        let p = node.parent || { raws: {} };
	        let childless =
	          node.type === 'decl' || (node.type === 'atrule' && !node.nodes);
	        if (!childless || node !== p.last || p.raws.semicolon) {
	          if (node.source && node.source.end) {
	            mapping.source = this.sourcePath(node);
	            mapping.original.line = node.source.end.line;
	            mapping.original.column = node.source.end.column - 1;
	            mapping.generated.line = line;
	            mapping.generated.column = column - 2;
	            this.map.addMapping(mapping);
	          } else {
	            mapping.source = noSource;
	            mapping.original.line = 1;
	            mapping.original.column = 0;
	            mapping.generated.line = line;
	            mapping.generated.column = column - 1;
	            this.map.addMapping(mapping);
	          }
	        }
	      }
	    });
	  }

	  isAnnotation() {
	    if (this.isInline()) {
	      return true
	    }
	    if (typeof this.mapOpts.annotation !== 'undefined') {
	      return this.mapOpts.annotation
	    }
	    if (this.previous().length) {
	      return this.previous().some(i => i.annotation)
	    }
	    return true
	  }

	  isInline() {
	    if (typeof this.mapOpts.inline !== 'undefined') {
	      return this.mapOpts.inline
	    }

	    let annotation = this.mapOpts.annotation;
	    if (typeof annotation !== 'undefined' && annotation !== true) {
	      return false
	    }

	    if (this.previous().length) {
	      return this.previous().some(i => i.inline)
	    }
	    return true
	  }

	  isMap() {
	    if (typeof this.opts.map !== 'undefined') {
	      return !!this.opts.map
	    }
	    return this.previous().length > 0
	  }

	  isSourcesContent() {
	    if (typeof this.mapOpts.sourcesContent !== 'undefined') {
	      return this.mapOpts.sourcesContent
	    }
	    if (this.previous().length) {
	      return this.previous().some(i => i.withContent())
	    }
	    return true
	  }

	  outputFile() {
	    if (this.opts.to) {
	      return this.path(this.opts.to)
	    } else if (this.opts.from) {
	      return this.path(this.opts.from)
	    } else {
	      return 'to.css'
	    }
	  }

	  path(file) {
	    if (this.mapOpts.absolute) return file
	    if (file.charCodeAt(0) === 60 /* `<` */) return file
	    if (/^\w+:\/\//.test(file)) return file
	    let cached = this.memoizedPaths.get(file);
	    if (cached) return cached

	    let from = this.opts.to ? dirname(this.opts.to) : '.';

	    if (typeof this.mapOpts.annotation === 'string') {
	      from = dirname(resolve(from, this.mapOpts.annotation));
	    }

	    let path = relative(from, file);
	    this.memoizedPaths.set(file, path);

	    return path
	  }

	  previous() {
	    if (!this.previousMaps) {
	      this.previousMaps = [];
	      if (this.root) {
	        this.root.walk(node => {
	          if (node.source && node.source.input.map) {
	            let map = node.source.input.map;
	            if (!this.previousMaps.includes(map)) {
	              this.previousMaps.push(map);
	            }
	          }
	        });
	      } else {
	        let input = new Input(this.originalCSS, this.opts);
	        if (input.map) this.previousMaps.push(input.map);
	      }
	    }

	    return this.previousMaps
	  }

	  setSourcesContent() {
	    let already = {};
	    if (this.root) {
	      this.root.walk(node => {
	        if (node.source) {
	          let from = node.source.input.from;
	          if (from && !already[from]) {
	            already[from] = true;
	            let fromUrl = this.usesFileUrls
	              ? this.toFileUrl(from)
	              : this.toUrl(this.path(from));
	            this.map.setSourceContent(fromUrl, node.source.input.css);
	          }
	        }
	      });
	    } else if (this.css) {
	      let from = this.opts.from
	        ? this.toUrl(this.path(this.opts.from))
	        : '<no source>';
	      this.map.setSourceContent(from, this.css);
	    }
	  }

	  sourcePath(node) {
	    if (this.mapOpts.from) {
	      return this.toUrl(this.mapOpts.from)
	    } else if (this.usesFileUrls) {
	      return this.toFileUrl(node.source.input.from)
	    } else {
	      return this.toUrl(this.path(node.source.input.from))
	    }
	  }

	  toBase64(str) {
	    if (Buffer) {
	      return Buffer.from(str).toString('base64')
	    } else {
	      return window.btoa(unescape(encodeURIComponent(str)))
	    }
	  }

	  toFileUrl(path) {
	    let cached = this.memoizedFileURLs.get(path);
	    if (cached) return cached

	    if (pathToFileURL) {
	      let fileURL = pathToFileURL(path).toString();
	      this.memoizedFileURLs.set(path, fileURL);

	      return fileURL
	    } else {
	      throw new Error(
	        '`map.absolute` option is not available in this PostCSS build'
	      )
	    }
	  }

	  toUrl(path) {
	    let cached = this.memoizedURLs.get(path);
	    if (cached) return cached

	    if (sep === '\\') {
	      path = path.replace(/\\/g, '/');
	    }

	    let url = encodeURI(path).replace(/[#?]/g, encodeURIComponent);
	    this.memoizedURLs.set(path, url);

	    return url
	  }
	}

	mapGenerator = MapGenerator;
	return mapGenerator;
}

var tokenize;
var hasRequiredTokenize;

function requireTokenize () {
	if (hasRequiredTokenize) return tokenize;
	hasRequiredTokenize = 1;

	const SINGLE_QUOTE = "'".charCodeAt(0);
	const DOUBLE_QUOTE = '"'.charCodeAt(0);
	const BACKSLASH = '\\'.charCodeAt(0);
	const SLASH = '/'.charCodeAt(0);
	const NEWLINE = '\n'.charCodeAt(0);
	const SPACE = ' '.charCodeAt(0);
	const FEED = '\f'.charCodeAt(0);
	const TAB = '\t'.charCodeAt(0);
	const CR = '\r'.charCodeAt(0);
	const OPEN_SQUARE = '['.charCodeAt(0);
	const CLOSE_SQUARE = ']'.charCodeAt(0);
	const OPEN_PARENTHESES = '('.charCodeAt(0);
	const CLOSE_PARENTHESES = ')'.charCodeAt(0);
	const OPEN_CURLY = '{'.charCodeAt(0);
	const CLOSE_CURLY = '}'.charCodeAt(0);
	const SEMICOLON = ';'.charCodeAt(0);
	const ASTERISK = '*'.charCodeAt(0);
	const COLON = ':'.charCodeAt(0);
	const AT = '@'.charCodeAt(0);

	const RE_AT_END = /[\t\n\f\r "#'()/;[\\\]{}]/g;
	const RE_WORD_END = /[\t\n\f\r !"#'():;@[\\\]{}]|\/(?=\*)/g;
	const RE_BAD_BRACKET = /.[\r\n"'(/\\]/;
	const RE_HEX_ESCAPE = /[\da-f]/i;

	tokenize = function tokenizer(input, options = {}) {
	  let css = input.css.valueOf();
	  let ignore = options.ignoreErrors;

	  let code, content, escape, next, quote;
	  let currentToken, escaped, escapePos, n, prev;

	  let length = css.length;
	  let pos = 0;
	  let buffer = [];
	  let returned = [];

	  function position() {
	    return pos
	  }

	  function unclosed(what) {
	    throw input.error('Unclosed ' + what, pos)
	  }

	  function endOfFile() {
	    return returned.length === 0 && pos >= length
	  }

	  function nextToken(opts) {
	    if (returned.length) return returned.pop()
	    if (pos >= length) return

	    let ignoreUnclosed = opts ? opts.ignoreUnclosed : false;

	    code = css.charCodeAt(pos);

	    switch (code) {
	      case NEWLINE:
	      case SPACE:
	      case TAB:
	      case CR:
	      case FEED: {
	        next = pos;
	        do {
	          next += 1;
	          code = css.charCodeAt(next);
	        } while (
	          code === SPACE ||
	          code === NEWLINE ||
	          code === TAB ||
	          code === CR ||
	          code === FEED
	        )

	        currentToken = ['space', css.slice(pos, next)];
	        pos = next - 1;
	        break
	      }

	      case OPEN_SQUARE:
	      case CLOSE_SQUARE:
	      case OPEN_CURLY:
	      case CLOSE_CURLY:
	      case COLON:
	      case SEMICOLON:
	      case CLOSE_PARENTHESES: {
	        let controlChar = String.fromCharCode(code);
	        currentToken = [controlChar, controlChar, pos];
	        break
	      }

	      case OPEN_PARENTHESES: {
	        prev = buffer.length ? buffer.pop()[1] : '';
	        n = css.charCodeAt(pos + 1);
	        if (
	          prev === 'url' &&
	          n !== SINGLE_QUOTE &&
	          n !== DOUBLE_QUOTE &&
	          n !== SPACE &&
	          n !== NEWLINE &&
	          n !== TAB &&
	          n !== FEED &&
	          n !== CR
	        ) {
	          next = pos;
	          do {
	            escaped = false;
	            next = css.indexOf(')', next + 1);
	            if (next === -1) {
	              if (ignore || ignoreUnclosed) {
	                next = pos;
	                break
	              } else {
	                unclosed('bracket');
	              }
	            }
	            escapePos = next;
	            while (css.charCodeAt(escapePos - 1) === BACKSLASH) {
	              escapePos -= 1;
	              escaped = !escaped;
	            }
	          } while (escaped)

	          currentToken = ['brackets', css.slice(pos, next + 1), pos, next];

	          pos = next;
	        } else {
	          next = css.indexOf(')', pos + 1);
	          content = css.slice(pos, next + 1);

	          if (next === -1 || RE_BAD_BRACKET.test(content)) {
	            currentToken = ['(', '(', pos];
	          } else {
	            currentToken = ['brackets', content, pos, next];
	            pos = next;
	          }
	        }

	        break
	      }

	      case SINGLE_QUOTE:
	      case DOUBLE_QUOTE: {
	        quote = code === SINGLE_QUOTE ? "'" : '"';
	        next = pos;
	        do {
	          escaped = false;
	          next = css.indexOf(quote, next + 1);
	          if (next === -1) {
	            if (ignore || ignoreUnclosed) {
	              next = pos + 1;
	              break
	            } else {
	              unclosed('string');
	            }
	          }
	          escapePos = next;
	          while (css.charCodeAt(escapePos - 1) === BACKSLASH) {
	            escapePos -= 1;
	            escaped = !escaped;
	          }
	        } while (escaped)

	        currentToken = ['string', css.slice(pos, next + 1), pos, next];
	        pos = next;
	        break
	      }

	      case AT: {
	        RE_AT_END.lastIndex = pos + 1;
	        RE_AT_END.test(css);
	        if (RE_AT_END.lastIndex === 0) {
	          next = css.length - 1;
	        } else {
	          next = RE_AT_END.lastIndex - 2;
	        }

	        currentToken = ['at-word', css.slice(pos, next + 1), pos, next];

	        pos = next;
	        break
	      }

	      case BACKSLASH: {
	        next = pos;
	        escape = true;
	        while (css.charCodeAt(next + 1) === BACKSLASH) {
	          next += 1;
	          escape = !escape;
	        }
	        code = css.charCodeAt(next + 1);
	        if (
	          escape &&
	          code !== SLASH &&
	          code !== SPACE &&
	          code !== NEWLINE &&
	          code !== TAB &&
	          code !== CR &&
	          code !== FEED
	        ) {
	          next += 1;
	          if (RE_HEX_ESCAPE.test(css.charAt(next))) {
	            while (RE_HEX_ESCAPE.test(css.charAt(next + 1))) {
	              next += 1;
	            }
	            if (css.charCodeAt(next + 1) === SPACE) {
	              next += 1;
	            }
	          }
	        }

	        currentToken = ['word', css.slice(pos, next + 1), pos, next];

	        pos = next;
	        break
	      }

	      default: {
	        if (code === SLASH && css.charCodeAt(pos + 1) === ASTERISK) {
	          next = css.indexOf('*/', pos + 2) + 1;
	          if (next === 0) {
	            if (ignore || ignoreUnclosed) {
	              next = css.length;
	            } else {
	              unclosed('comment');
	            }
	          }

	          currentToken = ['comment', css.slice(pos, next + 1), pos, next];
	          pos = next;
	        } else {
	          RE_WORD_END.lastIndex = pos + 1;
	          RE_WORD_END.test(css);
	          if (RE_WORD_END.lastIndex === 0) {
	            next = css.length - 1;
	          } else {
	            next = RE_WORD_END.lastIndex - 2;
	          }

	          currentToken = ['word', css.slice(pos, next + 1), pos, next];
	          buffer.push(currentToken);
	          pos = next;
	        }

	        break
	      }
	    }

	    pos++;
	    return currentToken
	  }

	  function back(token) {
	    returned.push(token);
	  }

	  return {
	    back,
	    endOfFile,
	    nextToken,
	    position
	  }
	};
	return tokenize;
}

var parser;
var hasRequiredParser;

function requireParser () {
	if (hasRequiredParser) return parser;
	hasRequiredParser = 1;

	let AtRule = requireAtRule();
	let Comment = requireComment();
	let Declaration = requireDeclaration();
	let Root = requireRoot();
	let Rule = requireRule();
	let tokenizer = requireTokenize();

	const SAFE_COMMENT_NEIGHBOR = {
	  empty: true,
	  space: true
	};

	function findLastWithPosition(tokens) {
	  for (let i = tokens.length - 1; i >= 0; i--) {
	    let token = tokens[i];
	    let pos = token[3] || token[2];
	    if (pos) return pos
	  }
	}

	class Parser {
	  constructor(input) {
	    this.input = input;

	    this.root = new Root();
	    this.current = this.root;
	    this.spaces = '';
	    this.semicolon = false;

	    this.createTokenizer();
	    this.root.source = { input, start: { column: 1, line: 1, offset: 0 } };
	  }

	  atrule(token) {
	    let node = new AtRule();
	    node.name = token[1].slice(1);
	    if (node.name === '') {
	      this.unnamedAtrule(node, token);
	    }
	    this.init(node, token[2]);

	    let type;
	    let prev;
	    let shift;
	    let last = false;
	    let open = false;
	    let params = [];
	    let brackets = [];

	    while (!this.tokenizer.endOfFile()) {
	      token = this.tokenizer.nextToken();
	      type = token[0];

	      if (type === '(' || type === '[') {
	        brackets.push(type === '(' ? ')' : ']');
	      } else if (type === '{' && brackets.length > 0) {
	        brackets.push('}');
	      } else if (type === brackets[brackets.length - 1]) {
	        brackets.pop();
	      }

	      if (brackets.length === 0) {
	        if (type === ';') {
	          node.source.end = this.getPosition(token[2]);
	          node.source.end.offset++;
	          this.semicolon = true;
	          break
	        } else if (type === '{') {
	          open = true;
	          break
	        } else if (type === '}') {
	          if (params.length > 0) {
	            shift = params.length - 1;
	            prev = params[shift];
	            while (prev && prev[0] === 'space') {
	              prev = params[--shift];
	            }
	            if (prev) {
	              node.source.end = this.getPosition(prev[3] || prev[2]);
	              node.source.end.offset++;
	            }
	          }
	          this.end(token);
	          break
	        } else {
	          params.push(token);
	        }
	      } else {
	        params.push(token);
	      }

	      if (this.tokenizer.endOfFile()) {
	        last = true;
	        break
	      }
	    }

	    node.raws.between = this.spacesAndCommentsFromEnd(params);
	    if (params.length) {
	      node.raws.afterName = this.spacesAndCommentsFromStart(params);
	      this.raw(node, 'params', params);
	      if (last) {
	        token = params[params.length - 1];
	        node.source.end = this.getPosition(token[3] || token[2]);
	        node.source.end.offset++;
	        this.spaces = node.raws.between;
	        node.raws.between = '';
	      }
	    } else {
	      node.raws.afterName = '';
	      node.params = '';
	    }

	    if (open) {
	      node.nodes = [];
	      this.current = node;
	    }
	  }

	  checkMissedSemicolon(tokens) {
	    let colon = this.colon(tokens);
	    if (colon === false) return

	    let founded = 0;
	    let token;
	    for (let j = colon - 1; j >= 0; j--) {
	      token = tokens[j];
	      if (token[0] !== 'space') {
	        founded += 1;
	        if (founded === 2) break
	      }
	    }
	    // If the token is a word, e.g. `!important`, `red` or any other valid property's value.
	    // Then we need to return the colon after that word token. [3] is the "end" colon of that word.
	    // And because we need it after that one we do +1 to get the next one.
	    throw this.input.error(
	      'Missed semicolon',
	      token[0] === 'word' ? token[3] + 1 : token[2]
	    )
	  }

	  colon(tokens) {
	    let brackets = 0;
	    let prev, token, type;
	    for (let [i, element] of tokens.entries()) {
	      token = element;
	      type = token[0];

	      if (type === '(') {
	        brackets += 1;
	      }
	      if (type === ')') {
	        brackets -= 1;
	      }
	      if (brackets === 0 && type === ':') {
	        if (!prev) {
	          this.doubleColon(token);
	        } else if (prev[0] === 'word' && prev[1] === 'progid') {
	          continue
	        } else {
	          return i
	        }
	      }

	      prev = token;
	    }
	    return false
	  }

	  comment(token) {
	    let node = new Comment();
	    this.init(node, token[2]);
	    node.source.end = this.getPosition(token[3] || token[2]);
	    node.source.end.offset++;

	    let text = token[1].slice(2, -2);
	    if (/^\s*$/.test(text)) {
	      node.text = '';
	      node.raws.left = text;
	      node.raws.right = '';
	    } else {
	      let match = text.match(/^(\s*)([^]*\S)(\s*)$/);
	      node.text = match[2];
	      node.raws.left = match[1];
	      node.raws.right = match[3];
	    }
	  }

	  createTokenizer() {
	    this.tokenizer = tokenizer(this.input);
	  }

	  decl(tokens, customProperty) {
	    let node = new Declaration();
	    this.init(node, tokens[0][2]);

	    let last = tokens[tokens.length - 1];
	    if (last[0] === ';') {
	      this.semicolon = true;
	      tokens.pop();
	    }

	    node.source.end = this.getPosition(
	      last[3] || last[2] || findLastWithPosition(tokens)
	    );
	    node.source.end.offset++;

	    while (tokens[0][0] !== 'word') {
	      if (tokens.length === 1) this.unknownWord(tokens);
	      node.raws.before += tokens.shift()[1];
	    }
	    node.source.start = this.getPosition(tokens[0][2]);

	    node.prop = '';
	    while (tokens.length) {
	      let type = tokens[0][0];
	      if (type === ':' || type === 'space' || type === 'comment') {
	        break
	      }
	      node.prop += tokens.shift()[1];
	    }

	    node.raws.between = '';

	    let token;
	    while (tokens.length) {
	      token = tokens.shift();

	      if (token[0] === ':') {
	        node.raws.between += token[1];
	        break
	      } else {
	        if (token[0] === 'word' && /\w/.test(token[1])) {
	          this.unknownWord([token]);
	        }
	        node.raws.between += token[1];
	      }
	    }

	    if (node.prop[0] === '_' || node.prop[0] === '*') {
	      node.raws.before += node.prop[0];
	      node.prop = node.prop.slice(1);
	    }

	    let firstSpaces = [];
	    let next;
	    while (tokens.length) {
	      next = tokens[0][0];
	      if (next !== 'space' && next !== 'comment') break
	      firstSpaces.push(tokens.shift());
	    }

	    this.precheckMissedSemicolon(tokens);

	    for (let i = tokens.length - 1; i >= 0; i--) {
	      token = tokens[i];
	      if (token[1].toLowerCase() === '!important') {
	        node.important = true;
	        let string = this.stringFrom(tokens, i);
	        string = this.spacesFromEnd(tokens) + string;
	        if (string !== ' !important') node.raws.important = string;
	        break
	      } else if (token[1].toLowerCase() === 'important') {
	        let cache = tokens.slice(0);
	        let str = '';
	        for (let j = i; j > 0; j--) {
	          let type = cache[j][0];
	          if (str.trim().startsWith('!') && type !== 'space') {
	            break
	          }
	          str = cache.pop()[1] + str;
	        }
	        if (str.trim().startsWith('!')) {
	          node.important = true;
	          node.raws.important = str;
	          tokens = cache;
	        }
	      }

	      if (token[0] !== 'space' && token[0] !== 'comment') {
	        break
	      }
	    }

	    let hasWord = tokens.some(i => i[0] !== 'space' && i[0] !== 'comment');

	    if (hasWord) {
	      node.raws.between += firstSpaces.map(i => i[1]).join('');
	      firstSpaces = [];
	    }
	    this.raw(node, 'value', firstSpaces.concat(tokens), customProperty);

	    if (node.value.includes(':') && !customProperty) {
	      this.checkMissedSemicolon(tokens);
	    }
	  }

	  doubleColon(token) {
	    throw this.input.error(
	      'Double colon',
	      { offset: token[2] },
	      { offset: token[2] + token[1].length }
	    )
	  }

	  emptyRule(token) {
	    let node = new Rule();
	    this.init(node, token[2]);
	    node.selector = '';
	    node.raws.between = '';
	    this.current = node;
	  }

	  end(token) {
	    if (this.current.nodes && this.current.nodes.length) {
	      this.current.raws.semicolon = this.semicolon;
	    }
	    this.semicolon = false;

	    this.current.raws.after = (this.current.raws.after || '') + this.spaces;
	    this.spaces = '';

	    if (this.current.parent) {
	      this.current.source.end = this.getPosition(token[2]);
	      this.current.source.end.offset++;
	      this.current = this.current.parent;
	    } else {
	      this.unexpectedClose(token);
	    }
	  }

	  endFile() {
	    if (this.current.parent) this.unclosedBlock();
	    if (this.current.nodes && this.current.nodes.length) {
	      this.current.raws.semicolon = this.semicolon;
	    }
	    this.current.raws.after = (this.current.raws.after || '') + this.spaces;
	    this.root.source.end = this.getPosition(this.tokenizer.position());
	  }

	  freeSemicolon(token) {
	    this.spaces += token[1];
	    if (this.current.nodes) {
	      let prev = this.current.nodes[this.current.nodes.length - 1];
	      if (prev && prev.type === 'rule' && !prev.raws.ownSemicolon) {
	        prev.raws.ownSemicolon = this.spaces;
	        this.spaces = '';
	        prev.source.end = this.getPosition(token[2]);
	        prev.source.end.offset += prev.raws.ownSemicolon.length;
	      }
	    }
	  }

	  // Helpers

	  getPosition(offset) {
	    let pos = this.input.fromOffset(offset);
	    return {
	      column: pos.col,
	      line: pos.line,
	      offset
	    }
	  }

	  init(node, offset) {
	    this.current.push(node);
	    node.source = {
	      input: this.input,
	      start: this.getPosition(offset)
	    };
	    node.raws.before = this.spaces;
	    this.spaces = '';
	    if (node.type !== 'comment') this.semicolon = false;
	  }

	  other(start) {
	    let end = false;
	    let type = null;
	    let colon = false;
	    let bracket = null;
	    let brackets = [];
	    let customProperty = start[1].startsWith('--');

	    let tokens = [];
	    let token = start;
	    while (token) {
	      type = token[0];
	      tokens.push(token);

	      if (type === '(' || type === '[') {
	        if (!bracket) bracket = token;
	        brackets.push(type === '(' ? ')' : ']');
	      } else if (customProperty && colon && type === '{') {
	        if (!bracket) bracket = token;
	        brackets.push('}');
	      } else if (brackets.length === 0) {
	        if (type === ';') {
	          if (colon) {
	            this.decl(tokens, customProperty);
	            return
	          } else {
	            break
	          }
	        } else if (type === '{') {
	          this.rule(tokens);
	          return
	        } else if (type === '}') {
	          this.tokenizer.back(tokens.pop());
	          end = true;
	          break
	        } else if (type === ':') {
	          colon = true;
	        }
	      } else if (type === brackets[brackets.length - 1]) {
	        brackets.pop();
	        if (brackets.length === 0) bracket = null;
	      }

	      token = this.tokenizer.nextToken();
	    }

	    if (this.tokenizer.endOfFile()) end = true;
	    if (brackets.length > 0) this.unclosedBracket(bracket);

	    if (end && colon) {
	      if (!customProperty) {
	        while (tokens.length) {
	          token = tokens[tokens.length - 1][0];
	          if (token !== 'space' && token !== 'comment') break
	          this.tokenizer.back(tokens.pop());
	        }
	      }
	      this.decl(tokens, customProperty);
	    } else {
	      this.unknownWord(tokens);
	    }
	  }

	  parse() {
	    let token;
	    while (!this.tokenizer.endOfFile()) {
	      token = this.tokenizer.nextToken();

	      switch (token[0]) {
	        case 'space':
	          this.spaces += token[1];
	          break

	        case ';':
	          this.freeSemicolon(token);
	          break

	        case '}':
	          this.end(token);
	          break

	        case 'comment':
	          this.comment(token);
	          break

	        case 'at-word':
	          this.atrule(token);
	          break

	        case '{':
	          this.emptyRule(token);
	          break

	        default:
	          this.other(token);
	          break
	      }
	    }
	    this.endFile();
	  }

	  precheckMissedSemicolon(/* tokens */) {
	    // Hook for Safe Parser
	  }

	  raw(node, prop, tokens, customProperty) {
	    let token, type;
	    let length = tokens.length;
	    let value = '';
	    let clean = true;
	    let next, prev;

	    for (let i = 0; i < length; i += 1) {
	      token = tokens[i];
	      type = token[0];
	      if (type === 'space' && i === length - 1 && !customProperty) {
	        clean = false;
	      } else if (type === 'comment') {
	        prev = tokens[i - 1] ? tokens[i - 1][0] : 'empty';
	        next = tokens[i + 1] ? tokens[i + 1][0] : 'empty';
	        if (!SAFE_COMMENT_NEIGHBOR[prev] && !SAFE_COMMENT_NEIGHBOR[next]) {
	          if (value.slice(-1) === ',') {
	            clean = false;
	          } else {
	            value += token[1];
	          }
	        } else {
	          clean = false;
	        }
	      } else {
	        value += token[1];
	      }
	    }
	    if (!clean) {
	      let raw = tokens.reduce((all, i) => all + i[1], '');
	      node.raws[prop] = { raw, value };
	    }
	    node[prop] = value;
	  }

	  rule(tokens) {
	    tokens.pop();

	    let node = new Rule();
	    this.init(node, tokens[0][2]);

	    node.raws.between = this.spacesAndCommentsFromEnd(tokens);
	    this.raw(node, 'selector', tokens);
	    this.current = node;
	  }

	  spacesAndCommentsFromEnd(tokens) {
	    let lastTokenType;
	    let spaces = '';
	    while (tokens.length) {
	      lastTokenType = tokens[tokens.length - 1][0];
	      if (lastTokenType !== 'space' && lastTokenType !== 'comment') break
	      spaces = tokens.pop()[1] + spaces;
	    }
	    return spaces
	  }

	  // Errors

	  spacesAndCommentsFromStart(tokens) {
	    let next;
	    let spaces = '';
	    while (tokens.length) {
	      next = tokens[0][0];
	      if (next !== 'space' && next !== 'comment') break
	      spaces += tokens.shift()[1];
	    }
	    return spaces
	  }

	  spacesFromEnd(tokens) {
	    let lastTokenType;
	    let spaces = '';
	    while (tokens.length) {
	      lastTokenType = tokens[tokens.length - 1][0];
	      if (lastTokenType !== 'space') break
	      spaces = tokens.pop()[1] + spaces;
	    }
	    return spaces
	  }

	  stringFrom(tokens, from) {
	    let result = '';
	    for (let i = from; i < tokens.length; i++) {
	      result += tokens[i][1];
	    }
	    tokens.splice(from, tokens.length - from);
	    return result
	  }

	  unclosedBlock() {
	    let pos = this.current.source.start;
	    throw this.input.error('Unclosed block', pos.line, pos.column)
	  }

	  unclosedBracket(bracket) {
	    throw this.input.error(
	      'Unclosed bracket',
	      { offset: bracket[2] },
	      { offset: bracket[2] + 1 }
	    )
	  }

	  unexpectedClose(token) {
	    throw this.input.error(
	      'Unexpected }',
	      { offset: token[2] },
	      { offset: token[2] + 1 }
	    )
	  }

	  unknownWord(tokens) {
	    throw this.input.error(
	      'Unknown word ' + tokens[0][1],
	      { offset: tokens[0][2] },
	      { offset: tokens[0][2] + tokens[0][1].length }
	    )
	  }

	  unnamedAtrule(node, token) {
	    throw this.input.error(
	      'At-rule without name',
	      { offset: token[2] },
	      { offset: token[2] + token[1].length }
	    )
	  }
	}

	parser = Parser;
	return parser;
}

var parse_1;
var hasRequiredParse;

function requireParse () {
	if (hasRequiredParse) return parse_1;
	hasRequiredParse = 1;

	let Container = requireContainer();
	let Input = requireInput();
	let Parser = requireParser();

	function parse(css, opts) {
	  let input = new Input(css, opts);
	  let parser = new Parser(input);
	  try {
	    parser.parse();
	  } catch (e) {
	    if (process.env.NODE_ENV !== 'production') {
	      if (e.name === 'CssSyntaxError' && opts && opts.from) {
	        if (/\.scss$/i.test(opts.from)) {
	          e.message +=
	            '\nYou tried to parse SCSS with ' +
	            'the standard CSS parser; ' +
	            'try again with the postcss-scss parser';
	        } else if (/\.sass/i.test(opts.from)) {
	          e.message +=
	            '\nYou tried to parse Sass with ' +
	            'the standard CSS parser; ' +
	            'try again with the postcss-sass parser';
	        } else if (/\.less$/i.test(opts.from)) {
	          e.message +=
	            '\nYou tried to parse Less with ' +
	            'the standard CSS parser; ' +
	            'try again with the postcss-less parser';
	        }
	      }
	    }
	    throw e
	  }

	  return parser.root
	}

	parse_1 = parse;
	parse.default = parse;

	Container.registerParse(parse);
	return parse_1;
}

var warning;
var hasRequiredWarning;

function requireWarning () {
	if (hasRequiredWarning) return warning;
	hasRequiredWarning = 1;

	class Warning {
	  constructor(text, opts = {}) {
	    this.type = 'warning';
	    this.text = text;

	    if (opts.node && opts.node.source) {
	      let range = opts.node.rangeBy(opts);
	      this.line = range.start.line;
	      this.column = range.start.column;
	      this.endLine = range.end.line;
	      this.endColumn = range.end.column;
	    }

	    for (let opt in opts) this[opt] = opts[opt];
	  }

	  toString() {
	    if (this.node) {
	      return this.node.error(this.text, {
	        index: this.index,
	        plugin: this.plugin,
	        word: this.word
	      }).message
	    }

	    if (this.plugin) {
	      return this.plugin + ': ' + this.text
	    }

	    return this.text
	  }
	}

	warning = Warning;
	Warning.default = Warning;
	return warning;
}

var result;
var hasRequiredResult;

function requireResult () {
	if (hasRequiredResult) return result;
	hasRequiredResult = 1;

	let Warning = requireWarning();

	class Result {
	  get content() {
	    return this.css
	  }

	  constructor(processor, root, opts) {
	    this.processor = processor;
	    this.messages = [];
	    this.root = root;
	    this.opts = opts;
	    this.css = undefined;
	    this.map = undefined;
	  }

	  toString() {
	    return this.css
	  }

	  warn(text, opts = {}) {
	    if (!opts.plugin) {
	      if (this.lastPlugin && this.lastPlugin.postcssPlugin) {
	        opts.plugin = this.lastPlugin.postcssPlugin;
	      }
	    }

	    let warning = new Warning(text, opts);
	    this.messages.push(warning);

	    return warning
	  }

	  warnings() {
	    return this.messages.filter(i => i.type === 'warning')
	  }
	}

	result = Result;
	Result.default = Result;
	return result;
}

/* eslint-disable no-console */

var warnOnce;
var hasRequiredWarnOnce;

function requireWarnOnce () {
	if (hasRequiredWarnOnce) return warnOnce;
	hasRequiredWarnOnce = 1;

	let printed = {};

	warnOnce = function warnOnce(message) {
	  if (printed[message]) return
	  printed[message] = true;

	  if (typeof console !== 'undefined' && console.warn) {
	    console.warn(message);
	  }
	};
	return warnOnce;
}

var lazyResult;
var hasRequiredLazyResult;

function requireLazyResult () {
	if (hasRequiredLazyResult) return lazyResult;
	hasRequiredLazyResult = 1;

	let Container = requireContainer();
	let Document = requireDocument();
	let MapGenerator = requireMapGenerator();
	let parse = requireParse();
	let Result = requireResult();
	let Root = requireRoot();
	let stringify = requireStringify();
	let { isClean, my } = requireSymbols();
	let warnOnce = requireWarnOnce();

	const TYPE_TO_CLASS_NAME = {
	  atrule: 'AtRule',
	  comment: 'Comment',
	  decl: 'Declaration',
	  document: 'Document',
	  root: 'Root',
	  rule: 'Rule'
	};

	const PLUGIN_PROPS = {
	  AtRule: true,
	  AtRuleExit: true,
	  Comment: true,
	  CommentExit: true,
	  Declaration: true,
	  DeclarationExit: true,
	  Document: true,
	  DocumentExit: true,
	  Once: true,
	  OnceExit: true,
	  postcssPlugin: true,
	  prepare: true,
	  Root: true,
	  RootExit: true,
	  Rule: true,
	  RuleExit: true
	};

	const NOT_VISITORS = {
	  Once: true,
	  postcssPlugin: true,
	  prepare: true
	};

	const CHILDREN = 0;

	function isPromise(obj) {
	  return typeof obj === 'object' && typeof obj.then === 'function'
	}

	function getEvents(node) {
	  let key = false;
	  let type = TYPE_TO_CLASS_NAME[node.type];
	  if (node.type === 'decl') {
	    key = node.prop.toLowerCase();
	  } else if (node.type === 'atrule') {
	    key = node.name.toLowerCase();
	  }

	  if (key && node.append) {
	    return [
	      type,
	      type + '-' + key,
	      CHILDREN,
	      type + 'Exit',
	      type + 'Exit-' + key
	    ]
	  } else if (key) {
	    return [type, type + '-' + key, type + 'Exit', type + 'Exit-' + key]
	  } else if (node.append) {
	    return [type, CHILDREN, type + 'Exit']
	  } else {
	    return [type, type + 'Exit']
	  }
	}

	function toStack(node) {
	  let events;
	  if (node.type === 'document') {
	    events = ['Document', CHILDREN, 'DocumentExit'];
	  } else if (node.type === 'root') {
	    events = ['Root', CHILDREN, 'RootExit'];
	  } else {
	    events = getEvents(node);
	  }

	  return {
	    eventIndex: 0,
	    events,
	    iterator: 0,
	    node,
	    visitorIndex: 0,
	    visitors: []
	  }
	}

	function cleanMarks(node) {
	  node[isClean] = false;
	  if (node.nodes) node.nodes.forEach(i => cleanMarks(i));
	  return node
	}

	let postcss = {};

	class LazyResult {
	  get content() {
	    return this.stringify().content
	  }

	  get css() {
	    return this.stringify().css
	  }

	  get map() {
	    return this.stringify().map
	  }

	  get messages() {
	    return this.sync().messages
	  }

	  get opts() {
	    return this.result.opts
	  }

	  get processor() {
	    return this.result.processor
	  }

	  get root() {
	    return this.sync().root
	  }

	  get [Symbol.toStringTag]() {
	    return 'LazyResult'
	  }

	  constructor(processor, css, opts) {
	    this.stringified = false;
	    this.processed = false;

	    let root;
	    if (
	      typeof css === 'object' &&
	      css !== null &&
	      (css.type === 'root' || css.type === 'document')
	    ) {
	      root = cleanMarks(css);
	    } else if (css instanceof LazyResult || css instanceof Result) {
	      root = cleanMarks(css.root);
	      if (css.map) {
	        if (typeof opts.map === 'undefined') opts.map = {};
	        if (!opts.map.inline) opts.map.inline = false;
	        opts.map.prev = css.map;
	      }
	    } else {
	      let parser = parse;
	      if (opts.syntax) parser = opts.syntax.parse;
	      if (opts.parser) parser = opts.parser;
	      if (parser.parse) parser = parser.parse;

	      try {
	        root = parser(css, opts);
	      } catch (error) {
	        this.processed = true;
	        this.error = error;
	      }

	      if (root && !root[my]) {
	        /* c8 ignore next 2 */
	        Container.rebuild(root);
	      }
	    }

	    this.result = new Result(processor, root, opts);
	    this.helpers = { ...postcss, postcss, result: this.result };
	    this.plugins = this.processor.plugins.map(plugin => {
	      if (typeof plugin === 'object' && plugin.prepare) {
	        return { ...plugin, ...plugin.prepare(this.result) }
	      } else {
	        return plugin
	      }
	    });
	  }

	  async() {
	    if (this.error) return Promise.reject(this.error)
	    if (this.processed) return Promise.resolve(this.result)
	    if (!this.processing) {
	      this.processing = this.runAsync();
	    }
	    return this.processing
	  }

	  catch(onRejected) {
	    return this.async().catch(onRejected)
	  }

	  finally(onFinally) {
	    return this.async().then(onFinally, onFinally)
	  }

	  getAsyncError() {
	    throw new Error('Use process(css).then(cb) to work with async plugins')
	  }

	  handleError(error, node) {
	    let plugin = this.result.lastPlugin;
	    try {
	      if (node) node.addToError(error);
	      this.error = error;
	      if (error.name === 'CssSyntaxError' && !error.plugin) {
	        error.plugin = plugin.postcssPlugin;
	        error.setMessage();
	      } else if (plugin.postcssVersion) {
	        if (process.env.NODE_ENV !== 'production') {
	          let pluginName = plugin.postcssPlugin;
	          let pluginVer = plugin.postcssVersion;
	          let runtimeVer = this.result.processor.version;
	          let a = pluginVer.split('.');
	          let b = runtimeVer.split('.');

	          if (a[0] !== b[0] || parseInt(a[1]) > parseInt(b[1])) {
	            // eslint-disable-next-line no-console
	            console.error(
	              'Unknown error from PostCSS plugin. Your current PostCSS ' +
	                'version is ' +
	                runtimeVer +
	                ', but ' +
	                pluginName +
	                ' uses ' +
	                pluginVer +
	                '. Perhaps this is the source of the error below.'
	            );
	          }
	        }
	      }
	    } catch (err) {
	      /* c8 ignore next 3 */
	      // eslint-disable-next-line no-console
	      if (console && console.error) console.error(err);
	    }
	    return error
	  }

	  prepareVisitors() {
	    this.listeners = {};
	    let add = (plugin, type, cb) => {
	      if (!this.listeners[type]) this.listeners[type] = [];
	      this.listeners[type].push([plugin, cb]);
	    };
	    for (let plugin of this.plugins) {
	      if (typeof plugin === 'object') {
	        for (let event in plugin) {
	          if (!PLUGIN_PROPS[event] && /^[A-Z]/.test(event)) {
	            throw new Error(
	              `Unknown event ${event} in ${plugin.postcssPlugin}. ` +
	                `Try to update PostCSS (${this.processor.version} now).`
	            )
	          }
	          if (!NOT_VISITORS[event]) {
	            if (typeof plugin[event] === 'object') {
	              for (let filter in plugin[event]) {
	                if (filter === '*') {
	                  add(plugin, event, plugin[event][filter]);
	                } else {
	                  add(
	                    plugin,
	                    event + '-' + filter.toLowerCase(),
	                    plugin[event][filter]
	                  );
	                }
	              }
	            } else if (typeof plugin[event] === 'function') {
	              add(plugin, event, plugin[event]);
	            }
	          }
	        }
	      }
	    }
	    this.hasListener = Object.keys(this.listeners).length > 0;
	  }

	  async runAsync() {
	    this.plugin = 0;
	    for (let i = 0; i < this.plugins.length; i++) {
	      let plugin = this.plugins[i];
	      let promise = this.runOnRoot(plugin);
	      if (isPromise(promise)) {
	        try {
	          await promise;
	        } catch (error) {
	          throw this.handleError(error)
	        }
	      }
	    }

	    this.prepareVisitors();
	    if (this.hasListener) {
	      let root = this.result.root;
	      while (!root[isClean]) {
	        root[isClean] = true;
	        let stack = [toStack(root)];
	        while (stack.length > 0) {
	          let promise = this.visitTick(stack);
	          if (isPromise(promise)) {
	            try {
	              await promise;
	            } catch (e) {
	              let node = stack[stack.length - 1].node;
	              throw this.handleError(e, node)
	            }
	          }
	        }
	      }

	      if (this.listeners.OnceExit) {
	        for (let [plugin, visitor] of this.listeners.OnceExit) {
	          this.result.lastPlugin = plugin;
	          try {
	            if (root.type === 'document') {
	              let roots = root.nodes.map(subRoot =>
	                visitor(subRoot, this.helpers)
	              );

	              await Promise.all(roots);
	            } else {
	              await visitor(root, this.helpers);
	            }
	          } catch (e) {
	            throw this.handleError(e)
	          }
	        }
	      }
	    }

	    this.processed = true;
	    return this.stringify()
	  }

	  runOnRoot(plugin) {
	    this.result.lastPlugin = plugin;
	    try {
	      if (typeof plugin === 'object' && plugin.Once) {
	        if (this.result.root.type === 'document') {
	          let roots = this.result.root.nodes.map(root =>
	            plugin.Once(root, this.helpers)
	          );

	          if (isPromise(roots[0])) {
	            return Promise.all(roots)
	          }

	          return roots
	        }

	        return plugin.Once(this.result.root, this.helpers)
	      } else if (typeof plugin === 'function') {
	        return plugin(this.result.root, this.result)
	      }
	    } catch (error) {
	      throw this.handleError(error)
	    }
	  }

	  stringify() {
	    if (this.error) throw this.error
	    if (this.stringified) return this.result
	    this.stringified = true;

	    this.sync();

	    let opts = this.result.opts;
	    let str = stringify;
	    if (opts.syntax) str = opts.syntax.stringify;
	    if (opts.stringifier) str = opts.stringifier;
	    if (str.stringify) str = str.stringify;

	    let map = new MapGenerator(str, this.result.root, this.result.opts);
	    let data = map.generate();
	    this.result.css = data[0];
	    this.result.map = data[1];

	    return this.result
	  }

	  sync() {
	    if (this.error) throw this.error
	    if (this.processed) return this.result
	    this.processed = true;

	    if (this.processing) {
	      throw this.getAsyncError()
	    }

	    for (let plugin of this.plugins) {
	      let promise = this.runOnRoot(plugin);
	      if (isPromise(promise)) {
	        throw this.getAsyncError()
	      }
	    }

	    this.prepareVisitors();
	    if (this.hasListener) {
	      let root = this.result.root;
	      while (!root[isClean]) {
	        root[isClean] = true;
	        this.walkSync(root);
	      }
	      if (this.listeners.OnceExit) {
	        if (root.type === 'document') {
	          for (let subRoot of root.nodes) {
	            this.visitSync(this.listeners.OnceExit, subRoot);
	          }
	        } else {
	          this.visitSync(this.listeners.OnceExit, root);
	        }
	      }
	    }

	    return this.result
	  }

	  then(onFulfilled, onRejected) {
	    if (process.env.NODE_ENV !== 'production') {
	      if (!('from' in this.opts)) {
	        warnOnce(
	          'Without `from` option PostCSS could generate wrong source map ' +
	            'and will not find Browserslist config. Set it to CSS file path ' +
	            'or to `undefined` to prevent this warning.'
	        );
	      }
	    }
	    return this.async().then(onFulfilled, onRejected)
	  }

	  toString() {
	    return this.css
	  }

	  visitSync(visitors, node) {
	    for (let [plugin, visitor] of visitors) {
	      this.result.lastPlugin = plugin;
	      let promise;
	      try {
	        promise = visitor(node, this.helpers);
	      } catch (e) {
	        throw this.handleError(e, node.proxyOf)
	      }
	      if (node.type !== 'root' && node.type !== 'document' && !node.parent) {
	        return true
	      }
	      if (isPromise(promise)) {
	        throw this.getAsyncError()
	      }
	    }
	  }

	  visitTick(stack) {
	    let visit = stack[stack.length - 1];
	    let { node, visitors } = visit;

	    if (node.type !== 'root' && node.type !== 'document' && !node.parent) {
	      stack.pop();
	      return
	    }

	    if (visitors.length > 0 && visit.visitorIndex < visitors.length) {
	      let [plugin, visitor] = visitors[visit.visitorIndex];
	      visit.visitorIndex += 1;
	      if (visit.visitorIndex === visitors.length) {
	        visit.visitors = [];
	        visit.visitorIndex = 0;
	      }
	      this.result.lastPlugin = plugin;
	      try {
	        return visitor(node.toProxy(), this.helpers)
	      } catch (e) {
	        throw this.handleError(e, node)
	      }
	    }

	    if (visit.iterator !== 0) {
	      let iterator = visit.iterator;
	      let child;
	      while ((child = node.nodes[node.indexes[iterator]])) {
	        node.indexes[iterator] += 1;
	        if (!child[isClean]) {
	          child[isClean] = true;
	          stack.push(toStack(child));
	          return
	        }
	      }
	      visit.iterator = 0;
	      delete node.indexes[iterator];
	    }

	    let events = visit.events;
	    while (visit.eventIndex < events.length) {
	      let event = events[visit.eventIndex];
	      visit.eventIndex += 1;
	      if (event === CHILDREN) {
	        if (node.nodes && node.nodes.length) {
	          node[isClean] = true;
	          visit.iterator = node.getIterator();
	        }
	        return
	      } else if (this.listeners[event]) {
	        visit.visitors = this.listeners[event];
	        return
	      }
	    }
	    stack.pop();
	  }

	  walkSync(node) {
	    node[isClean] = true;
	    let events = getEvents(node);
	    for (let event of events) {
	      if (event === CHILDREN) {
	        if (node.nodes) {
	          node.each(child => {
	            if (!child[isClean]) this.walkSync(child);
	          });
	        }
	      } else {
	        let visitors = this.listeners[event];
	        if (visitors) {
	          if (this.visitSync(visitors, node.toProxy())) return
	        }
	      }
	    }
	  }

	  warnings() {
	    return this.sync().warnings()
	  }
	}

	LazyResult.registerPostcss = dependant => {
	  postcss = dependant;
	};

	lazyResult = LazyResult;
	LazyResult.default = LazyResult;

	Root.registerLazyResult(LazyResult);
	Document.registerLazyResult(LazyResult);
	return lazyResult;
}

var noWorkResult;
var hasRequiredNoWorkResult;

function requireNoWorkResult () {
	if (hasRequiredNoWorkResult) return noWorkResult;
	hasRequiredNoWorkResult = 1;

	let MapGenerator = requireMapGenerator();
	let parse = requireParse();
	const Result = requireResult();
	let stringify = requireStringify();
	let warnOnce = requireWarnOnce();

	class NoWorkResult {
	  get content() {
	    return this.result.css
	  }

	  get css() {
	    return this.result.css
	  }

	  get map() {
	    return this.result.map
	  }

	  get messages() {
	    return []
	  }

	  get opts() {
	    return this.result.opts
	  }

	  get processor() {
	    return this.result.processor
	  }

	  get root() {
	    if (this._root) {
	      return this._root
	    }

	    let root;
	    let parser = parse;

	    try {
	      root = parser(this._css, this._opts);
	    } catch (error) {
	      this.error = error;
	    }

	    if (this.error) {
	      throw this.error
	    } else {
	      this._root = root;
	      return root
	    }
	  }

	  get [Symbol.toStringTag]() {
	    return 'NoWorkResult'
	  }

	  constructor(processor, css, opts) {
	    css = css.toString();
	    this.stringified = false;

	    this._processor = processor;
	    this._css = css;
	    this._opts = opts;
	    this._map = undefined;
	    let root;

	    let str = stringify;
	    this.result = new Result(this._processor, root, this._opts);
	    this.result.css = css;

	    let self = this;
	    Object.defineProperty(this.result, 'root', {
	      get() {
	        return self.root
	      }
	    });

	    let map = new MapGenerator(str, root, this._opts, css);
	    if (map.isMap()) {
	      let [generatedCSS, generatedMap] = map.generate();
	      if (generatedCSS) {
	        this.result.css = generatedCSS;
	      }
	      if (generatedMap) {
	        this.result.map = generatedMap;
	      }
	    } else {
	      map.clearAnnotation();
	      this.result.css = map.css;
	    }
	  }

	  async() {
	    if (this.error) return Promise.reject(this.error)
	    return Promise.resolve(this.result)
	  }

	  catch(onRejected) {
	    return this.async().catch(onRejected)
	  }

	  finally(onFinally) {
	    return this.async().then(onFinally, onFinally)
	  }

	  sync() {
	    if (this.error) throw this.error
	    return this.result
	  }

	  then(onFulfilled, onRejected) {
	    if (process.env.NODE_ENV !== 'production') {
	      if (!('from' in this._opts)) {
	        warnOnce(
	          'Without `from` option PostCSS could generate wrong source map ' +
	            'and will not find Browserslist config. Set it to CSS file path ' +
	            'or to `undefined` to prevent this warning.'
	        );
	      }
	    }

	    return this.async().then(onFulfilled, onRejected)
	  }

	  toString() {
	    return this._css
	  }

	  warnings() {
	    return []
	  }
	}

	noWorkResult = NoWorkResult;
	NoWorkResult.default = NoWorkResult;
	return noWorkResult;
}

var processor;
var hasRequiredProcessor;

function requireProcessor () {
	if (hasRequiredProcessor) return processor;
	hasRequiredProcessor = 1;

	let Document = requireDocument();
	let LazyResult = requireLazyResult();
	let NoWorkResult = requireNoWorkResult();
	let Root = requireRoot();

	class Processor {
	  constructor(plugins = []) {
	    this.version = '8.5.3';
	    this.plugins = this.normalize(plugins);
	  }

	  normalize(plugins) {
	    let normalized = [];
	    for (let i of plugins) {
	      if (i.postcss === true) {
	        i = i();
	      } else if (i.postcss) {
	        i = i.postcss;
	      }

	      if (typeof i === 'object' && Array.isArray(i.plugins)) {
	        normalized = normalized.concat(i.plugins);
	      } else if (typeof i === 'object' && i.postcssPlugin) {
	        normalized.push(i);
	      } else if (typeof i === 'function') {
	        normalized.push(i);
	      } else if (typeof i === 'object' && (i.parse || i.stringify)) {
	        if (process.env.NODE_ENV !== 'production') {
	          throw new Error(
	            'PostCSS syntaxes cannot be used as plugins. Instead, please use ' +
	              'one of the syntax/parser/stringifier options as outlined ' +
	              'in your PostCSS runner documentation.'
	          )
	        }
	      } else {
	        throw new Error(i + ' is not a PostCSS plugin')
	      }
	    }
	    return normalized
	  }

	  process(css, opts = {}) {
	    if (
	      !this.plugins.length &&
	      !opts.parser &&
	      !opts.stringifier &&
	      !opts.syntax
	    ) {
	      return new NoWorkResult(this, css, opts)
	    } else {
	      return new LazyResult(this, css, opts)
	    }
	  }

	  use(plugin) {
	    this.plugins = this.plugins.concat(this.normalize([plugin]));
	    return this
	  }
	}

	processor = Processor;
	Processor.default = Processor;

	Root.registerProcessor(Processor);
	Document.registerProcessor(Processor);
	return processor;
}

var postcss_1;
var hasRequiredPostcss;

function requirePostcss () {
	if (hasRequiredPostcss) return postcss_1;
	hasRequiredPostcss = 1;

	let AtRule = requireAtRule();
	let Comment = requireComment();
	let Container = requireContainer();
	let CssSyntaxError = requireCssSyntaxError();
	let Declaration = requireDeclaration();
	let Document = requireDocument();
	let fromJSON = requireFromJSON();
	let Input = requireInput();
	let LazyResult = requireLazyResult();
	let list = requireList();
	let Node = requireNode();
	let parse = requireParse();
	let Processor = requireProcessor();
	let Result = requireResult();
	let Root = requireRoot();
	let Rule = requireRule();
	let stringify = requireStringify();
	let Warning = requireWarning();

	function postcss(...plugins) {
	  if (plugins.length === 1 && Array.isArray(plugins[0])) {
	    plugins = plugins[0];
	  }
	  return new Processor(plugins)
	}

	postcss.plugin = function plugin(name, initializer) {
	  let warningPrinted = false;
	  function creator(...args) {
	    // eslint-disable-next-line no-console
	    if (console && console.warn && !warningPrinted) {
	      warningPrinted = true;
	      // eslint-disable-next-line no-console
	      console.warn(
	        name +
	          ': postcss.plugin was deprecated. Migration guide:\n' +
	          'https://evilmartians.com/chronicles/postcss-8-plugin-migration'
	      );
	      if (process.env.LANG && process.env.LANG.startsWith('cn')) {
	        /* c8 ignore next 7 */
	        // eslint-disable-next-line no-console
	        console.warn(
	          name +
	            ': 里面 postcss.plugin 被弃用. 迁移指南:\n' +
	            'https://www.w3ctech.com/topic/2226'
	        );
	      }
	    }
	    let transformer = initializer(...args);
	    transformer.postcssPlugin = name;
	    transformer.postcssVersion = new Processor().version;
	    return transformer
	  }

	  let cache;
	  Object.defineProperty(creator, 'postcss', {
	    get() {
	      if (!cache) cache = creator();
	      return cache
	    }
	  });

	  creator.process = function (css, processOpts, pluginOpts) {
	    return postcss([creator(pluginOpts)]).process(css, processOpts)
	  };

	  return creator
	};

	postcss.stringify = stringify;
	postcss.parse = parse;
	postcss.fromJSON = fromJSON;
	postcss.list = list;

	postcss.comment = defaults => new Comment(defaults);
	postcss.atRule = defaults => new AtRule(defaults);
	postcss.decl = defaults => new Declaration(defaults);
	postcss.rule = defaults => new Rule(defaults);
	postcss.root = defaults => new Root(defaults);
	postcss.document = defaults => new Document(defaults);

	postcss.CssSyntaxError = CssSyntaxError;
	postcss.Declaration = Declaration;
	postcss.Container = Container;
	postcss.Processor = Processor;
	postcss.Document = Document;
	postcss.Comment = Comment;
	postcss.Warning = Warning;
	postcss.AtRule = AtRule;
	postcss.Result = Result;
	postcss.Input = Input;
	postcss.Rule = Rule;
	postcss.Root = Root;
	postcss.Node = Node;

	LazyResult.registerPostcss(postcss);

	postcss_1 = postcss;
	postcss.default = postcss;
	return postcss_1;
}

var sanitizeHtml_1;
var hasRequiredSanitizeHtml;

function requireSanitizeHtml () {
	if (hasRequiredSanitizeHtml) return sanitizeHtml_1;
	hasRequiredSanitizeHtml = 1;
	const htmlparser = /*@__PURE__*/ requireLib();
	const escapeStringRegexp = requireEscapeStringRegexp();
	const { isPlainObject } = requireIsPlainObject();
	const deepmerge = requireCjs();
	const parseSrcset = requireParseSrcset();
	const { parse: postcssParse } = requirePostcss();
	// Tags that can conceivably represent stand-alone media.
	const mediaTags = [
	  'img', 'audio', 'video', 'picture', 'svg',
	  'object', 'map', 'iframe', 'embed'
	];
	// Tags that are inherently vulnerable to being used in XSS attacks.
	const vulnerableTags = [ 'script', 'style' ];

	function each(obj, cb) {
	  if (obj) {
	    Object.keys(obj).forEach(function (key) {
	      cb(obj[key], key);
	    });
	  }
	}

	// Avoid false positives with .__proto__, .hasOwnProperty, etc.
	function has(obj, key) {
	  return ({}).hasOwnProperty.call(obj, key);
	}

	// Returns those elements of `a` for which `cb(a)` returns truthy
	function filter(a, cb) {
	  const n = [];
	  each(a, function(v) {
	    if (cb(v)) {
	      n.push(v);
	    }
	  });
	  return n;
	}

	function isEmptyObject(obj) {
	  for (const key in obj) {
	    if (has(obj, key)) {
	      return false;
	    }
	  }
	  return true;
	}

	function stringifySrcset(parsedSrcset) {
	  return parsedSrcset.map(function(part) {
	    if (!part.url) {
	      throw new Error('URL missing');
	    }

	    return (
	      part.url +
	      (part.w ? ` ${part.w}w` : '') +
	      (part.h ? ` ${part.h}h` : '') +
	      (part.d ? ` ${part.d}x` : '')
	    );
	  }).join(', ');
	}

	sanitizeHtml_1 = sanitizeHtml;

	// A valid attribute name.
	// We use a tolerant definition based on the set of strings defined by
	// html.spec.whatwg.org/multipage/parsing.html#before-attribute-name-state
	// and html.spec.whatwg.org/multipage/parsing.html#attribute-name-state .
	// The characters accepted are ones which can be appended to the attribute
	// name buffer without triggering a parse error:
	//   * unexpected-equals-sign-before-attribute-name
	//   * unexpected-null-character
	//   * unexpected-character-in-attribute-name
	// We exclude the empty string because it's impossible to get to the after
	// attribute name state with an empty attribute name buffer.
	const VALID_HTML_ATTRIBUTE_NAME = /^[^\0\t\n\f\r /<=>]+$/;

	// Ignore the _recursing flag; it's there for recursive
	// invocation as a guard against this exploit:
	// https://github.com/fb55/htmlparser2/issues/105

	function sanitizeHtml(html, options, _recursing) {
	  if (html == null) {
	    return '';
	  }
	  if (typeof html === 'number') {
	    html = html.toString();
	  }

	  let result = '';
	  // Used for hot swapping the result variable with an empty string in order to "capture" the text written to it.
	  let tempResult = '';

	  function Frame(tag, attribs) {
	    const that = this;
	    this.tag = tag;
	    this.attribs = attribs || {};
	    this.tagPosition = result.length;
	    this.text = ''; // Node inner text
	    this.openingTagLength = 0;
	    this.mediaChildren = [];

	    this.updateParentNodeText = function() {
	      if (stack.length) {
	        const parentFrame = stack[stack.length - 1];
	        parentFrame.text += that.text;
	      }
	    };

	    this.updateParentNodeMediaChildren = function() {
	      if (stack.length && mediaTags.includes(this.tag)) {
	        const parentFrame = stack[stack.length - 1];
	        parentFrame.mediaChildren.push(this.tag);
	      }
	    };
	  }

	  options = Object.assign({}, sanitizeHtml.defaults, options);
	  options.parser = Object.assign({}, htmlParserDefaults, options.parser);

	  const tagAllowed = function (name) {
	    return options.allowedTags === false || (options.allowedTags || []).indexOf(name) > -1;
	  };

	  // vulnerableTags
	  vulnerableTags.forEach(function (tag) {
	    if (tagAllowed(tag) && !options.allowVulnerableTags) {
	      console.warn(`\n\n⚠️ Your \`allowedTags\` option includes, \`${tag}\`, which is inherently\nvulnerable to XSS attacks. Please remove it from \`allowedTags\`.\nOr, to disable this warning, add the \`allowVulnerableTags\` option\nand ensure you are accounting for this risk.\n\n`);
	    }
	  });

	  // Tags that contain something other than HTML, or where discarding
	  // the text when the tag is disallowed makes sense for other reasons.
	  // If we are not allowing these tags, we should drop their content too.
	  // For other tags you would drop the tag but keep its content.
	  const nonTextTagsArray = options.nonTextTags || [
	    'script',
	    'style',
	    'textarea',
	    'option'
	  ];
	  let allowedAttributesMap;
	  let allowedAttributesGlobMap;
	  if (options.allowedAttributes) {
	    allowedAttributesMap = {};
	    allowedAttributesGlobMap = {};
	    each(options.allowedAttributes, function(attributes, tag) {
	      allowedAttributesMap[tag] = [];
	      const globRegex = [];
	      attributes.forEach(function(obj) {
	        if (typeof obj === 'string' && obj.indexOf('*') >= 0) {
	          globRegex.push(escapeStringRegexp(obj).replace(/\\\*/g, '.*'));
	        } else {
	          allowedAttributesMap[tag].push(obj);
	        }
	      });
	      if (globRegex.length) {
	        allowedAttributesGlobMap[tag] = new RegExp('^(' + globRegex.join('|') + ')$');
	      }
	    });
	  }
	  const allowedClassesMap = {};
	  const allowedClassesGlobMap = {};
	  const allowedClassesRegexMap = {};
	  each(options.allowedClasses, function(classes, tag) {
	    // Implicitly allows the class attribute
	    if (allowedAttributesMap) {
	      if (!has(allowedAttributesMap, tag)) {
	        allowedAttributesMap[tag] = [];
	      }
	      allowedAttributesMap[tag].push('class');
	    }

	    allowedClassesMap[tag] = classes;

	    if (Array.isArray(classes)) {
	      const globRegex = [];
	      allowedClassesMap[tag] = [];
	      allowedClassesRegexMap[tag] = [];
	      classes.forEach(function(obj) {
	        if (typeof obj === 'string' && obj.indexOf('*') >= 0) {
	          globRegex.push(escapeStringRegexp(obj).replace(/\\\*/g, '.*'));
	        } else if (obj instanceof RegExp) {
	          allowedClassesRegexMap[tag].push(obj);
	        } else {
	          allowedClassesMap[tag].push(obj);
	        }
	      });
	      if (globRegex.length) {
	        allowedClassesGlobMap[tag] = new RegExp('^(' + globRegex.join('|') + ')$');
	      }
	    }
	  });

	  const transformTagsMap = {};
	  let transformTagsAll;
	  each(options.transformTags, function(transform, tag) {
	    let transFun;
	    if (typeof transform === 'function') {
	      transFun = transform;
	    } else if (typeof transform === 'string') {
	      transFun = sanitizeHtml.simpleTransform(transform);
	    }
	    if (tag === '*') {
	      transformTagsAll = transFun;
	    } else {
	      transformTagsMap[tag] = transFun;
	    }
	  });

	  let depth;
	  let stack;
	  let skipMap;
	  let transformMap;
	  let skipText;
	  let skipTextDepth;
	  let addedText = false;

	  initializeState();

	  const parser = new htmlparser.Parser({
	    onopentag: function(name, attribs) {
	      if (options.onOpenTag) {
	        options.onOpenTag(name, attribs);
	      }

	      // If `enforceHtmlBoundary` is `true` and this has found the opening
	      // `html` tag, reset the state.
	      if (options.enforceHtmlBoundary && name === 'html') {
	        initializeState();
	      }

	      if (skipText) {
	        skipTextDepth++;
	        return;
	      }
	      const frame = new Frame(name, attribs);
	      stack.push(frame);

	      let skip = false;
	      const hasText = !!frame.text;
	      let transformedTag;
	      if (has(transformTagsMap, name)) {
	        transformedTag = transformTagsMap[name](name, attribs);

	        frame.attribs = attribs = transformedTag.attribs;

	        if (transformedTag.text !== undefined) {
	          frame.innerText = transformedTag.text;
	        }

	        if (name !== transformedTag.tagName) {
	          frame.name = name = transformedTag.tagName;
	          transformMap[depth] = transformedTag.tagName;
	        }
	      }
	      if (transformTagsAll) {
	        transformedTag = transformTagsAll(name, attribs);

	        frame.attribs = attribs = transformedTag.attribs;
	        if (name !== transformedTag.tagName) {
	          frame.name = name = transformedTag.tagName;
	          transformMap[depth] = transformedTag.tagName;
	        }
	      }

	      if (!tagAllowed(name) || (options.disallowedTagsMode === 'recursiveEscape' && !isEmptyObject(skipMap)) || (options.nestingLimit != null && depth >= options.nestingLimit)) {
	        skip = true;
	        skipMap[depth] = true;
	        if (options.disallowedTagsMode === 'discard' || options.disallowedTagsMode === 'completelyDiscard') {
	          if (nonTextTagsArray.indexOf(name) !== -1) {
	            skipText = true;
	            skipTextDepth = 1;
	          }
	        }
	      }
	      depth++;
	      if (skip) {
	        if (options.disallowedTagsMode === 'discard' || options.disallowedTagsMode === 'completelyDiscard') {
	          // We want the contents but not this tag
	          if (frame.innerText && !hasText) {
	            const escaped = escapeHtml(frame.innerText);
	            if (options.textFilter) {
	              result += options.textFilter(escaped, name);
	            } else {
	              result += escaped;
	            }
	            addedText = true;
	          }
	          return;
	        }
	        tempResult = result;
	        result = '';
	      }
	      result += '<' + name;

	      if (name === 'script') {
	        if (options.allowedScriptHostnames || options.allowedScriptDomains) {
	          frame.innerText = '';
	        }
	      }

	      const isBeingEscaped = skip && (options.disallowedTagsMode === 'escape' || options.disallowedTagsMode === 'recursiveEscape');
	      const shouldPreserveEscapedAttributes = isBeingEscaped && options.preserveEscapedAttributes;

	      if (shouldPreserveEscapedAttributes) {
	        each(attribs, function(value, a) {
	          result += ' ' + a + '="' + escapeHtml((value || ''), true) + '"';
	        });
	      } else if (!allowedAttributesMap || has(allowedAttributesMap, name) || allowedAttributesMap['*']) {
	        each(attribs, function(value, a) {
	          if (!VALID_HTML_ATTRIBUTE_NAME.test(a)) {
	            // This prevents part of an attribute name in the output from being
	            // interpreted as the end of an attribute, or end of a tag.
	            delete frame.attribs[a];
	            return;
	          }
	          // If the value is empty, check if the attribute is in the allowedEmptyAttributes array.
	          // If it is not in the allowedEmptyAttributes array, and it is a known non-boolean attribute, delete it
	          // List taken from https://html.spec.whatwg.org/multipage/indices.html#attributes-3
	          if (value === '' && (!options.allowedEmptyAttributes.includes(a)) &&
	            (options.nonBooleanAttributes.includes(a) || options.nonBooleanAttributes.includes('*'))) {
	            delete frame.attribs[a];
	            return;
	          }
	          // check allowedAttributesMap for the element and attribute and modify the value
	          // as necessary if there are specific values defined.
	          let passedAllowedAttributesMapCheck = false;
	          if (!allowedAttributesMap ||
	            (has(allowedAttributesMap, name) && allowedAttributesMap[name].indexOf(a) !== -1) ||
	            (allowedAttributesMap['*'] && allowedAttributesMap['*'].indexOf(a) !== -1) ||
	            (has(allowedAttributesGlobMap, name) && allowedAttributesGlobMap[name].test(a)) ||
	            (allowedAttributesGlobMap['*'] && allowedAttributesGlobMap['*'].test(a))) {
	            passedAllowedAttributesMapCheck = true;
	          } else if (allowedAttributesMap && allowedAttributesMap[name]) {
	            for (const o of allowedAttributesMap[name]) {
	              if (isPlainObject(o) && o.name && (o.name === a)) {
	                passedAllowedAttributesMapCheck = true;
	                let newValue = '';
	                if (o.multiple === true) {
	                  // verify the values that are allowed
	                  const splitStrArray = value.split(' ');
	                  for (const s of splitStrArray) {
	                    if (o.values.indexOf(s) !== -1) {
	                      if (newValue === '') {
	                        newValue = s;
	                      } else {
	                        newValue += ' ' + s;
	                      }
	                    }
	                  }
	                } else if (o.values.indexOf(value) >= 0) {
	                  // verified an allowed value matches the entire attribute value
	                  newValue = value;
	                }
	                value = newValue;
	              }
	            }
	          }
	          if (passedAllowedAttributesMapCheck) {
	            if (options.allowedSchemesAppliedToAttributes.indexOf(a) !== -1) {
	              if (naughtyHref(name, value)) {
	                delete frame.attribs[a];
	                return;
	              }
	            }

	            if (name === 'script' && a === 'src') {

	              let allowed = true;

	              try {
	                const parsed = parseUrl(value);

	                if (options.allowedScriptHostnames || options.allowedScriptDomains) {
	                  const allowedHostname = (options.allowedScriptHostnames || []).find(function (hostname) {
	                    return hostname === parsed.url.hostname;
	                  });
	                  const allowedDomain = (options.allowedScriptDomains || []).find(function(domain) {
	                    return parsed.url.hostname === domain || parsed.url.hostname.endsWith(`.${domain}`);
	                  });
	                  allowed = allowedHostname || allowedDomain;
	                }
	              } catch (e) {
	                allowed = false;
	              }

	              if (!allowed) {
	                delete frame.attribs[a];
	                return;
	              }
	            }

	            if (name === 'iframe' && a === 'src') {
	              let allowed = true;
	              try {
	                const parsed = parseUrl(value);

	                if (parsed.isRelativeUrl) {
	                  // default value of allowIframeRelativeUrls is true
	                  // unless allowedIframeHostnames or allowedIframeDomains specified
	                  allowed = has(options, 'allowIframeRelativeUrls')
	                    ? options.allowIframeRelativeUrls
	                    : (!options.allowedIframeHostnames && !options.allowedIframeDomains);
	                } else if (options.allowedIframeHostnames || options.allowedIframeDomains) {
	                  const allowedHostname = (options.allowedIframeHostnames || []).find(function (hostname) {
	                    return hostname === parsed.url.hostname;
	                  });
	                  const allowedDomain = (options.allowedIframeDomains || []).find(function(domain) {
	                    return parsed.url.hostname === domain || parsed.url.hostname.endsWith(`.${domain}`);
	                  });
	                  allowed = allowedHostname || allowedDomain;
	                }
	              } catch (e) {
	                // Unparseable iframe src
	                allowed = false;
	              }
	              if (!allowed) {
	                delete frame.attribs[a];
	                return;
	              }
	            }
	            if (a === 'srcset') {
	              try {
	                let parsed = parseSrcset(value);
	                parsed.forEach(function(value) {
	                  if (naughtyHref('srcset', value.url)) {
	                    value.evil = true;
	                  }
	                });
	                parsed = filter(parsed, function(v) {
	                  return !v.evil;
	                });
	                if (!parsed.length) {
	                  delete frame.attribs[a];
	                  return;
	                } else {
	                  value = stringifySrcset(filter(parsed, function(v) {
	                    return !v.evil;
	                  }));
	                  frame.attribs[a] = value;
	                }
	              } catch (e) {
	                // Unparseable srcset
	                delete frame.attribs[a];
	                return;
	              }
	            }
	            if (a === 'class') {
	              const allowedSpecificClasses = allowedClassesMap[name];
	              const allowedWildcardClasses = allowedClassesMap['*'];
	              const allowedSpecificClassesGlob = allowedClassesGlobMap[name];
	              const allowedSpecificClassesRegex = allowedClassesRegexMap[name];
	              const allowedWildcardClassesRegex = allowedClassesRegexMap['*'];
	              const allowedWildcardClassesGlob = allowedClassesGlobMap['*'];
	              const allowedClassesGlobs = [
	                allowedSpecificClassesGlob,
	                allowedWildcardClassesGlob
	              ]
	                .concat(allowedSpecificClassesRegex, allowedWildcardClassesRegex)
	                .filter(function (t) {
	                  return t;
	                });
	              if (allowedSpecificClasses && allowedWildcardClasses) {
	                value = filterClasses(value, deepmerge(allowedSpecificClasses, allowedWildcardClasses), allowedClassesGlobs);
	              } else {
	                value = filterClasses(value, allowedSpecificClasses || allowedWildcardClasses, allowedClassesGlobs);
	              }
	              if (!value.length) {
	                delete frame.attribs[a];
	                return;
	              }
	            }
	            if (a === 'style') {
	              if (options.parseStyleAttributes) {
	                try {
	                  const abstractSyntaxTree = postcssParse(name + ' {' + value + '}', { map: false });
	                  const filteredAST = filterCss(abstractSyntaxTree, options.allowedStyles);

	                  value = stringifyStyleAttributes(filteredAST);

	                  if (value.length === 0) {
	                    delete frame.attribs[a];
	                    return;
	                  }
	                } catch (e) {
	                  if (typeof window !== 'undefined') {
	                    console.warn('Failed to parse "' + name + ' {' + value + '}' + '", If you\'re running this in a browser, we recommend to disable style parsing: options.parseStyleAttributes: false, since this only works in a node environment due to a postcss dependency, More info: https://github.com/apostrophecms/sanitize-html/issues/547');
	                  }
	                  delete frame.attribs[a];
	                  return;
	                }
	              } else if (options.allowedStyles) {
	                throw new Error('allowedStyles option cannot be used together with parseStyleAttributes: false.');
	              }
	            }
	            result += ' ' + a;
	            if (value && value.length) {
	              result += '="' + escapeHtml(value, true) + '"';
	            } else if (options.allowedEmptyAttributes.includes(a)) {
	              result += '=""';
	            }
	          } else {
	            delete frame.attribs[a];
	          }
	        });
	      }
	      if (options.selfClosing.indexOf(name) !== -1) {
	        result += ' />';
	      } else {
	        result += '>';
	        if (frame.innerText && !hasText && !options.textFilter) {
	          result += escapeHtml(frame.innerText);
	          addedText = true;
	        }
	      }
	      if (skip) {
	        result = tempResult + escapeHtml(result);
	        tempResult = '';
	      }
	      frame.openingTagLength = result.length - frame.tagPosition;
	    },
	    ontext: function(text) {
	      if (skipText) {
	        return;
	      }
	      const lastFrame = stack[stack.length - 1];
	      let tag;

	      if (lastFrame) {
	        tag = lastFrame.tag;
	        // If inner text was set by transform function then let's use it
	        text = lastFrame.innerText !== undefined ? lastFrame.innerText : text;
	      }

	      if (options.disallowedTagsMode === 'completelyDiscard' && !tagAllowed(tag)) {
	        text = '';
	      } else if ((options.disallowedTagsMode === 'discard' || options.disallowedTagsMode === 'completelyDiscard') && ((tag === 'script') || (tag === 'style'))) {
	        // htmlparser2 gives us these as-is. Escaping them ruins the content. Allowing
	        // script tags is, by definition, game over for XSS protection, so if that's
	        // your concern, don't allow them. The same is essentially true for style tags
	        // which have their own collection of XSS vectors.
	        result += text;
	      } else if (!addedText) {
	        const escaped = escapeHtml(text, false);
	        if (options.textFilter) {
	          result += options.textFilter(escaped, tag);
	        } else {
	          result += escaped;
	        }
	      }
	      if (stack.length) {
	        const frame = stack[stack.length - 1];
	        frame.text += text;
	      }
	    },
	    onclosetag: function(name, isImplied) {
	      if (options.onCloseTag) {
	        options.onCloseTag(name, isImplied);
	      }

	      if (skipText) {
	        skipTextDepth--;
	        if (!skipTextDepth) {
	          skipText = false;
	        } else {
	          return;
	        }
	      }

	      const frame = stack.pop();
	      if (!frame) {
	        // Do not crash on bad markup
	        return;
	      }

	      if (frame.tag !== name) {
	        // Another case of bad markup.
	        // Push to stack, so that it will be used in future closing tags.
	        stack.push(frame);
	        return;
	      }

	      skipText = options.enforceHtmlBoundary ? name === 'html' : false;
	      depth--;
	      const skip = skipMap[depth];
	      if (skip) {
	        delete skipMap[depth];
	        if (options.disallowedTagsMode === 'discard' || options.disallowedTagsMode === 'completelyDiscard') {
	          frame.updateParentNodeText();
	          return;
	        }
	        tempResult = result;
	        result = '';
	      }

	      if (transformMap[depth]) {
	        name = transformMap[depth];
	        delete transformMap[depth];
	      }

	      if (options.exclusiveFilter) {
	        const filterResult = options.exclusiveFilter(frame);
	        if (filterResult === 'excludeTag') {
	          if (skip) {
	            // no longer escaping the tag since it's not added at all
	            result = tempResult;
	            tempResult = '';
	          }
	          // remove the opening tag from the result
	          result = result.substring(0, frame.tagPosition) + result.substring(frame.tagPosition + frame.openingTagLength);
	          return;
	        } else if (filterResult) {
	          result = result.substring(0, frame.tagPosition);
	          return;
	        }
	      }

	      frame.updateParentNodeMediaChildren();
	      frame.updateParentNodeText();

	      if (
	        // Already output />
	        options.selfClosing.indexOf(name) !== -1 ||
	        // Escaped tag, closing tag is implied
	        (isImplied && !tagAllowed(name) && [ 'escape', 'recursiveEscape' ].indexOf(options.disallowedTagsMode) >= 0)
	      ) {
	        if (skip) {
	          result = tempResult;
	          tempResult = '';
	        }
	        return;
	      }

	      result += '</' + name + '>';
	      if (skip) {
	        result = tempResult + escapeHtml(result);
	        tempResult = '';
	      }
	      addedText = false;
	    }
	  }, options.parser);
	  parser.write(html);
	  parser.end();

	  return result;

	  function initializeState() {
	    result = '';
	    depth = 0;
	    stack = [];
	    skipMap = {};
	    transformMap = {};
	    skipText = false;
	    skipTextDepth = 0;
	  }

	  function escapeHtml(s, quote) {
	    if (typeof (s) !== 'string') {
	      s = s + '';
	    }
	    if (options.parser.decodeEntities) {
	      s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	      if (quote) {
	        s = s.replace(/"/g, '&quot;');
	      }
	    }
	    // TODO: this is inadequate because it will pass `&0;`. This approach
	    // will not work, each & must be considered with regard to whether it
	    // is followed by a 100% syntactically valid entity or not, and escaped
	    // if it is not. If this bothers you, don't set parser.decodeEntities
	    // to false. (The default is true.)
	    s = s.replace(/&(?![a-zA-Z0-9#]{1,20};)/g, '&amp;') // Match ampersands not part of existing HTML entity
	      .replace(/</g, '&lt;')
	      .replace(/>/g, '&gt;');
	    if (quote) {
	      s = s.replace(/"/g, '&quot;');
	    }
	    return s;
	  }

	  function naughtyHref(name, href) {
	    // Browsers ignore character codes of 32 (space) and below in a surprising
	    // number of situations. Start reading here:
	    // https://www.owasp.org/index.php/XSS_Filter_Evasion_Cheat_Sheet#Embedded_tab
	    // eslint-disable-next-line no-control-regex
	    href = href.replace(/[\x00-\x20]+/g, '');
	    // Clobber any comments in URLs, which the browser might
	    // interpret inside an XML data island, allowing
	    // a javascript: URL to be snuck through
	    while (true) {
	      const firstIndex = href.indexOf('<!--');
	      if (firstIndex === -1) {
	        break;
	      }
	      const lastIndex = href.indexOf('-->', firstIndex + 4);
	      if (lastIndex === -1) {
	        break;
	      }
	      href = href.substring(0, firstIndex) + href.substring(lastIndex + 3);
	    }
	    // Case insensitive so we don't get faked out by JAVASCRIPT #1
	    // Allow more characters after the first so we don't get faked
	    // out by certain schemes browsers accept
	    const matches = href.match(/^([a-zA-Z][a-zA-Z0-9.\-+]*):/);
	    if (!matches) {
	      // Protocol-relative URL starting with any combination of '/' and '\'
	      if (href.match(/^[/\\]{2}/)) {
	        return !options.allowProtocolRelative;
	      }

	      // No scheme
	      return false;
	    }
	    const scheme = matches[1].toLowerCase();

	    if (has(options.allowedSchemesByTag, name)) {
	      return options.allowedSchemesByTag[name].indexOf(scheme) === -1;
	    }

	    return !options.allowedSchemes || options.allowedSchemes.indexOf(scheme) === -1;
	  }

	  function parseUrl(value) {
	    value = value.replace(/^(\w+:)?\s*[\\/]\s*[\\/]/, '$1//');
	    if (value.startsWith('relative:')) {
	      // An attempt to exploit our workaround for base URLs being
	      // mandatory for relative URL validation in the WHATWG
	      // URL parser, reject it
	      throw new Error('relative: exploit attempt');
	    }
	    // naughtyHref is in charge of whether protocol relative URLs
	    // are cool. Here we are concerned just with allowed hostnames and
	    // whether to allow relative URLs.
	    //
	    // Build a placeholder "base URL" against which any reasonable
	    // relative URL may be parsed successfully
	    let base = 'relative://relative-site';
	    for (let i = 0; (i < 100); i++) {
	      base += `/${i}`;
	    }

	    const parsed = new URL(value, base);

	    const isRelativeUrl = parsed && parsed.hostname === 'relative-site' && parsed.protocol === 'relative:';
	    return {
	      isRelativeUrl,
	      url: parsed
	    };
	  }
	  /**
	   * Filters user input css properties by allowlisted regex attributes.
	   * Modifies the abstractSyntaxTree object.
	   *
	   * @param {object} abstractSyntaxTree  - Object representation of CSS attributes.
	   * @property {array[Declaration]} abstractSyntaxTree.nodes[0] - Each object cointains prop and value key, i.e { prop: 'color', value: 'red' }.
	   * @param {object} allowedStyles       - Keys are properties (i.e color), value is list of permitted regex rules (i.e /green/i).
	   * @return {object}                    - The modified tree.
	   */
	  function filterCss(abstractSyntaxTree, allowedStyles) {
	    if (!allowedStyles) {
	      return abstractSyntaxTree;
	    }

	    const astRules = abstractSyntaxTree.nodes[0];
	    let selectedRule;

	    // Merge global and tag-specific styles into new AST.
	    if (allowedStyles[astRules.selector] && allowedStyles['*']) {
	      selectedRule = deepmerge(
	        allowedStyles[astRules.selector],
	        allowedStyles['*']
	      );
	    } else {
	      selectedRule = allowedStyles[astRules.selector] || allowedStyles['*'];
	    }

	    if (selectedRule) {
	      abstractSyntaxTree.nodes[0].nodes = astRules.nodes.reduce(filterDeclarations(selectedRule), []);
	    }

	    return abstractSyntaxTree;
	  }

	  /**
	   * Extracts the style attributes from an AbstractSyntaxTree and formats those
	   * values in the inline style attribute format.
	   *
	   * @param  {AbstractSyntaxTree} filteredAST
	   * @return {string}             - Example: "color:yellow;text-align:center !important;font-family:helvetica;"
	   */
	  function stringifyStyleAttributes(filteredAST) {
	    return filteredAST.nodes[0].nodes
	      .reduce(function(extractedAttributes, attrObject) {
	        extractedAttributes.push(
	          `${attrObject.prop}:${attrObject.value}${attrObject.important ? ' !important' : ''}`
	        );
	        return extractedAttributes;
	      }, [])
	      .join(';');
	  }

	  /**
	    * Filters the existing attributes for the given property. Discards any attributes
	    * which don't match the allowlist.
	    *
	    * @param  {object} selectedRule             - Example: { color: red, font-family: helvetica }
	    * @param  {array} allowedDeclarationsList   - List of declarations which pass the allowlist.
	    * @param  {object} attributeObject          - Object representing the current css property.
	    * @property {string} attributeObject.type   - Typically 'declaration'.
	    * @property {string} attributeObject.prop   - The CSS property, i.e 'color'.
	    * @property {string} attributeObject.value  - The corresponding value to the css property, i.e 'red'.
	    * @return {function}                        - When used in Array.reduce, will return an array of Declaration objects
	    */
	  function filterDeclarations(selectedRule) {
	    return function (allowedDeclarationsList, attributeObject) {
	      // If this property is allowlisted...
	      if (has(selectedRule, attributeObject.prop)) {
	        const matchesRegex = selectedRule[attributeObject.prop].some(function(regularExpression) {
	          return regularExpression.test(attributeObject.value);
	        });

	        if (matchesRegex) {
	          allowedDeclarationsList.push(attributeObject);
	        }
	      }
	      return allowedDeclarationsList;
	    };
	  }

	  function filterClasses(classes, allowed, allowedGlobs) {
	    if (!allowed) {
	      // The class attribute is allowed without filtering on this tag
	      return classes;
	    }
	    classes = classes.split(/\s+/);
	    return classes.filter(function(clss) {
	      return allowed.indexOf(clss) !== -1 || allowedGlobs.some(function(glob) {
	        return glob.test(clss);
	      });
	    }).join(' ');
	  }
	}

	// Defaults are accessible to you so that you can use them as a starting point
	// programmatically if you wish

	const htmlParserDefaults = {
	  decodeEntities: true
	};
	sanitizeHtml.defaults = {
	  allowedTags: [
	    // Sections derived from MDN element categories and limited to the more
	    // benign categories.
	    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element
	    // Content sectioning
	    'address', 'article', 'aside', 'footer', 'header',
	    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hgroup',
	    'main', 'nav', 'section',
	    // Text content
	    'blockquote', 'dd', 'div', 'dl', 'dt', 'figcaption', 'figure',
	    'hr', 'li', 'menu', 'ol', 'p', 'pre', 'ul',
	    // Inline text semantics
	    'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn',
	    'em', 'i', 'kbd', 'mark', 'q',
	    'rb', 'rp', 'rt', 'rtc', 'ruby',
	    's', 'samp', 'small', 'span', 'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr',
	    // Table content
	    'caption', 'col', 'colgroup', 'table', 'tbody', 'td', 'tfoot', 'th',
	    'thead', 'tr'
	  ],
	  // Tags that cannot be boolean
	  nonBooleanAttributes: [
	    'abbr', 'accept', 'accept-charset', 'accesskey', 'action',
	    'allow', 'alt', 'as', 'autocapitalize', 'autocomplete',
	    'blocking', 'charset', 'cite', 'class', 'color', 'cols',
	    'colspan', 'content', 'contenteditable', 'coords', 'crossorigin',
	    'data', 'datetime', 'decoding', 'dir', 'dirname', 'download',
	    'draggable', 'enctype', 'enterkeyhint', 'fetchpriority', 'for',
	    'form', 'formaction', 'formenctype', 'formmethod', 'formtarget',
	    'headers', 'height', 'hidden', 'high', 'href', 'hreflang',
	    'http-equiv', 'id', 'imagesizes', 'imagesrcset', 'inputmode',
	    'integrity', 'is', 'itemid', 'itemprop', 'itemref', 'itemtype',
	    'kind', 'label', 'lang', 'list', 'loading', 'low', 'max',
	    'maxlength', 'media', 'method', 'min', 'minlength', 'name',
	    'nonce', 'optimum', 'pattern', 'ping', 'placeholder', 'popover',
	    'popovertarget', 'popovertargetaction', 'poster', 'preload',
	    'referrerpolicy', 'rel', 'rows', 'rowspan', 'sandbox', 'scope',
	    'shape', 'size', 'sizes', 'slot', 'span', 'spellcheck', 'src',
	    'srcdoc', 'srclang', 'srcset', 'start', 'step', 'style',
	    'tabindex', 'target', 'title', 'translate', 'type', 'usemap',
	    'value', 'width', 'wrap',
	    // Event handlers
	    'onauxclick', 'onafterprint', 'onbeforematch', 'onbeforeprint',
	    'onbeforeunload', 'onbeforetoggle', 'onblur', 'oncancel',
	    'oncanplay', 'oncanplaythrough', 'onchange', 'onclick', 'onclose',
	    'oncontextlost', 'oncontextmenu', 'oncontextrestored', 'oncopy',
	    'oncuechange', 'oncut', 'ondblclick', 'ondrag', 'ondragend',
	    'ondragenter', 'ondragleave', 'ondragover', 'ondragstart',
	    'ondrop', 'ondurationchange', 'onemptied', 'onended',
	    'onerror', 'onfocus', 'onformdata', 'onhashchange', 'oninput',
	    'oninvalid', 'onkeydown', 'onkeypress', 'onkeyup',
	    'onlanguagechange', 'onload', 'onloadeddata', 'onloadedmetadata',
	    'onloadstart', 'onmessage', 'onmessageerror', 'onmousedown',
	    'onmouseenter', 'onmouseleave', 'onmousemove', 'onmouseout',
	    'onmouseover', 'onmouseup', 'onoffline', 'ononline', 'onpagehide',
	    'onpageshow', 'onpaste', 'onpause', 'onplay', 'onplaying',
	    'onpopstate', 'onprogress', 'onratechange', 'onreset', 'onresize',
	    'onrejectionhandled', 'onscroll', 'onscrollend',
	    'onsecuritypolicyviolation', 'onseeked', 'onseeking', 'onselect',
	    'onslotchange', 'onstalled', 'onstorage', 'onsubmit', 'onsuspend',
	    'ontimeupdate', 'ontoggle', 'onunhandledrejection', 'onunload',
	    'onvolumechange', 'onwaiting', 'onwheel'
	  ],
	  disallowedTagsMode: 'discard',
	  allowedAttributes: {
	    a: [ 'href', 'name', 'target' ],
	    // We don't currently allow img itself by default, but
	    // these attributes would make sense if we did.
	    img: [ 'src', 'srcset', 'alt', 'title', 'width', 'height', 'loading' ]
	  },
	  allowedEmptyAttributes: [
	    'alt'
	  ],
	  // Lots of these won't come up by default because we don't allow them
	  selfClosing: [ 'img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta' ],
	  // URL schemes we permit
	  allowedSchemes: [ 'http', 'https', 'ftp', 'mailto', 'tel' ],
	  allowedSchemesByTag: {},
	  allowedSchemesAppliedToAttributes: [ 'href', 'src', 'cite' ],
	  allowProtocolRelative: true,
	  enforceHtmlBoundary: false,
	  parseStyleAttributes: true,
	  preserveEscapedAttributes: false
	};

	sanitizeHtml.simpleTransform = function(newTagName, newAttribs, merge) {
	  merge = (merge === undefined) ? true : merge;
	  newAttribs = newAttribs || {};

	  return function(tagName, attribs) {
	    let attrib;
	    if (merge) {
	      for (attrib in newAttribs) {
	        attribs[attrib] = newAttribs[attrib];
	      }
	    } else {
	      attribs = newAttribs;
	    }

	    return {
	      tagName: newTagName,
	      attribs: attribs
	    };
	  };
	};
	return sanitizeHtml_1;
}

var sanitizeHtmlExports = /*@__PURE__*/ requireSanitizeHtml();
const sanitizeHtml = /*@__PURE__*/getDefaultExportFromCjs(sanitizeHtmlExports);

function cleanHtml(value, noHtml = false, allowedTags) {
    if (!value) {
        return '';
    }
    const params = {
        allowedTags: [
            'div',
            'p',
            'img',
            'a',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'section',
            'li',
            'ol',
            'ul',
            'br',
            'i',
            'span',
            'strong',
            'table',
            'tbody',
            'td',
            'tfoot',
            'th',
            'thead',
            'tr'
        ],
        allowedAttributes: {
            a: [
                'href',
                'name',
                'target',
                'class'
            ],
            img: [
                'src',
                'alt',
                'class'
            ]
        },
        selfClosing: [
            'img'
        ],
        allowedSchemes: [
            'http',
            'https',
            'ftp',
            'mailto',
            'data'
        ]
    };
    if (noHtml) {
        params.allowedTags = [];
        params.allowedAttributes = {};
    } else if (allowedTags) {
        params.allowedTags = allowedTags;
    }
    return sanitizeHtml(value, params);
}

export { BreakpointObserver, CancellablePromise, CursorPaginatedService, DESKTOP, DateFormatPattern, ListService, PHONE, PaginatedService, RGBAToHexA, RGBToHex, SearchService, TABLET, anyRGBToHex, clamp, cleanHtml, createDate, date, dateFormat, dateRangeComparison, debounce, extractAlphaValue, formatDateYYYYMMDD, generateReferralsQRCodeImage, getArrayOfHours, getBaseDomain, getDesignTokenValue, getWindowWidth, hexAToRGBA, hexToRGB, isAlpha, isDesktop, isEmail, isHex, isHexA, isOnMobileDevice, isPhone, isRGB, isRGBA, isTablet, isUrl, isValidValue, parseTime, shouldShowBanner, timestampToDateHour, toRoute, transformProductCategoryName };
